        // ── Engine shim (migration Phase 1) ──────────────────────────────────
        // The scoring engine is now a single HAND-OWNED module served at /engine.js
        // (loaded via <script src="/engine.js"> before this file), which sets
        // window.PivotaEngine. These bindings replace the formerly-inline engine
        // slices — edit engine math in lib/engine.js, never here. Destructures ALL
        // exports (over-provisioned) so no consumed symbol can ever be missing.
        const {
            setEngineContext, getOmegaSignals, BETA_RECENCY_BARS,
            MathLib, calculateBuyableLeaderScore, getUnifiedScore, computeRowTradeRating,
            TA_SECTION_WEIGHT, gradeCredit, convergenceFactors, BQS,
            computeVarsSeries, varsStateLabel, computeProxyRsRating, _coilQuality,
            detectVcpBase, computeRsPower, computeRsLineSeries, computeRsLineSignals,
            computeRsLineSignalSeries, computeRsRawScore, rsRawToPercentile,
            calculateMarketQualityMetrics, getChannelStyle, detectChannelOmega, detectStage,
            computeRsBreadthBits, lastWhere, evaluateMinerviniLite, detectVolumeOutlierLite,
            detectBetaSignal, buildSpyCtx, computeSignalFactors, getTierFromScore,
            computeAvwapDefense, computeFormingState, getRmvZone, getExtensionStatus,
            detectVolumeSignalLite, scoreScreenerCandidate, growthLevelScore,
            accelerationScore, gaugeScore, INDUSTRY_GROUP_SECTORS, classifyIndustry,
        } = window.PivotaEngine;

        // ── Phase 5 extracted leaf modules ───────────────────────────────────
        import { Logger } from './logger.js';
        import { EXT_DOT_THRESHOLD, VOL_COLORS, HUGE_RATIO, TICKER_CACHE_TTL } from './constants.js';
        import { formatVol, formatBig, formatDollarBig, formatPctHTML } from './format.js';
        import { QS } from './qs.js';
        import { Portfolio } from './portfolio.js';
        import { DebugPanel } from './debug-panel.js';
        import { ui } from './ui.js';
        import { ScreenerThrottle } from './screener-throttle.js';
        import { applyScreenerFilters, setScreenerPreset, FilterUI, SCR_FILTER_CATALOG, configure as configureScreenerFilters } from './screener-filters.js';
        import { encodeScreen, decodeScreen } from './screen/url-codec.js';
        import { SavedScreens, configure as configureSavedScreens } from './saved-screens.js';
        import { RowNav, configure as configureRowNav } from './row-nav.js';
        import { setHubTab, configure as configureHub } from './hub.js';
        import { Watchlist, configure as configureWatchlist } from './watchlist.js';
        import { Auth, configure as configureAuth } from './auth.js';
        import { state } from './state.js';
        import { engineState } from './engineState.js';
        import { AppState, configure as configureAppState } from './app-state.js';
        import { TaskCoordinator } from './task-coordinator.js';
        if (typeof window !== 'undefined') window.engineState = engineState; // debug/verification surface (data only)

        // ui (DOM map) extracted to a module (imported at top).

        // --- GLOBALS & CHART STATE ---
        // ═══════════════════════════════════════════════════════════════════════════
        // STRUCTURED LOGGER — leveled, context-aware logging (TODO-4). Replaces silent
        // `catch (e) {}` swallowing on critical paths with logged, contextual errors so
        // failures are visible and debuggable. Levels: error < warn < info < debug.
        // Set Logger.level = 'debug' in the console to see everything; default 'warn'
        // keeps normal output quiet. A ring buffer keeps the last 200 events so a future
        // debug panel (TODO-8) can display them without re-instrumenting anything.
        // ═══════════════════════════════════════════════════════════════════════════
        // Logger extracted to src/logger.js (imported above; self-registers window.Logger).

        // ═══════════════════════════════════════════════════════════════════════════
        // DATA CONTRACTS (TODO-6) — documented interfaces for the core scoring path.
        // These JSDoc typedefs are the single source of truth for the shapes that flow
        // between the engine functions (calculateBuyableLeaderScore, BQS.compute,
        // detectSmartSignalLite, computeRsPower) and the screener/dashboard. Documentation
        // only (no runtime cost), but they make the system far easier to extend — in
        // particular they define the inputs the future Young Leader Synthesis Engine
        // (TODO-7) will consume. Keep these in sync when a function's shape changes.
        //
        /**
         * @typedef {Object} TickerMetrics
         * Normalized per-ticker inputs fed to the scoring engine. Percent fields are
         * whole-number percents (e.g. dist50 = 3.2 means +3.2%).
         * @property {number}  [price]            Last price
         * @property {number}  [low]              Day low (stop math)
         * @property {number}  [stage]            Weinstein stage (1–4)
         * @property {string}  [stageLabel]       e.g. "Stage 2A"
         * @property {number}  [rmv]              Range-momentum-volatility (0–100, lower = tighter)
         * @property {number}  [channelStage]     Channel position bucket (1–6)
         * @property {number}  [channelPosition]  0–1 position within the channel
         * @property {string}  [omegaSignal]      'OMEGA20' | 'OMEGA55' | 'OMEGA_EXIT' | 'NONE'
         * @property {string}  [volumeSignal]     Latest volume event tag
         * @property {number}  [epsScore]         Earnings-acceleration score
         * @property {number}  [salesScore]       Sales-acceleration score
         * @property {number}  [groupRank]        Industry-group rank (smaller = stronger)
         * @property {number}  [groupRankTotal]   Group size for rank context
         * @property {number}  [stopDistPct]      Distance to stop (%)
         * @property {number}  [atrMult]          ATR% multiple from 50-MA (extension)
         * @property {number}  [atrPct]           14d ATR as % of price
         * @property {number}  [dollarVolume]     price × volume
         * @property {boolean} [mvPassed]         Passed the Minervini trend template
         * @property {number}  [rsProxy]          RS rating (0–99) — engine backbone
         * @property {number}  [rvol]             Relative volume vs 50-day average
         * @property {number}  [pct3m]            3-month % change
         * @property {number}  [pct6m]            6-month % change
         * @property {number}  [dist9]            % distance from 9-EMA
         * @property {number}  [dist50]           % distance from 50-MA
         * @property {number}  [dist200]          % distance from 200-MA
         * @property {number}  [sma200Trend]      200-MA slope (% over ~1mo)
         * @property {number}  [upDownVolRatio]   Accumulation/distribution volume ratio
         * @property {number}  [accumulationDays] Accumulation days in base
         * @property {number}  [distributionDays] Distribution days in base
         * @property {boolean} [hasAlpha]         Alpha (prime entry) present
         * @property {boolean} [hasBeta]          Beta (reclaim) present
         * @property {number}  [alphaDays]        Days since last Alpha
         * @property {number}  [betaDays]         Days since last Beta
         */
        /**
         * @typedef {Object} BuyableLeaderScore  Output of calculateBuyableLeaderScore.
         * @property {number}   finalScore   0–1000 composite (headline SCORE)
         * @property {number}   score100     0–100 pre-cap raw score
         * @property {number}   scoreStruct  Trend/structure sub-score (×10 scale)
         * @property {number}   scoreLiq     Demand/liquidity sub-score (×10 scale)
         * @property {number}   scoreGeom    Buyability/entry sub-score (×10 scale)
         * @property {number}   cap          Active score cap (×10)
         * @property {string[]} caps         Reasons a cap was applied
         */
        /**
         * @typedef {Object} BaseQuality  Output of BQS.compute (Breakout Quality Score).
         * @property {number} grade      0–100 display grade
         * @property {('A+'|'A'|'B'|'C'|'D'|'F')} tier
         * @property {string} tierLabel  e.g. "Strong base"
         * @property {Object} base       Per-component base breakdown
         * @property {Object} ctx        Contextual multiplier breakdown
         * @property {Object} bonus      Confluence bonuses
         * @property {Object} pen        Penalties
         * @property {Object} pattern    Detected base pattern
         * @property {Object} failed     Failed-breakout detection
         * @property {{afterMult:number, composite:number}} raw
         */
        /**
         * @typedef {Object} SmartSignal  Output of detectSmartSignalLite.
         * @property {string}      signal     Latest signal type, or 'None'
         * @property {boolean}     hasAlpha
         * @property {boolean}     hasBeta
         * @property {number|null} alphaDays  Days since last (recent-window) Alpha
         * @property {number|null} betaDays   Days since last (recent-window) Beta
         */
        // {RsSignals} and {RsPower} are documented above computeRsPower's definition.
        // ═══════════════════════════════════════════════════════════════════════════

        // ═══════════════════════════════════════════════════════════════════════════
        // APP STATE — single source of truth for all mutable module-level state.
        // Grouped by concern. These remain `let` bindings so every existing read/write site
        // works unchanged; they are additionally mirrored onto window.AppState (below) purely
        // for debugging/inspection. Constants (VARS_*, EXT_DOT_THRESHOLD, etc.) stay separate.
        // ═══════════════════════════════════════════════════════════════════════════

        // ── Loaded ticker ───────────────────────────────────────────────────────────
        // state.currentTicker / state.currentTickerName now live in src/state.js (migrated off app.js
        // `let`s so AppState's setters, in src/app-state.js, write through them across ESM) — Step 13.
        let currentTickerDescription = '';   // business description (Polygon reference)
        let currentTickerSic = '';           // SIC industry description
        let currentTickerClassification = null; // sector/industry-group classification
        let currentTickerShares = null;      // shares outstanding (Polygon weighted/share-class) — supply proxy
        let currentTickerMarketCap = null;   // market cap (Polygon, or shares × last close)
        let currentTickerAdrPct = null;      // latest ADR% → drives volatility-based sizing
        let currentTickerAvgVol50 = null;    // 50-day avg volume of the loaded ticker
        let currentTickerRefreshTimer = null;// live-refresh interval handle
        let currentScoreInput = null;        // last metrics object fed to the engine
        // state.currentChartRange now lives in src/state.js (migrated off an app.js `let`) — Step 13.
        let intradayCache = {};              // range → intraday bars cache
        const tickerHistoryCache = {};       // ticker → { results, ts } daily-history cache
        // TICKER_CACHE_TTL extracted to src/constants.js (imported at top).
        let priceChartInstance;              // Chart.js instance (destroyed/recreated per load)
        // Chart.js is loaded on demand the first time a chart is drawn, so the initial page load
        // doesn't pay for ~200KB it may never use. Cached after the first fetch.
        let __chartJsPromise = null;
        function ensureChartJs() {
            if (typeof Chart !== 'undefined') return Promise.resolve();
            if (__chartJsPromise) return __chartJsPromise;
            __chartJsPromise = new Promise((resolve, reject) => {
                const sc = document.createElement('script');
                sc.src = 'https://cdn.jsdelivr.net/npm/chart.js';
                sc.onload = () => resolve();
                sc.onerror = () => { __chartJsPromise = null; reject(new Error('Chart.js failed to load')); };
                document.head.appendChild(sc);
            });
            return __chartJsPromise;
        }
        let volumeClass = [];                // per-bar vol classification aligned to engineState.cachedDataResults
        let volumeSignals = [];              // notable vol events (PPVs + outliers)
        let extDots = [];                    // chart indices flagged as extended (grey dots)
        let wedgeSignals = [];               // Alpha/Beta smart-signal markers
        // Per-layer visibility for the on-chart alert markers, toggled by the header pills
        // (Alpha/Beta/Delta/Ω Prime/Ω Nova/Ω Titan). true = hidden from the chart.
        // Alpha / Beta / Delta ON by default (the primary signals); the 3 Omega layers OFF by
        // default (toggle via the header pills) — Ω-Exit sits in the same spot below the line as
        // Alpha, so default-on omega buried the Alpha dots. All show their FULL history when on.
        const chartLayerHidden = { alpha: false, beta: false, delta: false, oprime: true, onova: true, otitan: true, ext: false };
        let deltaDots = [];              // bars where the RS line made a new high (vs benchmark)
        let lastUnifiedScore = null;         // cached engine output (assistant reconciles caps)
        let lastMarketTape = null;           // cached breadth verdict string for the CONF context layer

        // ── Screener ────────────────────────────────────────────────────────────────
        let scannedResults = [];             // legacy ETF-scan results
        let screenerUniverse = [];           // full ticker universe to scan
        let screenerSnapshot = [];           // snapshot universe (price/volume only)
        let screenerSortKey = 'score';       // active sort column
        let screenerSortDir = 'desc';        // active sort direction
        // screenerRunning / screenerPaused migrated to src/state.js (state.screenerRunning / .screenerPaused).
        let screenerIndex = 0;               // scan cursor
        let screenerRunId = 0;               // monotonic id to cancel stale scans
        let screenerAutoTimer = null;        // auto-refresh interval handle
        let screenerLastSync = null;         // last successful scan timestamp
        let industryGroupStats = new Map();  // group → {rank,total,perf6m,breadth,...}
        let groupRankingDimension = 'industryGroup'; // 'industryGroup' | 'sector'
        let groupRankingSort = 'rotation';   // group-ranking sort column
        let groupRankingData = [];           // computed group-ranking rows

        // ── Market structure ────────────────────────────────────────────────────────
        let spyBarsCache = null;             // session cache of SPY daily bars (VARS benchmark)
        const VARS_WINDOW = 10;              // VARS return lookback (bars) — fast breakout read
        const VARS_ATR_LEN = 14;             // VARS fixed ATR denominator length
        let marketStructureIndices = {};     // ticker → {closes,highs,lows} for index breadth
        let marketStructureIndicesLoaded = false;
        let serverMarketStructure = null;    // the SERVER-computed regime shipped in the snapshot (single source
                                             // of truth). The client renders it + scores with it instead of
                                             // recomputing its own — so the dashboard tier matches the screener.
        let serverGroupRotation = null;      // {industryGroup:[...], sector:[...]} — the SERVER-computed group-RS
                                             // rotation shipped in the snapshot; the client renders it instead of
                                             // re-aggregating the whole universe (Step 4 dedup).
        let _ratingMktEpoch = 0;             // bumped when market structure changes → invalidates row-rating cache
        let lastRsDivergence = null;         // cached RS 3v10-EMA divergence (leadership velocity)
        let lastVcpBase = null;              // cached VCP + base-depth read (loaded ticker)

        // ── Centralized AppState (TODO-1) ────────────────────────────────────────
        // Extracted to src/app-state.js (Phase 5 Step 13 — final extraction). It is the controlled
        // write-path for the cross-cutting, UI-reactive globals: setCurrentTicker/…Name/ChartRange
        // (now writing state.currentTicker / state.currentTickerName / state.currentChartRange) + the screener/hub
        // setters + snapshot()/dump(). The old DOM CustomEvent pub/sub (_bus/subscribe/notify) had
        // ZERO subscribers and was dropped; setters keep the one observable effect (a Logger.debug).
        //
        // Registered on window here (the shell) for tests/devtools, keeping app-state.js browser-
        // global-free. snapshot()'s debug-only extras are shell-owned hot-path lets (screenerIndex,
        // groupRanking*, spyBarsCache, …) injected via getExtras rather than migrated — read only by
        // the console dump, so a setter/event on them would be cost without benefit.
        window.AppState = AppState;
        configureAppState({ getExtras: () => ({
            screenerIndex, groupRankingDimension, groupRankingSort,
            spyBarsCached: !!spyBarsCache, marketStructureIndicesLoaded, lastUnifiedScore
        }) });
        if (typeof window !== 'undefined') window.appState = { configure: configureAppState }; // DI verification seam (tests)

        // (etfList / etfDict removed — the 72-ETF sector scan now runs server-side in lib/marketdata.js)
        // Constants (EXT_DOT_THRESHOLD/VOL_COLORS/HUGE_RATIO) -> src/constants.js;
        // format helpers (formatVol/formatBig/formatDollarBig/formatPctHTML) -> src/format.js (imported at top).

        // ═══════════════════════════════════════════════════════════════════════
        // CALC NAMESPACE  (Phase 1 modularization — pure, no DOM, no globals)
        // Indicator math + scoring engine. Behavior identical to the prior inline
        // definitions; legacy names are aliased below for backward compatibility.
        // ═══════════════════════════════════════════════════════════════════════

        // Backward-compatible aliases (existing call sites untouched).

        // Per-section weight multiplier on the HEADLINE rating (not the per-section /5).
        // Shifts emphasis toward Conviction + Context, away from Trend + Entry. Both raters
        // read this same map so the screener column and the panel stay in agreement.

        // Graded credit in [0,1] via a clamped linear ramp from failAt → passAt. If
        // failAt > passAt the ramp is inverted (lower input = better — extension, RMV,
        // distance-below-high). Used to replace pass/caution/fail cliffs on continuous
        // factors so RS 88 outscores RS 71 and 5.7× extension isn't a flat half-credit.

        // ═══════════════════════════════════════════════════════════════════════════
        // SHARED CONVERGENCE FACTORS
        // One classifier for BOTH the screener Rating column and the loaded-ticker Trade
        // Assistant, so the two never drift. Returns scored rows (pass/caution/fail) for:
        //   • conviction[] — Breakout, Leader, Tailwind, RSI aligned, Vol confluence, RS
        //     thrust (its own first-class Trade-Assistant section, equal-weighted; RS
        //     thrust is HEAVY — direction-over-level rhythm).
        //   • context[]    — Sector rotation + New-leadership "alert rhythm" (high Delta /
        //     fresh-Δ / RS-NH breadth = leadership broadening).
        // Pure: no DOM. Factors with no data are simply omitted (graceful degrade).
        // ═══════════════════════════════════════════════════════════════════════════

        // ═══════════════════════════════════════════════════════════════════════════
        // BREAKOUT QUALITY SCORE (BQS)
        // A standalone, self-contained scoring system shown alongside the existing engine.
        // Base Score (100 pts, 6 components) × Contextual Multiplier (market·sector·breadth·
        // synergy, compounding) + Confluence Bonuses − Tiered Penalties.
        // All scoring functions are PURE (no DOM, no globals); rendering is separated.
        // Two new detectors live here too: pattern classification and failed-breakout counting.
        // ═══════════════════════════════════════════════════════════════════════════

        // Renders the BQS breakdown into #bqsPanel. Display-only; reads the object from BQS.compute.
        function renderBQS(bqs) {
            const head = document.getElementById('bqsVerdict');
            const tagsEl = document.getElementById('bqsTags');
            const body = document.getElementById('bqsBody');
            if (!head || !body || !bqs) return;

            const grade = bqs.grade;
            const coil = bqs.coil;

            // ── Score pill: the PURE coil quality, coloured by quality (no tier label) ──
            const gradeCls = grade >= 75 ? 'border-emerald-500/50 bg-emerald-900/30 text-emerald-300'
                : grade >= 60 ? 'border-teal-500/50 bg-teal-900/30 text-teal-300'
                : grade >= 45 ? 'border-amber-500/50 bg-amber-900/30 text-amber-300'
                : 'border-rose-500/50 bg-rose-900/30 text-rose-300';
            head.className = `text-xs font-black px-3 py-1 rounded-lg border ${gradeCls}`;
            head.innerHTML = `<span class="tabular-nums">${grade}</span><span class="opacity-60 text-[10px]">/100</span>`;

            // ── Tags: SHAPE · TIMING (a few words = what it is + whether to act) ──
            const mode  = coil ? coil.vcpMode : null;
            const stage = coil ? coil.stage   : null;
            const poleRaw  = (coil && coil.coilRaw) ? coil.coilRaw.runUp : null;
            const tightest = coil ? coil.tightestPct : null;
            const days     = bqs.pattern ? bqs.pattern.baseLengthDays : null;
            const shapeCls = 'text-fuchsia-300 border-fuchsia-500/40 bg-fuchsia-900/20';
            const shapeTag = (() => {
                if (mode === 'ma-ride') return { label: 'MA RIDE', cls: 'text-teal-300 border-teal-500/40 bg-teal-900/20' };
                if (poleRaw != null && poleRaw >= 80 && tightest != null && tightest <= 10) return { label: 'HIGH TIGHT FLAG', cls: shapeCls };
                if (tightest != null && tightest <= 12) return { label: 'TIGHT FLAG', cls: shapeCls };
                if (poleRaw != null && poleRaw < 18 && days != null && days >= 30) return { label: 'FLAT BASE', cls: shapeCls };
                return { label: 'BULL FLAG', cls: shapeCls };
            })();
            const timingMap = {
                forming:  { label: 'BUILDING',     cls: 'text-slate-400 border-slate-600/40 bg-slate-800/40' },
                coiling:  { label: 'READY',        cls: 'text-emerald-300 border-emerald-500/40 bg-emerald-900/20' },
                breakout: { label: 'BREAKING OUT', cls: 'text-blue-300 border-blue-500/40 bg-blue-900/20' },
                extended: { label: 'EXTENDED',     cls: 'text-amber-300 border-amber-500/40 bg-amber-900/20' }
            };
            const timingTag = timingMap[stage] || null;
            const pill = (t) => t ? `<span class="px-2 py-0.5 rounded-md text-[10px] font-extrabold uppercase tracking-wide border ${t.cls}">${t.label}</span>` : '';
            tagsEl.innerHTML = (mode ? pill(shapeTag) : '') + (timingTag ? pill(timingTag) : '');

            // ── Fallback when there is no coil (base too short) ──
            if (!coil || !coil.coilScores) {
                body.innerHTML = `<div class="text-[11px] text-slate-500 italic py-1">Base too short for a coil breakdown — score shown above.</div>`;
                return;
            }

            const S = coil.coilScores, R = coil.coilRaw || {};
            const pct = (v, d = 0) => (v == null || !Number.isFinite(v)) ? '—' : `${v.toFixed(d)}%`;
            const barCol = (f) => f >= 0.75 ? 'bg-emerald-400' : f >= 0.5 ? 'bg-teal-400' : f >= 0.3 ? 'bg-amber-400' : 'bg-rose-400';
            const txtCol = (f) => f >= 0.75 ? 'text-emerald-300' : f >= 0.5 ? 'text-teal-300' : f >= 0.3 ? 'text-amber-300' : 'text-rose-300';

            // ── How the score is comprised — the real coil parameters, scored ──
            const rows = [];
            if (mode === 'ma-ride') {
                rows.push(['Trend',      R.sma50Rising ? 'rising 50' : 'flat 50', S.advance,    R.sma50Rising ? 'price riding a rising 50' : 'MA not yet rising']);
                rows.push(['Give-back',  pct(coil.depthPct, 0),                    S.giveBack,   (coil.depthPct != null && coil.depthPct <= 15) ? 'shallow dip to the line' : 'deeper pullback']);
                rows.push(['At MA',      pct(R.minDistMA, 1),                      S.maEvidence, (R.minDistMA != null && R.minDistMA <= 3) ? 'sitting on the MA' : 'away from the MA']);
                rows.push(['Vol dry-up', (R.rvol != null ? R.rvol.toFixed(1) + '×' : '—'), S.dryUp, coil.volumeDryUp ? 'supply drying' : 'volume flat']);
                rows.push(['Near high',  pct(R.belowHighPct, 0),                   S.nearHigh,   (R.belowHighPct != null && R.belowHighPct <= 8) ? 'near 52-wk high' : 'mid prior range']);
            } else {
                rows.push(['Pole',       (R.runUp != null ? '+' + R.runUp + '%' : '—'), S.advance, (R.runUp != null && R.runUp >= 25) ? 'strong prior thrust' : 'modest advance']);
                rows.push(['Give-back',  pct(coil.depthPct, 0),                    S.giveBack,    (coil.depthPct != null && coil.depthPct <= 20) ? 'shallow — held tight' : 'deep pullback']);
                rows.push(['Tightness',  pct(coil.tightestPct, 0),                 S.tightEvidence, coil.higherLows ? 'tightest · higher lows' : 'not yet tight']);
                rows.push(['Vol dry-up', (R.rvol != null ? R.rvol.toFixed(1) + '×' : '—'), S.dryUp, coil.volumeDryUp ? 'supply drying into coil' : 'no volume dry-up']);
                rows.push(['Near high',  pct(R.belowHighPct, 0),                   S.nearHigh,    (R.belowHighPct != null && R.belowHighPct <= 8) ? 'under 52-wk high' : 'mid prior range']);
            }
            const rowHtml = rows.map(([nm, val, sc, note]) => {
                const f = Math.max(0, Math.min(1, sc || 0));
                return `<div class="grid items-center gap-2 py-[1.5px]" style="grid-template-columns:84px 1fr 56px 1.1fr">
                    <span class="text-[10px] text-slate-400 font-semibold">${nm}</span>
                    <div class="h-[7px] bg-slate-800/70 rounded-full overflow-hidden border border-slate-800"><div class="h-full ${barCol(f)} rounded-full" style="width:${Math.max(3, f * 100)}%"></div></div>
                    <span class="text-[11px] font-extrabold tabular-nums text-right ${txtCol(f)}">${val}</span>
                    <span class="text-[9px] text-slate-500 truncate" title="${note}">${note}</span>
                </div>`;
            }).join('');

            // ── CONS showcase — risk penalties + any weak parameter (informational; never moves the score) ──
            const cons = [];
            ((bqs.pen && bqs.pen.list) ? bqs.pen.list : []).forEach(p => {
                if (/active risk factors/i.test(p.label)) return; // compounding meta — skip
                cons.push({ label: p.label, reason: '' });
            });
            // Macro late-stage gate — surfaced as a con only when it actually trims the score.
            const _g12 = (R && R.gain12 != null) ? R.gain12 : null;
            const _mf = (R && R.macroFactor != null) ? R.macroFactor : 1;
            if (_g12 != null && _mf <= 0.97) cons.push({ label: 'Late-stage advance', reason: `+${_g12}% over 12 mo` });
            rows.forEach(([nm, val, sc]) => { if ((sc || 0) < 0.5) cons.push({ label: nm + ' weak', reason: (val && val !== '—') ? val : '' }); });

            const consHtml = cons.length
                ? `<div class="mt-2 rounded-lg border border-rose-700/30 bg-rose-950/20 px-2.5 py-1.5">
                    <div class="flex items-center gap-1.5 mb-1">
                        <span class="text-[9px] font-extrabold uppercase tracking-wider text-rose-400">⚠ Cons · what could go wrong</span>
                        <span class="text-[9px] font-bold text-rose-300 bg-rose-900/40 border border-rose-700/40 rounded-full px-1.5">${cons.length}</span>
                    </div>
                    ${cons.map(c => `<div class="flex items-baseline gap-1.5 py-[1px] text-[10.5px]"><span class="text-rose-400 text-[9px]">▸</span><span class="font-semibold text-rose-200/90">${c.label}</span>${c.reason ? `<span class="text-slate-500 text-[9.5px]">— ${c.reason}</span>` : ''}</div>`).join('')}
                  </div>`
                : `<div class="mt-2 rounded-lg border border-emerald-700/30 bg-emerald-950/20 px-2.5 py-1.5"><span class="text-[10.5px] font-bold text-emerald-300">✓ Clean setup — no cons flagged.</span></div>`;

            // ── Thin footer: mode · where in the coil · width/days · failed B/O ──
            const dp = coil.distToPivotPct;
            const pivTxt = (dp == null) ? '' : dp >= 0 ? `coiling ${dp.toFixed(1)}% under pivot` : `${Math.abs(dp).toFixed(1)}% past pivot`;
            const pivPx = Number.isFinite(coil.pivot) ? ` $${coil.pivot >= 1000 ? Math.round(coil.pivot) : coil.pivot.toFixed(2)}` : '';
            const wide = (bqs.pattern && bqs.pattern.tightnessPct != null) ? ` · ${bqs.pattern.tightnessPct.toFixed(0)}% wide · ${bqs.pattern.baseLengthDays}d` : '';
            const failTxt = (bqs.failed && bqs.failed.count) ? ` · ${bqs.failed.count} failed B/O` : '';
            const footer = `<div class="pt-1.5 mt-1.5 border-t border-slate-800 text-[9px] text-slate-500"><span class="text-slate-400 font-semibold">${mode || 'no coil'}</span>${pivTxt ? ' · ' + pivTxt + pivPx : ''}${wide}${failTxt}</div>`;

            body.innerHTML = rowHtml + consHtml + footer;
        }

        // ═══════════════════════════════════════════════════════════════════════════
        // WATCHLIST — persistent watchlist + (later) smart alerts.
        // Client-side only (localStorage, versioned keys). All state lives behind this
        // namespace via a thin storage adapter, so a future backend swap touches one place.
        // Phase A: storage + add/remove from screener + watchlist table.
        // Mirrored to window.Watchlist for debugging/inspection.
        // ═══════════════════════════════════════════════════════════════════════════
        // Watchlist extracted to src/watchlist.js (imported at top; self-registers window.Watchlist). DI-configured below.

        // ── Watchlist UI rendering ────────────────────────────────────────────────
        // Renders the watchlist table from stored items, pulling the freshest engine
        // values from screenerAllResults when the ticker is in the current scan, else
        // falling back to the stored snapshot (shown slightly dimmed).
        // ── Watchlist sort (persisted across sessions) ─────────────────────────────
        const WL_SORT_KEY = 'abWatchlistSortV1';
        let watchlistSort = (() => {
            try { const r = JSON.parse(localStorage.getItem(WL_SORT_KEY)); if (r && r.key) return r; } catch (e) {}
            return { key: 'added', dir: 'desc' };
        })();
        function persistWatchlistSort() {
            try { localStorage.setItem(WL_SORT_KEY, JSON.stringify(watchlistSort)); } catch (e) {}
        }
        function updateWatchlistSortIndicators() {
            document.querySelectorAll('.wl-sort').forEach(th => {
                const ind = th.querySelector('.wl-sort-ind');
                if (!ind) return;
                if (th.dataset.wlsort === watchlistSort.key) {
                    ind.textContent = watchlistSort.dir === 'asc' ? ' ▲' : ' ▼';
                    th.classList.add('text-slate-300');
                } else {
                    ind.textContent = '';
                    th.classList.remove('text-slate-300');
                }
            });
        }

        // ── Watchlist column config / filter / selection state ─────────────────────
        // Configurable columns (Symbol + actions are always present and not in this list).
        const WL_ALL_COLS = [
            { key: 'price',   label: 'Price',  sortable: true,  align: 'right' },
            { key: 'score',   label: 'Score',  sortable: true,  align: 'right' },
            { key: 'bqs',     label: 'BQS',    sortable: true,  align: 'right' },
            { key: 'rating',  label: 'Rating', sortable: true,  align: 'right' },
            { key: 'stage',   label: 'Stage',  sortable: true,  align: 'right' },
            { key: 'rmv',     label: 'RMV',    sortable: true,  align: 'right' },
            { key: 'rspower', label: 'RS',     sortable: true,  align: 'right' },
            { key: 'dvol',    label: '$Vol',   sortable: true,  align: 'right' },
            { key: 'pct3m',   label: '3M%',    sortable: true,  align: 'right' },
            { key: 'adr',     label: 'ADR%',   sortable: true,  align: 'right' },
            { key: 'alert',   label: 'Alert',  sortable: true,  align: 'left'  },
            { key: 'tags',    label: 'Tags',   sortable: false, align: 'left'  },
        ];
        const WL_COLS_KEY = 'abWatchlistColsV1';
        let wlVisibleCols = (() => {
            try { const r = JSON.parse(localStorage.getItem(WL_COLS_KEY)); if (Array.isArray(r)) return new Set(r); } catch (e) {}
            return new Set(['price', 'score', 'bqs', 'rating', 'stage', 'rmv', 'adr', 'alert', 'tags']);
        })();
        function persistWatchlistCols() { try { localStorage.setItem(WL_COLS_KEY, JSON.stringify([...wlVisibleCols])); } catch (e) {} }
        // Column display order (drag-to-reorder). Stored as an array of column keys.
        const WL_COLORDER_KEY = 'abWatchlistColOrderV1';
        let wlColOrder = (() => {
            try { const r = JSON.parse(localStorage.getItem(WL_COLORDER_KEY)); if (Array.isArray(r) && r.length) return r; } catch (e) {}
            return WL_ALL_COLS.map(c => c.key);
        })();
        function persistWatchlistColOrder() { try { localStorage.setItem(WL_COLORDER_KEY, JSON.stringify(wlColOrder)); } catch (e) {} }
        // Resolve the ordered list of currently-visible column defs (new keys appended).
        function wlOrderedVisibleCols() {
            const known = new Set(WL_ALL_COLS.map(c => c.key));
            const order = wlColOrder.filter(k => known.has(k));
            WL_ALL_COLS.forEach(c => { if (!order.includes(c.key)) order.push(c.key); });
            return order.filter(k => wlVisibleCols.has(k)).map(k => WL_ALL_COLS.find(c => c.key === k)).filter(Boolean);
        }
        let wlFilter = { tag: '', flag: '', adrMin: 0 };
        let wlSuppressSort = false;   // true briefly during a column/row drag so it doesn't also sort
        let wlDragInProgress = false; // skip re-renders mid-drag so the dragged handle isn't detached
        let wlShownCount = 0;         // count of currently-shown (filtered) rows, for the summary toggle
        let wlSelected = new Set();
        const FLAG_COLORS = ['red', 'amber', 'green', 'blue', 'purple'];
        const wlEsc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

        // Build a tiny sparkline SVG (inner markup) from a closes[] series.
        function wlSparkSVG(closes) {
            if (!Array.isArray(closes) || closes.length < 2) return '';
            const w = 64, h = 20, pad = 2;
            const min = Math.min(...closes), max = Math.max(...closes);
            const span = (max - min) || 1;
            const n = closes.length;
            const pts = closes.map((c, i) => {
                const x = pad + (i / (n - 1)) * (w - pad * 2);
                const y = h - pad - ((c - min) / span) * (h - pad * 2);
                return `${x.toFixed(1)},${y.toFixed(1)}`;
            }).join(' ');
            const up = closes[n - 1] >= closes[0];
            const col = up ? '#34d399' : '#fb7185';
            return `<polyline points="${pts}" fill="none" stroke="${col}" stroke-width="1.3" stroke-linejoin="round" stroke-linecap="round"/>`;
        }

        // Per-(list, section) collapse state for watchlist section headers (client-only).
        const WL_COLLAPSE_KEY = 'abWatchlistCollapsedV1';
        let wlCollapsed = (() => { try { return JSON.parse(localStorage.getItem(WL_COLLAPSE_KEY)) || {}; } catch (e) { return {}; } })();
        const wlCollapseKey = (sec) => (typeof Watchlist !== 'undefined' ? Watchlist.getActiveId() : 0) + ':' + sec;
        const isSectionCollapsed = (sec) => !!wlCollapsed[wlCollapseKey(sec)];
        function toggleSectionCollapsed(sec) {
            const k = wlCollapseKey(sec);
            if (wlCollapsed[k]) delete wlCollapsed[k]; else wlCollapsed[k] = true;
            try { localStorage.setItem(WL_COLLAPSE_KEY, JSON.stringify(wlCollapsed)); } catch (e) {}
        }

        function renderWatchlist() {
            // Don't rebuild the table mid-drag — replacing the dragged handle's DOM node would
            // strand the HTML5 drag (no dragend), leaving sort suppressed. The drop handler
            // clears this flag before its own re-render.
            if (wlDragInProgress) return;
            const body = document.getElementById('watchlistBody');
            const head = document.getElementById('watchlistHead');
            const empty = document.getElementById('watchlistEmpty');
            const wrap = document.getElementById('watchlistTableWrap');
            const countEl = document.getElementById('watchlistCount');
            const summaryEl = document.getElementById('wlSummary');
            if (!body || !empty || !wrap) return;

            // ── Auth gate: watchlists are per-account, so show a sign-in panel when the
            // visitor isn't logged in (the screener stays fully usable anonymously). ──
            const signin = document.getElementById('wlSignin');
            const tabsEl = document.getElementById('wlListTabs');
            const toolbarEl = document.getElementById('wlToolbar');
            const authed = (typeof Auth === 'undefined') || Auth.isAuthed();
            if (signin) signin.classList.toggle('hidden', authed);
            if (tabsEl) tabsEl.classList.toggle('hidden', !authed);
            if (toolbarEl) toolbarEl.classList.toggle('hidden', !authed);
            if (!authed) {
                empty.classList.add('hidden');
                wrap.classList.add('hidden');
                if (summaryEl) summaryEl.classList.add('hidden');
                body.innerHTML = '';
                if (countEl) countEl.textContent = '';
                return;
            }

            const allItems = Watchlist.all();
            if (countEl) countEl.textContent = allItems.length ? `· ${allItems.length}` : '';
            // Keep the tag-filter dropdown populated with the tags actually in use.
            refreshWlTagFilterOptions(allItems);

            if (!allItems.length) {
                empty.classList.remove('hidden');
                wrap.classList.add('hidden');
                if (summaryEl) summaryEl.classList.add('hidden');
                body.innerHTML = '';
                return;
            }

            // Snapshot resolver (live scan row if present, else stored snapshot) — defined
            // here so the ADR filter below can read it too.
            const byTicker = (typeof state.screenerAllResults !== 'undefined' && Array.isArray(state.screenerAllResults))
                ? new Map(state.screenerAllResults.map(r => [r.ticker, r])) : new Map();
            const liveOf = (it) => byTicker.get(it.ticker);
            // Ensure the live row's Trade Assistant rating is computed on demand (it's only
            // precomputed for the visible screener slice), so the watchlist Rating isn't blank.
            const snapOf = (it) => { const l = liveOf(it); if (l && typeof ensureRowRating === 'function') ensureRowRating(l); return l ? Watchlist.snapshotFromRow(l) : (it.snapshot || {}); };

            // ── Filter (tag / flag / min ADR%) — the search box is now an "add from
            // screener" typeahead, so it no longer narrows the saved list. ───────────
            const items = allItems.filter(it => {
                if (wlFilter.tag && !(Array.isArray(it.tags) && it.tags.includes(wlFilter.tag))) return false;
                if (wlFilter.flag) {
                    if (wlFilter.flag === 'flagged') { if (!it.flag) return false; }
                    else if (it.flag !== wlFilter.flag) return false;
                }
                if (wlFilter.adrMin > 0) { const a = snapOf(it).adrPct; if (Number.isFinite(a) && a < wlFilter.adrMin) return false; }
                return true;
            });

            empty.classList.add('hidden');
            wrap.classList.remove('hidden');

            const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
            const sortKey = watchlistSort.key, dir = watchlistSort.dir === 'asc' ? 1 : -1;
            const sortVal = (it) => {
                const s = snapOf(it);
                switch (sortKey) {
                    case 'ticker':  return it.ticker || '';
                    case 'price':   return s.price;
                    case 'score':   return s.score;
                    case 'bqs':     return s.bqs;
                    case 'rating':  return s.rating;
                    case 'stage':   return s.stage;
                    case 'rmv':     return s.rmv;
                    case 'rspower': return s.rsPower;
                    case 'dvol':    return s.dollarVolume;
                    case 'pct3m':   return s.pct3m;
                    case 'adr':     return s.adrPct;
                    case 'alert': {
                        const la = latestAlertForRow(s);
                        if (!la) return -1;
                        return (1000 - (la.days || 0) * 10) + (ALERT_STRENGTH[la.type] || 0);
                    }
                    default: return null;
                }
            };
            let sorted;
            if (sortKey === 'manual') {
                // Manual drag order: by persisted position (sections preserve this within group).
                sorted = items.slice().sort((a, b) => (a.position || 0) - (b.position || 0));
            } else if (!sortKey || sortKey === 'added') {
                sorted = items.slice().sort((a, b) => (b.addedAt || 0) - (a.addedAt || 0));
            } else if (sortKey === 'ticker') {
                sorted = items.slice().sort((a, b) => collator.compare(a.ticker || '', b.ticker || '') * dir);
            } else {
                sorted = items.slice().sort((a, b) => {
                    const va = sortVal(a), vb = sortVal(b);
                    const na = (va == null || va === '' || !Number.isFinite(Number(va))) ? null : Number(va);
                    const nb = (vb == null || vb === '' || !Number.isFinite(Number(vb))) ? null : Number(vb);
                    if (na === null && nb === null) return 0;
                    if (na === null) return 1;
                    if (nb === null) return -1;
                    if (na === nb) return (b.addedAt || 0) - (a.addedAt || 0);
                    return (na - nb) * dir;
                });
            }

            const ratingCls = v => v == null ? 'text-slate-600'
                : v >= 4.0 ? 'text-emerald-300' : v >= 3.0 ? 'text-blue-300' : v >= 2.0 ? 'text-amber-300' : 'text-rose-300';
            const scoreCls = s => s == null ? 'text-slate-500'
                : s >= 900 ? 'text-fuchsia-300' : s >= 750 ? 'text-emerald-300' : s >= 600 ? 'text-blue-300' : 'text-slate-300';
            const rmvCls = v => v == null ? 'text-slate-500' : v <= 40 ? 'text-emerald-300' : v <= 70 ? 'text-blue-300' : 'text-amber-300';
            const rsCls  = v => v == null ? 'text-slate-500' : v >= 80 ? 'text-emerald-300' : v >= 50 ? 'text-blue-300' : 'text-slate-400';

            // ── Header ────────────────────────────────────────────────────────────
            const allChecked = sorted.length && sorted.every(it => wlSelected.has(it.ticker));
            const colsOn = wlOrderedVisibleCols();
            const sortInd = (k) => watchlistSort.key === k ? (watchlistSort.dir === 'asc' ? ' ▲' : ' ▼') : '';
            const thFor = (c) => {
                const a = c.align === 'right' ? 'text-right' : 'text-left';
                const drag = `draggable="true" data-colkey="${c.key}"`;
                if (c.sortable) return `<th ${drag} data-wlsort="${c.key}" class="wl-sort wl-col-th px-2 ${a} cursor-pointer select-none hover:text-slate-300" title="Drag to reorder · click to sort">${c.label}<span class="wl-sort-ind">${sortInd(c.key)}</span></th>`;
                return `<th ${drag} class="wl-col-th px-2 ${a}" title="Drag to reorder">${c.label}</th>`;
            };
            head.innerHTML = `<tr>
                <th class="pl-4 pr-1"><input type="checkbox" id="wlSelectAll" ${allChecked ? 'checked' : ''} class="accent-blue-500 cursor-pointer" title="Select all"></th>
                <th data-wlsort="ticker" class="wl-sort pl-1 pr-2 cursor-pointer select-none hover:text-slate-300">Symbol<span class="wl-sort-ind">${sortInd('ticker')}</span></th>
                ${colsOn.map(thFor).join('')}
                <th class="px-2 text-right pr-4"></th>
            </tr>`;

            // ── Rows ──────────────────────────────────────────────────────────────
            const renderCell = (key, s, item) => {
                const align = WL_ALL_COLS.find(c => c.key === key).align === 'right' ? 'text-right' : 'text-left';
                switch (key) {
                    case 'spark': {
                        const closes = item.spark && Array.isArray(item.spark.closes) ? item.spark.closes : null;
                        const inner = closes ? wlSparkSVG(closes) : '';
                        return `<td class="px-2"><svg class="wl-spark" data-wl-spark="${item.ticker}" width="64" height="20" viewBox="0 0 64 20">${inner}</svg></td>`;
                    }
                    case 'price': {
                        const price = s.price != null ? `$${s.price.toFixed(2)}` : '--';
                        return `<td class="px-2 text-right text-slate-200" data-wl-px="${item.ticker}">${price}<span class="block text-[9px] leading-tight text-slate-600" data-wl-chg="${item.ticker}"></span></td>`;
                    }
                    case 'score':   return `<td class="px-2 text-right font-black ${scoreCls(s.score)}">${s.score != null ? s.score : '--'}</td>`;
                    case 'bqs': {
                        const v = s.bqs;
                        if (v == null) return `<td class="px-2 text-right text-slate-600">--</td>`;
                        const cls = (typeof QS !== 'undefined' && QS.text) ? QS.text(v, 100) : (v >= 70 ? 'text-emerald-300' : v >= 45 ? 'text-amber-300' : 'text-slate-400');
                        const mode = s.bqsMode ? `<span class="ml-1 text-[8px] font-bold uppercase ${s.bqsMode === 'ma-ride' ? 'text-teal-400' : 'text-blue-400'}">${s.bqsMode === 'ma-ride' ? 'MA' : 'FLG'}</span>` : '';
                        return `<td class="px-2 text-right font-black tabular-nums ${cls}" title="Breakout Quality Score (0–100)${s.bqsMode ? ' · ' + s.bqsMode : ''}">${v}${mode}</td>`;
                    }
                    case 'rating':  return `<td class="px-2 text-right font-black tabular-nums ${ratingCls(s.rating)}">${s.rating != null ? s.rating.toFixed(1) : '--'}</td>`;
                    case 'stage':   return `<td class="px-2 text-right text-slate-300">${s.stageLabel || (s.stage ? 'S' + s.stage : '--')}</td>`;
                    case 'rmv':     return `<td class="px-2 text-right ${rmvCls(s.rmv)}">${s.rmv != null ? s.rmv.toFixed(0) : '--'}</td>`;
                    case 'rspower': return `<td class="px-2 text-right tabular-nums ${rsCls(s.rsPower)}">${s.rsPower != null ? s.rsPower.toFixed(0) : '--'}</td>`;
                    case 'dvol':    return `<td class="px-2 text-right text-slate-400 tabular-nums">${s.dollarVolume != null ? formatDollarBig(s.dollarVolume) : '--'}</td>`;
                    case 'pct3m': {
                        const v = s.pct3m;
                        const c = v == null ? 'text-slate-500' : v >= 0 ? 'text-emerald-300' : 'text-rose-300';
                        return `<td class="px-2 text-right tabular-nums ${c}">${v == null ? '--' : (v >= 0 ? '+' : '') + v.toFixed(1) + '%'}</td>`;
                    }
                    case 'adr': {
                        const v = s.adrPct;
                        const c = v == null ? 'text-slate-500' : v >= 8 ? 'text-amber-300' : v >= 4 ? 'text-emerald-300' : 'text-slate-400';
                        return `<td class="px-2 text-right tabular-nums ${c}" title="ADR% (20-day average daily range)">${v == null ? '--' : v.toFixed(1) + '%'}</td>`;
                    }
                    case 'alert': {
                        const la = latestAlertForRow(s);
                        return `<td class="px-2">${la
                            ? `<span title="${la.label} — ${alertDaysText(la.days)}" class="px-2 py-0.5 rounded border ${la.meta.chip} ${la.meta.text} font-bold">${la.label}</span>`
                            : `<span class="px-2 py-0.5 rounded border bg-slate-900 border-slate-700 text-slate-500">Watch</span>`}</td>`;
                    }
                    case 'tags': {
                        const tags = (item.tags && item.tags.length)
                            ? item.tags.map(t => `<span class="wl-tag-chip">${wlEsc(t)}</span>`).join(' ')
                            : '<span class="text-slate-700">—</span>';
                        return `<td class="px-2"><div class="flex flex-wrap gap-1">${tags}</div></td>`;
                    }
                    default: return `<td class="px-2 ${align}">--</td>`;
                }
            };

            const rowHTML = (item) => {
                const live = byTicker.get(item.ticker);
                const s = live ? Watchlist.snapshotFromRow(live) : (item.snapshot || {});
                const stale = !live;
                const dim = stale ? 'opacity-60' : '';
                const checked = wlSelected.has(item.ticker);
                const alertCount = (item.alerts && item.alerts.length) ? item.alerts.length : 0;
                const dragHandle = `<span data-wl-noload class="wl-drag-handle" title="Drag the row to reorder / move to a section">⠿</span>`;
                const flagDot = `<span data-wl-edit="${item.ticker}" data-wl-noload title="${item.flag ? 'Flag: ' + item.flag + ' — click to change' : 'Click to flag, tag or add a note'}" class="wl-flagdot ${item.flag ? 'wl-flag-' + item.flag : 'wl-flagdot-empty'}" style="cursor:pointer"></span>`;
                const noteDot = (item.note && item.note.trim()) ? `<span data-wl-edit="${item.ticker}" data-wl-noload class="wl-note-dot ml-1" title="${wlEsc(item.note)} — click to edit" style="cursor:pointer">📝</span>` : '';
                const tagsInline = (item.tags && item.tags.length)
                    ? `<div class="flex flex-wrap gap-1 mt-0.5" data-wl-edit="${item.ticker}" data-wl-noload style="cursor:pointer">${item.tags.slice(0, 5).map(t => `<span class="wl-tag-chip">${wlEsc(t)}</span>`).join('')}</div>` : '';
                return `<tr data-ticker="${item.ticker}" data-section="${wlEsc(item.section || '')}" draggable="true" class="wl-row border-b border-slate-900 hover:bg-blue-950/20 transition-colors cursor-pointer ${dim} ${checked ? 'wl-row-checked' : ''}">
                    <td class="pl-4 pr-1" data-wl-noload><input type="checkbox" data-wl-check="${item.ticker}" ${checked ? 'checked' : ''} class="accent-blue-500 cursor-pointer"></td>
                    <td class="pl-1 pr-2">
                        <div class="flex items-center gap-1.5">${dragHandle}${flagDot}<span class="font-black text-white">${item.ticker}</span>${stale ? '<span class="ml-1 text-[8px] text-slate-600 uppercase">stale</span>' : ''}${noteDot}</div>
                        <div class="text-[10px] text-slate-500 truncate max-w-[150px]">${wlEsc(item.name || item.ticker)}</div>
                        ${tagsInline}
                    </td>
                    ${colsOn.map(c => renderCell(c.key, s, item)).join('')}
                    <td class="px-2 text-right pr-4 whitespace-nowrap" data-wl-noload>
                        <button data-wl-edit="${item.ticker}" title="Notes, flag & tags" class="text-slate-500 hover:text-amber-300 transition-colors">⋯</button>
                        <button data-wl-alert="${item.ticker}" title="Alerts for this ticker" class="text-slate-500 hover:text-blue-300 transition-colors ml-1">🔔${alertCount ? `<span class="text-[9px] ml-0.5 align-top">${alertCount}</span>` : ''}</button>
                        <button data-wl-remove="${item.ticker}" title="Remove from watchlist" class="text-slate-600 hover:text-rose-400 transition-colors text-sm leading-none ml-1">×</button>
                    </td>
                </tr>`;
            };
            const sectionHeadHTML = (name, count) => {
                const collapsed = isSectionCollapsed(name);
                return `<tr class="wl-section-head" data-section-head="${wlEsc(name)}" draggable="true">
                    <td colspan="${colsOn.length + 3}" class="px-3 py-1.5">
                        <div class="flex items-center gap-2">
                            <span class="wl-sec-caret" data-sec-toggle="${wlEsc(name)}">${collapsed ? '▸' : '▾'}</span>
                            <span class="wl-drag-handle wl-sec-grip" title="Drag the section to reorder">⠿</span>
                            <span class="font-black text-slate-200 text-[11px] uppercase tracking-wide cursor-pointer" data-sec-name="${wlEsc(name)}" title="Double-click to rename">${wlEsc(name)}</span>
                            <span class="text-[10px] text-slate-500 tabular-nums">${count}</span>
                            <span class="flex-1"></span>
                            <span class="wl-sec-act" data-sec-del="${wlEsc(name)}" title="Delete section (items move to Ungrouped)">×</span>
                        </div>
                    </td>
                </tr>`;
            };

            const activeSections = (typeof Watchlist.getSections === 'function') ? Watchlist.getSections() : [];
            const hasSections = activeSections.length > 0 || sorted.some(it => it.section);
            if (!sorted.length && !hasSections) {
                body.innerHTML = `<tr><td colspan="20" class="text-center py-6 text-slate-600 italic">No matches in this filter.</td></tr>`;
            } else if (!hasSections) {
                body.innerHTML = sorted.map(rowHTML).join('');
            } else {
                // Grouped view: ungrouped items first (no header), then each section in order.
                const groups = new Map(); activeSections.forEach(n => groups.set(n, []));
                const ungrouped = [];
                sorted.forEach(it => { const sec = (it.section && groups.has(it.section)) ? it.section : null; if (sec) groups.get(sec).push(it); else ungrouped.push(it); });
                let html = ungrouped.map(rowHTML).join('');
                activeSections.forEach(name => {
                    const g = groups.get(name) || [];
                    html += sectionHeadHTML(name, g.length);
                    if (!isSectionCollapsed(name)) html += g.map(rowHTML).join('');
                });
                body.innerHTML = html;
            }

            updateWatchlistSortIndicators();
            // Pin the table header just below the (sticky) header bar + toolbar, measured
            // live so it never overlaps or gaps regardless of toolbar wrapping.
            const hb = document.getElementById('wlHeaderBar');
            const tb = document.getElementById('wlToolbar');
            if (hb && tb) { tb.style.top = hb.offsetHeight + 'px'; head.style.top = (hb.offsetHeight + tb.offsetHeight) + 'px'; }
            renderWlSummary(sorted, snapOf);
            wlShownCount = sorted.length;
            updateWlBulkBar();
            // Sparklines removed for speed — no per-row background fetching / SVG redraws.
        }

        // Breadth summary line over the currently-shown (filtered) rows.
        function renderWlSummary(rows, snapOf) {
            const el = document.getElementById('wlSummary');
            if (!el) return;
            if (!rows.length) { el.classList.add('hidden'); return; }
            let s2 = 0, scoreSum = 0, scoreN = 0, leaders = 0, rsN = 0, rsSum = 0;
            rows.forEach(it => {
                const s = snapOf(it);
                if (s.stage2 || s.stage === 2) s2++;
                if (Number.isFinite(s.score)) { scoreSum += s.score; scoreN++; }
                if (Number.isFinite(s.rsPower)) { rsSum += s.rsPower; rsN++; if (s.rsPower >= 80) leaders++; }
            });
            const pct = (n) => Math.round((n / rows.length) * 100);
            const parts = [`${rows.length} shown`];
            parts.push(`${pct(s2)}% Stage 2`);
            if (scoreN) parts.push(`avg score ${Math.round(scoreSum / scoreN)}`);
            if (rsN) parts.push(`${leaders} leaders (RS≥80)`);
            el.textContent = parts.join('  ·  ');
            el.classList.remove('hidden');
        }

        // Populate the tag-filter dropdown from tags actually in use (preserving selection).
        function refreshWlTagFilterOptions(items) {
            const sel = document.getElementById('wlTagFilter');
            if (!sel) return;
            const tags = new Set();
            (items || []).forEach(it => (it.tags || []).forEach(t => tags.add(t)));
            const sortedTags = [...tags].sort((a, b) => a.localeCompare(b));
            const cur = sel.value;
            sel.innerHTML = `<option value="">All tags</option>` + sortedTags.map(t => `<option value="${wlEsc(t)}">${wlEsc(t)}</option>`).join('');
            if (cur && sortedTags.includes(cur)) sel.value = cur; else if (cur && cur !== '') { wlFilter.tag = ''; }
        }

        // Show/refresh the bulk-action bar based on current selection.
        function updateWlBulkBar() {
            const bar = document.getElementById('wlBulkBar');
            const cnt = document.getElementById('wlBulkCount');
            const summary = document.getElementById('wlSummary');
            if (!bar) return;
            if (wlSelected.size) {
                bar.classList.remove('hidden'); bar.classList.add('flex');
                if (summary) summary.classList.add('hidden');
                if (cnt) cnt.textContent = `${wlSelected.size} selected`;
            } else {
                bar.classList.add('hidden'); bar.classList.remove('flex');
                if (summary && wlShownCount > 0) summary.classList.remove('hidden');
            }
        }


        // Unread-alert badge on the Watchlist hub tab (Phase C populates the count).
        function updateWatchlistTabBadge() {
            const tab = document.querySelector('.hub-tab[data-hub="watchlist"]');
            if (!tab) return;
            const n = Watchlist.count();
            // Phase A: show the watched count as a subtle suffix; Phase C will switch to unread alerts.
            tab.dataset.count = n ? String(n) : '';
        }

        // ── Alerts feed ───────────────────────────────────────────────────────────
        let alertsConviction = 'high'; // high | bal | all — noise filter (default: curated High-confidence only)
        // Independent per-signal visibility toggles (multi-select, not radio).
        const ALERT_TYPE_KEYS = ['alpha', 'beta', 'delta', 'oprime', 'onova', 'otitan'];
        let alertsTypesOn = new Set(ALERT_TYPE_KEYS);   // all on by default
        let alertsShowProximity = false;                // master on/off for forming (◷) alerts — default OFF (clean list)
        let alertsHistoryMode = false;                  // Live (current snapshot) vs History (server signal_events + outcomes)
        let _sigHistory = null;                         // cached server history feed
        let alertsRsFilter = 'any';                      // 'any' | 'leaders' | 'delta' — RS Power filter
        let alertsDollarVol = 25_000_000;                // $-volume floor for the feed (default $25M+; 0 = any)
        const ALERTS_SORT_KEY = 'abAlertsSortV1';
        let alertsSort = (() => {
            try { const v = JSON.parse(localStorage.getItem(ALERTS_SORT_KEY)); if (v && v.key) return v; } catch (e) {}
            return { key: 'recent', dir: 'desc' }; // recent | ticker | type | price
        })();
        function persistAlertsSort() { try { localStorage.setItem(ALERTS_SORT_KEY, JSON.stringify(alertsSort)); } catch (e) {} }
        function updateAlertsSortIndicators() {
            document.querySelectorAll('.al-sort').forEach(th => {
                const ind = th.querySelector('.al-sort-ind');
                if (!ind) return;
                if (th.dataset.alsort === alertsSort.key) {
                    ind.textContent = alertsSort.dir === 'asc' ? ' ▲' : ' ▼';
                    th.classList.add('text-slate-300');
                } else { ind.textContent = ''; th.classList.remove('text-slate-300'); }
            });
        }

        // Per-type display: accent color + label. Mirrors the chart marker palette exactly
        // so each signal reads as ONE color everywhere (chart, pills, screener, watchlist, etc).
        const ALERT_TYPE_META = {
            alpha:  { label: 'Alpha',     short: 'Alpha',  border: '#d99a3a', text: 'text-amber-300',   chip: 'bg-amber-900/25 border-amber-500/30' },   // amber ● filled
            beta:   { label: 'Beta',      short: 'Beta',   border: '#5aa9d6', text: 'text-sky-300',     chip: 'bg-sky-900/25 border-sky-500/30' },       // sky ○ ring
            delta:  { label: 'Delta',     short: 'Delta',  border: '#7ec8e8', text: 'text-sky-200',     chip: 'bg-sky-900/20 border-sky-400/30' },       // cyan ◆ diamond
            oprime: { label: 'Ω Prime',   short: 'Prime',  border: '#5fb88a', text: 'text-green-300',   chip: 'bg-green-900/25 border-green-500/30' },    // green ▲ filled
            onova:  { label: 'Ω Nova',    short: 'Nova',   border: '#4aa888', text: 'text-emerald-300', chip: 'bg-emerald-900/25 border-emerald-500/30' },// teal △ ring
            otitan: { label: 'Ω Titan',   short: 'Titan',  border: '#cf6b6b', text: 'text-rose-300',    chip: 'bg-rose-900/25 border-rose-500/30' }       // red ▽ down-tri
        };

        // Pick the LATEST alert on a screener row: smallest days-since wins; ties (same day,
        // e.g. delta/omega both fire "today" = 0d) break by strength order. Returns
        // { type, label, days, meta } or null when the row has no active alert.
        // Strength on ties: Ω Nova > Ω Prime > Alpha > Beta > Delta > Ω Titan.
        const ALERT_STRENGTH = { onova: 6, oprime: 5, alpha: 4, beta: 3, delta: 2, otitan: 1 };
        function latestAlertForRow(row) {
            if (!row) return null;
            const candidates = [];
            if (row.hasAlpha) candidates.push({ type: 'alpha', days: Number.isFinite(row.alphaDays) ? row.alphaDays : 0 });
            if (row.hasBeta)  candidates.push({ type: 'beta',  days: Number.isFinite(row.betaDays) ? row.betaDays : 0 });
            // Delta: light for recent events (days from the last Delta), unified with Alpha/Beta.
            if (row.deltaTrigger || Number.isFinite(row.deltaDays)) candidates.push({ type: 'delta', days: Number.isFinite(row.deltaDays) ? row.deltaDays : 0 });
            const om = row.omegaSignal;
            if (om === 'OMEGA55') candidates.push({ type: 'onova',  days: 0 });
            else if (om === 'OMEGA20') candidates.push({ type: 'oprime', days: 0 });
            else if (om === 'OMEGA_EXIT') candidates.push({ type: 'otitan', days: 0 });
            if (!candidates.length) return null;
            // Sort: most recent first (fewest days), then strongest on a tie.
            candidates.sort((a, b) => (a.days - b.days) || (ALERT_STRENGTH[b.type] - ALERT_STRENGTH[a.type]));
            const win = candidates[0];
            return { type: win.type, days: win.days, meta: ALERT_TYPE_META[win.type], label: ALERT_TYPE_META[win.type].label };
        }
        function alertDaysText(days) {
            if (days == null) return '';
            return days <= 0 ? 'today' : `${days} day${days === 1 ? '' : 's'} ago`;
        }

        // "When" for a Live-mode alert. A signal still being validated (sigState fresh/
        // confirming) shows when it STARTED being perceived (firstSeenAt); an established one
        // (confirmed / failed / untracked delta+omega) shows when it was DETECTED (signal age).
        function alertWhenText(a) {
            if (!a) return '';
            const ageTxt = (d) => d == null ? '' : (d <= 0 ? 'today' : `${d}d ago`);
            const dateTxt = (ts) => new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            const forming = (a.sigState === 'fresh' || a.sigState === 'confirming');
            if (forming) {
                const since = a.firstSeenAt ? dateTxt(a.firstSeenAt) : ageTxt(a.days);
                return since ? `<span class="text-sky-400/80">forming</span> · ${since}` : 'forming';
            }
            const det = ageTxt(a.days) || (a.firstSeenAt ? dateTxt(a.firstSeenAt) : '');
            return det ? `<span class="text-slate-500">detected</span> · ${det}` : '';
        }

        function updateAlertsTabBadge() {
            const tab = document.querySelector('.hub-tab[data-hub="alerts"]');
            const badge = document.getElementById('alertsTabBadge');
            if (!tab || typeof Watchlist === 'undefined') return;
            const n = Watchlist.unreadCount();
            if (badge) {
                badge.textContent = n > 99 ? '99+' : String(n);
                badge.classList.toggle('hidden', n === 0);
            }
        }

        // History view: the server's complete signal_events feed (all tickers, with
        // forward-return outcomes) — what the live "state view" can't show. Cached;
        // re-uses the same type toggles. Server types map to the client layer keys.
        const SIG_SRV2CLIENT = { alpha: 'alpha', beta: 'beta', delta: 'delta', omega20: 'oprime', omega55: 'onova', omega_exit: 'otitan' };
        const SIG_TYPE_LABEL = { alpha: 'Alpha', beta: 'Beta', delta: 'Delta', oprime: 'Ω Prime', onova: 'Ω Nova', otitan: 'Ω Titan' };
        async function renderSignalHistory() {
            const feed = document.getElementById('alertsFeed'), empty = document.getElementById('alertsEmpty');
            const countEl = document.getElementById('alertsCount'), tbody = document.getElementById('alertsFeedBody');
            if (!feed || !empty || !tbody) return;
            if (!_sigHistory) {
                tbody.innerHTML = '<tr><td colspan="6" class="py-8 text-center text-slate-500 italic">Loading signal history…</td></tr>';
                feed.classList.remove('hidden'); empty.classList.add('hidden');
                try { const r = await fetch('/api/signals/recent?days=60&limit=400'); _sigHistory = r.ok ? await r.json() : []; }
                catch (e) { _sigHistory = []; }
                if (!alertsHistoryMode) return; // user toggled back while loading
            }
            const rows = _sigHistory.map(e => ({ ...e, ctype: SIG_SRV2CLIENT[e.type] || e.type }))
                .filter(e => alertsTypesOn.has(e.ctype));
            if (!rows.length) { feed.classList.add('hidden'); empty.classList.remove('hidden'); if (countEl) countEl.textContent = ''; return; }
            // Apply the same column sort the header clicks set (default: newest first).
            const _coll = new Intl.Collator(undefined, { sensitivity: 'base' });
            const _dir = alertsSort.dir === 'asc' ? 1 : -1;
            const _ts = (e) => new Date(e.event_date).getTime() || 0;
            rows.sort((a, b) => {
                switch (alertsSort.key) {
                    case 'ticker': return _coll.compare(a.ticker || '', b.ticker || '') * _dir || (_ts(b) - _ts(a));
                    case 'type':   return ((ALERT_STRENGTH[a.ctype] || 0) - (ALERT_STRENGTH[b.ctype] || 0)) * _dir || (_ts(b) - _ts(a));
                    case 'conf':   return (((a.score0 ?? -1) - (b.score0 ?? -1)) * _dir) || (_ts(b) - _ts(a));
                    case 'price':  return (((a.price ?? -1) - (b.price ?? -1)) * _dir) || (_ts(b) - _ts(a));
                    case 'recent': return (_ts(a) - _ts(b)) * _dir;
                    default:       return _ts(b) - _ts(a);   // newest first
                }
            });
            feed.classList.remove('hidden'); empty.classList.add('hidden');
            if (countEl) countEl.textContent = `${rows.length} historical`;
            updateAlertsSortIndicators();
            const pct = v => v == null ? '<span class="text-slate-600">—</span>' : `<span class="${v >= 0 ? 'text-emerald-400' : 'text-rose-400'}">${v >= 0 ? '+' : ''}${Number(v).toFixed(1)}%</span>`;
            const ago = d => { const n = Math.round((Date.now() - new Date(d).getTime()) / 86400000); return n <= 0 ? 'today' : n + 'd ago'; };
            tbody.innerHTML = rows.map(e => `
                <tr class="hover:bg-blue-950/20 transition-colors cursor-pointer" data-alert-ticker="${e.ticker}">
                    <td class="py-2 pl-5 pr-2 font-black text-white">${e.ticker}</td>
                    <td class="py-2 px-2 text-[10px] font-bold text-slate-300">${SIG_TYPE_LABEL[e.ctype] || e.ctype}</td>
                    <td class="py-2 px-2 text-center text-[10px] tabular-nums">${e.grade ? `${e.grade}${e.score0 != null ? ' · ' + e.score0 : ''}` : '<span class="text-slate-600">—</span>'}</td>
                    <td class="py-2 px-2 text-right tabular-nums text-slate-300">${e.price != null ? '$' + Number(e.price).toFixed(2) : '—'}</td>
                    <td class="py-2 px-2 text-[10px] text-slate-400 whitespace-nowrap">5d ${pct(e.ret_5d)} · 10d ${pct(e.ret_10d)}${e.rvol != null ? ' · RVOL ' + Number(e.rvol).toFixed(1) + '×' : ''}${e.regime ? ' · ' + e.regime : ''}</td>
                    <td class="py-2 px-2 text-right pr-5 text-[10px] text-slate-500">${ago(e.event_date)}</td>
                </tr>`).join('');
        }

        function renderAlertsFeed() {
            if (alertsHistoryMode) { renderSignalHistory(); return; }   // Live vs History router
            const feed = document.getElementById('alertsFeed');
            const empty = document.getElementById('alertsEmpty');
            const countEl = document.getElementById('alertsCount');
            const tbody = document.getElementById('alertsFeedBody');
            if (!feed || !empty || !tbody || typeof Watchlist === 'undefined') return;

            // STATE VIEW: render the live snapshot's current alerts (every row that has an
            // active signal right now), not the witnessed-transition log. This is what makes
            // a fresh beta (e.g. DDOG) reliably appear regardless of poll timing. The
            // transition log still drives the unread badge for genuinely NEW alerts.
            let alerts = Watchlist.currentAlerts(state.screenerAllResults);
            // Per-signal visibility (multi-select) + proximity master switch.
            alerts = alerts.filter(a => {
                if (a.status === 'forming' && !alertsShowProximity) return false;
                if (!alertsTypesOn.has(a.type)) return false;
                // RS Power leadership filter: 'leaders' = RS NH / Delta / Primed; 'delta' = Delta only.
                if (alertsRsFilter === 'delta' && a.rsPowerTier !== 'Delta') return false;
                if (alertsRsFilter === 'leaders' && !['Delta', 'RS NH', 'Primed'].includes(a.rsPowerTier)) return false;
                // Liquidity (display-only): hide alerts below the $-volume floor. Data is NEVER
                // dropped at the source — only hidden here — so nothing chokes the engine and the
                // filter is fully reversible. null $-vol (older alerts) is NOT hidden.
                if (alertsDollarVol > 0 && Number.isFinite(a.dollarVolume) && a.dollarVolume < alertsDollarVol) return false;
                return true;
            });

            // ── CONVICTION FILTER (noise control) ──────────────────────────────
            // 'high'  → only gate-passing A/B confirmed alerts
            // 'bal'   → confirmed A/B/C + high-proximity forming
            // 'all'   → everything (including unconfirmed + forming)
            const conv = (typeof alertsConviction !== 'undefined') ? alertsConviction : 'bal';
            alerts = alerts.filter(a => {
                if (conv === 'all') return true; // All shows everything, incl. undefended
                // AVWAP pullback-defense quality filter (daily swing-low AVWAP):
                //   null  → no clean anchor; never filtered out (no penalty for missing data)
                //   <40   → undefended / low-quality → hidden from High AND Balanced (All only)
                //   ≥80   → comfortably above AVWAP → required for High
                const av = Number.isFinite(a.avwapDefense) ? a.avwapDefense : null;
                if (av !== null && av < 40) return false; // low-quality: only visible in All
                if (a.status === 'forming') return conv !== 'high' && (a.proximity ?? 0) >= (conv === 'bal' ? 70 : 0);
                if (conv === 'high') {
                    // Grade now encodes per-type gating server-side (lib/signals.js): a Beta A/B
                    // requires follow-through (held), an Alpha A/B requires the RVOL≥1.5 gate.
                    // Legacy types' A/B already imply their gate too, so trust the grade.
                    if (!(a.grade === 'A' || a.grade === 'B')) return false;
                    return av === null || av >= 80; // High = strictly defended (or no-anchor)
                }
                return (a.grade || 'D') !== 'D'; // 'bal': hide weakest + (above) undefended
            });

            // Default ordering: confirmation score first (strongest at top), then recency.
            // Explicit column sorts still override via alertsSort.key.
            const coll = new Intl.Collator(undefined, { sensitivity: 'base' });
            const key = alertsSort.key, dir = alertsSort.dir === 'asc' ? 1 : -1;
            // Recency for the "When" sort: firstSeenAt if tracked, else derived from the
            // signal's market age (days). Higher = more recent. (firedAt is null in Live mode.)
            // firstSeenAt is an ISO string (from signalState), so parse it to ms — subtracting
            // raw strings yields NaN, which silently breaks the sort (today's alerts get buried).
            const _rec = (x) => {
                if (x.firstSeenAt != null) { const t = new Date(x.firstSeenAt).getTime(); if (Number.isFinite(t)) return t; }
                return Date.now() - (Number.isFinite(x.days) ? x.days : 0) * 86400000;
            };
            const cmp = (a, b) => {
                switch (key) {
                    case 'ticker': return coll.compare(a.ticker || '', b.ticker || '') * dir || (_rec(b) - _rec(a));
                    case 'type':   return ((ALERT_STRENGTH[a.type] || 0) - (ALERT_STRENGTH[b.type] || 0)) * dir || (_rec(b) - _rec(a));
                    case 'conf':   return ((a.confScore || 0) - (b.confScore || 0)) * dir || (_rec(b) - _rec(a));
                    case 'price': {
                        const pa = a.price == null ? null : a.price, pb = b.price == null ? null : b.price;
                        if (pa === null && pb === null) return 0;
                        if (pa === null) return 1; if (pb === null) return -1;
                        return (pa - pb) * dir;
                    }
                    case 'recent':
                        return (_rec(a) - _rec(b)) * dir;
                    default:
                        // Confirmed before forming, then by confScore, then recency.
                        if ((a.status === 'forming') !== (b.status === 'forming'))
                            return a.status === 'forming' ? 1 : -1;
                        return (b.confScore || 0) - (a.confScore || 0) || (_rec(b) - _rec(a));
                }
            };
            alerts = alerts.slice().sort(cmp);
            updateAlertsSortIndicators();

            if (countEl) countEl.textContent = alerts.length ? `· ${alerts.length}` : '';

            if (!alerts.length) {
                empty.classList.remove('hidden');
                feed.classList.add('hidden');
                tbody.innerHTML = '';
                return;
            }
            empty.classList.add('hidden');
            feed.classList.remove('hidden');

            tbody.innerHTML = alerts.map(a => {
                const meta = ALERT_TYPE_META[a.type] || ALERT_TYPE_META.alpha;
                const price = a.price != null ? `$${a.price.toFixed(2)}` : '--';
                const unreadDot = a.read ? '' : '<span class="inline-block w-1.5 h-1.5 rounded-full bg-rose-500 ml-1 align-middle"></span>';
                const forming = a.status === 'forming';
                // Confirmation: plain text, no icons. Grade+score for confirmed; %% for forming.
                // Forming uses sky color; that (plus the dimmed row) IS the forming cue.
                const gradeCls = a.grade === 'A' ? 'text-indigo-300'
                    : a.grade === 'B' ? 'text-emerald-300'
                    : a.grade === 'C' ? 'text-amber-300'
                    : 'text-slate-400';
                // CONF breakdown. Beta/Alpha use the SERVER components (RVOL · alignment/RS ·
                // follow-through); legacy types use the old Trigger/Setup/Context bars.
                let hasBd, tW, sW, cW, cxCol, bdTip;
                const sc = a.confComponents;
                if (sc) {
                    hasBd = true;
                    const rvol = Number.isFinite(sc.reclaimRvol) ? sc.reclaimRvol : null;
                    const aligned = sc.dipAligned, held = sc.held;
                    const rs = Number.isFinite(sc.relStrInDip) ? sc.relStrInDip : 0;
                    tW = Math.max(4, Math.min(100, ((rvol || 0) - 1) * 20 / 40 * 100));            // RVOL (blue)
                    const alignRs = (aligned === true ? 15 : aligned === false ? -15 : 0) + Math.max(-20, Math.min(30, rs * 4));
                    sW = Math.max(4, Math.min(100, (alignRs + 20) / 50 * 100));                     // alignment+RS (purple)
                    cW = Math.max(4, held === true ? 100 : held === false ? 4 : 50);                // follow-through (state col)
                    cxCol = held === true ? '#5dcaa5' : held === false ? '#fb7185' : '#38bdf8';
                    const alignTxt = aligned === true ? 'market-aligned' : aligned === false ? 'divergent · weak RS' : 'align n/a';
                    const stateTxt = a.sigState === 'confirmed' ? 'confirmed (held EMA)' : a.sigState === 'failed' ? 'failed (lost EMA)' : 'fresh (unconfirmed)';
                    bdTip = `CONF ${a.grade || '–'} ${a.confScore} — RVOL ${rvol ? rvol.toFixed(1) : '?'}× · ${alignTxt} · RS ${rs >= 0 ? '+' : ''}${rs.toFixed(1)} · ${stateTxt}.${a.gatePass ? '' : ' Below RVOL gate.'}`;
                } else {
                    hasBd = Number.isFinite(a.confTrigger) && Number.isFinite(a.confSetup);
                    const cxRaw = Number.isFinite(a.confContext) ? a.confContext : 0;
                    tW = hasBd ? Math.max(4, Math.min(100, a.confTrigger / 40 * 100)) : 0;
                    sW = hasBd ? Math.max(4, Math.min(100, a.confSetup / 35 * 100)) : 0;
                    cW = hasBd ? Math.max(4, Math.min(100, (cxRaw + 5) / 10 * 100)) : 0;
                    cxCol = cxRaw > 0 ? '#5dcaa5' : cxRaw < 0 ? '#fb7185' : '#475569';
                    bdTip = hasBd
                        ? `CONF ${a.grade || '–'} ${a.confScore} — Trigger ${a.confTrigger}/40 (is the signal real) · Setup ${a.confSetup}/35 (is the stock worth it) · Context ${cxRaw >= 0 ? '+' : ''}${cxRaw} (tape/sector). ${a.gatePass ? 'Gate ✓ RVOL≥1.5 + Stage 2' : 'Gate ✗ — capped at C'}.${(a.confCtxBits && a.confCtxBits.length) ? ' [' + a.confCtxBits.join(', ') + ']' : ''}`
                        : (a.confState === 'provisional' ? 'Provisional — live projection, firms up at close' : 'Final — session complete');
                }
                // Signal lifecycle badge (server beta/alpha): fresh ◷ / confirmed ✓ / failed ✗.
                const stCol = a.sigState === 'confirmed' ? 'text-emerald-400' : a.sigState === 'failed' ? 'text-rose-400' : a.sigState === 'confirming' ? 'text-amber-300' : a.sigState === 'fresh' ? 'text-sky-300' : '';
                const stGlyph = a.sigState === 'confirmed' ? '✓' : a.sigState === 'failed' ? '✗' : a.sigState === 'confirming' ? ('◐' + (Number.isFinite(a.sigConfirmPct) ? a.sigConfirmPct + '%' : '')) : a.sigState === 'fresh' ? '◷' : '';
                const stTitle = a.sigState === 'confirming' && Number.isFinite(a.sigConfirmPct) ? `confirming ${a.sigConfirmPct}%` : (a.sigState || '');
                const stateBadge = a.sigState ? `<span class="${stCol} text-[8px] align-super ml-0.5" title="${stTitle}">${stGlyph}</span>` : (a.confState === 'provisional' ? '<span class="text-amber-400/70 text-[8px] align-super ml-0.5">●</span>' : '');
                const confCell = forming
                    ? `<span class="text-sky-300 font-bold tabular-nums whitespace-nowrap">${a.proximity ?? '--'}%</span>`
                    : `<span class="inline-flex flex-col items-end gap-0.5 cursor-help" title="${bdTip}"><span class="font-black tabular-nums whitespace-nowrap ${gradeCls}">${a.grade || '–'}${Number.isFinite(a.confScore) ? ' ' + a.confScore : ''}${stateBadge}</span>${hasBd ? `<span class="flex gap-0.5" style="width:46px"><span style="flex:1;height:3px;border-radius:2px;background:#1e293b;overflow:hidden"><span style="display:block;height:100%;width:${tW}%;background:#38bdf8"></span></span><span style="flex:1;height:3px;border-radius:2px;background:#1e293b;overflow:hidden"><span style="display:block;height:100%;width:${sW}%;background:#a78bfa"></span></span><span style="flex:1;height:3px;border-radius:2px;background:#1e293b;overflow:hidden"><span style="display:block;height:100%;width:${cW}%;background:${cxCol}"></span></span></span>` : ''}</span>`;
                // AVWAP pullback-defense micro-tag (daily swing-low AVWAP). Shown next to the
                // detail so you can see WHY a low-quality alert was demoted, and recognise the
                // institutionally-defended ones. null anchor → no tag.
                const av = Number.isFinite(a.avwapDefense) ? a.avwapDefense : null;
                let avTag = '';
                if (av !== null) {
                    if (av >= 80) avTag = `<span class="ml-1 text-emerald-400/90" title="Comfortably above swing-low AVWAP — institutionally defended">▲ AVWAP</span>`;
                    else if (av >= 40) avTag = `<span class="ml-1 text-slate-400" title="Holding swing-low AVWAP">◆ AVWAP</span>`;
                    else avTag = `<span class="ml-1 text-amber-400/90" title="Below swing-low AVWAP — undefended, low-quality (All view only)">▽ below AVWAP</span>`;
                }
                // RS Power tag (leadership). Delta = RS new high before price (gold), RS NH,
                // then proximity tiers. Lets you see leadership at a glance in the feed.
                const rsT = a.rsPowerTier || null;
                let rsTag = '';
                if (rsT) {
                    const rsCls = rsT === 'Delta' ? 'text-fuchsia-300' : rsT === 'RS NH' ? 'text-emerald-400/90'
                        : rsT === 'Primed' ? 'text-teal-300' : rsT === 'Building' ? 'text-blue-300'
                        : rsT === 'Firm' ? 'text-slate-400' : 'text-amber-400/70';
                    rsTag = `<span class="ml-1 ${rsCls}" title="RS Power — ${rsT === 'Delta' ? 'RS new high BEFORE price (leading accumulation)' : rsT === 'RS NH' ? 'RS line at a new high' : 'RS line ' + rsT.toLowerCase() + ' toward a new high'}">◇ ${rsT}</span>`;
                }
                // Alert label: type only, no spinner glyph. Forming shown via row dimming + sky conf.
                const rowStyle = forming
                    ? `border-left:3px solid #38bdf8;opacity:0.8;`
                    : `border-left:3px solid ${meta.border};`;
                // Watchlist marker: filled amber star if watched, hollow grey star (click to add)
                // if this alert is on a stock NOT in the watchlist.
                const star = a.watched
                    ? `<button data-alert-watch="${a.ticker}" title="In watchlist" class="shrink-0 text-amber-400 hover:text-amber-300 text-sm leading-none">★</button>`
                    : `<button data-alert-watch="${a.ticker}" title="Add to watchlist" class="shrink-0 text-slate-600 hover:text-amber-400 text-sm leading-none">☆</button>`;
                return `<tr data-ticker="${a.ticker}" class="cursor-pointer hover:bg-blue-950/20 transition-colors" style="${rowStyle}">
                    <td class="py-2 pl-5 pr-2 font-black text-white"><span class="inline-flex items-center gap-1.5">${star}<span>${a.ticker}</span>${unreadDot}</span></td>
                    <td class="py-2 px-2"><span class="px-1.5 py-0.5 rounded border ${meta.chip} ${meta.text} font-bold text-[10px] whitespace-nowrap">${meta.short || meta.label}</span></td>
                    <td class="py-2 px-2 text-center text-[11px]">${confCell}</td>
                    <td class="py-2 px-2 text-right text-slate-300 tabular-nums whitespace-nowrap">${price}</td>
                    <td class="py-2 px-2 text-[10px] text-slate-400 truncate max-w-[260px]" title="${a.detail || ''}">${a.detail || ''}${avTag}${rsTag}</td>
                    <td class="py-2 px-2 text-right pr-5 text-[10px] text-slate-500 whitespace-nowrap">${alertWhenText(a)}</td>
                </tr>`;
            }).join('');
        }


        function renderUnifiedScore(metrics, unified = getUnifiedScore(metrics)) {
            const scoreStruct = unified.scoreStruct;
            const scoreLiq = unified.scoreLiq;
            const scoreGeom = unified.scoreGeom;
            const finalScore = unified.finalScore;

            // 5-Tier Classification Logic
            let tier = 5, gradeStr = "F", tierNameStr = "Tier 5: Broken / Distribution", actionStr = "Not buyable. Wrong regime or active distribution.", colorKey = "rose";
            
            if (finalScore >= 900) { 
                tier = 1; gradeStr = "S"; tierNameStr = "Tier 1: Buyable Leader Now"; actionStr = "Leadership quality plus clean entry risk. Buy/add candidate."; colorKey = "fuchsia"; 
            } else if (finalScore >= 750) { 
                tier = 2; gradeStr = "A"; tierNameStr = "Tier 2: Prime Candidate"; actionStr = "Strong candidate. Entry is acceptable, but confirm risk and demand."; colorKey = "emerald"; 
            } else if (finalScore >= 600) { 
                tier = 3; gradeStr = "B"; tierNameStr = "Tier 3: Watch / Tactical"; actionStr = "Interesting stock, but not ideal now. Wait for cleaner buyability."; colorKey = "blue"; 
            } else if (finalScore >= 400) { 
                tier = 4; gradeStr = "C"; tierNameStr = "Tier 4: Avoid Today"; actionStr = "Too extended, too risky, weak group, or poor demand."; colorKey = "amber"; 
            }

            // Tailwind Map for Dynamic UI Changes
            const colorMap = {
                'fuchsia': { border: 'border-fuchsia-500', text: 'text-fuchsia-400', bgPanel: 'bg-fuchsia-900/10', glow: 'shadow-[0_0_25px_rgba(217,70,239,0.3)]', panelBorder: 'border-l-fuchsia-500', bgCir: 'bg-fuchsia-900/40', badge: 'bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/50' },
                'emerald': { border: 'border-emerald-500', text: 'text-emerald-400', bgPanel: 'bg-emerald-900/10', glow: 'shadow-[0_0_25px_rgba(52,211,153,0.3)]', panelBorder: 'border-l-emerald-500', bgCir: 'bg-emerald-900/40', badge: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/50' },
                'blue': { border: 'border-blue-500', text: 'text-blue-400', bgPanel: 'bg-blue-900/10', glow: 'shadow-[0_0_20px_rgba(59,130,246,0.25)]', panelBorder: 'border-l-blue-500', bgCir: 'bg-blue-900/40', badge: 'bg-blue-500/20 text-blue-300 border-blue-500/50' },
                'amber': { border: 'border-amber-500', text: 'text-amber-400', bgPanel: 'bg-amber-900/10', glow: 'shadow-[0_0_15px_rgba(245,158,11,0.15)]', panelBorder: 'border-l-amber-500', bgCir: 'bg-amber-900/40', badge: 'bg-amber-500/20 text-amber-300 border-amber-500/50' },
                'rose': { border: 'border-rose-500', text: 'text-rose-400', bgPanel: 'bg-rose-900/10', glow: 'shadow-[0_0_15px_rgba(244,63,94,0.15)]', panelBorder: 'border-l-rose-500', bgCir: 'bg-rose-900/40', badge: 'bg-rose-500/20 text-rose-300 border-rose-500/50' }
            };
            const theme = colorMap[colorKey];
            const bucketStatus = (value, max, kind = 'default') => {
                const pct = max > 0 ? value / max : 0;
                if (kind === 'timing') {
                    if (pct >= 0.70) return { label: 'Clean', cls: 'text-emerald-400 border border-emerald-500/30 bg-emerald-900/20' };
                    if (pct >= 0.50) return { label: 'OK', cls: 'text-blue-300 border border-blue-500/30 bg-blue-900/20' };
                    return { label: 'Weak', cls: 'text-amber-300 border border-amber-500/30 bg-amber-900/20' };
                }
                if (pct >= 0.80) return { label: 'Strong', cls: 'text-emerald-400 border border-emerald-500/30 bg-emerald-900/20' };
                if (pct >= 0.60) return { label: 'Good', cls: 'text-blue-300 border border-blue-500/30 bg-blue-900/20' };
                if (pct >= 0.40) return { label: 'Mixed', cls: 'text-amber-300 border border-amber-500/30 bg-amber-900/20' };
                return { label: 'Weak', cls: 'text-rose-300 border border-rose-500/30 bg-rose-900/20' };
            };
            const formatBucket = (value, max, kind) => {
                const state = bucketStatus(value, max, kind);
                const tcls = (state.cls.match(/text-[a-z]+-\d+/) || ['text-slate-300'])[0];
                return { ...state, tcls, text: `${state.label} ${value.toFixed(value < 10 ? 1 : 0)}/${max}` };
            };
            const trendBucket = formatBucket(scoreStruct, 420);
            const demandBucket = formatBucket(scoreLiq, 230);
            const timingBucket = formatBucket(scoreGeom, 350, 'timing');
            const capNote = unified.caps.length ? ` Capped: ${unified.caps.slice(0, 2).join(', ')}.` : '';
            // Dense, explanatory summary: states what the score reflects and the limiting factor,
            // so the line teaches the reading rather than restating the tier name.
            const cl = unified.clusters || {};
            const clusterReadout = (cl.trend && cl.entry && cl.demand)
                ? `Trend ${cl.trend.score}/40 · Entry ${cl.entry.score}/40 · Demand ${cl.demand.score}/20.`
                : '';
            // Identify the cluster dragging the score most (lowest % of its budget) for coaching.
            let limiter = '';
            if (cl.trend && cl.entry && cl.demand) {
                const pct = { Trend: cl.trend.score/40, Entry: cl.entry.score/40, Demand: cl.demand.score/20 };
                const weakKey = Object.keys(pct).reduce((a,b) => pct[a] <= pct[b] ? a : b);
                const weakObj = weakKey === 'Trend' ? cl.trend : weakKey === 'Entry' ? cl.entry : cl.demand;
                limiter = ` Limiting factor: ${weakKey.toLowerCase()} (weakest link: ${weakObj.weakest}).`;
            }
            // ── BALANCED PRO/CON COMMENTARY ─────────────────────────────────────────
            // Lays out the case both ways (supports entry / argues against) from real engine
            // data — never declares a buy. A fresh, in-range Alpha/Beta is the lead consideration.
            function buildProConCommentary() {
                const pros = [], cons = [], lead = [];
                const m = metrics;
                const sig = (typeof window !== 'undefined' && window.__signalContext) || {};

                // Signal freshness + in-range (the headline trigger when present).
                // Alpha: fresh ≤30d & within 15% of signal; Beta: ≤10d & within 8%.
                const a = sig.alpha, b = sig.beta;
                if (a && a.days != null && a.fromNow != null && a.days <= 30 && a.fromNow <= 15 && a.fromNow >= -8) {
                    lead.push(`Fresh Alpha ${a.days <= 0 ? 'today' : a.days + 'd ago'} — in-range (${a.fromNow >= 0 ? '+' : ''}${a.fromNow.toFixed(0)}%).`);
                    pros.push('defined in-range Alpha pivot');
                } else if (a && a.days != null && (a.days > 30 || a.fromNow > 15)) {
                    cons.push(a.fromNow > 15 ? `Alpha +${a.fromNow.toFixed(0)}% past entry` : 'Alpha aged past entry');
                }
                if (b && b.days != null && b.fromNow != null && b.days <= 10 && b.fromNow <= 8 && b.fromNow >= -5) {
                    lead.push(`Fresh Beta ${b.days <= 0 ? 'today' : b.days + 'd ago'} — in-range (${b.fromNow >= 0 ? '+' : ''}${b.fromNow.toFixed(0)}%).`);
                    pros.push('recent in-range Beta reclaim');
                }

                // RVOL (participation/confirmation).
                const rv = Number.isFinite(m.rvol) ? m.rvol : null;
                if (rv != null) {
                    if (rv >= 1.5) pros.push(`volume confirms (RVOL ${rv.toFixed(1)}×)`);
                    else if (rv < 0.9) cons.push(`light volume (RVOL ${rv.toFixed(1)}×)`);
                }

                // Channel stage (where in the breakout structure).
                const ch = Number(m.channelStage);
                const chPos = Number(m.channelPosition);
                const chMap = {1:'Breakdown',2:'Lower',3:'Mid',4:'Upper',5:'Breaking',6:'Breakout'};
                if (ch >= 1 && ch <= 6) {
                    if (ch === 5 || ch === 4) pros.push(`well-timed in the channel (${ch}/6 ${chMap[ch]})`);
                    else if (ch === 6 && chPos <= 103) pros.push(`fresh breakout (6/6, still near the line)`);
                    else if (ch === 6) cons.push(`breakout already extended (6/6, price stretched past the line)`);
                    else if (ch <= 2) cons.push(`weak channel position (${ch}/6 ${chMap[ch]})`);
                    else cons.push(`mid-channel (${ch}/6) — no clear edge yet`);
                }

                // RMV (tightness).
                const rmv = Number.isFinite(m.rmv) ? m.rmv : null;
                if (rmv != null) {
                    if (rmv < 25) pros.push('coiled tight');
                    else if (rmv > 50) cons.push('range expanded (wider stop)');
                }

                // Extension.
                if (Number.isFinite(m.atrMult)) {
                    if (m.atrMult > 10) cons.push('extreme extension');
                    else if (m.atrMult > 7) cons.push('extended from 50-MA');
                    else if (m.atrMult >= 0 && m.atrMult <= 4) pros.push('not extended');
                }

                // Stage.
                const st = Number(m.stage);
                if (st === 2) pros.push('Stage 2 advancing');
                else if (st === 4) cons.push('Stage 4 decline — structurally a pass');
                else if (st === 3) cons.push('Stage 3 topping — distribution risk');
                else if (st === 1) cons.push('Stage 1 base — not yet advancing');

                // Sector / group (when available): rank PLUS what the group is doing (rotation).
                const grpRank = Number(m.groupRank), grpTot = Number(m.groupRankTotal);
                if (grpTot > 0) {
                    if (grpRank <= 10) pros.push(`leading group (#${grpRank}/${grpTot})`);
                    else if (grpRank / grpTot > 0.6) cons.push(`lagging group (#${grpRank}/${grpTot})`);
                }
                // Rotation read from the Group RS engine: is money flowing into or out of this stock's group right now?
                const grpName = currentTickerClassification?.industryGroup;
                const grpRot = grpName && Array.isArray(groupRankingData)
                    ? groupRankingData.find(g => g.name === grpName) : null;
                if (grpRot && grpRot.rotationTag) {
                    const grvolTxt = Number.isFinite(grpRot.grvol) && grpRot.grvol >= 1.3 ? ` on heavy group volume (GRVOL ${grpRot.grvol.toFixed(1)}×)` : '';
                    if (grpRot.rotationTag === 'in') pros.push(`group is rotating in${grvolTxt} — sector tailwind`);
                    else if (grpRot.rotationTag === 'leader') pros.push(`group is a stable leader (RS ${grpRot.rsComposite})`);
                    else if (grpRot.rotationTag === 'out') cons.push(`group is rotating out — sector headwind`);
                    else if (grpRot.rotationTag === 'dormant') cons.push(`group is dormant (low RS, light volume) — no rotation support`);
                }

                // Weakest cluster (the standing structural drag).
                if (cl.trend && cl.entry && cl.demand) {
                    const pct = { trend: cl.trend.score/40, entry: cl.entry.score/40, demand: cl.demand.score/20 };
                    const weakKey = Object.keys(pct).reduce((x,y) => pct[x] <= pct[y] ? x : y);
                    if (pct[weakKey] < 0.5) {
                        const wo = weakKey === 'trend' ? cl.trend : weakKey === 'entry' ? cl.entry : cl.demand;
                        cons.push(`${weakKey} is the weak cluster (${wo.weakest})`);
                    }
                }

                // Market regime: prefer the Breadth & Situational Awareness read (broad
                // participation, equal-weight divergence), fall back to the VIX benchmark.
                let market = '';
                const ms = engineState.lastMarketStructure;
                if (ms && ms.n) {
                    const bits = [];
                    if (Number.isFinite(ms.above50)) bits.push(`${ms.above50.toFixed(0)}% >50-MA`);
                    if (ms.rspTrend === 'up') bits.push('broad');
                    else if (ms.rspTrend === 'down') bits.push('narrow');
                    const detail = bits.length ? ` — ${bits.join(', ')}` : '';
                    market = ` Tape: ${ms.verdict}${detail}.`;
                } else {
                    const vixEl = document.getElementById('bench_1m_vixy');
                    if (vixEl && vixEl.textContent && vixEl.textContent !== '--') {
                        const vixUp = vixEl.textContent.trim().startsWith('+') || vixEl.textContent.includes('▲');
                        market = vixUp
                            ? ' Market: volatility rising — a more hostile backdrop for new entries.'
                            : ' Market: volatility subdued — a constructive backdrop for entries.';
                    }
                }

                // Assemble: lead (signal) → pros → cons → market.
                let out = '';
                if (lead.length) out += lead.join(' ') + ' ';
                if (pros.length) out += `For: ${pros.slice(0, 2).join('; ')}. `;
                if (cons.length) out += `Against: ${cons.slice(0, 2).join('; ')}. `;
                if (!pros.length && !cons.length) out += `Trend ${cl.trend?.score ?? '--'}/40 · Entry ${cl.entry?.score ?? '--'}/40 · Demand ${cl.demand?.score ?? '--'}/20. `;
                out += capNote;
                out += market;
                return out.trim();
            }
            const dynamicAction = buildProConCommentary();

            // Render UI securely via Optional Chaining just in case DOM is mid-paint
            if (ui.convictionPanel) {
                ui.convictionPanel.classList.remove('hidden');
                ui.convictionPanel.className = `premium-card rounded-2xl px-5 py-4 shadow-2xl transition-all duration-500 border-l-[4px] ${theme.panelBorder || 'border-accent'}`;
                // Release the height reservation / dim applied during the ticker switch.
                ui.convictionPanel.style.minHeight = '';
                ui.convictionPanel.style.opacity = '';


                // Ticker + name (group split into its own muted badge)
                if (ui.tierStockBadge) {
                    const displayTicker = metrics.ticker || state.currentTicker || '';
                    const displayName = metrics.stockName || state.currentTickerName || '';
                    ui.tierStockBadge.classList.remove('hidden');
                    ui.tierStockBadge.textContent = (displayName && displayName !== displayTicker) ? `${displayTicker} · ${displayName}` : (displayTicker || 'Loaded stock');
                }
                if (ui.tierGroupBadge) {
                    const group = currentTickerClassification?.industryGroup;
                    const stat = group ? industryGroupStats.get(group) : null;
                    if (group) {
                        ui.tierGroupBadge.classList.remove('hidden');
                        ui.tierGroupBadge.textContent = `${group}${stat ? ` #${stat.rank}/${stat.total}` : ''}`;
                    } else ui.tierGroupBadge.classList.add('hidden');
                }

                // Compact tier label, e.g. "Tier 3 · Watch / Tactical"
                ui.tierName.textContent = tierNameStr.replace(':', ' ·');
                ui.tierName.className = `text-[13px] font-bold tracking-wide uppercase leading-none ${theme.text}`;

                // Company description — "what this company does". Layered fallback so the
                // line is never empty even when Polygon's rich `description` is absent (Starter):
                //   1) full business description  →  first sentence
                //   2) SIC industry description    →  "<Name> operates in <SIC>."
                //   3) app's own classification    →  "<Name> — <Group> (<Sector>)."
                if (ui.tierCompany) {
                    const nm = (state.currentTickerName && state.currentTickerName !== state.currentTicker) ? state.currentTickerName : (state.currentTicker || 'This company');
                    const cls = currentTickerClassification || {};
                    let line = '';
                    const full = (typeof currentTickerDescription === 'string') ? currentTickerDescription.trim() : '';
                    const sic = (typeof currentTickerSic === 'string') ? currentTickerSic.trim() : '';
                    if (full && full.length > 12) {
                        line = full.split(/(?<=\.)\s/)[0];                       // first sentence
                        if (line.length > 170) line = line.slice(0, 167).replace(/[\s,;]+\S*$/, '') + '…';
                    } else if (sic) {
                        const sicNice = sic.charAt(0).toUpperCase() + sic.slice(1).toLowerCase();
                        line = `${nm} operates in ${sicNice}.`;
                    } else if (cls.industryGroup && cls.industryGroup !== 'Unknown') {
                        line = `${nm} — ${cls.industryGroup}${cls.sector && cls.sector !== cls.industryGroup ? ` · ${cls.sector}` : ''}.`;
                    }
                    if (line) { ui.tierCompany.textContent = line; ui.tierCompany.classList.remove('hidden'); }
                    else { ui.tierCompany.textContent = ''; ui.tierCompany.classList.add('hidden'); }
                }

                ui.tierAction.textContent = dynamicAction;
                ui.tierAction.className = 'text-[11px] text-slate-500 font-medium leading-relaxed max-w-2xl normal-case tracking-normal';

                ui.scStruct.textContent = trendBucket.text;
                ui.scStruct.title = unified.detail ? `Quality detail: Stage ${unified.detail.quality.stage}/120, Trend template ${unified.detail.quality.trendTemplate}/100, Momentum ${unified.detail.quality.momentum}/120, Group ${unified.detail.quality.group}/80.` : "Leadership quality: stage, trend template, momentum, and industry group. Max 420.";
                ui.scStruct.className = `font-mono font-bold ${trendBucket.tcls || 'text-slate-300'}`;

                ui.scLiq.textContent = demandBucket.text;
                ui.scLiq.title = unified.detail ? `Demand detail: Volume ${unified.detail.demand.volume}/90, Accumulation/alerts ${unified.detail.demand.accumulation}/60, Growth/liquidity ${unified.detail.demand.acceleration}/80.` : "Demand, fundamentals, and liquidity confirmation. Max 230.";
                ui.scLiq.className = `font-mono font-bold ${demandBucket.tcls || 'text-slate-300'}`;

                ui.scGeom.textContent = timingBucket.text;
                ui.scGeom.title = unified.detail ? `Buyable detail: RMV ${unified.detail.buyable.rmv}/80, MA location ${unified.detail.buyable.movingAverages}/80, Stop ${unified.detail.buyable.stop}/70, Channel ${unified.detail.buyable.channel}/60, Extension ${unified.detail.buyable.extension}/60.` : "Buyability now: RMV, MA distance, stop geometry, channel position, and extension. Max 350.";
                ui.scGeom.className = `font-mono font-bold ${timingBucket.tcls || 'text-slate-300'}`;

                ui.scoreValue.textContent = finalScore;
                ui.scoreValue.className = `score-value-sm tabular-nums`;
                ui.scoreValue.style.color = unified.gradeColor || '';

                // Compact conic-gradient score ring — continuously graded by the score band.
                const deg = Math.max(0, Math.min(360, (finalScore / 1000) * 360));
                ui.scoreCircle.style.setProperty('--score-deg', `${deg}deg`);
                if (unified.gradeColor) ui.scoreCircle.style.setProperty('--accent-primary', unified.gradeColor);
                ui.scoreCircle.classList.toggle('animate-score', tier <= 2);

                // Percentile-of-fit readout + market-tape / raw chain under the gauge.
                const _pf = document.getElementById('scorePctFit');
                if (_pf) _pf.textContent = Number.isFinite(unified.pctFit) ? `${unified.pctFit}ᵗʰ pct fit` : '/ 1000';
                const _tc = document.getElementById('scoreTapeChip');
                if (_tc) {
                    const tp = Math.round(((unified.tape || 1) - 1) * 100);
                    const penOff = Math.round((1 - (unified.penaltyMult ?? 1)) * 100);
                    const bits = [];
                    if (tp !== 0) bits.push(`tape ${tp > 0 ? '+' : ''}${tp}%`);
                    if (penOff > 0) bits.push(`pen −${penOff}%`);
                    if (bits.length) { _tc.textContent = bits.join(' · '); _tc.classList.remove('hidden'); }
                    else _tc.classList.add('hidden');
                }

                // Key-drivers list — impact-ranked attribution: what is actually moving the score.
                const _dr = document.getElementById('scoreDrivers');
                if (_dr) {
                    const ds = Array.isArray(unified.drivers) ? unified.drivers.slice(0, 5) : [];
                    if (ds.length) {
                        const barCol = s => s >= 66 ? '#10b981' : s >= 40 ? '#f59e0b' : '#ef4444';
                        const rows = ds.map(d => {
                            const impCol = d.dir === 'up' ? '#34d399' : '#f87171';
                            const sign = d.impact > 0 ? '+' : '';
                            return `<div class="flex items-center gap-2 text-[9px]">
                                <span class="w-[84px] shrink-0 truncate text-slate-300" title="${d.name}">${d.name}</span>
                                <div class="flex-1 h-[4px] bg-slate-800 rounded-full overflow-hidden"><div class="h-full rounded-full" style="width:${Math.max(2, Math.min(100, d.strength))}%;background:${barCol(d.strength)}"></div></div>
                                <span class="w-[40px] shrink-0 text-right tabular-nums font-semibold" style="color:${impCol}">${sign}${d.impact}</span>
                            </div>`;
                        }).join('');
                        _dr.innerHTML = `<div class="text-[8px] uppercase tracking-wider text-slate-600 mb-1">Key drivers · impact</div>${rows}`;
                        _dr.classList.remove('hidden');
                    } else { _dr.classList.add('hidden'); }
                }
            }
            return unified;
        }

        /**
         * Single-ticker conviction engine: scores the loaded ticker and renders the hero
         * gauge + breakdown. Same buckets/penalties as the screener's row scorer.
         * @param {TickerMetrics & {ticker:string, stockName?:string}} metrics
         * @returns {Object}  The unified score object (also cached as lastUnifiedScore)
         */
        function calculateConvictionScore(metrics) {
            currentScoreInput = { ...metrics };
            const unified = getUnifiedScore(currentScoreInput);
            lastUnifiedScore = unified;
            return renderUnifiedScore(currentScoreInput, unified);
        }

        // ─────────────────────────────────────────────────────────────────────────
        // TRADE ASSISTANT — pre-trade checklist
        // Collapses every signal the dashboard computes into PASS / CAUTION / FAIL
        // checks, with VARS as the volatility-adjusted RS confirmation. Headline verdict
        // is gated by the weakest CRITICAL check: one critical FAIL = no-go.
        // ─────────────────────────────────────────────────────────────────────────
        // SHARED TRADE RATING (1.0–5.0) — pure, no DOM, no async.
        // Same tier weights & thresholds as the live Trade Assistant, computed from a
        // row's stored fields. VARS is omitted (too heavy per-row); group rotation &
        // market backdrop are used when available via ctx. Used by the screener column.
        // ─────────────────────────────────────────────────────────────────────────

        // ─────────────────────────────────────────────────────────────────────────
        // TRADE ASSISTANT — full pre-trade checklist (three tiers)
        //   VETO     : a fail caps the verdict (reconciled with the engine's own caps)
        //   HEAVY    : the cluster-leading factors (RS, VARS, RMV, stop, RVOL, etc.)
        //   SUPPORT  : confirming factors (template, momentum, group, growth, backdrop)
        // Fresh Alpha/Beta surfaces as a prominent high-conviction banner.
        // ─────────────────────────────────────────────────────────────────────────
        async function renderTradeAssistant() {
            const wrap = document.getElementById('tradeAssistantBody');
            const head = document.getElementById('tradeAssistantVerdict');
            const banner = document.getElementById('tradeAssistantBanner');
            if (!wrap) return;
            const m = currentScoreInput;
            if (!m) {
                wrap.innerHTML = `<div class="text-[11px] text-slate-600 italic px-1 py-2">Load a ticker to run the full factor checklist.</div>`;
                if (head) { head.textContent = '—'; head.className = 'text-xs font-black px-3 py-1 rounded-lg border border-slate-700 text-slate-500'; }
                if (banner) banner.classList.add('hidden');
                return;
            }

            try {
            const P = 'pass', C = 'caution', F = 'fail';
            const num = (v) => Number.isFinite(Number(v)) ? Number(v) : null;
            // tier: 'veto' | 'heavy' | 'support'
            const factors = [];
            const add = (section, label, status, detail, tier, grade) => factors.push({ section, label, status, detail, tier, grade: (typeof grade === 'number' ? grade : null) });
            const g2s = g => g >= 0.7 ? P : g >= 0.4 ? C : F;

            const stage = num(m.stage);
            const dist50 = num(m.dist50), dist200 = num(m.dist200), sma200Trend = num(m.sma200Trend);
            const atrMult = num(m.atrMult), rmv = num(m.rmv), stopDist = num(m.stopDistPct);
            const rvol = num(m.rvol), rs = num(m.rsProxy), mvPassed = num(m.mvPassed);
            const pct1m = num(m.pct1m), pct3m = num(m.pct3m), pct6m = num(m.pct6m);
            const belowHigh = num(m.belowHighPct), volContraction = m.volatilityContraction;
            const accumD = num(m.accumulationDays), distD = num(m.distributionDays);
            const udVol = num(m.upDownVolRatio), volSig = m.volumeSignal;
            const epsScore = num(m.epsScore), salesScore = num(m.salesScore);
            const dollarVol = num(m.dollarVolume), adr = currentTickerAdrPct;

            // ═══ TREND & STAGE ═══
            // 1. Stage 2 (VETO — Stage 3/4 caps in the engine)
            if (stage === 2) add('Trend', 'Stage 2 advance', P, 'in the buyable advance', 'veto');
            else if (stage === 1) add('Trend', 'Stage', C, 'Stage 1 base — not advancing yet', 'veto');
            else if (stage === 3) add('Trend', 'Stage', F, 'Stage 3 topping — distribution risk', 'veto');
            else if (stage === 4) add('Trend', 'Stage', F, 'Stage 4 decline — structural pass', 'veto');
            else add('Trend', 'Stage', C, 'stage unclear', 'veto');

            // 2. Above 200-MA (VETO — engine caps below-200MA)
            if (dist200 !== null) {
                if (dist200 >= 0) add('Trend', 'Above 200-MA', P, `${dist200.toFixed(0)}% above`, 'veto');
                else add('Trend', 'Above 200-MA', F, `${dist200.toFixed(0)}% — below 200-MA`, 'veto');
            }
            // 3. 200-MA rising (SUPPORT)
            if (sma200Trend !== null) {
                if (sma200Trend > 0) add('Trend', '200-MA rising', P, 'long-term trend up', 'support');
                else add('Trend', '200-MA slope', F, 'flat/falling 200-MA', 'support');
            }
            // 4. Above 50-MA (SUPPORT)
            if (dist50 !== null) {
                if (dist50 >= 0) add('Trend', 'Above 50-MA', P, `${dist50.toFixed(0)}% above`, 'support');
                else add('Trend', 'Above 50-MA', C, `${dist50.toFixed(0)}% — below 50-MA`, 'support');
            }
            // 5. Minervini template (HEAVY)
            if (mvPassed !== null) {
                { const g = gradeCredit(mvPassed, 4, 8); add('Trend', 'Trend Template', g2s(g), `${mvPassed}/8 Minervini criteria`, 'heavy', g); }
            }

            // ═══ RELATIVE STRENGTH ═══
            // 6. VARS (HEAVY)
            const vars = await getCurrentVars();
            if (vars.value === null) add('Strength', 'VARS (vol-adj RS)', C, 'unavailable — load index data', 'heavy');
            else {
                const v = vars.value, st = vars.state?.txt || '';
                let g = gradeCredit(v, -0.5, 2.0);
                if (st === 'Leading') g = Math.max(g, 0.85); else if (st === 'Recovering') g = Math.max(g, 0.5);
                const det = v > 0 ? `+${v.toFixed(2)} ATR vs SPY${st ? ' · ' + st.toLowerCase() : ''}` : `${v.toFixed(2)} ATR · ${st ? st.toLowerCase() : 'lagging'}`;
                add('Strength', 'VARS (vol-adj RS)', g2s(g), det, 'heavy', g);
            }
            // 7. RS Rating (HEAVY)
            if (rs !== null) {
                const g = gradeCredit(rs, 45, 85);
                const det = rs >= 80 ? `${rs} — strong leadership` : rs >= 70 ? `${rs} — leading` : rs >= 50 ? `${rs} — middling` : `${rs} — laggard`;
                add('Strength', 'RS Rating', g2s(g), det, 'heavy', g);
            }
            // 8. Multi-timeframe momentum aligned (SUPPORT)
            if (pct1m !== null && pct3m !== null && pct6m !== null) {
                const aligned = pct1m > 0 && pct3m > 0 && pct6m > 0;
                if (aligned) add('Strength', 'Momentum aligned', P, `1M/3M/6M all positive`, 'support');
                else if (pct3m > 0 && pct6m > 0) add('Strength', 'Momentum', C, `longer TF up, 1M soft`, 'support');
                else add('Strength', 'Momentum', F, `momentum not aligned`, 'support');
            }
            // 9. Near 52-week high (SUPPORT)
            if (belowHigh !== null) {
                const g = gradeCredit(belowHigh, 25, 8);
                const lbl = belowHigh <= 25 ? 'Near 52w high' : 'Off 52w high';
                add('Strength', lbl, g2s(g), `${belowHigh.toFixed(0)}% below high`, 'support', g);
            }
            // 9b. RS divergence (SUPPORT) — leadership VELOCITY: is the RS edge accelerating or
            // fading right now (3-EMA vs 10-EMA of the RS line). Distinct from RS level/leadership.
            const dv = (typeof lastRsDivergence !== 'undefined') ? lastRsDivergence : null;
            if (dv && dv.state) {
                const gmap = { 'accelerating': 0.9, 'leading-fading': 0.6, 'lagging-recovering': 0.45, 'lagging-worse': 0.2 };
                const g = gmap[dv.state] != null ? gmap[dv.state] : 0.5;
                const sp = Number.isFinite(dv.spreadPct) ? `${dv.spreadPct >= 0 ? '+' : ''}${dv.spreadPct.toFixed(1)}%` : '';
                const dir = dv.widening ? 'widening' : 'narrowing';
                const txt = { 'accelerating': 'accelerating ahead', 'leading-fading': 'leading, converging', 'lagging-recovering': 'lagging, recovering', 'lagging-worse': 'falling behind' }[dv.state] || dv.state;
                add('Strength', 'RS divergence', g2s(g), `${txt}${sp ? ' · ' + sp : ''} · ${dir}`, 'support', g);
            }
            // 9c. Base depth / quality (SUPPORT) — from the raw swing structure (independent of BQS).
            // Shallow, tight, higher-low base = constructive; a deep (>~35%) base is damaged goods.
            const vcp = (typeof lastVcpBase !== 'undefined') ? lastVcpBase : null;
            if (vcp && vcp.count >= 1 && Number.isFinite(vcp.depthPct)) {
                // Constructive-weighted, NOT depth-punishing: rising lows + a tightening sequence are
                // what matter. A deep pullback only counts against it when the lows are ALSO breaking
                // (damaged); a deep base on higher lows — the classic post-momentum flag — stays good.
                let bg = 0.55;
                if (vcp.higherLows) bg += 0.20;
                if (vcp.tightening) bg += 0.15;
                if (vcp.depthPct <= 15) bg += 0.10;
                else if (vcp.depthPct >= 40 && !vcp.higherLows) bg -= 0.30;
                bg = Math.max(0, Math.min(1, bg));
                const dlbl = vcp.depthPct <= 15 ? 'tight' : vcp.depthPct <= 30 ? 'normal' : 'deep';
                add('Strength', 'Base quality', g2s(bg), `${vcp.depthPct.toFixed(0)}% ${dlbl}${vcp.higherLows ? ' · higher lows' : ''}${vcp.tightening ? ' · tightening' : ''}`, 'support', bg);
            }

            // ═══ ENTRY & RISK ═══
            // 10. Extension (VETO — engine caps extreme extension)
            if (atrMult !== null) {
                if (atrMult <= 4) add('Entry', 'Extension', P, `${atrMult.toFixed(1)}× ATR — not extended`, 'veto');
                else if (atrMult <= 7) add('Entry', 'Extension', C, `${atrMult.toFixed(1)}× ATR — getting extended`, 'veto');
                else if (atrMult <= 10) add('Entry', 'Extension', F, `${atrMult.toFixed(1)}× ATR — extended`, 'veto');
                else add('Entry', 'Extension', F, `${atrMult.toFixed(1)}× ATR — extreme`, 'veto');
            }
            // 11. Stop distance (VETO — engine caps stop >7/10%)
            if (stopDist !== null) {
                if (stopDist <= 7) add('Entry', 'Stop distance', P, `${stopDist.toFixed(1)}% — within 7%`, 'veto');
                else if (stopDist <= 10) add('Entry', 'Stop distance', C, `${stopDist.toFixed(1)}% — over 7%`, 'veto');
                else add('Entry', 'Stop distance', F, `${stopDist.toFixed(1)}% — over 10%`, 'veto');
            }
            // 11b. Reward:risk (SUPPORT) — a standard +20% objective vs the actual stop.
            // ≥3:1 = prime, <1.5:1 = poor. Distinct from the binary stop veto: grades trade quality.
            if (stopDist !== null && stopDist > 0) {
                const rr = 20 / stopDist; const g = gradeCredit(rr, 1, 3);
                add('Entry', 'Reward:risk', g2s(g), `${rr.toFixed(1)}:1 to +20% · stop ${stopDist.toFixed(1)}%`, 'support', g);
            }
            // 11c. Liquidity (SUPPORT) — tradeability: enough $-volume to get in/out at size.
            if (dollarVol !== null && dollarVol > 0) {
                const gl = gradeCredit(Math.log10(dollarVol), 6.3, 7.5); // ~$2M fail → ~$32M pass
                const dtxt = (typeof formatDollarBig === 'function') ? formatDollarBig(dollarVol) : `${Math.round(dollarVol / 1e6)}M`;
                add('Demand', 'Liquidity', g2s(gl), `$${dtxt}/day`, 'support', gl);
            }
            // 11d. VCP (UPGRADED) — graded by the unified coil engine's quality (vcpQuality),
            // not just present/absent. High-quality coils score strong + HEAVY; moderate coils
            // earn SUPPORT; genuinely weak coils are OMITTED (no credit). Mode (flag vs MA-ride),
            // stage, distance-to-pivot and volume confirmation are all surfaced. Never penalises.
            if (vcp && vcp.isVcp && vcp.stage !== 'extended' && Number.isFinite(vcp.vcpQuality)) {
                const q = vcp.vcpQuality;                       // 0..100 coil quality
                const g = Math.max(0, Math.min(1, q / 100));
                if (q >= 40) {                                  // omit genuinely weak coils
                    const ppv = volSig && (volSig.type || volSig) === 'ppv';
                    const atPivot = vcp.stage === 'coiling' || vcp.stage === 'breakout';
                    const confirmed = vcp.confirmed || (ppv && atPivot);
                    const weight = (q >= 65 || confirmed) ? 'heavy' : 'support';
                    const modeLbl = vcp.vcpMode === 'ma-ride' ? 'MA-ride' : 'flag';
                    const stageLbl = vcp.stage === 'coiling' ? 'coiling under pivot' : vcp.stage === 'breakout' ? 'breaking out' : 'forming';
                    const pivTxt = Number.isFinite(vcp.distToPivotPct) ? (vcp.distToPivotPct >= 0 ? ` · ${vcp.distToPivotPct.toFixed(0)}% to pivot` : ` · ${Math.abs(vcp.distToPivotPct).toFixed(0)}% past pivot`) : '';
                    const contr = (vcp.contractions && vcp.contractions.length) ? vcp.contractions.join('→') + '% · ' : '';
                    const tags = `${vcp.confirmed ? ' · vol dry-up' : ''}${ppv && atPivot ? ' · PPV' : ''}`;
                    add('Entry', 'VCP', g2s(g), `Q${q} · ${modeLbl} · ${contr}${stageLbl}${pivTxt}${tags}`, weight, g);
                }
            }
            // 11e. CANDLE (Nison, VCP-stage gated) — ONE factor. The same candle means different
            // things at different points of the coil, so direction × LOCATION decides the effect,
            // not the shape alone. Reads the last few daily bars (confirmation window), whitelists
            // only the patterns that matter to a tight base, and is CONTRARIAN at the edges:
            // exhaustion candles while EXTENDED warn (and ding the rating); reversal candles at a
            // washout LOW flag a possible shakeout bottom. Never fires mid-air. Loaded ticker only.
            (function candleFactor() {
                const src = (typeof engineState.cachedDataResults !== 'undefined' && engineState.cachedDataResults.length >= 3) ? engineState.cachedDataResults.slice(-5) : null;
                if (!src || !vcp) return;
                const A = src.map(b => {
                    const o = b.o, h = b.h, l = b.l, c = b.c, range = Math.max(1e-9, h - l);
                    const body = Math.abs(c - o), up = h - Math.max(o, c), lo = Math.min(o, c) - l;
                    return { o, h, l, c, v: b.v || 0, range, body, bull: c >= o,
                             bodyPct: body / range, upPct: up / range, loPct: lo / range, closePos: (c - l) / range };
                });
                const k = A.length, T = A[k - 1], Y = A[k - 2];
                const avgRange = A.reduce((s, x) => s + x.range, 0) / k;
                // shape primitives (whitelisted to the VCP-relevant set)
                const small      = T.bodyPct <= 0.34 && T.range <= avgRange * 1.1;            // quiet / doji
                const inside     = T.h <= Y.h && T.l >= Y.l;                                  // harami / inside bar
                const hammer     = T.loPct >= 0.5 && T.upPct <= 0.18 && T.bodyPct <= 0.4;     // long lower tail
                const star       = T.upPct >= 0.5 && T.loPct <= 0.18 && T.bodyPct <= 0.4;     // shooting star / gravestone
                const bullMaru   = T.bull && T.bodyPct >= 0.8;
                const bearWide   = !T.bull && T.bodyPct >= 0.6 && T.range >= avgRange * 1.4;
                const bullEngulf = T.bull && !Y.bull && T.c >= Y.o && T.o <= Y.c && T.body >= Y.body;
                const bearEngulf = !T.bull && Y.bull && T.o >= Y.c && T.c <= Y.o && T.body >= Y.body;
                const strongClose= T.bull && T.closePos >= 0.7 && T.bodyPct >= 0.5;
                // location context, straight from the coil engine
                const stage = vcp.stage, raw = vcp.coilRaw || {};
                const nearMA   = Number.isFinite(raw.minDistMA) && Math.abs(raw.minDistMA) <= 4;
                const atPivot  = stage === 'breakout' || (Number.isFinite(vcp.distToPivotPct) && Math.abs(vcp.distToPivotPct) <= 4);
                const extended = stage === 'extended' || (Number.isFinite(raw.gain12) && raw.gain12 >= 80 && Number.isFinite(raw.minDistMA) && raw.minDistMA >= 12);
                const washout  = (stage === 'coiling' || stage === 'forming') && nearMA && vcp.higherLows;

                let dir = 0, strength = 0, detail = null, weight = 'support';
                if (atPivot) {                                   // PIVOT — thrust vs failed breakout (decisive)
                    if (bullEngulf || bullMaru || strongClose) { dir = 1; strength = bullEngulf ? 0.75 : 0.62; weight = 'heavy'; detail = (bullEngulf ? 'Bullish engulfing' : bullMaru ? 'Bullish marubozu' : 'Strong-close push') + ' @ pivot — breakout thrust'; }
                    else if (star || bearEngulf) { dir = -1; strength = 0.7; weight = 'heavy'; detail = (bearEngulf ? 'Bearish engulfing' : 'Shooting star') + ' @ pivot — failed breakout'; }
                }
                if (!detail && (washout || nearMA)) {            // SUPPORT — shakeout held (contrarian buy-watch)
                    if (hammer || bullEngulf) { dir = 1; strength = hammer ? 0.6 : 0.65; weight = 'support'; detail = (hammer ? 'Hammer' : 'Bullish engulfing') + ' at support — shakeout held'; }
                }
                if (!detail && extended) {                       // EXTENDED — exhaustion (contrarian caution)
                    if (star || bearEngulf) { dir = -1; strength = 0.7; weight = 'heavy'; detail = (bearEngulf ? 'Bearish engulfing' : 'Shooting star') + ' while extended — exhaustion'; }
                }
                if (!detail && (stage === 'coiling' || stage === 'forming')) {  // INSIDE THE COIL
                    if (small || inside) { dir = 1; strength = 0.5; weight = 'support'; detail = (inside ? 'Inside day' : 'Quiet range') + ' in the coil — volatility drying up'; }
                    else if (bearWide) { dir = -1; strength = 0.55; weight = 'support'; detail = 'Wide down day in the coil — supply'; }
                }
                if (!detail) return;                             // mid-air / nothing relevant → omit (no clutter)
                const g = dir > 0 ? Math.min(1, 0.45 + strength * 0.55) : Math.max(0, 0.45 - strength * 0.55);
                add('Entry', 'Candle', g2s(g), detail, weight, g);
            })();
            // 12. RMV contraction (HEAVY)
            if (rmv !== null) {
                { const g = gradeCredit(rmv, 45, 20); const det = rmv < 25 ? `RMV ${rmv.toFixed(0)} — coiled tight` : rmv < 50 ? `RMV ${rmv.toFixed(0)} — normal` : `RMV ${rmv.toFixed(0)} — expanded`; add('Entry', 'RMV tightness', g2s(g), det, 'heavy', g); }
            }
            // 13. Volatility contraction flag (SUPPORT)
            if (volContraction !== undefined && volContraction !== null) {
                if (volContraction) add('Demand', 'Vol contraction', P, 'tightening into the base', 'support');
                else add('Demand', 'Vol contraction', C, 'no clear contraction', 'support');
            }
            // 14. ADR% tradeability — Qullamaggie momentum lens (graduated penalty).
            //   <3% genuinely too slow → VETO (hard cap). 3–4% slow → strong penalty (heavy
            //   fail, no hard cap). 4–9% perfect. 9–15% also good. 15%+ wild/risky.
            if (Number.isFinite(adr)) {
                if (adr < 3) add('Entry', 'ADR% (tradeability)', F, `${adr.toFixed(1)}% — far too slow for momentum`, 'veto');
                else if (adr < 4) add('Entry', 'ADR% (tradeability)', F, `${adr.toFixed(1)}% — slow, suboptimal vehicle`, 'heavy');
                else if (adr <= 9) add('Entry', 'ADR% (tradeability)', P, `${adr.toFixed(1)}% — perfect momentum range`, 'heavy');
                else if (adr <= 15) add('Entry', 'ADR% (tradeability)', P, `${adr.toFixed(1)}% — wilder but good`, 'heavy');
                else add('Entry', 'ADR% (tradeability)', C, `${adr.toFixed(1)}% — wild, high risk`, 'heavy');
            }
            // 15. Fresh in-range Alpha/Beta trigger (HEAVY) — recent in time AND still in range.
            //   Alpha: ≤30d & price −8% to +15% from signal · Beta: ≤10d & −5% to +8%.
            const sigCtx = (typeof window !== 'undefined' && window.__signalContext) ? window.__signalContext : {};
            const a = sigCtx.alpha, b = sigCtx.beta;
            const alphaInRange = a && a.days != null && a.fromNow != null && a.days <= 30 && a.fromNow <= 15 && a.fromNow >= -8;
            const betaInRange  = b && b.days != null && b.fromNow != null && b.days <= 10 && b.fromNow <= 8  && b.fromNow >= -5;
            if (alphaInRange) {
                add('Entry', 'Fresh trigger', P, `Alpha ${a.days <= 0 ? 'today' : a.days + 'd ago'} · ${a.fromNow >= 0 ? '+' : ''}${a.fromNow.toFixed(0)}% — in range`, 'heavy');
            } else if (betaInRange) {
                add('Entry', 'Fresh trigger', P, `Beta ${b.days <= 0 ? 'today' : b.days + 'd ago'} · ${b.fromNow >= 0 ? '+' : ''}${b.fromNow.toFixed(0)}% — in range`, 'heavy');
            } else if ((a && a.days != null && a.days <= 30) || (b && b.days != null && b.days <= 10)) {
                // a signal exists recently but price has run past the entry zone
                const ext = (a && a.fromNow != null && a.fromNow > 15) ? `Alpha +${a.fromNow.toFixed(0)}%` : (b && b.fromNow != null && b.fromNow > 8) ? `Beta +${b.fromNow.toFixed(0)}%` : 'signal';
                add('Entry', 'Fresh trigger', C, `${ext} past entry zone`, 'heavy');
            } else if ((m.hasAlpha || m.hasBeta)) {
                add('Entry', 'Fresh trigger', C, 'signal present but aged', 'heavy');
            } else {
                add('Entry', 'Fresh trigger', C, 'no fresh in-range trigger', 'heavy');
            }

            // ═══ DEMAND & FUEL ═══
            // 15. RVOL (HEAVY)
            if (rvol !== null) {
                { const g = gradeCredit(rvol, 0.8, 1.6); const det = rvol >= 1.5 ? `${rvol.toFixed(1)}× — institutions present` : rvol >= 1.0 ? `${rvol.toFixed(1)}× — average` : `${rvol.toFixed(1)}× — light, unconfirmed`; add('Demand', 'RVOL', g2s(g), det, 'heavy', g); }
            }
            // 16. Volume signal / Pocket Pivot (VETO — engine caps distribution outlier)
            if (volSig) {
                const t = volSig.type || volSig;
                if (t === 'ppv' || t === 'accum') add('Demand', 'Volume signal', P, t === 'ppv' ? 'pocket pivot' : 'accumulation', 'veto');
                else if (t === 'dist') add('Demand', 'Volume signal', F, 'distribution outlier', 'veto');
                else add('Demand', 'Volume signal', C, 'no notable signal', 'veto');
            }
            // 16b. Pocket pivot (SUPPORT) — surface the PPV buy footprint as its own line.
            if (volSig && (volSig.type || volSig) === 'ppv') add('Demand', 'Pocket pivot', P, 'PPV — up-day eclipsing 10d down-volume', 'support');
            // 17. Accumulation vs distribution days (SUPPORT)
            if (accumD !== null && distD !== null) {
                if (accumD > distD) add('Demand', 'Accum/Dist days', P, `${accumD} accum vs ${distD} dist`, 'support');
                else if (accumD === distD) add('Demand', 'Accum/Dist days', C, `${accumD} vs ${distD} — balanced`, 'support');
                else add('Demand', 'Accum/Dist days', F, `${distD} dist vs ${accumD} accum`, 'support');
            }
            // 18. Up/Down volume ratio (SUPPORT)
            if (udVol !== null) {
                { const g = gradeCredit(udVol, 0.9, 1.4); const det = udVol >= 1.25 ? `${udVol.toFixed(2)} — buyers dominant` : udVol >= 1.0 ? `${udVol.toFixed(2)} — balanced` : `${udVol.toFixed(2)} — sellers dominant`; add('Demand', 'Up/Down vol', g2s(g), det, 'support', g); }
            }
            // 19. EPS / Sales acceleration (SUPPORT) — uses the growth gauge scores (0–100).
            //   Optional: only scored when financial history exists. Thresholds match the gauge.
            if (epsScore !== null || salesScore !== null) {
                const best = Math.max(epsScore ?? -1, salesScore ?? -1);
                const both = (epsScore ?? 0) >= 50 && (salesScore ?? 0) >= 50;
                const eps = epsScore != null ? `EPS ${epsScore}` : '';
                const sales = salesScore != null ? `Sales ${salesScore}` : '';
                const lbl = [eps, sales].filter(Boolean).join(' · ');
                { const g = gradeCredit(best, 25, 80); const det = (best >= 75) ? `${lbl} — top-tier acceleration` : both ? `${lbl} — solid & accelerating` : best >= 50 ? `${lbl} — mixed` : best >= 25 ? `${lbl} — soft` : `${lbl} — weak/decelerating`; add('Demand', 'Growth accel', g2s(g), det, 'support', g); }
            }

            // ═══ CONTEXT ═══
            // 19. Group rotation (SUPPORT, with weak-group veto reconciliation)
            const grpName = currentTickerClassification?.industryGroup;
            const grpRot = grpName && Array.isArray(groupRankingData) ? groupRankingData.find(g => g.name === grpName) : null;
            if (grpRot) {
                if (grpRot.rotationTag === 'in') add('Context', 'Group rotation', P, `${grpName} rotating in`, 'support');
                else if (grpRot.rotationTag === 'leader') add('Context', 'Group rotation', P, `${grpName} stable leader`, 'support');
                else if (grpRot.rotationTag === 'out') add('Context', 'Group rotation', F, `${grpName} rotating out`, 'support');
                else if (grpRot.rotationTag === 'dormant') add('Context', 'Group rotation', C, `${grpName} dormant`, 'support');
                else add('Context', 'Group rotation', C, `${grpName} neutral`, 'support');
            } else add('Context', 'Group rotation', C, 'run market scan', 'support');
            // 20. Market backdrop (SUPPORT)
            const ms = engineState.lastMarketStructure;
            if (ms && Number.isFinite(ms.regimeR)) {
                // Risk regime as a GRADED factor (the risk-on/off gauge). Centered at 0.6 so a
                // neutral / sideways tape reads mildly constructive — markets often coil before
                // they move — rather than a fail. Risk-on → pass; risk-off trims toward fail.
                const g = Math.max(0, Math.min(1, 0.6 + 0.4 * ms.regimeR));
                const det = `${ms.verdict || 'regime'} · R ${ms.regimeR >= 0 ? '+' : ''}${ms.regimeR.toFixed(2)}`;
                add('Context', 'Risk regime', g2s(g), det, 'support', g);
            } else if (ms && ms.verdict) {
                if (/Risk-On|Constructive/.test(ms.verdict)) add('Context', 'Risk regime', P, ms.verdict, 'support');
                else if (/Mixed|Neutral/.test(ms.verdict)) add('Context', 'Risk regime', C, ms.verdict, 'support');
                else add('Context', 'Risk regime', F, ms.verdict, 'support');
            } else add('Context', 'Risk regime', C, 'load breadth data', 'support');

            // ═══ CONVERGENCE ═══ Sector + New-leadership rhythm join Context; Conviction is
            // its own equal-weighted section (Breakout · Leader · Tailwind · RSI aligned ·
            // Vol confluence · RS thrust). Shared classifier with the screener Rating column.
            let convGrpRot = grpRot || null, convSecRot = (m.convSecRot || null);
            try {
                const _gr = (typeof computeGroupRankings === 'function') ? computeGroupRankings('industryGroup', false) : null;
                if (grpName && Array.isArray(_gr)) { const f = _gr.find(g => g.name === grpName); if (f) convGrpRot = f; }
            } catch (e) { /* keep fallback */ }
            if (!convSecRot) {
                try {
                    const _sn = currentTickerClassification?.sector;
                    const _gs = (typeof computeGroupRankings === 'function') ? computeGroupRankings('sector', false) : null;
                    if (_sn && Array.isArray(_gs)) convSecRot = _gs.find(g => g.name === _sn) || null;
                } catch (e) { /* none */ }
            }
            const statusToTA = { pass: P, caution: C, fail: F };
            const cf = convergenceFactors({
                varsValue: vars.value, varsState: vars.state, rsProxy: m.rsProxy, rsRating: m.rsRating,
                deltaTrigger: m.deltaTrigger, deltaDays: m.deltaDays, rsNewHigh: m.rsNewHigh,
                extensionStatus: m.extensionStatus, atrMult: m.atrMult, rmv: m.rmv, belowHighPct: m.belowHighPct,
                rvol: m.rvol, stage: m.stage, secGrvol: m.secGrvol,
                rsi1d: m.rsi1d, rsi1w: m.rsi1w, rsi1m: m.rsi1m,
                grpRot: convGrpRot, secRot: convSecRot, market: engineState.lastMarketStructure
            });
            cf.context.forEach(f => add('Context', f.label, statusToTA[f.status], f.detail, f.tier));
            cf.conviction.forEach(f => add('Conviction', f.label, statusToTA[f.status], f.detail, f.tier));

            // ── Tier weights: veto 3, heavy 2, support 1 (PASS=full, CAUTION=half, FAIL=0)
            const sections = ['Trend', 'Strength', 'Entry', 'Demand', 'Context', 'Conviction'];
            const tierW = { veto: 3, heavy: 2, support: 1 };
            const statusMult = s => s === P ? 1 : s === C ? 0.5 : 0;

            // Per-section weighted fraction (0..1), then a granular 1.0–5.0 rating.
            const fracToRating = f => Math.round((1 + f * 4) * 10) / 10; // 1.0 floor, 5.0 ceil, 1 decimal
            const sectionScore = {};
            sections.forEach(sec => {
                const items = factors.filter(fa => fa.section === sec);
                let e = 0, p = 0;
                items.forEach(fa => { const w = tierW[fa.tier]; const sv = (fa.grade != null ? fa.grade : statusMult(fa.status)); p += w; e += w * sv; });
                sectionScore[sec] = { frac: p ? e / p : 0, rating: p ? fracToRating(e / p) : null, n: items.length };
            });

            let earned = 0, possible = 0;
            factors.forEach(fa => { const w = tierW[fa.tier] * (TA_SECTION_WEIGHT[fa.section] || 1); const sv = (fa.grade != null ? fa.grade : statusMult(fa.status)); possible += w; earned += w * sv; });
            const frac = possible ? earned / possible : 0;
            let rating = fracToRating(frac); // 1.0 – 5.0

            // Veto / heavy-fail caps consider only the core sections — Conviction is
            // equal-weighted but never gates (per design), so it is excluded from the caps.
            const vetoFails = factors.filter(fa => fa.tier === 'veto' && fa.status === F);
            const heavyFails = factors.filter(fa => fa.tier === 'heavy' && fa.section !== 'Conviction' && (fa.grade != null ? fa.grade < 0.25 : fa.status === F)).length;
            if (vetoFails.length > 0) rating = Math.min(rating, 2.0);
            else if (heavyFails >= 2) rating = Math.min(rating, 2.9);

            // Rating → label + color band.
            let verdict, vcls;
            if (rating >= 4.3) { verdict = 'Prime'; vcls = 'border-emerald-500/50 bg-emerald-900/40 text-emerald-300'; }
            else if (rating >= 3.5) { verdict = 'Strong'; vcls = 'border-emerald-600/40 bg-emerald-900/25 text-emerald-300'; }
            else if (rating >= 2.8) { verdict = 'Watch'; vcls = 'border-blue-500/50 bg-blue-900/40 text-blue-300'; }
            else if (rating >= 2.0) { verdict = 'Caution'; vcls = 'border-amber-500/50 bg-amber-900/40 text-amber-300'; }
            else { verdict = 'Avoid'; vcls = 'border-rose-500/50 bg-rose-900/40 text-rose-300'; }

            if (head) {
                head.innerHTML = `<span class="tabular-nums">${rating.toFixed(1)}</span><span class="opacity-60 text-[10px]">/5</span> · ${verdict} <span class="opacity-50 text-[10px] font-normal">· ${factors.length} factors</span>`;
                head.className = `text-sm font-black px-3 py-1 rounded-lg border ${vcls}`;
            }

            // ── High-conviction Alpha/Beta banner (fresh in time AND in range) ──
            if (banner) {
                // Reuse the in-range evaluation from the Fresh trigger factor.
                const showAlpha = alphaInRange || (a && a.days != null && a.days <= 30 && a.fromNow != null && a.fromNow > 15);
                const showBeta  = betaInRange  || (b && b.days != null && b.days <= 10 && b.fromNow != null && b.fromNow > 8);
                if (showAlpha || showBeta) {
                    // Prefer the in-range one; Alpha outranks Beta.
                    const useAlpha = alphaInRange || (showAlpha && !betaInRange);
                    const which = useAlpha ? 'ALPHA' : 'BETA';
                    const sig = useAlpha ? a : b;
                    const inRange = useAlpha ? alphaInRange : betaInRange;
                    const days = sig?.days ?? 0;
                    const dist = sig?.fromNow;
                    // "Clean" mirrors the headline rating: gated on the checklist's own veto/heavy
                    // fails, not the separate engine caps — so the banner and the rating agree.
                    const clean = vetoFails.length === 0 && heavyFails < 2;
                    const distTxt = (dist != null) ? `${dist >= 0 ? '+' : ''}${dist.toFixed(0)}% from signal` : '';
                    let sub, bcls;
                    if (inRange && clean) {
                        sub = `${distTxt} · in range, structure clean — high-conviction long`;
                        bcls = 'border-emerald-500/50 bg-gradient-to-r from-emerald-900/50 to-emerald-900/10 text-emerald-200';
                    } else if (inRange) {
                        sub = `${distTxt} · in range, but check the vetoes below`;
                        bcls = 'border-amber-500/50 bg-gradient-to-r from-amber-900/40 to-amber-900/10 text-amber-200';
                    } else {
                        sub = `${distTxt} · run past the entry zone — wait for a pullback`;
                        bcls = 'border-amber-500/50 bg-gradient-to-r from-amber-900/40 to-amber-900/10 text-amber-200';
                    }
                    banner.className = `mb-2 flex items-center gap-2 px-2.5 py-1.5 rounded-lg border ${bcls}`;
                    banner.innerHTML = `
                        <span class="text-base leading-none">⚡</span>
                        <div class="flex-1 min-w-0">
                            <span class="text-[10px] font-black uppercase tracking-wide">Fresh ${which} Trigger${days <= 0 ? ' (today)' : ` · ${days}d ago`}</span>
                            <span class="text-[9px] opacity-80 ml-1.5">${sub}</span>
                        </div>`;
                    banner.classList.remove('hidden');
                } else {
                    banner.classList.add('hidden');
                }
            }

            // ── Render grouped by section ──
            const dot = s => s === P ? '<span class="text-emerald-400">●</span>' : s === C ? '<span class="text-amber-400">●</span>' : '<span class="text-rose-400">●</span>';
            const tierTag = (fa) => (fa.tier === 'veto' && fa.status === F)
                ? '<span class="text-[8px] text-rose-400 font-bold uppercase bg-rose-900/40 px-1 rounded">veto</span>'
                : '';
            const sectionLabel = { Trend: 'Trend & Stage', Strength: 'Relative Strength', Entry: 'Entry & Risk', Demand: 'Demand & Fuel', Context: 'Context', Conviction: 'Conviction' };
            // Compact: one tight grid, sections as inline header chips, short detail.
            const sectionAbbr = { Trend: 'Trend', Strength: 'RS', Entry: 'Entry/Risk', Demand: 'Demand', Context: 'Context', Conviction: 'Conviction' };
            const cell = c => `
                <div class="flex items-baseline gap-1.5 text-[10.5px] leading-tight py-[1px]" title="${c.label} — ${c.detail}">
                    <span class="text-[9px] leading-none">${dot(c.status)}</span>
                    <span class="font-bold text-slate-200 whitespace-nowrap">${c.label}</span>
                    ${tierTag(c)}
                    <span class="text-slate-500 truncate">${c.detail}</span>
                </div>`;
            const ratingColor = r => r == null ? 'text-slate-500' : r >= 4.0 ? 'text-emerald-300' : r >= 3.0 ? 'text-blue-300' : r >= 2.0 ? 'text-amber-300' : 'text-rose-300';
            wrap.innerHTML = `
                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-0.5">
                    ${sections.map(sec => {
                        const items = factors.filter(fa => fa.section === sec);
                        if (!items.length) return '';
                        const sc = sectionScore[sec];
                        const isConv = sec === 'Conviction';
                        const wrapCls = 'break-inside-avoid';
                        const hdrCls = isConv ? 'text-[8px] uppercase tracking-widest text-indigo-300 font-black' : 'text-[8px] uppercase tracking-widest text-indigo-400/70 font-black';
                        return `<div class="${wrapCls}">
                            <div class="flex items-center justify-between mb-0.5 mt-0.5">
                                <span class="${hdrCls}">${sectionAbbr[sec]}</span>
                                <span class="text-[9px] font-black tabular-nums ${ratingColor(sc?.rating)}">${sc?.rating != null ? sc.rating.toFixed(1) : '--'}<span class="opacity-50">/5</span></span>
                            </div>
                            ${items.map(cell).join('')}
                        </div>`;
                    }).join('')}
                </div>`;
            } catch (taErr) {
                // Fail-safe: never leave the panel stuck on the placeholder. Show an error
                // state with a retry, and log the real cause for debugging.
                console.error('Trade Assistant render failed:', taErr);
                if (wrap) wrap.innerHTML = `<div class="text-[11px] text-rose-300/90 px-1 py-2">
                    Couldn't build the checklist for this ticker.
                    <button onclick="renderTradeAssistant()" class="ml-1 underline decoration-dotted hover:text-rose-200">retry</button>
                    <span class="block text-[9px] text-slate-500 mt-0.5">${(taErr && taErr.message ? taErr.message : 'unexpected error').toString().slice(0, 120)}</span>
                </div>`;
                if (head) { head.textContent = 'error'; head.className = 'text-xs font-black px-3 py-1 rounded-lg border border-rose-700/50 bg-rose-900/30 text-rose-300'; }
                if (banner) banner.classList.add('hidden');
            }
        }

        // ─────────────────────────────────────────────────────────────────────────
        // VARS — Volatility-Adjusted Relative Strength ("Real RS")
        // Per bar: (assetΔ/ATR_asset) − (benchΔ/ATR_bench), where Δ is the WINDOW-bar
        // return and ATR is a FIXED length (14). Both legs normalized by their OWN ATR
        // so high-beta names can't fake strength. Result is in ATR-units of excess move.
        // ─────────────────────────────────────────────────────────────────────────
        async function fetchSpyBars(key) {
            if (spyBarsCache) return spyBarsCache;
            // Prefer bars already captured by the Structure module.
            if (marketStructureIndices['SPY'] && marketStructureIndices['SPY'].closes?.length > 250) {
                const s = marketStructureIndices['SPY'];
                spyBarsCache = { t: s.t || null, closes: s.closes, highs: s.highs, lows: s.lows };
                return spyBarsCache;
            }
            if (!key) return null;
            try {
                const toDate = new Date().toISOString().split('T')[0];
                const fromObj = new Date(); fromObj.setDate(fromObj.getDate() - 1100);
                const fromDate = fromObj.toISOString().split('T')[0];
                const data = await polygonFetch(`https://api.polygon.io/v2/aggs/ticker/SPY/range/1/day/${fromDate}/${toDate}?adjusted=true&sort=asc&limit=50000&apiKey=${key}`, 'SPY');
                if (!data.results || data.results.length < 60) return null;
                spyBarsCache = {
                    t: data.results.map(r => r.t),
                    closes: data.results.map(r => r.c),
                    highs: data.results.map(r => r.h),
                    lows: data.results.map(r => r.l)
                };
                return spyBarsCache;
            } catch (e) { return null; }
        }

        // Build a VARS series aligned to the asset's `results` (array of {t,c,h,l}).
        // Returns array (same length as results) of VARS values or null per bar.


        // Latest VARS value for the loaded ticker (drives the trade-assistant verdict).
        async function getCurrentVars() {
            const key = (ui.apiKey?.value || '').trim();
            const spy = await fetchSpyBars(key);
            if (!spy || !engineState.cachedDataResults?.length) return { value: null, state: null };
            const series = computeVarsSeries(engineState.cachedDataResults, spy, VARS_WINDOW, VARS_ATR_LEN);
            if (!series) return { value: null, state: null };
            const value = [...series].reverse().find(Number.isFinite) ?? null;
            return { value, state: varsStateLabel(series) };
        }

        function renderPriceChart(labels, prices, intraday, markers, volume, volColors, fastEma, crossSignals, extDotsLocal, omegaMarkers, rsDots) {
            // Lazy-load Chart.js on first use, then re-enter with the same args.
            if (typeof Chart === 'undefined') {
                ensureChartJs().then(() => renderPriceChart(labels, prices, intraday, markers, volume, volColors, fastEma, crossSignals, extDotsLocal, omegaMarkers, rsDots))
                    .catch(e => { if (typeof Logger !== 'undefined') Logger.warn('Chart.js load failed', { error: String(e && e.message || e) }); });
                return;
            }
            const fewPoints = !intraday && prices.length <= 10;
            const ctx = document.getElementById('priceChart').getContext('2d');
            // NOTE: chart teardown/reuse is decided at the END of this function (seamless
            // switch). We intentionally do NOT destroy here, so the existing instance can be
            // updated in place when the new render is structurally identical.

            // Neutral price line (reverted). The coloured volume bars carry the signal;
            // the line itself stays a calm slate so it never reads as "bad" on a down period.
            const periodUp = prices.length > 1 && prices[prices.length - 1] >= prices[0];
            const lineColor = '#cbd5e1';
            const areaGrad = 'rgba(148, 163, 184, 0.06)';
            const datasets = [{
                type: 'line',
                label: 'Price', data: prices, borderColor: lineColor, backgroundColor: areaGrad,
                fill: true, tension: intraday ? 0.25 : 0.1, pointRadius: fewPoints ? 4 : 0,
                pointBackgroundColor: lineColor, borderWidth: 2, order: 1, yAxisID: 'y'
            }];
            const lo = Math.min(...prices), hi = Math.max(...prices);
            const offset = (hi - lo) * 0.05 || (hi * 0.02);

            // Volume histogram — bars confined to the bottom ~25% via a hidden axis.
            // Drawn on every range (daily AND intraday) whenever volume data is supplied.
            let maxVol = 0;
            if (volume && volume.length) {
                maxVol = Math.max(...volume);
                datasets.push({
                    type: 'bar',
                    label: 'Volume',
                    data: volume,
                    backgroundColor: volColors,
                    borderWidth: 0,
                    borderRadius: 2,
                    yAxisID: 'yVol',
                    order: 5,
                    barPercentage: 1.0,
                    categoryPercentage: 0.92
                });
            }

            if (markers && markers.length) {
                // Alpha (prime entry) — small filled amber dot.
                const popArr = new Array(prices.length).fill(null);
                markers.forEach(m => { if (m.x >= 0 && m.x < prices.length && m.type === 'POP') popArr[m.x] = m.y - offset * 0.6; });
                if (!chartLayerHidden.alpha) datasets.push({
                    type: 'line',
                    label: 'Alpha', data: popArr, showLine: false, fill: false,
                    pointStyle: 'circle', pointRadius: 4, pointHoverRadius: 6,
                    backgroundColor: '#d99a3a', borderColor: '#0b1120', borderWidth: 0.8, order: 0, yAxisID: 'y'
                });

                // Beta (lighter reclaim) — small hollow sky ring (same circle, hollow = lighter).
                const weakArr = new Array(prices.length).fill(null);
                markers.forEach(m => { if (m.x >= 0 && m.x < prices.length && m.type === 'WEAK') weakArr[m.x] = m.y - offset * 0.6; });
                if (weakArr.some(v => v !== null) && !chartLayerHidden.beta) {
                    datasets.push({
                        type: 'line',
                        label: 'Beta', data: weakArr, showLine: false, fill: false,
                        pointStyle: 'circle', pointRadius: 4, pointHoverRadius: 6,
                        backgroundColor: 'rgba(0,0,0,0)', borderColor: '#5aa9d6', borderWidth: 1.6, order: 0, yAxisID: 'y'
                    });
                }
            }

            if (!intraday && omegaMarkers && omegaMarkers.length) {
                const omegaPrimeArr = new Array(prices.length).fill(null); // 20D breakout → circle
                const omegaNovaArr = new Array(prices.length).fill(null);  // 55D breakout → star
                const omegaExitArr = new Array(prices.length).fill(null);  // 20D exit → inverted triangle
                omegaMarkers.forEach(m => {
                    if (m.x < 0 || m.x >= prices.length) return;
                    if (m.type === 'OMEGA20') omegaPrimeArr[m.x] = m.y + offset * 0.7;
                    if (m.type === 'OMEGA55') omegaNovaArr[m.x] = m.y + offset * 0.7;
                    if (m.type === 'OMEGA_EXIT') omegaExitArr[m.x] = m.y - offset * 0.7;
                });
                if (omegaPrimeArr.some(v => v !== null) && !chartLayerHidden.oprime) {
                    datasets.push({
                        type: 'line',
                        label: 'Ω Prime', data: omegaPrimeArr, showLine: false, fill: false,
                        pointStyle: 'triangle', pointRadius: 5, pointHoverRadius: 7,
                        backgroundColor: '#5fb88a', borderColor: '#0b1120', borderWidth: 0.8, order: 0, yAxisID: 'y'
                    });
                }
                if (omegaNovaArr.some(v => v !== null) && !chartLayerHidden.onova) {
                    datasets.push({
                        type: 'line',
                        label: 'Ω Nova', data: omegaNovaArr, showLine: false, fill: false,
                        pointStyle: 'triangle', pointRadius: 5.5, pointHoverRadius: 7.5,
                        backgroundColor: 'rgba(0,0,0,0)', borderColor: '#4aa888', borderWidth: 1.5, order: 0, yAxisID: 'y'
                    });
                }
                if (omegaExitArr.some(v => v !== null) && !chartLayerHidden.otitan) {
                    datasets.push({
                        type: 'line',
                        label: 'Ω Titan', data: omegaExitArr, showLine: false, fill: false,
                        pointStyle: 'triangle', pointRotation: 180, pointRadius: 5, pointHoverRadius: 7,
                        backgroundColor: '#cf6b6b', borderColor: '#0b1120', borderWidth: 0.8, order: 0, yAxisID: 'y'
                    });
                }
            }

            // Grey extension dots: bars where ATR% multiple from 50-MA >= threshold (placed above the line).
            if (extDotsLocal && extDotsLocal.length && !chartLayerHidden.ext) {
                const extArr = new Array(prices.length).fill(null);
                extDotsLocal.forEach(d => { if (d.x >= 0 && d.x < prices.length) extArr[d.x] = prices[d.x] + offset; });
                if (extArr.some(v => v !== null)) {
                    datasets.push({
                        type: 'line',
                        label: 'Extension', data: extArr, showLine: false, fill: false,
                        pointStyle: 'circle', pointRadius: 3, pointHoverRadius: 5,
                        backgroundColor: '#94a3b8', borderColor: '#cbd5e1', borderWidth: 0.5, order: 0, yAxisID: 'y'
                    });
                }
            }

            // RS-line new-high dots (vs benchmark). Only the leading signal is drawn:
            //   'before' = RS new high BEFORE price made its own new high (the blue dot).
            // Plain RS-new-high events are still tracked in deltaDots (panel/screener),
            // just not plotted here.
            if (!intraday && rsDots && rsDots.length) {
                const beforeArr = new Array(prices.length).fill(null);
                rsDots.forEach(d => {
                    if (d.x < 0 || d.x >= prices.length) return;
                    if (d.kind === 'before') beforeArr[d.x] = prices[d.x] - offset * 0.6; // sit just below the line
                });
                if (beforeArr.some(v => v !== null) && !chartLayerHidden.delta) {
                    datasets.push({
                        type: 'line', label: 'Delta', data: beforeArr, showLine: false, fill: false,
                        pointStyle: 'rectRot', pointRadius: 4.5, pointHoverRadius: 6.5,
                        backgroundColor: '#7ec8e8', borderColor: '#0b1120', borderWidth: 0.8, order: 0, yAxisID: 'y'
                    });
                }
            }

            // Fast 5-EMA overlay + crossover markers (intraday 1D/1W only; daily calls omit these args)
            if (fastEma && fastEma.length) {
                datasets.push({
                    type: 'line', label: '5 EMA', data: fastEma, borderColor: 'rgba(56, 189, 248, 0.9)',
                    backgroundColor: 'rgba(0,0,0,0)', fill: false, tension: 0.25,
                    pointRadius: 0, borderWidth: 1.5, borderDash: [4, 3], order: 2, yAxisID: 'y'
                });
            }
            // MOVING-AVERAGE STACK (8/21 EMA + 50/200 SMA), drawn INSIDE the chart on
            // daily/long ranges. Each line is computed from full history upstream
            // (window.__maStack) so it spans the entire visible window. Visibility is
            // controlled per-line by window.__maShow (toggled by the chart MA buttons).
            if (!intraday && typeof window !== 'undefined' && window.__maStack) {
                const show = window.__maShow || { ema8: true, ema21: true, sma50: true, sma200: true };
                const defs = [
                    { key: 'ema8',   label: '8 EMA',   color: 'rgba(248, 113, 113, 0.9)', dash: [4, 3], w: 1.3 },  // red, fastest
                    { key: 'ema21',  label: '21 EMA',  color: 'rgba(96, 165, 250, 0.9)',  dash: [4, 3], w: 1.3 },  // blue, middle
                    { key: 'sma50',  label: '50 MA',   color: 'rgba(52, 211, 153, 0.9)',  dash: [6, 4], w: 1.6 },  // green, average
                    { key: 'sma200', label: '200 MA',  color: 'rgba(251, 146, 60, 0.9)',  dash: [6, 4], w: 1.6 }   // orange, slow
                ];
                defs.forEach(d => {
                    const series = window.__maStack[d.key];
                    if (!show[d.key] || !Array.isArray(series) || series.length !== prices.length) return;
                    if (!series.some(v => Number.isFinite(v))) return;
                    datasets.push({
                        type: 'line', label: d.label, data: series, borderColor: d.color,
                        backgroundColor: 'rgba(0,0,0,0)', fill: false, tension: 0.25,
                        pointRadius: 0, borderWidth: d.w, borderDash: d.dash, order: 2, yAxisID: 'y', spanGaps: false
                    });
                });
            }
            if (crossSignals && crossSignals.length) {
                const upArr = new Array(prices.length).fill(null);
                const dnArr = new Array(prices.length).fill(null);
                crossSignals.forEach(s => {
                    if (s.index < 0 || s.index >= prices.length) return;
                    if (s.dir === 'UP') upArr[s.index] = prices[s.index] - offset * 0.5;
                    else dnArr[s.index] = prices[s.index] + offset * 0.5;
                });
                datasets.push({
                    type: 'line', label: '5 EMA Cross Up', data: upArr, showLine: false, fill: false,
                    pointStyle: 'triangle', pointRadius: 6, pointHoverRadius: 8,
                    backgroundColor: '#10b981', borderColor: '#065f46', borderWidth: 1, order: 0, yAxisID: 'y'
                });
                datasets.push({
                    type: 'line', label: '5 EMA Cross Down', data: dnArr, showLine: false, fill: false,
                    pointStyle: 'triangle', pointRotation: 180, pointRadius: 6, pointHoverRadius: 8,
                    backgroundColor: '#f43f5e', borderColor: '#7f1d1d', borderWidth: 1, order: 0, yAxisID: 'y'
                });
            }

            const chartConfig = {
                plugins: [{
                    id: 'goldenLine',
                    afterDatasetsDraw(chart) {
                        // Consolidation boundary along the highs. Two modes:
                        //   AUTO   — adaptive line computed in computeGoldenLine (grey).
                        //   MANUAL — a horizontal level the user has DRAGGED (amber). The
                        //            more bars it touches, the more touches/attempts it
                        //            reports. Stats recompute live against the dragged level.
                        // Gated by the Golden toggle. Manual overrides auto once dragged.
                        if (typeof window === 'undefined' || !window.__goldenShow) return;
                        // While the user is dragging, the line is painted on a snapshot overlay
                        // (see scheduleDragDraw) for zero-lag movement; skip the heavy in-chart
                        // draw + per-bar stats loop until the drag ends.
                        if (window.__goldenDragging) return;
                        const meta = chart.getDatasetMeta(0);
                        if (!meta || !meta.data || !meta.data.length) return;
                        const c = chart.ctx, area = chart.chartArea, yS = chart.scales.y;
                        if (!area || !yS) return;
                        const N = meta.data.length;
                        const xAt = (i) => {
                            const ii = Math.max(0, Math.min(N - 1, i));
                            const pt = meta.data[ii];
                            return pt ? pt.x : null;
                        };

                        const man = window.__goldenManual;
                        const bars = window.__chartVisibleBars || [];
                        const lastPx = (bars.length ? bars[bars.length - 1].c : null);
                        let label, lineColorStroke, swatchColor, dashed, drawLine;

                        if (man && Number.isFinite(man.yL) && Number.isFinite(man.yR)) {
                            // ── MANUAL: a SEGMENT set by two endpoints, each free in X and Y.
                            // Drag an end handle to tilt AND resize (move it left/right to make
                            // the line shorter/longer); grab the middle to move the whole line.
                            // Touches/attempts/depth/break count only bars UNDER the line's span.
                            const yL = man.yL, yR = man.yR;
                            const highs = bars.map(b => b.h), lows = bars.map(b => b.l);
                            const M = bars.length;
                            // Endpoint X in pixels (default to plot edges if not yet set).
                            let xLpx = Number.isFinite(man.xL) ? man.xL : area.left;
                            let xRpx = Number.isFinite(man.xR) ? man.xR : area.right;
                            // Keep left ≤ right.
                            if (xLpx > xRpx) { const t = xLpx; xLpx = xRpx; xRpx = t; }
                            const pyL = yS.getPixelForValue(yL), pyR = yS.getPixelForValue(yR);
                            if (!Number.isFinite(pyL) || !Number.isFinite(pyR)) return;
                            // Line price at any pixel-x along the segment (linear in x).
                            const lineAtPx = (px) => xRpx === xLpx ? yL : yL + (yR - yL) * ((px - xLpx) / (xRpx - xLpx));
                            // Store endpoint pixels for the drag hit-test.
                            window.__goldenManual.pxL = { x: xLpx, y: pyL };
                            window.__goldenManual.pxR = { x: xRpx, y: pyR };
                            // Stats over bars whose x falls within [xLpx, xRpx].
                            let attempts = 0, inZone = false, firstTouch = -1, lastBarInSpan = -1, touchCount = 0;
                            let maxH = -Infinity, minL = Infinity;
                            for (let i = 0; i < M; i++) {
                                const bx = xAt(i);
                                if (bx == null || bx < xLpx - 0.5 || bx > xRpx + 0.5) continue; // outside span
                                lastBarInSpan = i;
                                maxH = Math.max(maxH, highs[i]); minL = Math.min(minL, lows[i]);
                                const lv = lineAtPx(bx);
                                const touching = lv > 0 && highs[i] >= lv * 0.985 && highs[i] <= lv * 1.012;
                                if (touching) { touchCount++; if (firstTouch < 0) firstTouch = i; }
                                if (touching && !inZone) { attempts++; inZone = true; }
                                else if (!touching && lv > 0 && highs[i] < lv * 0.97) { inZone = false; }
                            }
                            const baseLen = (firstTouch >= 0 && lastBarInSpan >= 0) ? (lastBarInSpan - firstTouch) : 0;
                            const depthPct = maxH > 0 ? ((maxH - minL) / maxH) * 100 : 0;
                            const rightLine = lineAtPx(xRpx); // line value at the segment's right end
                            const brkAbs = Number.isFinite(lastPx) ? (lastPx - rightLine) : 0;
                            const brkPct = rightLine > 0 ? (brkAbs / rightLine) * 100 : 0;
                            const ord = (k) => k === 1 ? '1st' : k === 2 ? '2nd' : k === 3 ? '3rd' : `${k}th`;
                            const att = attempts > 0 ? ` · ${ord(attempts)} attempt` : '';
                            const brkSign = brkAbs >= 0 ? '+' : '';
                            const tiltTag = Math.abs(yR - yL) / (Math.abs(yL) || 1) > 0.005 ? (yR < yL ? ' · ↓' : ' · ↑') : '';
                            label = `${rightLine.toFixed(2)} · ${baseLen}d · ${depthPct.toFixed(0)}% · ${touchCount}t${att} · ${brkSign}${brkPct.toFixed(1)}%${tiltTag}`;
                            lineColorStroke = 'rgba(224,181,106,0.95)';
                            swatchColor = 'rgba(224,181,106,0.95)';
                            dashed = false;
                            // touch dots (only bars under the span)
                            c.save();
                            c.fillStyle = 'rgba(224,181,106,0.95)';
                            for (let i = 0; i < M; i++) {
                                const bx = xAt(i);
                                if (bx == null || bx < xLpx - 0.5 || bx > xRpx + 0.5) continue;
                                const lv = lineAtPx(bx);
                                if (lv > 0 && highs[i] >= lv * 0.985 && highs[i] <= lv * 1.012) {
                                    const hy = yS.getPixelForValue(highs[i]);
                                    if (Number.isFinite(hy)) { c.beginPath(); c.arc(bx, hy, 3, 0, Math.PI * 2); c.fill(); }
                                }
                            }
                            c.restore();
                            drawLine = () => {
                                c.save();
                                c.beginPath(); c.moveTo(xLpx, pyL); c.lineTo(xRpx, pyR);
                                c.strokeStyle = lineColorStroke; c.lineWidth = 1.6;
                                c.stroke();
                                // end handles at the segment ends — larger with a halo so
                                // they read as obvious grab targets.
                                [[xLpx, pyL], [xRpx, pyR]].forEach(([hx, hy]) => {
                                    c.beginPath(); c.arc(hx, hy, 9, 0, Math.PI * 2);
                                    c.fillStyle = 'rgba(224,181,106,0.18)'; c.fill();
                                    c.beginPath(); c.arc(hx, hy, 5.5, 0, Math.PI * 2);
                                    c.fillStyle = lineColorStroke; c.fill();
                                    c.strokeStyle = '#070a12'; c.lineWidth = 1.5; c.stroke();
                                });
                                c.restore();
                            };
                        } else {
                            // ── AUTO: the computed adaptive boundary.
                            const gl = window.__goldenLine;
                            if (!gl) {
                                // No clean auto boundary (e.g. rising highs). Rather than show
                                // nothing, draw a faint guide at the visible high so the line is
                                // always present and grabbable — drag it to set your own level.
                                const hs = bars.map(b => b.h).filter(Number.isFinite);
                                if (!hs.length) return;
                                const hiVal = Math.max(...hs);
                                const py = yS.getPixelForValue(hiVal);
                                if (!Number.isFinite(py)) return;
                                window.__goldenAutoPixelY = py;
                                window.__goldenAutoEdges = { L: { x: area.left, y: py }, R: { x: area.right, y: py } };
                                label = `${hiVal.toFixed(2)} · drag to set`;
                                lineColorStroke = 'rgba(148,163,184,0.55)';
                                swatchColor = 'rgba(148,163,184,0.7)';
                                drawLine = () => {
                                    c.save();
                                    c.beginPath(); c.moveTo(area.left, py); c.lineTo(area.right, py);
                                    c.strokeStyle = lineColorStroke; c.lineWidth = 1.1;
                                    c.setLineDash([2, 4]); c.stroke(); c.setLineDash([]);
                                    c.restore();
                                };
                                drawLine();
                                // fall through to draw the chip below
                                c.save();
                                c.font = '700 9px Inter, system-ui, sans-serif';
                                c.textAlign = 'left'; c.textBaseline = 'middle';
                                const padX = 7, swatch = 9, swGap = 5;
                                const tw = c.measureText(label).width;
                                const chipH = 16, chipW = padX + swatch + swGap + tw + padX;
                                const chipX = area.right - 6 - chipW, chipY = area.top + 6;
                                window.__goldenChipRect = { x: chipX, y: chipY, w: chipW, h: chipH };
                                c.fillStyle = 'rgba(15,22,38,0.92)';
                                c.strokeStyle = 'rgba(148,163,184,0.35)'; c.lineWidth = 0.5;
                                const r = 4;
                                c.beginPath();
                                c.moveTo(chipX + r, chipY);
                                c.arcTo(chipX + chipW, chipY, chipX + chipW, chipY + chipH, r);
                                c.arcTo(chipX + chipW, chipY + chipH, chipX, chipY + chipH, r);
                                c.arcTo(chipX, chipY + chipH, chipX, chipY, r);
                                c.arcTo(chipX, chipY, chipX + chipW, chipY, r);
                                c.closePath(); c.fill(); c.stroke();
                                c.fillStyle = swatchColor;
                                c.beginPath(); c.arc(chipX + padX + swatch / 2, chipY + chipH / 2, 3, 0, Math.PI * 2); c.fill();
                                c.fillStyle = 'rgba(203,213,225,0.95)';
                                c.fillText(label, chipX + padX + swatch + swGap, chipY + chipH / 2 + 0.5);
                                c.restore();
                                return;
                            }
                            const x1 = xAt(gl.i1), x2 = area.right;
                            const py1 = yS.getPixelForValue(gl.y1);
                            const py2 = yS.getPixelForValue(gl.basePrice);
                            if (x1 == null || !Number.isFinite(py1) || !Number.isFinite(py2)) return;
                            window.__goldenAutoPixelY = py2; // right-edge level, for grab-to-drag
                            // Extrapolate the auto line to BOTH plot edges so seeding a manual
                            // line from it preserves the auto slope (left edge isn't the anchor).
                            const aSlope = (x2 !== x1) ? (py2 - py1) / (x2 - x1) : 0;
                            window.__goldenAutoEdges = {
                                L: { x: area.left, y: py1 + aSlope * (area.left - x1) },
                                R: { x: area.right, y: py2 }
                            };
                            const lvl = gl.basePrice;
                            const ord = (k) => k === 1 ? '1st' : k === 2 ? '2nd' : k === 3 ? '3rd' : `${k}th`;
                            const att = gl.attempts > 0 ? ` · ${ord(gl.attempts)} attempt` : '';
                            label = `${lvl.toFixed(2)} · ${gl.baseLenBars}d · ${gl.depthPct.toFixed(0)}%${att}`;
                            lineColorStroke = 'rgba(148,163,184,0.85)';
                            swatchColor = 'rgba(148,163,184,0.9)';
                            dashed = gl.orientation !== 'diagonal';
                            drawLine = () => {
                                c.save();
                                c.beginPath(); c.moveTo(x1, py1); c.lineTo(x2, py2);
                                c.strokeStyle = lineColorStroke; c.lineWidth = 1.3;
                                c.setLineDash(dashed ? [6, 4] : []);
                                c.stroke(); c.setLineDash([]);
                                c.restore();
                            };
                        }

                        drawLine();

                        // Annotation chip — top-right, clear of the hover OHLC readout.
                        c.save();
                        c.font = '700 9px Inter, system-ui, sans-serif';
                        c.textAlign = 'left';
                        c.textBaseline = 'middle';
                        const padX = 7, swatch = 9, swGap = 5;
                        const tw = c.measureText(label).width;
                        const chipH = 16;
                        const chipW = padX + swatch + swGap + tw + padX;
                        const chipX = area.right - 6 - chipW, chipY = area.top + 6;
                        window.__goldenChipRect = { x: chipX, y: chipY, w: chipW, h: chipH }; // click-to-reset
                        c.fillStyle = 'rgba(15,22,38,0.92)';
                        c.strokeStyle = man ? 'rgba(224,181,106,0.5)' : 'rgba(148,163,184,0.35)';
                        c.lineWidth = 0.5;
                        const r = 4, bx = chipX, by = chipY, bw = chipW, bh = chipH;
                        c.beginPath();
                        c.moveTo(bx + r, by);
                        c.arcTo(bx + bw, by, bx + bw, by + bh, r);
                        c.arcTo(bx + bw, by + bh, bx, by + bh, r);
                        c.arcTo(bx, by + bh, bx, by, r);
                        c.arcTo(bx, by, bx + bw, by, r);
                        c.closePath();
                        c.fill();
                        c.stroke();
                        c.fillStyle = swatchColor;
                        c.beginPath();
                        c.arc(chipX + padX + swatch / 2, chipY + chipH / 2, 3, 0, Math.PI * 2);
                        c.fill();
                        c.fillStyle = man ? 'rgba(224,181,106,0.95)' : 'rgba(203,213,225,0.95)';
                        c.fillText(label, chipX + padX + swatch + swGap, chipY + chipH / 2 + 0.5);
                        c.restore();
                    }
                }, {
                    id: 'livePriceDot',
                    afterDatasetsDraw(chart) {
                        // Pulsing "current price" marker at the right edge (dot + halo only).
                        // The price LABEL is drawn by the priceRail plugin below.
                        const meta = chart.getDatasetMeta(0);
                        if (!meta || !meta.data || !meta.data.length) return;
                        const pt = meta.data[meta.data.length - 1];
                        if (!pt) return;
                        const c = chart.ctx;
                        const lastVal = prices[prices.length - 1];
                        if (!Number.isFinite(lastVal)) return;
                        const phase = (Date.now() % 2000) / 2000;
                        const halo = 8 + Math.sin(phase * Math.PI * 2) * 3.5;
                        c.save();
                        c.beginPath(); c.arc(pt.x, pt.y, halo, 0, Math.PI * 2);
                        c.fillStyle = 'rgba(148,163,184,0.16)'; c.fill();
                        c.beginPath(); c.arc(pt.x, pt.y, 3.5, 0, Math.PI * 2);
                        c.fillStyle = lineColor; c.fill();
                        c.strokeStyle = '#070a12'; c.lineWidth = 1.5; c.stroke();
                        c.restore();
                    }
                }, {
                    id: 'priceRail',
                    afterDatasetsDraw(chart) {
                        // TradingView-style vertical price rail on the right edge: stacked,
                        // colour-coded price pills for current price, pre-market, 5-EMA,
                        // 50-MA and 200-MA — each with a faint guide line to its level.
                        // Collision-resolved so pills never overlap. Pure draw; reads only
                        // already-computed series.
                        const c = chart.ctx, area = chart.chartArea, y = chart.scales.y;
                        if (!area || !y) return;
                        const last = prices[prices.length - 1];
                        if (!Number.isFinite(last)) return;

                        // Build the level list (value + label + colour theme).
                        const levels = [];
                        // Current price — the anchor pill (neutral dark, bright text).
                        levels.push({ v: last, text: last.toFixed(2), kind: 'price',
                            fill: '#e2e8f0', stroke: '#e2e8f0', textCol: '#0b1120', line: 'rgba(226,232,240,0.35)', weight: 800 });
                        // Pre-market (if present on long-term charts) — orange gap-up / slate otherwise.
                        const g = (typeof window !== 'undefined' && window.__gapLine) ? window.__gapLine : null;
                        if (g && Number.isFinite(g.prePrice) && !(typeof state.currentChartRange !== 'undefined' && ['1D','1W','1M'].includes(state.currentChartRange))) {
                            const up = !!g.isGapUp;
                            levels.push({ v: g.prePrice, text: g.prePrice.toFixed(2), tag: 'PRE',
                                fill: up ? '#f59e0b' : '#334155', stroke: up ? '#f59e0b' : '#475569',
                                textCol: up ? '#1a1206' : '#cbd5e1', line: up ? 'rgba(245,158,11,0.5)' : 'rgba(148,163,184,0.3)', weight: 700 });
                        }
                        // 5-EMA (intraday charts pass fastEma)
                        if (fastEma && fastEma.length) {
                            const e = fastEma[fastEma.length - 1];
                            if (Number.isFinite(e)) levels.push({ v: e, text: e.toFixed(2), tag: 'EMA',
                                fill: '#0c4a6e', stroke: '#38bdf8', textCol: '#bae6fd', line: 'rgba(56,189,248,0.28)', weight: 600 });
                        }
                        // (50-MA is drawn as a green line inside the chart, not a rail pill.)

                        // Map to pixel Y, drop off-screen ones, sort top→bottom.
                        const boxH = 16, gap = 2, padX = 4;
                        c.save();
                        c.font = '700 9px ui-monospace, monospace';
                        let items = levels.map(l => {
                            const yPix = y.getPixelForValue(l.v);
                            const label = (l.tag ? l.tag + ' ' : '') + l.text;
                            return { ...l, yPix, label, w: c.measureText(label).width + padX * 2 };
                        }).filter(it => Number.isFinite(it.yPix));
                        items.sort((a, b) => a.yPix - b.yPix);

                        // Collision resolution: push overlapping pills apart vertically.
                        for (let i = 1; i < items.length; i++) {
                            const prev = items[i - 1];
                            if (items[i].yPix - prev.yPix < boxH + gap) items[i].yPix = prev.yPix + boxH + gap;
                        }
                        // Keep within plot bounds (shift the whole stack up if it overflows).
                        const overflow = (items.length ? items[items.length - 1].yPix + boxH : 0) - area.bottom;
                        if (overflow > 0) items.forEach(it => it.yPix -= overflow);

                        const railX = area.right;
                        items.forEach(it => {
                            const trueY = y.getPixelForValue(it.v);
                            // Horizontal level line across the plot at the TRUE price level.
                            c.beginPath(); c.setLineDash([3, 3]); c.lineWidth = 1;
                            c.strokeStyle = it.line;
                            c.moveTo(area.left, trueY); c.lineTo(railX, trueY); c.stroke();
                            // Tiny connector in the gutter from the level to the (nudged) pill,
                            // so a collision-shifted pill still visually ties to its line.
                            if (Math.abs(trueY - (it.yPix + boxH / 2)) > 1) {
                                c.beginPath();
                                c.moveTo(railX, trueY); c.lineTo(railX + 2, it.yPix + boxH / 2); c.stroke();
                            }
                            c.setLineDash([]);
                            // pill
                            const bx = railX + 2, by = it.yPix, bw = Math.max(it.w, 38);
                            c.fillStyle = it.fill;
                            if (c.roundRect) { c.beginPath(); c.roundRect(bx, by, bw, boxH, 3); c.fill(); }
                            else c.fillRect(bx, by, bw, boxH);
                            c.lineWidth = 1; c.strokeStyle = it.stroke;
                            if (c.roundRect) { c.beginPath(); c.roundRect(bx, by, bw, boxH, 3); c.stroke(); }
                            c.fillStyle = it.textCol;
                            c.font = `${it.weight} 9px ui-monospace, monospace`;
                            c.textBaseline = 'middle'; c.textAlign = 'left';
                            c.fillText(it.label, bx + padX, by + boxH / 2 + 0.5);
                        });
                        c.restore();
                    }
                }, {
                    id: 'crosshair',
                    afterDatasetsDraw(chart) {
                        // Stylish dashed crosshair that follows the cursor: faint dashed
                        // vertical + horizontal lines, with a small price tag on the right axis
                        // and a date tag on the bottom axis. Only when hovering inside the plot.
                        const cross = chart.__cross;
                        const area = chart.chartArea, c = chart.ctx;
                        const xScale = chart.scales.x, yScale = chart.scales.y;
                        if (!cross || !area || !yScale) return;
                        const { x, y } = cross;
                        if (x < area.left || x > area.right || y < area.top || y > area.bottom) return;
                        c.save();
                        c.lineWidth = 1; c.setLineDash([4, 4]);
                        c.strokeStyle = 'rgba(148,163,184,0.45)';
                        // vertical
                        c.beginPath(); c.moveTo(x, area.top); c.lineTo(x, area.bottom); c.stroke();
                        // horizontal
                        c.beginPath(); c.moveTo(area.left, y); c.lineTo(area.right, y); c.stroke();
                        c.setLineDash([]);
                        // price tag on the right axis at cursor height
                        const price = yScale.getValueForPixel(y);
                        if (Number.isFinite(price)) {
                            const label = price.toFixed(2);
                            c.font = '700 10px ui-monospace, monospace';
                            const tw = c.measureText(label).width, padX = 5, bw = tw + padX * 2, bh = 16;
                            const bx = area.right + 2, by = Math.max(area.top, Math.min(y - bh / 2, area.bottom - bh));
                            c.fillStyle = '#1e293b';
                            if (c.roundRect) { c.beginPath(); c.roundRect(bx, by, bw, bh, 3); c.fill(); } else c.fillRect(bx, by, bw, bh);
                            c.strokeStyle = '#475569'; c.lineWidth = 1;
                            if (c.roundRect) { c.beginPath(); c.roundRect(bx, by, bw, bh, 3); c.stroke(); }
                            c.fillStyle = '#e2e8f0'; c.textBaseline = 'middle'; c.textAlign = 'left';
                            c.fillText(label, bx + padX, by + bh / 2 + 0.5);
                        }
                        // focus dot where the crosshair meets the price line
                        const meta = chart.getDatasetMeta(0);
                        if (meta && meta.data && meta.data.length && xScale) {
                            // nearest index to cursor x
                            let nearest = null, best = Infinity;
                            meta.data.forEach((pt, i) => { const d = Math.abs(pt.x - x); if (d < best) { best = d; nearest = pt; } });
                            if (nearest) {
                                c.beginPath(); c.arc(nearest.x, nearest.y, 3, 0, Math.PI * 2);
                                c.fillStyle = '#f8fafc'; c.fill();
                                c.strokeStyle = '#0b1120'; c.lineWidth = 1.5; c.stroke();
                            }
                        }
                        c.restore();
                    }
                }, {
                    id: 'extHoursBands',
                    beforeDraw(chart) {
                        // Shade pre-market / after-hours bars on intraday charts. Bars are
                        // classified in America/New_York time (DST-safe) and stashed on
                        // window.__intradayBars by drawIntraday. No-op for daily charts.
                        if (!intraday) return;
                        const bands = (typeof window !== 'undefined' && window.__intradayBands) ? window.__intradayBands : null;
                        if (!bands || !bands.length) return;
                        const { ctx: c, chartArea: area, scales: { x } } = chart;
                        if (!area || !x) return;
                        c.save();
                        c.fillStyle = 'rgba(56, 189, 248, 0.13)'; // cyan tint = extended hours (pre/after-market)
                        bands.forEach(b => {
                            const x1 = x.getPixelForValue(b.start);
                            const x2 = x.getPixelForValue(b.end);
                            if (x1 == null || x2 == null) return;
                            const left = Math.max(area.left, Math.min(x1, x2));
                            const right = Math.min(area.right, Math.max(x1, x2));
                            if (right > left) c.fillRect(left, area.top, right - left, area.bottom - area.top);
                        });
                        c.restore();
                    }
                }, {
                    id: 'gapLine',
                    afterDraw(chart) {
                        // Forming, unconfirmed PRE-MARKET price line, drawn on long-term
                        // (non-intraday) charts. Orange = ≥+2% gap-up (alert-worthy);
                        // grey = any other pre-market price (informational). Set in checkGapUp.
                        if (intraday) return;
                        // Long-term ranges only (3M+). Skip 1D/1W (intraday) and 1M.
                        if (typeof state.currentChartRange !== 'undefined' && ['1D', '1W', '1M'].includes(state.currentChartRange)) return;
                        const g = (typeof window !== 'undefined' && window.__gapLine) ? window.__gapLine : null;
                        if (!g || !Number.isFinite(g.prePrice)) return;
                        const { ctx: c, chartArea: area, scales: { y } } = chart;
                        if (!area || !y) return;
                        const yPix = y.getPixelForValue(g.prePrice);
                        if (yPix == null || yPix < area.top || yPix > area.bottom) return;
                        // Palette: orange for a real gap-up, muted slate-grey otherwise.
                        const up = !!g.isGapUp;
                        const lineCol = up ? '#f59e0b' : '#94a3b8';
                        const boxFill = up ? '#412402' : '#1e293b';
                        const boxStroke = up ? '#854f0b' : '#475569';
                        const textCol = up ? '#fac775' : '#cbd5e1';
                        c.save();
                        // Dotted line across the plot.
                        c.beginPath();
                        c.setLineDash([4, 4]);
                        c.lineWidth = 1.2;
                        c.strokeStyle = lineCol;
                        c.moveTo(area.left, yPix);
                        c.lineTo(area.right, yPix);
                        c.stroke();
                        // Dot at the right edge (where the rail PRE price pill sits).
                        c.setLineDash([]);
                        c.beginPath();
                        c.arc(area.right - 2, yPix, 3, 0, Math.PI * 2);
                        c.fillStyle = lineCol;
                        c.fill();
                        // (Left-side "pre %" pill removed — the PRE price lives on the right rail.)
                        c.restore();
                    }
                }],
                type: 'line', data: { labels: labels, datasets: datasets },
                options: {
                    responsive: true, maintainAspectRatio: false,
                    layout: { padding: { right: 48 } }, // room for the vertical price rail
                    animation: { duration: 0 }, active: { animation: { duration: 0 } },
                    transitions: { active: { animation: { duration: 0 } } },
                    onHover: (evt, els, chart) => {
                        // Stash cursor position for the crosshair plugin.
                        try {
                            const c = chart || priceChartInstance;
                            if (c && evt && evt.x != null) { c.__cross = { x: evt.x, y: evt.y }; }
                        } catch (e) {}
                        // Floating OHLC readout (top-left card over the chart) on hover.
                        const ro = document.getElementById('chartOhlcReadout');
                        if (!ro) return;
                        if (els && els.length) {
                            const i = els[0].index;
                            const bars = (typeof engineState.cachedDataResults !== 'undefined' && window.__chartVisibleBars) ? window.__chartVisibleBars : null;
                            let o, h, l, cl, dl = labels[i];
                            if (bars && bars[i]) { o = bars[i].o; h = bars[i].h; l = bars[i].l; cl = bars[i].c; }
                            else { cl = prices[i]; o = prices[Math.max(0, i - 1)]; h = Math.max(o, cl); l = Math.min(o, cl); }
                            const f = (x) => Number.isFinite(x) ? x.toFixed(2) : '--';
                            ro.querySelector('[data-ro="d"]').textContent = dl || '';
                            ro.querySelector('[data-ro="o"]').textContent = f(o);
                            ro.querySelector('[data-ro="h"]').textContent = f(h);
                            ro.querySelector('[data-ro="l"]').textContent = f(l);
                            ro.querySelector('[data-ro="c"]').textContent = f(cl);
                            ro.style.opacity = '1';
                        } else { ro.style.opacity = '0'; }
                    },
                    plugins: { legend: { display: false },
                        tooltip: { enabled: false } },
                    interaction: { mode: 'nearest', axis: 'x', intersect: false },
                    scales: { x: { display: true, ticks: { color: '#475569', font: {size: 9}, maxTicksLimit: intraday ? 8 : 10, maxRotation: 0, autoSkip: true }, grid: { display: false } },
                              y: { position: 'right', ticks: { display: false }, grid: { color: 'rgba(30,41,59,0.45)' } },
                              yVol: { display: false, position: 'left', min: 0, max: (maxVol || 1) * 4, grid: { display: false } } }
                }
            };

            // ── Seamless switch: reuse the chart instance in place when the new render has
            // the SAME structural shape (same dataset count, same intraday mode) as the
            // current chart — the common case when flipping between two daily charts on the
            // same range. Swapping data on the live instance avoids the destroy+recreate
            // flicker and re-init cost. Anything structurally different falls back to a
            // clean rebuild so we never corrupt the chart.
            const canReuse = priceChartInstance
                && !fewPoints
                && priceChartInstance.data
                && priceChartInstance.data.datasets
                && priceChartInstance.data.datasets.length === chartConfig.data.datasets.length
                && priceChartInstance.__intraday === intraday;

            if (canReuse) {
                try {
                    priceChartInstance.data.labels = chartConfig.data.labels;
                    chartConfig.data.datasets.forEach((ds, i) => {
                        // Replace data + visual props in place, preserving the instance.
                        Object.assign(priceChartInstance.data.datasets[i], ds);
                    });
                    // Refresh the volume axis ceiling (depends on the new bars).
                    if (priceChartInstance.options.scales && priceChartInstance.options.scales.yVol) {
                        priceChartInstance.options.scales.yVol.max = (maxVol || 1) * 4;
                    }
                    priceChartInstance.__intraday = intraday;
                    priceChartInstance.update('none'); // no animation → instant swap
                } catch (e) {
                    // If an in-place swap ever fails, rebuild cleanly.
                    if (typeof Logger !== 'undefined') Logger.warn('Chart in-place update failed, rebuilding', { error: String(e && e.message || e) });
                    priceChartInstance.destroy();
                    priceChartInstance = new Chart(ctx, chartConfig);
                    priceChartInstance.__intraday = intraday;
                }
            } else {
                if (priceChartInstance) priceChartInstance.destroy();
                priceChartInstance = new Chart(ctx, chartConfig);
                priceChartInstance.__intraday = intraday;
            }

            // Continuous repaint so the live current-price dot pulses smoothly.
            // Throttled to ~12fps; single shared loop, restarted on each rebuild.
            if (window.__livePulseRAF) cancelAnimationFrame(window.__livePulseRAF);
            // Hide the crosshair when the cursor leaves the canvas.
            try {
                const cv = priceChartInstance.canvas;
                if (cv && !cv.__crossBound) {
                    cv.addEventListener('mouseleave', () => { if (priceChartInstance) { priceChartInstance.__cross = null; priceChartInstance.draw(); } });
                    cv.__crossBound = true;
                }
                // ── Draggable / tiltable Golden Line ─────────────────────────────
                // Grab an END handle to TILT that end; grab the MIDDLE to move the whole
                // line. Free angle. Touches/attempts/depth/break recompute live against
                // the line's value at each bar. Click the chip (or double-click) to reset.
                if (cv && !cv.__goldenDragBound) {
                    cv.__goldenDragBound = true;
                    let mode = null; // 'left' | 'right' | 'move'
                    let moveStart = null; // {y, yL, yR} for whole-line drag
                    // ZERO-LAG DRAG: a full priceChartInstance.draw() re-renders every dataset
                    // (price, volume, 4 MAs, all marker layers) + the per-bar golden stats loop —
                    // far too heavy to run 60×/s. Instead we snapshot the chart ONCE at drag start
                    // (with the line suppressed), then each frame just blit that snapshot and stroke
                    // the moving line on top. One real redraw happens on drop to bake in the result.
                    let __dragRAF = null;
                    let __goldenSnap = null;   // offscreen snapshot of the chart minus the line
                    const drawGoldenSegment = (ctx) => {
                        const m = window.__goldenManual; if (!m) return;
                        const yS = priceChartInstance.scales.y, area = priceChartInstance.chartArea;
                        if (!yS || !area || !Number.isFinite(m.yL) || !Number.isFinite(m.yR)) return;
                        let xL = Number.isFinite(m.xL) ? m.xL : area.left;
                        let xR = Number.isFinite(m.xR) ? m.xR : area.right;
                        if (xL > xR) { const t = xL; xL = xR; xR = t; }
                        const pyL = yS.getPixelForValue(m.yL), pyR = yS.getPixelForValue(m.yR);
                        if (!Number.isFinite(pyL) || !Number.isFinite(pyR)) return;
                        ctx.save();
                        ctx.beginPath(); ctx.setLineDash([6, 4]); ctx.lineWidth = 1.5; ctx.strokeStyle = '#e0b56a';
                        ctx.moveTo(xL, pyL); ctx.lineTo(xR, pyR); ctx.stroke(); ctx.setLineDash([]);
                        ctx.fillStyle = '#e0b56a';
                        [[xL, pyL], [xR, pyR]].forEach(([px, py]) => { ctx.beginPath(); ctx.arc(px, py, 3.5, 0, Math.PI * 2); ctx.fill(); });
                        // live value chip near the dragged (right) end
                        const txt = m.yR.toFixed(2) + ' · drag to set';
                        ctx.font = '600 11px ui-monospace, SFMono-Regular, monospace';
                        const tw = ctx.measureText(txt).width;
                        const bx = Math.min(xR + 8, area.right - tw - 10), by = pyR - 8;
                        ctx.fillStyle = 'rgba(11,17,32,0.85)'; ctx.fillRect(bx - 5, by - 3, tw + 10, 17);
                        ctx.fillStyle = '#e0b56a'; ctx.textBaseline = 'top'; ctx.fillText(txt, bx, by);
                        ctx.restore();
                    };
                    const paintDrag = () => {
                        if (!__goldenSnap) {
                            // First frame: render the chart with the line suppressed, then snapshot it.
                            // Also clear the crosshair so it isn't baked frozen into the snapshot
                            // (it reappears on the next mouse-move once the drag ends).
                            try { priceChartInstance.__cross = null; } catch (e) {}
                            try { priceChartInstance.draw(); } catch (e) {}
                            try {
                                __goldenSnap = document.createElement('canvas');
                                __goldenSnap.width = cv.width; __goldenSnap.height = cv.height;
                                __goldenSnap.getContext('2d').drawImage(cv, 0, 0);
                            } catch (e) { __goldenSnap = null; }
                        }
                        const ctx = cv.getContext('2d');
                        const dpr = window.devicePixelRatio || 1;
                        ctx.save();
                        ctx.setTransform(1, 0, 0, 1, 0, 0);
                        ctx.clearRect(0, 0, cv.width, cv.height);
                        if (__goldenSnap) ctx.drawImage(__goldenSnap, 0, 0);
                        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
                        drawGoldenSegment(ctx);
                        ctx.restore();
                    };
                    const scheduleDragDraw = () => {
                        window.__goldenDragging = true;
                        if (__dragRAF != null) return;
                        __dragRAF = requestAnimationFrame(() => { __dragRAF = null; paintDrag(); });
                    };
                    const endDrag = () => {
                        if (!window.__goldenDragging) return;
                        window.__goldenDragging = false;
                        __goldenSnap = null;
                        if (__dragRAF != null) { cancelAnimationFrame(__dragRAF); __dragRAF = null; }
                        try { priceChartInstance.draw(); } catch (e) {} // bake final line + recompute stats once
                    };
                    const localXY = (ev) => {
                        const r = cv.getBoundingClientRect();
                        const t = ev.touches && ev.touches[0];
                        const cx = (t ? t.clientX : ev.clientX), cy = (t ? t.clientY : ev.clientY);
                        return { x: cx - r.left, y: cy - r.top };
                    };
                    const valAt = (y) => {
                        const yS = priceChartInstance.scales.y, area = priceChartInstance.chartArea;
                        const cy = Math.max(area.top, Math.min(area.bottom, y));
                        return yS.getValueForPixel(cy);
                    };
                    // Current endpoint pixels for the manual line (or the auto line's pixels
                    // when seeding a fresh manual line from the existing auto line).
                    const endpoints = () => {
                        const man = window.__goldenManual;
                        if (man && man.pxL && man.pxR) return { L: man.pxL, R: man.pxR };
                        if (window.__goldenAutoEdges) return window.__goldenAutoEdges;
                        const area = priceChartInstance.chartArea;
                        if (Number.isFinite(window.__goldenAutoPixelY) && area) {
                            return { L: { x: area.left, y: window.__goldenAutoPixelY },
                                     R: { x: area.right, y: window.__goldenAutoPixelY } };
                        }
                        return null;
                    };
                    const lineYAt = (x, ep) => {
                        if (ep.R.x === ep.L.x) return ep.L.y;
                        return ep.L.y + (ep.R.y - ep.L.y) * ((x - ep.L.x) / (ep.R.x - ep.L.x));
                    };
                    const ensureManual = (ep) => {
                        const m = window.__goldenManual || {};
                        if (!Number.isFinite(m.yL) || !Number.isFinite(m.yR)) {
                            m.yL = valAt(ep.L.y); m.yR = valAt(ep.R.y);
                        }
                        if (!Number.isFinite(m.xL) || !Number.isFinite(m.xR)) {
                            m.xL = ep.L.x; m.xR = ep.R.x;
                        }
                        window.__goldenManual = m;
                        return m;
                    };
                    const onDown = (ev) => {
                        if (!window.__goldenShow) return;
                        const { x, y } = localXY(ev);
                        const area = priceChartInstance.chartArea;
                        if (!area || x < area.left || x > area.right) return;
                        // Click the chip → reset to auto.
                        const chip = window.__goldenChipRect;
                        if (chip && x >= chip.x && x <= chip.x + chip.w && y >= chip.y && y <= chip.y + chip.h) {
                            window.__goldenManual = null; priceChartInstance.draw(); ev.preventDefault(); return;
                        }
                        const ep = endpoints();
                        if (!ep) return;
                        const HANDLE_R = 20;   // generous grab radius around each end
                        const LINE_BAND = 14;  // wide hit-band along the whole line
                        const dL = Math.hypot(x - ep.L.x, y - ep.L.y);
                        const dR = Math.hypot(x - ep.R.x, y - ep.R.y);
                        const minX = Math.min(ep.L.x, ep.R.x), maxX = Math.max(ep.L.x, ep.R.x);
                        const withinSpan = x >= minX - LINE_BAND && x <= maxX + LINE_BAND;
                        const distToLine = withinSpan ? Math.abs(y - lineYAt(x, ep)) : Infinity;
                        ensureManual(ep);
                        // Priority: a clear end-handle grab → tilt/resize that end.
                        if (dL <= HANDLE_R || dR <= HANDLE_R) {
                            mode = (dL <= dR) ? 'left' : 'right';
                            ev.preventDefault();
                            handleDrag(x, y); // snap the grabbed end straight to the cursor
                            return;
                        }
                        // Near the line but past an endpoint horizontally → grab the closer END
                        // (so you can extend the line without hitting the tiny dot exactly).
                        if (distToLine <= LINE_BAND && (x < minX || x > maxX)) {
                            mode = (x < minX)
                                ? (ep.L.x <= ep.R.x ? 'left' : 'right')
                                : (ep.R.x >= ep.L.x ? 'right' : 'left');
                            ev.preventDefault();
                            handleDrag(x, y);
                            return;
                        }
                        // Otherwise, anywhere along the body → move the whole line.
                        if (distToLine <= LINE_BAND) {
                            mode = 'move';
                            const m = window.__goldenManual;
                            moveStart = { y, yL: m.yL, yR: m.yR };
                            ev.preventDefault();
                            return;
                        }
                        return;
                    };
                    const handleDrag = (x, y) => {
                        const m = window.__goldenManual; if (!m) return;
                        const area = priceChartInstance.chartArea;
                        const clampX = (px) => Math.max(area.left, Math.min(area.right, px));
                        const MIN_SPAN = 24; // keep a grabbable minimum length in px
                        if (mode === 'left') {
                            m.yL = valAt(y);
                            m.xL = Math.min(clampX(x), (Number.isFinite(m.xR) ? m.xR : area.right) - MIN_SPAN);
                        } else if (mode === 'right') {
                            m.yR = valAt(y);
                            m.xR = Math.max(clampX(x), (Number.isFinite(m.xL) ? m.xL : area.left) + MIN_SPAN);
                        } else if (mode === 'move' && moveStart) {
                            const dV = valAt(y) - valAt(moveStart.y); // price delta of the drag
                            m.yL = moveStart.yL + dV; m.yR = moveStart.yR + dV;
                        }
                        scheduleDragDraw();
                    };
                    const onMove = (ev) => {
                        if (!window.__goldenShow) { cv.style.cursor = ''; return; }
                        const { x, y } = localXY(ev);
                        if (mode) { handleDrag(x, y); ev.preventDefault(); return; }
                        const area = priceChartInstance.chartArea, ep = endpoints();
                        if (!area || !ep || x < area.left || x > area.right) { cv.style.cursor = ''; return; }
                        const dL = Math.hypot(x - ep.L.x, y - ep.L.y), dR = Math.hypot(x - ep.R.x, y - ep.R.y);
                        const minX = Math.min(ep.L.x, ep.R.x), maxX = Math.max(ep.L.x, ep.R.x);
                        const withinSpan = x >= minX - 14 && x <= maxX + 14;
                        const distToLine = withinSpan ? Math.abs(y - lineYAt(x, ep)) : Infinity;
                        const nearEnd = dL <= 20 || dR <= 20;
                        cv.style.cursor = nearEnd ? 'ew-resize' : (distToLine <= 14 ? 'ns-resize' : '');
                    };
                    const onUp = () => { mode = null; moveStart = null; endDrag(); };
                    cv.addEventListener('mousedown', onDown);
                    window.addEventListener('mousemove', onMove);
                    window.addEventListener('mouseup', onUp);
                    cv.addEventListener('touchstart', onDown, { passive: false });
                    window.addEventListener('touchmove', onMove, { passive: false });
                    window.addEventListener('touchend', onUp);
                    // Double-click resets to auto.
                    cv.addEventListener('dblclick', () => {
                        if (window.__goldenManual) { window.__goldenManual = null; priceChartInstance.draw(); }
                    });
                }
            } catch (e) {}
            let __lastPulse = 0;
            (function pulse(ts) {
                if (!priceChartInstance) return;
                // Pause entirely when the tab/window isn't visible — no point repainting
                // a chart nobody's looking at, and it's the single biggest idle CPU cost.
                if (document.hidden) { window.__livePulseRAF = requestAnimationFrame(pulse); return; }
                // ~8fps is plenty for the pulsing price dot; a full chart.draw() re-runs
                // every plugin, so redrawing less often is a big, safe saving.
                if (!ts || ts - __lastPulse > 120) {
                    __lastPulse = ts || 0;
                    try { priceChartInstance.draw(); } catch (e) {}
                }
                window.__livePulseRAF = requestAnimationFrame(pulse);
            })();
        }

        // Populate the in-chart stat strip (top-right band) with the key live reads —
        // RVOL, extension (ATR% multiple), and ADR% — colour-coded for quick scanning.
        // Reads the already-rendered left-column values so it never drifts out of sync.
        function updateChartStatStrip() {
            const strip = document.getElementById('chartStatStrip');
            if (!strip) return;
            const rv = document.getElementById('rvolValue');
            const ext = document.getElementById('mtAtrMult');
            const adr = document.getElementById('mtAdrPct');
            const rvEl = document.getElementById('csRvol');
            const extEl = document.getElementById('csExt');
            const adrEl = document.getElementById('csAdr');
            if (!rvEl || !extEl || !adrEl) return;
            const rvTxt = rv ? (rv.textContent || '').trim() : '';
            const extTxt = ext ? (ext.textContent || '').trim() : '';
            const adrTxt = adr ? (adr.textContent || '').trim() : '';
            if (!rvTxt && !extTxt && !adrTxt) { strip.classList.add('hidden'); strip.classList.remove('flex'); return; }
            strip.classList.remove('hidden'); strip.classList.add('flex');
            rvEl.textContent = rvTxt || '--';
            extEl.textContent = (extTxt && extTxt !== '--') ? (extTxt.endsWith('x') ? extTxt : extTxt + 'x') : '--';
            adrEl.textContent = (adrTxt && adrTxt !== '--') ? (adrTxt.endsWith('%') ? adrTxt : adrTxt + '%') : '--';
            // Colour the RVOL value (surge/avg/quiet) for a fast read.
            const rvNum = parseFloat(rvTxt);
            rvEl.style.color = !Number.isFinite(rvNum) ? '#e2e8f0'
                : rvNum >= 1.5 ? '#6ee7b7' : rvNum >= 1.0 ? '#93c5fd' : '#94a3b8';
            // Colour extension amber→red as it stretches.
            const extNum = parseFloat(extTxt);
            extEl.style.color = !Number.isFinite(extNum) ? '#e2e8f0'
                : extNum > 10 ? '#fca5a5' : extNum > 7 ? '#fdba74' : '#e2e8f0';
        }

        // Update the header live-price block (last price + % change vs the prior daily close).
        // flash=true briefly glows green/red on a live tick.
        function updateChartPriceBlock(price, prevClose, flash) {
            const block = document.getElementById('chartPriceBlock');
            const pxEl = document.getElementById('chartLastPx');
            const pctEl = document.getElementById('chartChgPct');
            const absEl = document.getElementById('chartChgAbs');
            if (!block || !pxEl || !Number.isFinite(price)) return;
            block.classList.remove('hidden'); block.classList.add('flex');
            pxEl.textContent = '$' + price.toFixed(2);
            if (Number.isFinite(prevClose) && prevClose > 0) {
                const abs = price - prevClose, pct = (abs / prevClose) * 100;
                const up = abs >= 0;
                const col = up ? 'text-emerald-400' : 'text-rose-400';
                pctEl.className = 'text-xs font-bold metric-value ' + col;
                pctEl.textContent = (up ? '+' : '') + pct.toFixed(2) + '%';
                absEl.className = 'text-[10px] metric-value ' + (up ? 'text-emerald-400/70' : 'text-rose-400/70');
                absEl.textContent = (up ? '+' : '') + abs.toFixed(2) + ' today';
                if (flash) {
                    // Restart the CSS tick-flash keyframe (reduced-motion disables it in CSS).
                    const cls = up ? 'tick-up' : 'tick-down';
                    pxEl.classList.remove('tick-up', 'tick-down');
                    void pxEl.offsetWidth;   // force reflow so the animation replays on every tick
                    pxEl.classList.add(cls);
                }
            }
        }

        // ───────────────────────────────────────────────────────────────────────
        // GOLDEN LINE — the adaptive consolidation boundary along the highs.
        // Drawn across the most recent base: HORIZONTAL when the highs are flat
        // (cup lip / flat base → break = reclaim of the old high) or DIAGONAL when
        // the highs drift down (right side still forming → line meets price early,
        // so a break fires BEFORE the old high — the Qullamaggie early catch).
        // Pure geometry from the VISIBLE highs/closes; no trigger logic here.
        // Returns index-space anchors so the chart plugin can map them to pixels:
        //   { i1, y1, i2, y2, slope, orientation, attempts, basePrice, baseLenBars, depthPct }
        // or null when there's no clean line to draw (rising base, too few touches).
        function computeGoldenLine(closes, highs, lows) {
            const n = closes.length;
            if (n < 20) return null;
            // The golden line is the boundary of the LATEST consolidation we can see — the
            // recent flag/pause, NOT the whole prior decline. The look-back ADAPTS to how
            // many bars are on screen so the base scales with the timeframe: a 1M chart
            // gets a tight ~2-3 week boundary, a 3M chart a ~6-8 week one, etc. (Fixed 28
            // bars made every timeframe read "28d" and ignored longer visible bases.)
            // ~60% of the visible window, floored at 14 and capped at 120 trading days,
            // so a 3M chart traces a ~5-8 week base rather than a stale fixed 28 bars.
            const WINDOW = Math.min(n - 1, Math.max(14, Math.min(120, Math.round(n * 0.6))));
            let bStart = n - WINDOW;
            const m = n - bStart;
            if (m < 6) return null;

            // %/bar slope of the highs across the recent window.
            const segH = highs.slice(bStart);
            const k = segH.length;
            const xm = (k - 1) / 2;
            const ym = segH.reduce((a, b) => a + b, 0) / k;
            let num = 0, den = 0;
            for (let i = 0; i < k; i++) { num += (i - xm) * (segH[i] - ym); den += (i - xm) ** 2; }
            const bPerBar = den ? num / den : 0;            // price per bar
            const midH = segH[Math.floor(k / 2)] || 1;
            const hiSlopePct = midH ? (bPerBar / midH) * 100 : 0;
            const flatTol = 0.15;                            // %/bar considered "flat"

            // Suppress on a rising-highs window — no overhead line to break there.
            if (hiSlopePct > flatTol) return null;

            const bandHi = Math.max(...segH);
            const bandLo = Math.min(...lows.slice(bStart));
            const depthPct = bandHi ? ((bandHi - bandLo) / bandHi) * 100 : 0;

            // Swing-high pivots inside the window.
            const peaks = [];
            for (let i = 1; i < m - 1; i++) {
                const gi = bStart + i;
                if (highs[gi] >= highs[gi - 1] && highs[gi] >= highs[gi + 1]) peaks.push({ idx: gi, h: highs[gi] });
            }

            let orientation, i1, y1, i2, y2;
            if (Math.abs(hiSlopePct) <= flatTol) {
                // FLAT → horizontal line at the resistance band across the recent window.
                orientation = 'horizontal';
                i1 = bStart; y1 = bandHi;
                i2 = n - 1;  y2 = bandHi;
            } else {
                // DOWN-DRIFT → diagonal across the two most relevant swing highs in the
                // recent window. Left anchor = the FIRST significant high; right anchor =
                // the most recent lower high. This draws the latest descending boundary.
                orientation = 'diagonal';
                if (peaks.length < 2) {
                    // Not enough swing highs in the window — fall back to first/last highs
                    // so we still trace the recent boundary rather than drawing nothing.
                    i1 = bStart;       y1 = highs[bStart];
                    i2 = n - 1;        y2 = highs[n - 1];
                    if (y2 >= y1) return null; // not actually descending
                } else {
                    // Left: highest peak in the EARLIER part of the window.
                    const half = bStart + Math.floor(m / 2);
                    let left = peaks[0];
                    peaks.forEach(p => { if (p.idx <= half && p.h > left.h) left = p; });
                    // Right: the most RECENT peak after the left anchor (latest lower high).
                    let right = null;
                    peaks.forEach(p => { if (p.idx > left.idx) right = p; });
                    if (!right || right.idx <= left.idx || right.h >= left.h) return null;
                    i1 = left.idx;  y1 = left.h;
                    i2 = right.idx; y2 = right.h;
                }
            }

            // Line value at any index (extrapolated to today for the right edge).
            const slope = (i2 !== i1) ? (y2 - y1) / (i2 - i1) : 0;
            const lineAt = (idx) => y1 + slope * (idx - i1);
            const basePrice = lineAt(n - 1);                 // today's line value (the live level)

            // Attempt count: distinct times price PUSHED UP to the line and got rejected
            // (a touch event = entering the line's zone after pulling back below it). The
            // sweet spot is the 3rd–4th attempt, as overhead supply exhausts.
            let attempts = 0, inZone = false;
            for (let gi = bStart; gi < n; gi++) {
                const lv = lineAt(gi);
                const touching = lv > 0 && highs[gi] >= lv * 0.985 && highs[gi] <= lv * 1.012;
                if (touching && !inZone) { attempts++; inZone = true; }
                else if (!touching && highs[gi] < lv * 0.97) { inZone = false; } // pull back to re-arm
            }

            return { i1, y1, i2, y2, slope, orientation, attempts,
                     basePrice, baseLenBars: m, depthPct, hiSlopePct };
        }

        function updateChartDisplay(range) {
            // A manually-dragged golden level is tied to the timeframe it was set on;
            // drop it when the visible range changes so it doesn't carry over wrongly.
            if (window.__goldenManualRange !== range) { window.__goldenManual = null; window.__goldenManualRange = range; }
            if (range === '1D' || range === '1W') { fetchIntraday(range); return; }
            if(!engineState.cachedDataResults || !engineState.cachedDataResults.length) return;
            const full = engineState.cachedDataResults; let startIdx = 0;

            if (range === '1M') startIdx = Math.max(0, full.length - 21);
            else if (range === '3M') startIdx = Math.max(0, full.length - 63);
            else if (range === '1Y') startIdx = Math.max(0, full.length - 252);
            else if (range === 'YTD') {
                const currentYear = new Date(full[full.length-1].t).getFullYear();
                startIdx = full.findIndex(r => new Date(r.t).getFullYear() === currentYear);
                if (startIdx < 0) startIdx = 0;
            }
            const data = full.slice(startIdx);
            // On long ranges show the YEAR (month + 'YY) so a multi-year MAX/1Y chart is legible
            // — month/day alone made a 5-year span read like one year. Short ranges keep day.
            const _longRange = (range === 'MAX' || range === '1Y' || range === 'YTD' || data.length > 200);
            const _dateFmt = _longRange ? { month: 'short', year: '2-digit' } : { month: 'short', day: 'numeric' };
            const dates = data.map(r => new Date(r.t).toLocaleDateString('en-US', _dateFmt));
            const prices = data.map(r => r.c);
            const volume = data.map(r => r.v || 0);
            const volColors = volumeClass.slice(startIdx).map(c => VOL_COLORS[c] || VOL_COLORS.noise);
            const markers = wedgeSignals.filter(s => s.index >= startIdx && s.index < full.length && full[s.index]).map(s => ({ x: s.index - startIdx, y: full[s.index].c, type: s.type }));
            const omegaLocal = engineState.omegaSignals.filter(s => s.index >= startIdx && s.index < full.length && full[s.index]).map(s => ({ x: s.index - startIdx, y: full[s.index].c, type: s.type }));
            const extLocal = extDots.filter(d => d.index >= startIdx && d.index < full.length).map(d => ({ x: d.index - startIdx, mult: d.mult }));
            // Keep BOTH kinds in deltaDots (data preserved for the panel/screener),
            // but only the leading "before price" Delta dots are drawn on the chart.
            const deltaDotsLocal = deltaDots
                .filter(d => d.kind === 'before' && d.index >= startIdx && d.index < full.length)
                .map(d => ({ x: d.index - startIdx, kind: d.kind }));
            window.__chartVisibleBars = data.map(r => ({ o: r.o, h: r.h, l: r.l, c: r.c }));
            // MOVING-AVERAGE STACK (Seykota-inspired 8/21/50/200), computed from the FULL
            // history so every line is valid from the first visible bar, then sliced to the
            // visible window. The full-history MAs don't change between timeframe switches —
            // only the slice does — so we MEMOIZE them per ticker+barcount and just re-slice
            // on a range change. This is the main reason daily switches felt slow: we were
            // recomputing 8/21/50/200 over ~16y of bars on every click.
            const fullCloses = full.map(r => r.c);
            const maCacheKey = (state.currentTicker || '?') + ':' + full.length;
            if (!window.__maFullCache || window.__maFullCache.key !== maCacheKey) {
                const emaFull = (period) => {
                    const k = 2 / (period + 1); const out = []; let prev = null;
                    fullCloses.forEach((c, i) => {
                        if (i === 0) { prev = c; out.push(i >= period - 1 ? c : null); return; }
                        prev = c * k + prev * (1 - k);
                        out.push(i >= period - 1 ? prev : null);
                    });
                    return out;
                };
                const smaFull = (period) => fullCloses.map((_, i) => {
                    if (i < period - 1) return null;
                    let s = 0; for (let k = i - period + 1; k <= i; k++) s += fullCloses[k];
                    return s / period;
                });
                window.__maFullCache = {
                    key: maCacheKey,
                    ema8: emaFull(8), ema21: emaFull(21), sma50: smaFull(50), sma200: smaFull(200)
                };
            }
            const mc = window.__maFullCache;
            window.__maStack = {
                ema8:  mc.ema8.slice(startIdx),
                ema21: mc.ema21.slice(startIdx),
                sma50: mc.sma50.slice(startIdx),
                sma200: mc.sma200.slice(startIdx)
            };
            window.__ma50Visible = window.__maStack.sma50; // back-compat

            // GOLDEN LINE — adaptive consolidation boundary, computed from the VISIBLE
            // bars so its index-space anchors map straight onto the drawn candles.
            // Drawing only (toggle-gated by window.__goldenShow); no trigger yet.
            try {
                const visHighs = data.map(r => r.h);
                const visLows  = data.map(r => r.l);
                window.__goldenLine = computeGoldenLine(prices, visHighs, visLows);
            } catch (e) { window.__goldenLine = null; }

            renderPriceChart(dates, prices, false, markers, volume, volColors, null, null, extLocal, omegaLocal, deltaDotsLocal);
            restoreChartFade();
        }

        async function fetchIntraday(range) {
            if (!state.currentTicker) { ui.status.textContent = "Load a ticker first."; return; }
            const key = ui.apiKey.value.trim(); if (!key) return;
            // Cached within the session → instant draw, no network, fade restored by drawIntraday.
            if (intradayCache[range]) { drawIntraday(intradayCache[range], range); return; }

            const mult = range === '1D' ? 5 : 30; const span = 'minute';
            const lookbackDays = range === '1D' ? 5 : 9;
            const toObj = new Date(); const fromObj = new Date(); fromObj.setDate(toObj.getDate() - lookbackDays);
            const to = toObj.toISOString().split('T')[0]; const from = fromObj.toISOString().split('T')[0];

            ui.status.className = 'text-xs text-slate-400 font-mono bg-slate-900/50 px-3 py-1 rounded border border-slate-800';
            ui.status.textContent = `Loading ${range} intraday...`;
            // Show the subtle refreshing hint while the intraday bars are fetched.
            const sh = document.getElementById('chartRefreshHint');
            if (sh) sh.classList.remove('hidden');

            try {
                const url = `https://api.polygon.io/v2/aggs/ticker/${state.currentTicker}/range/${mult}/${span}/${from}/${to}?adjusted=true&sort=asc&limit=50000&apiKey=${key}`;
                const data = await polygonFetch(url, state.currentTicker);
                let bars = data.results || [];
                if (!bars.length) throw new Error("Intraday's being shy right now — try the daily view. 🕐");

                // Classify each bar by its ET CALENDAR DATE and ET session, DST-safe. Using ET
                // (not the browser's local date) is essential: a 4:00 AM ET pre-market bar and a
                // prior 8:00 PM ET after-hours bar can fall on the same LOCAL date in other
                // timezones (e.g. Romania), which scrambled the old toDateString() filter and
                // dropped pre-market bars.
                const etParts = (ms) => {
                    const p = new Intl.DateTimeFormat('en-US', {
                        timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
                        hour: '2-digit', minute: '2-digit', hour12: false
                    }).formatToParts(new Date(ms));
                    const g = {}; for (const x of p) g[x.type] = x.value;
                    return { date: `${g.year}-${g.month}-${g.day}`, min: (Number(g.hour) % 24) * 60 + Number(g.minute) };
                };
                const REG_OPEN = 9 * 60 + 30, REG_CLOSE = 16 * 60;
                const sessionOf = (m) => m < REG_OPEN ? 'pre' : (m >= REG_CLOSE ? 'after' : 'regular');
                const tagged = bars.map(b => { const e = etParts(b.t); return { b, etDate: e.date, session: sessionOf(e.min) }; });

                // DIAGNOSTIC: report what the feed actually returned, by ET date + session.
                const diag = {};
                tagged.forEach(t => { (diag[t.etDate] = diag[t.etDate] || { pre:0, regular:0, after:0 })[t.session]++; });
                if (typeof Logger !== 'undefined') Logger.debug('Intraday bars by ET date/session', { range, bars: bars.length, diag });

                const etDates = [...new Set(tagged.map(t => t.etDate))].sort();
                if (range === '1D') {
                    // Keep the latest ET date. If that date is currently only pre-market (today,
                    // before the open), also include the previous full session for context.
                    const lastDate = etDates[etDates.length - 1];
                    const lastSessions = new Set(tagged.filter(t => t.etDate === lastDate).map(t => t.session));
                    const keepDates = new Set([lastDate]);
                    if (!lastSessions.has('regular') && etDates.length > 1) keepDates.add(etDates[etDates.length - 2]);
                    bars = tagged.filter(t => keepDates.has(t.etDate)).map(t => t.b);
                } else {
                    const keep = new Set(etDates.slice(-5));
                    bars = tagged.filter(t => keep.has(t.etDate)).map(t => t.b);
                }
                intradayCache[range] = bars; drawIntraday(bars, range);
                if (bars.length) updateDataFreshness(bars[bars.length - 1].t);
                ui.status.className = 'text-xs text-emerald-400 font-mono bg-emerald-900/20 px-3 py-1 rounded border border-emerald-800';
                ui.status.textContent = `Loaded ${state.currentTicker} (${range})`;
            } catch (e) {
                ui.status.className = 'text-xs text-red-400 font-mono bg-red-900/20 px-3 py-1 rounded border border-red-800';
                ui.status.textContent = e.message;
                restoreChartFade();
                const sh = document.getElementById('chartRefreshHint');
                if (sh) sh.classList.add('hidden');
            }
        }

        // Noise-filtered fast 5-EMA crossovers for the intraday (1D/1W) views.
        // A raw 5-EMA cross fires on every wiggle, so two filters trim it to ~2–4 per window:
        //   1) NOISE BAND  — the close must clear the EMA by >= FAST_BAND_PCT to switch sides;
        //                    while price hugs the line (inside the band) it holds its previous side, so no flip.
        //   2) COOLDOWN    — at least FAST_COOLDOWN bars must pass between accepted signals (a hard frequency cap).
        // Signals strictly alternate up/down. Want more prints? lower the two numbers. Fewer? raise them.
        function detectFastEmaCrosses(closes, ema, range) {
            const FAST_BAND_PCT = 0.005;                     // 0.5% clearance needed to flip sides
            const FAST_COOLDOWN = range === '1D' ? 24 : 16;  // min bars between signals (≈ 2–4 per visible window)
            const out = [];
            let decisive = 0, emittedDir = 0, lastEmitIdx = -Infinity;
            for (let i = 0; i < closes.length; i++) {
                if (ema[i] === null) continue;
                const band = ema[i] * FAST_BAND_PCT;
                if (closes[i] > ema[i] + band) decisive = 1;
                else if (closes[i] < ema[i] - band) decisive = -1;
                // inside the band: keep the previous decisive side (ignore the wiggle)
                if (decisive !== 0) {
                    if (emittedDir === 0) { emittedDir = decisive; }            // establish baseline, no marker
                    else if (decisive !== emittedDir && (i - lastEmitIdx) >= FAST_COOLDOWN) {
                        out.push({ index: i, dir: decisive === 1 ? 'UP' : 'DOWN' });
                        emittedDir = decisive; lastEmitIdx = i;
                    }
                }
            }
            return out;
        }

        function drawIntraday(bars, range) {
            // Classify each bar as regular (09:30–16:00 ET) or extended (pre/after) hours,
            // DST-safe via America/New_York. Build contiguous extended-hours index ranges
            // for the chart's session-shading plugin.
            const etMinutes = (ms) => {
                const parts = new Intl.DateTimeFormat('en-US', {
                    timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: false
                }).formatToParts(new Date(ms));
                let h = 0, m = 0;
                for (const p of parts) { if (p.type === 'hour') h = +p.value; if (p.type === 'minute') m = +p.value; }
                return (h % 24) * 60 + m;
            };
            const REG_OPEN = 9 * 60 + 30, REG_CLOSE = 16 * 60;
            const isExt = bars.map(b => { const t = etMinutes(b.t); return t < REG_OPEN || t >= REG_CLOSE; });
            const bands = [];
            for (let i = 0; i < isExt.length; i++) {
                if (!isExt[i]) continue;
                let j = i; while (j + 1 < isExt.length && isExt[j + 1]) j++;
                // span the band slightly to cover the bar width on the category scale
                bands.push({ start: Math.max(0, i - 0.5), end: j + 0.5 });
                i = j;
            }
            window.__intradayBands = bands;

            const labels = bars.map(b => {
                const d = new Date(b.t); const t = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                return range === '1D' ? t : `${d.getMonth()+1}/${d.getDate()} ${t}`;
            });
            const closes = bars.map(b => b.c);

            // Shorter-term, intraday-only signal: price crossing the fast 5-period EMA (noise-filtered)
            const fastEma = MathLib.emaSeries(closes, 5);
            const crossSignals = detectFastEmaCrosses(closes, fastEma, range);

            // VOLUME BARS — same scheme as the daily/3M chart (PPV / up>avg / down>avg /
            // noise), but computed per intraday bar. The "50d average" becomes a rolling
            // 50-bar average over the intraday series.
            const vols = bars.map(b => b.v || 0);
            const volMA = MathLib.smaSeries(vols, 50);
            const volClass = new Array(bars.length).fill('noise');
            for (let i = 1; i < bars.length; i++) {
                const isUp = bars[i].c > bars[i - 1].c;
                const isDown = bars[i].c < bars[i - 1].c;
                const ma = volMA[i];
                let cls = 'noise';
                if (isUp) {
                    let maxDownVol = 0;
                    for (let j = Math.max(1, i - 10); j < i; j++) {
                        if (bars[j].c < bars[j - 1].c) maxDownVol = Math.max(maxDownVol, vols[j]);
                    }
                    if (maxDownVol > 0 && vols[i] > maxDownVol) cls = 'ppv';
                }
                if (cls === 'noise' && ma) {
                    if (isUp && vols[i] > ma) cls = 'green';
                    else if (isDown && vols[i] > ma) cls = 'red';
                }
                volClass[i] = cls;
            }
            const volColors = volClass.map(cls => VOL_COLORS[cls] || VOL_COLORS.noise);

            window.__chartVisibleBars = bars.map(b => ({ o: b.o, h: b.h, l: b.l, c: b.c }));
            // GOLDEN LINE for intraday too (was daily-only). Same adaptive boundary,
            // computed from the visible intraday highs/lows so the toggle works on 1D/1W.
            try {
                const ih = bars.map(b => b.h), il = bars.map(b => b.l);
                window.__goldenLine = computeGoldenLine(closes, ih, il);
            } catch (e) { window.__goldenLine = null; }
            renderPriceChart(labels, closes, true, null, vols, volColors, fastEma, crossSignals);

            // Intraday draw complete → restore the cross-fade and clear the refreshing hint.
            restoreChartFade();
            const sh = document.getElementById('chartRefreshHint');
            if (sh) sh.classList.add('hidden');
        }

        function setRange(range) {
            AppState.setChartRange(range);
            document.querySelectorAll('#chartRangeBtns button').forEach(b => {
                b.classList.remove('active-range', 'bg-blue-600', 'border-blue-600', 'text-white');
                b.classList.add('bg-slate-800', 'border-slate-700', 'text-slate-300');
            });
            const tgt = (typeof event !== 'undefined' && event && event.target) ? event.target : null;
            if (tgt) {
                tgt.classList.remove('bg-slate-800', 'border-slate-700', 'text-slate-300');
                tgt.classList.add('active-range', 'bg-blue-600', 'border-blue-600', 'text-white');
            }
            // Cross-fade the chart card so the timeframe swap reads smoothly. Restored by
            // restoreChartFade() once the chart has painted (daily) or after the intraday
            // fetch+draw completes.
            const cc = document.getElementById('chartCard');
            if (cc) cc.style.opacity = '0.55';
            updateChartDisplay(range);
        }

        // Restore the chart card opacity after a range swap finishes painting.
        function restoreChartFade() {
            const cc = document.getElementById('chartCard');
            if (cc) cc.style.opacity = '1';
        }

        // ── Full-screen chart toggle ──────────────────────────────────────────────
        // Expands the whole chart panel (header + canvas) to cover the viewport, then
        // resizes the Chart.js instance to fill it. Esc exits.
        const FS_ICON_OPEN  = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3"/></svg>';
        const FS_ICON_CLOSE = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3v3a2 2 0 0 1-2 2H3M16 3v3a2 2 0 0 0 2 2h3M8 21v-3a2 2 0 0 0-2-2H3M16 21v-3a2 2 0 0 1 2-2h3"/></svg>';
        function toggleChartFullscreen() {
            const panel = document.getElementById('chartPanel');
            if (!panel) return;
            const fs = panel.classList.toggle('chart-fs');
            document.body.style.overflow = fs ? 'hidden' : '';
            const btn = document.getElementById('chartFsBtn');
            if (btn) { btn.innerHTML = fs ? FS_ICON_CLOSE : FS_ICON_OPEN; btn.title = fs ? 'Exit full screen (Esc)' : 'Full screen'; }
            // Let layout settle, then tell Chart.js to redraw at the new canvas size.
            setTimeout(() => { try { if (typeof priceChartInstance !== 'undefined' && priceChartInstance && priceChartInstance.resize) priceChartInstance.resize(); } catch (e) {} }, 70);
        }
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape') {
                const p = document.getElementById('chartPanel');
                if (p && p.classList.contains('chart-fs')) toggleChartFullscreen();
            }
        });

        // Compact, color-coded strip of the key gathered signals, shown under the thesis.
        // Pure string-building from an already-computed snapshot (no network/async) so it
        // paints instantly. Each chip is gated on data + notability — misses are skipped,
        // never shown as blanks, so it stays lean.
        // ── Moving-average line toggles ──────────────────────────────────────────
        // Each chip shows/hides one MA on the price chart. All four on by default
        // (8 & 21 EMA fast/momentum, 50 & 200 SMA trend); click a chip to hide it.
        window.__maShow = window.__maShow || { ema8: true, ema21: true, sma50: true, sma200: true };
        window.__goldenShow = window.__goldenShow || false; // Golden Line off by default (opt-in)
        function paintMaToggles() {
            document.querySelectorAll('#maToggleBar .ma-toggle').forEach(b => {
                const on = !!window.__maShow[b.dataset.ma];
                b.style.opacity = on ? '1' : '0.35';
                b.style.filter = on ? 'none' : 'grayscale(0.6)';
            });
            const g = document.querySelector('#maToggleBar .golden-toggle');
            if (g) { g.style.opacity = window.__goldenShow ? '1' : '0.35'; g.style.filter = window.__goldenShow ? 'none' : 'grayscale(0.6)'; }
        }
        (function initMaToggles() {
            const bar = document.getElementById('maToggleBar');
            if (!bar) return;
            paintMaToggles();
            bar.addEventListener('click', (e) => {
                const gbtn = e.target.closest('[data-golden]');
                if (gbtn) {
                    window.__goldenShow = !window.__goldenShow;
                    paintMaToggles();
                    if (typeof state.currentChartRange !== 'undefined' && !['1D','1W'].includes(state.currentChartRange)
                        && typeof updateChartDisplay === 'function') updateChartDisplay(state.currentChartRange);
                    return;
                }
                const btn = e.target.closest('[data-ma]');
                if (!btn) return;
                const k = btn.dataset.ma;
                window.__maShow[k] = !window.__maShow[k];
                paintMaToggles();
                // Redraw the chart with the new visibility (daily ranges only).
                if (typeof state.currentChartRange !== 'undefined' && !['1D','1W'].includes(state.currentChartRange)
                    && typeof updateChartDisplay === 'function') updateChartDisplay(state.currentChartRange);
            });
        })();


        function setMvRow(id, pass, detail) {
            const row = document.getElementById(id); if (!row) return;
            const icon = row.querySelector('.mv-icon'); const det = row.querySelector('.mv-detail');
            det.textContent = detail;
            if (pass === null) { icon.textContent = "⚪"; row.className = "flex items-center gap-2 bg-slate-900/50 rounded-lg p-2 border border-slate-800"; } 
            else if (pass) { icon.textContent = "✅"; row.className = "flex items-center gap-2 bg-emerald-900/20 rounded-lg p-2 border border-emerald-500/40"; } 
            else { icon.textContent = "❌"; row.className = "flex items-center gap-2 bg-rose-900/20 rounded-lg p-2 border border-rose-500/40"; }
        }


        // ─────────────────────────────────────────────────────────────────────────
        //  REAL RELATIVE STRENGTH  (RS Line + IBD-style RS Rating)
        //  Replaces the proxy. Two distinct outputs, exactly as IBD/MarketSurge:
        //   • RS LINE  = stock close ÷ benchmark close, date-aligned. Drives the
        //               new-high / "blue dot" signals.
        //   • RS RATING (1–99) = IBD-weighted 12-month price performance of the
        //               STOCK, then converted to a cross-sectional PERCENTILE.
        //               Real percentile is only possible with the whole universe
        //               in hand (the screener), so the dashboard borrows the most
        //               recent screener distribution; absent that, we fall back to
        //               a labeled approximation (and never claim it's a percentile).
        // ─────────────────────────────────────────────────────────────────────────

        // User-selectable benchmark. Default SPY; any ticker/ETF allowed.
        let rsBenchmarkSymbol = (localStorage.getItem('rsBenchmarkSymbol') || 'SPY').toUpperCase();
        // Session cache of benchmark bars, keyed by symbol (mirrors spyBarsCache).
        const benchmarkBarsCache = {};
        // Sorted array of raw RS strength scores from the last full screener run,
        // used as the percentile reference for the single-ticker dashboard.
        try {
            const d = JSON.parse(localStorage.getItem('rsUniverseDistributionV1') || 'null');
            if (d && Array.isArray(d.scores) && (Date.now() - d.savedAt) < 36 * 60 * 60 * 1000) engineState.rsUniverseDistribution = d.scores;
            setEngineContext({ rsUniverseDistribution: engineState.rsUniverseDistribution }); // push into shared engine (used by evaluateMinerviniLite)
        } catch (e) {}

        // Fetch daily bars for ANY benchmark symbol, session-cached per symbol.
        // SPY reuses the existing spyBarsCache / Structure-module bars for free.
        async function fetchBenchmarkBars(symbol, key) {
            symbol = (symbol || 'SPY').toUpperCase();
            if (symbol === 'SPY' && typeof fetchSpyBars === 'function') return fetchSpyBars(key);
            if (benchmarkBarsCache[symbol]) return benchmarkBarsCache[symbol];
            if (!key) return null;
            try {
                const toDate = new Date().toISOString().split('T')[0];
                const fromObj = new Date(); fromObj.setDate(fromObj.getDate() - 1100);
                const fromDate = fromObj.toISOString().split('T')[0];
                const data = await polygonFetch(`https://api.polygon.io/v2/aggs/ticker/${symbol}/range/1/day/${fromDate}/${toDate}?adjusted=true&sort=asc&limit=50000&apiKey=${key}`, symbol);
                if (!data.results || data.results.length < 60) return null;
                benchmarkBarsCache[symbol] = {
                    t: data.results.map(r => r.t),
                    closes: data.results.map(r => r.c),
                    highs: data.results.map(r => r.h),
                    lows: data.results.map(r => r.l)
                };
                return benchmarkBarsCache[symbol];
            } catch (e) { return null; }
        }

        // Build the RS LINE aligned to the asset's `results` ({t,c,...}). Returns an
        // array (same length as results) of stock/benchmark ratios, null where the
        // benchmark has no matching session. Alignment mirrors computeVarsSeries.
        // DIVERGENCE STRENGTH (short-term RS separation vs the benchmark).
        // Takes the RS line (stock/benchmark), runs a fast 3-EMA and a 10-EMA on it, and
        // measures the spread between them as a % of the 10-EMA. Positive & widening = the
        // stock is accelerating away from the benchmark (fresh short-term leadership);
        // negative & widening = accelerating underperformance. We also report the 5-bar
        // slope of the spread so the UI can say "widening" vs "converging".
        // Volatility Contraction Pattern (VCP) + base depth from the raw swing structure.
        // Walks 2-bar fractal pivots, builds the sequence of peak→trough contractions, and
        // tests the Minervini signature: progressively SHALLOWER pullbacks on rising lows.
        // depthPct = deepest contraction (the base depth); tightestPct = the final, tightest one.
        // Unified coil quality + mode (validated on synthetic archetypes + the standalone tester).
        // Grades the SAME setup two ways and takes the better: a tight contraction near a pivot
        // ("flag") or a shallow pullback riding a rising MA ("ma-ride"). 'advance' is the pole/uptrend
        // precondition — a coil with no prior thrust can't score. Reads the full series (MA/pole/vol),
        // plus the base object detectVcpBase already built (depth/tightest/higherLows/dry-up).

        // Paint the VCP row under the Trend Template from the cached lastVcpBase.
        function renderVcpRow() {
            const statusEl = document.getElementById('vcpStatus');
            if (!statusEl) return;
            const cEl = document.getElementById('vcpContractions'), sEl = document.getElementById('vcpStage'),
                  vEl = document.getElementById('vcpVolume'), pEl = document.getElementById('vcpPivot');
            const badge = (el, tone) => { el.className = 'px-2 py-0.5 rounded-md text-[10px] font-semibold border ' + ({
                emerald: 'border-emerald-500/40 bg-emerald-900/30 text-emerald-300',
                amber:   'border-amber-500/40 bg-amber-900/30 text-amber-300',
                rose:    'border-rose-500/40 bg-rose-900/30 text-rose-300',
                slate:   'border-slate-700 bg-slate-800/50 text-slate-400'
            }[tone] || 'border-slate-700 bg-slate-800/50 text-slate-400'); };
            const hide = el => el && el.classList.add('hidden');
            const show = el => el && el.classList.remove('hidden');
            const v = (typeof lastVcpBase !== 'undefined') ? lastVcpBase : null;
            if (!v) { statusEl.textContent = 'Awaiting data'; badge(statusEl, 'slate'); [cEl, sEl, vEl, pEl].forEach(hide); return; }
            if (!v.isVcp) {
                statusEl.textContent = v.count >= 2 ? 'No clean VCP' : 'No VCP';
                badge(statusEl, 'slate'); [cEl, sEl, vEl, pEl].forEach(hide);
                return;
            }
            statusEl.textContent = v.confirmed ? 'VCP confirmed' : 'VCP forming';
            badge(statusEl, v.confirmed ? 'emerald' : 'amber');
            show(cEl); cEl.textContent = v.contractions.join('→') + '%';
            show(sEl);
            const stageLbl = v.stage === 'coiling' ? 'Coiling under pivot' : v.stage === 'breakout' ? 'Breaking out' : v.stage === 'extended' ? 'Extended past pivot' : 'Forming';
            const pivTxt = Number.isFinite(v.distToPivotPct) ? (v.distToPivotPct >= 0 ? ` · ${v.distToPivotPct.toFixed(1)}% to pivot` : ` · ${Math.abs(v.distToPivotPct).toFixed(1)}% past`) : '';
            sEl.textContent = stageLbl + pivTxt;
            badge(sEl, (v.stage === 'coiling' || v.stage === 'breakout') ? 'emerald' : v.stage === 'extended' ? 'rose' : 'amber');
            show(vEl); vEl.textContent = v.volumeDryUp ? 'Volume drying up ✓' : 'Volume flat';
            badge(vEl, v.volumeDryUp ? 'emerald' : 'slate');
            if (Number.isFinite(v.pivot)) { show(pEl); pEl.className = 'px-2 py-0.5 rounded-md text-[10px] font-mono text-slate-400 bg-slate-800/40 border border-slate-800'; pEl.textContent = 'pivot ~' + (v.pivot >= 1000 ? Math.round(v.pivot) : v.pivot.toFixed(2)); }
            else hide(pEl);
        }

        function computeRsDivergence(rsLine, fastLen = 3, slowLen = 10) {
            if (!rsLine || !rsLine.length) return null;
            const clean = rsLine.filter(v => v != null && Number.isFinite(v));
            if (clean.length < slowLen + 6) return null;
            const fast = MathLib.emaSeries(clean, fastLen);
            const slow = MathLib.emaSeries(clean, slowLen);
            const n = clean.length;
            // spread series as % of slow EMA
            const spread = new Array(n).fill(null);
            for (let i = 0; i < n; i++) {
                if (fast[i] == null || slow[i] == null || !slow[i]) continue;
                spread[i] = ((fast[i] - slow[i]) / slow[i]) * 100;
            }
            const lastSpread = [...spread].reverse().find(v => v != null) ?? null;
            if (lastSpread == null) return null;
            // 5-bar slope of the spread (are the EMAs separating or closing?)
            const valid = spread.filter(v => v != null);
            const prevSpread = valid.length >= 6 ? valid[valid.length - 6] : valid[0];
            const slope = lastSpread - prevSpread;
            const widening = Math.abs(lastSpread) > Math.abs(prevSpread);
            // Normalize magnitude to a 0–100 strength score. ±1.5% spread on the RS line is
            // already a strong short-term divergence for daily bars, so we scale against that.
            const strength = Math.min(100, Math.round((Math.abs(lastSpread) / 1.5) * 100));
            let state;
            if (lastSpread >= 0 && widening) state = 'accelerating';     // pulling ahead, faster
            else if (lastSpread >= 0 && !widening) state = 'leading-fading'; // ahead but converging
            else if (lastSpread < 0 && widening) state = 'lagging-worse'; // falling behind, faster
            else state = 'lagging-recovering';                           // behind but closing gap
            return { spreadPct: lastSpread, slope, widening, strength, state, fastLen, slowLen };
        }

        // BENCHMARK REGIME: is the market (SPY/QQQ/…) itself rising or correcting?
        // Used to flag "stealth leaders" — stocks holding up while the index falls.
        // Returns { trendPct, falling, deep } based on the benchmark's own 1-month change
        // and its position vs a 50-bar SMA.
        function computeBenchmarkRegime(bench) {
            if (!bench || !bench.closes || bench.closes.length < 30) return null;
            const c = bench.closes;
            const last = c[c.length - 1];
            const ref = c[Math.max(0, c.length - 21)];           // ~1 month ago
            const trendPct = ref ? ((last - ref) / ref) * 100 : 0;
            const smaLen = Math.min(50, c.length);
            const sma = c.slice(-smaLen).reduce((a, b) => a + b, 0) / smaLen;
            const belowSma = last < sma;
            return {
                trendPct,
                falling: trendPct < -1.5 || belowSma,             // pulling back / under 50-SMA
                deep: trendPct < -5,                              // meaningful correction
                belowSma
            };
        }

        // ── RS POWER / DELTA (pure, reusable) ────────────────────────────────────
        // Extracted from analyzeScreenerTicker so the single-ticker dashboard, the
        // screener, the alerts log, and the future Young Leader engine all derive RS
        // leadership the SAME way. Pure: no side effects, no globals, no DOM.
        //
        // Tiers (strongest → weakest):
        //   Delta    — RS line at a new high BEFORE price confirms (leading accumulation)
        //   RS NH    — RS line at a new high (with price)
        //   Primed   — proximity ≥ 98% of the prior 52w RS high
        //   Building — proximity ≥ 92%
        //   Firm     — proximity ≥ 80%
        //   Lagging  — below 80%
        //
        /**
         * @typedef {Object} RsSignals
         * @property {boolean} [deltaTrigger]  RS new high before price (leading)
         * @property {boolean} [rsNewHigh]     RS line at a new 52w high
         * @property {number}  [rsProximity]   rsToday / prior-52w-RS-high * 100 (100 = at the high)
         *
         * @typedef {Object} RsPower
         * @property {number|null} rsPower      0–100 strength score
         * @property {('Delta'|'RS NH'|'Primed'|'Building'|'Firm'|'Lagging'|null)} rsPowerTier
         * @property {number|null} rsProximity  pass-through proximity (or null)
         *
         * @param {RsSignals|null} rsSignals
         * @returns {RsPower}
         */

        // RELATIVE LEADERSHIP SCORE (0–100): one number for "is this stock stronger
        // than the average?" Blends four institutional signals:
        //   • RS Rating (IBD percentile)            — long-term relative leadership
        //   • RS-line new high / Delta              — fresh leadership / leading the price
        //   • Divergence strength (signed)          — short-term acceleration vs benchmark
        //   • Benchmark regime                      — rewards strength during market weakness
        // Also returns a `stealth` flag: stock leading WHILE the benchmark is falling —
        // exactly the "next leader during a correction" the desk wants to catch early.
        function computeLeadershipScore({ rsRating, rsSignals, divergence, regime }) {
            // Base: RS Rating is the backbone (0–99 → 0–100).
            let score = (rsRating != null) ? rsRating : 50;

            // Short-term divergence nudges the score ±18 around the RS backbone.
            if (divergence) {
                const signed = (divergence.spreadPct >= 0 ? 1 : -1) * divergence.strength; // -100..100
                score += (signed / 100) * 18;
                if (divergence.state === 'accelerating') score += 4;
                else if (divergence.state === 'lagging-worse') score -= 4;
            }
            // RS-line leadership signals.
            if (rsSignals) {
                if (rsSignals.deltaTrigger) score += 8;       // RS new high while price isn't — leading
                else if (rsSignals.rsNewHigh) score += 5;     // RS line at a fresh 52w high
            }
            // Regime bonus: outperforming while the market is weak is the highest-quality strength.
            let stealth = false, stealthDetail = '';
            const leadingNow = divergence ? divergence.spreadPct >= 0 : (rsRating != null && rsRating >= 70);
            if (regime && regime.falling && leadingNow && (rsRating == null || rsRating >= 70)) {
                score += regime.deep ? 10 : 6;
                stealth = true;
                const benchMove = regime.trendPct.toFixed(1);
                stealthDetail = `Holding strong while ${rsBenchmarkSymbol} is ${benchMove}% over ~1mo` +
                    (rsSignals && rsSignals.deltaTrigger ? ' · RS at new high' : '') + '.';
            }
            score = Math.max(0, Math.min(100, Math.round(score)));

            let tier, label, color;
            if (score >= 80)      { tier = 'leader';   label = 'Strong Leader';   color = '#10b981'; }
            else if (score >= 65) { tier = 'outperf';  label = 'Outperforming';   color = '#34d399'; }
            else if (score >= 50) { tier = 'inline';   label = 'In Line';         color = '#eab308'; }
            else if (score >= 35) { tier = 'soft';     label = 'Lagging';         color = '#fb923c'; }
            else                  { tier = 'laggard';  label = 'Laggard';         color = '#f43f5e'; }
            return { score, tier, label, color, stealth, stealthDetail, leadingNow };
        }


        // RS-line new-high signals over `lookback` bars (IBD default 252 = 52 weeks).
        //   rsNewHigh             : RS line at a new lookback high today.
        //   deltaTrigger  : the "blue dot" — RS line at a new high while the
        //                           stock price is NOT (leading institutional signal).

        // Per-bar version for charting: walk every bar and flag where the RS line made
        // a new `lookback`-bar high. `kind` is 'before' (RS NH while price has NOT made
        // its own new high — the blue dot) or 'rs' (RS NH, price also at/над new high).
        // Returns an array of { index, kind } events (one per qualifying bar).

        // Stage 1 of the rating: IBD-weighted trailing price performance of the STOCK
        // (front quarter double-weighted). This is the raw strength score that gets
        // percentiled. Computed on the stock's own closes — matches IBD's RS Rating
        // (the benchmark is used for the LINE, not the rating's raw score).

        // Stage 2: map a raw score to a true 1–99 percentile against `distribution`
        // (a sorted/unsorted array of raw scores for the whole universe).

        // Single-ticker convenience: real percentile if a fresh universe distribution
        // exists; otherwise a clearly-labeled approximation (NOT a true percentile).
        // Returns { rating, isPercentile }.
        function computeRsRating(closes) {
            const raw = computeRsRawScore(closes);
            if (raw == null) return { rating: null, isPercentile: false };
            if (engineState.rsUniverseDistribution && engineState.rsUniverseDistribution.length > 50) {
                return { rating: rsRawToPercentile(raw, engineState.rsUniverseDistribution), isPercentile: true };
            }
            // Fallback: squash raw to 1–99 (same shape as the old proxy) — labeled approx.
            return { rating: Math.max(1, Math.min(99, Math.round(50 + raw * 60))), isPercentile: false };
        }

        // --- POCKET PIVOT VOLUME (PPV) ENGINE ---
        // Classifies every bar into one of four buckets so the volume histogram tells the
        // story by color alone (no MA line, no noise):
        //   ppv   (blue)  : up-day whose volume > the highest DOWN-day volume of the prior 10 days
        //   green        : up-day with volume > 50d average (but not a PPV)
        //   red          : down-day with volume > 50d average  -> unconsumed supply
        //   noise (grey) : everything else (below-average volume, ignore it)
        function computeVolumeProfile(results) {
            volumeClass = new Array(results.length).fill('noise');
            volumeSignals = [];
            if (!results || !results.length) return;

            const vols = results.map(r => r.v || 0);
            const volMA = MathLib.smaSeries(vols, 50); // rolling 50d avg, null for first 49 bars

            for (let i = 1; i < results.length; i++) {
                const bar = results[i];
                const prevClose = results[i - 1].c;
                const vol = vols[i];
                const isUp = bar.c > prevClose;
                const isDown = bar.c < prevClose;
                const ma = volMA[i];
                let cls = 'noise';

                // 1) Pocket Pivot: up-day eclipsing the largest down-day volume of the last 10 sessions
                if (isUp) {
                    let maxDownVol = 0;
                    for (let j = Math.max(1, i - 10); j < i; j++) {
                        if (results[j].c < results[j - 1].c) maxDownVol = Math.max(maxDownVol, vols[j]);
                    }
                    if (maxDownVol > 0 && vol > maxDownVol) cls = 'ppv';
                }

                // 2) Otherwise, flag conviction days that clear the 50d average
                if (cls === 'noise' && ma) {
                    if (isUp && vol > ma) cls = 'green';
                    else if (isDown && vol > ma) cls = 'red';
                }

                volumeClass[i] = cls;

                // Record only the noteworthy events for the textual signal
                const ratio = ma ? vol / ma : null;
                if (cls === 'ppv') {
                    volumeSignals.push({ index: i, type: 'PPV', volume: vol, ratio });
                } else if (ratio && ratio >= HUGE_RATIO) {
                    if (isDown) volumeSignals.push({ index: i, type: 'HUGE_RED', volume: vol, ratio });
                    else if (isUp) volumeSignals.push({ index: i, type: 'HUGE_GREEN', volume: vol, ratio });
                }
            }
        }


        function evaluateMinervini(closes, lows, highs, results = null, bench = null) {
            if (!closes || closes.length < 200) {
                ['mv1','mv2','mv3','mv4','mv5','mv6','mv7','mv8'].forEach(id => setMvRow(id, null, "Insufficient history (need 200+ days)"));
                ui.minScore.textContent = "-- / 8"; ui.minVerdict.textContent = "Insufficient Data";
                ui.minVerdict.className = "px-4 py-2 rounded-lg text-sm font-bold border border-slate-700 bg-slate-800/50 text-slate-400"; 
                return { passed: 0, rs: 0 };
            }
            const price = closes[closes.length - 1];
            const sma50 = MathLib.sma(closes, 50); const sma150 = MathLib.sma(closes, 150);
            const sma200 = MathLib.sma(closes, 200); const sma200_1mo = MathLib.smaAsOf(closes, 200, 21);
            const window = 252; const low52 = Math.min(...lows.slice(-window)); const high52 = Math.max(...highs.slice(-window));

            const c1 = price > sma150 && price > sma200; setMvRow('mv1', c1, `Px $${price.toFixed(2)} | 150d $${sma150.toFixed(2)} | 200d $${sma200.toFixed(2)}`);
            const c2 = sma150 > sma200; setMvRow('mv2', c2, `150d $${sma150.toFixed(2)} vs 200d $${sma200.toFixed(2)}`);
            const trendPct = sma200_1mo ? ((sma200 - sma200_1mo) / sma200_1mo * 100) : 0;
            const c3 = sma200_1mo !== null && sma200 > sma200_1mo; setMvRow('mv3', c3, sma200_1mo ? `200d ${trendPct >= 0 ? '+' : ''}${trendPct.toFixed(2)}% over ~1mo` : "Not enough data");
            const c4 = sma50 > sma150 && sma50 > sma200; setMvRow('mv4', c4, `50d $${sma50.toFixed(2)} | 150d $${sma150.toFixed(2)} | 200d $${sma200.toFixed(2)}`);
            const c5 = price > sma50; setMvRow('mv5', c5, `Px $${price.toFixed(2)} vs 50d $${sma50.toFixed(2)}`);
            const aboveLowPct = (price - low52) / low52 * 100; const c6 = aboveLowPct >= 25; setMvRow('mv6', c6, `${aboveLowPct.toFixed(1)}% above 52w low ($${low52.toFixed(2)})`);
            const belowHighPct = (high52 - price) / high52 * 100; const c7 = belowHighPct <= 25; setMvRow('mv7', c7, `${belowHighPct.toFixed(1)}% below 52w high ($${high52.toFixed(2)})`);
            // Real RS Rating: IBD-weighted 12-month performance → universe percentile.
            const rsRes = computeRsRating(closes);
            const rs = rsRes.rating;
            const c8 = rs !== null && rs >= 70;
            const rsLabel = rs == null ? "RS unavailable"
                : `RS Rating: ${rs}${rsRes.isPercentile ? ' (percentile)' : ' (approx — run screener for true percentile)'}`;
            setMvRow('mv8', c8, rsLabel);

            // RS LINE (vs selected benchmark) + new-high / blue-dot signals.
            let rsSignals = null;
            if (results && bench) {
                const rsLine = computeRsLineSeries(results, bench);
                if (rsLine) rsSignals = computeRsLineSignals(rsLine, closes, 252);
            }

            const passed = [c1, c2, c3, c4, c5, c6, c7, c8].filter(Boolean).length;
            ui.minScore.textContent = `${passed} / 8`;
            if (passed === 8) { ui.minVerdict.textContent = "✓ STAGE 2 LEADER"; ui.minVerdict.className = "px-4 py-2 rounded-lg text-sm font-bold border border-emerald-500/50 bg-emerald-600/20 text-emerald-300"; ui.minScore.className = "text-sm font-bold text-emerald-400 font-mono bg-emerald-900/20 px-4 py-2 rounded-lg border border-emerald-800"; }
            else if (passed >= 6) { ui.minVerdict.textContent = "Near Miss"; ui.minVerdict.className = "px-4 py-2 rounded-lg text-sm font-bold border border-yellow-500/40 bg-yellow-900/20 text-yellow-300"; ui.minScore.className = "text-sm font-bold text-yellow-400 font-mono bg-yellow-900/20 px-4 py-2 rounded-lg border border-yellow-800"; }
            else { ui.minVerdict.textContent = "Not Qualified"; ui.minVerdict.className = "px-4 py-2 rounded-lg text-sm font-bold border border-rose-500/40 bg-rose-900/20 text-rose-300"; ui.minScore.className = "text-sm font-bold text-rose-400 font-mono bg-rose-900/20 px-4 py-2 rounded-lg border border-rose-800"; }
            
            return { passed, rs: rs || 0, rsIsPercentile: rsRes.isPercentile, rsSignals };
        }

        // Render the dashboard RS-line panel: rating badge + new-high / blue-dot state.
        function renderRsLinePanel(signals, rating, isPercentile, deltaRecency) {
            const rd = document.getElementById('rsRatingDetail');
            const rv = document.getElementById('rsRatingValue');
            if (rv) {
                rv.textContent = (rating == null) ? '--' : rating;
                let cls = 'text-base font-black leading-none metric-value ';
                if (rating == null) cls += 'text-slate-500';
                else if (rating >= 80) cls += 'text-emerald-300';
                else if (rating >= 70) cls += 'text-emerald-400';
                else if (rating >= 50) cls += 'text-amber-300';
                else cls += 'text-rose-300';
                rv.className = cls;
            }
            if (rd) {
                if (rating == null) rd.textContent = 'RS unavailable';
                else rd.textContent = `${isPercentile ? 'true percentile' : 'approx — run screener'}`;
            }
            // Keep the chart-header Delta pill in sync — UNIFIED with Alpha/Beta: light it
            // whenever a recent Delta event exists (pass {days}), not only when firing today.
            // deltaRecency = { days } from the most recent Delta event, or null.
            const deltaObj = deltaRecency || ((signals && signals.deltaTrigger) ? { days: 0 } : null);
            window.__pillCtx = { ...(window.__pillCtx || {}), delta: deltaObj,
                benchmark: (typeof rsBenchmarkSymbol !== 'undefined') ? rsBenchmarkSymbol : 'SPY' };
            if (typeof renderAlertPills === 'function') renderAlertPills(window.__pillCtx);
        }

        // Render the Divergence Strength block as a diverging meter centred on zero:
        // fill grows RIGHT (green) when outperforming, LEFT (red) when underperforming.
        function renderRsDivergence(div) {
            const stateEl = document.getElementById('rsDivState');
            const spreadEl = document.getElementById('rsDivSpread');
            const trendEl = document.getElementById('rsDivTrend');
            const fill = document.getElementById('rsDivFill');
            const note = document.getElementById('rsDivNote');
            if (!stateEl || !spreadEl || !fill) return;
            if (!div) {
                stateEl.textContent = 'Awaiting data'; stateEl.className = 'text-[11px] font-bold text-slate-500';
                spreadEl.textContent = '--'; spreadEl.className = 'text-[11px] font-mono font-bold text-slate-500 normal-case tracking-normal';
                if (trendEl) trendEl.textContent = '--';
                fill.style.width = '0%'; fill.style.left = '50%'; fill.style.background = '#475569';
                if (note) note.textContent = `Needs RS line vs ${rsBenchmarkSymbol} (16+ bars).`;
                return;
            }
            const map = {
                'accelerating':       { txt: 'Accelerating ahead',  cls: 'text-emerald-300', col: '#10b981' },
                'leading-fading':     { txt: 'Leading, converging', cls: 'text-amber-300',   col: '#eab308' },
                'lagging-recovering': { txt: 'Lagging, recovering', cls: 'text-blue-300',    col: '#3b82f6' },
                'lagging-worse':      { txt: 'Falling behind',      cls: 'text-rose-300',    col: '#f43f5e' }
            };
            const m = map[div.state] || map['lagging-recovering'];
            stateEl.textContent = m.txt; stateEl.className = 'text-[11px] font-bold ' + m.cls;
            const sign = div.spreadPct >= 0 ? '+' : '';
            spreadEl.textContent = `${sign}${div.spreadPct.toFixed(2)}%`;
            spreadEl.className = 'text-[11px] font-mono font-bold normal-case tracking-normal ' + m.cls;
            // Diverging fill: half-width proportional to strength, anchored at the 50% zero line.
            const halfW = Math.max(2, div.strength / 2); // 0–50% of the track per side
            if (div.spreadPct >= 0) { fill.style.left = '50%'; fill.style.width = halfW + '%'; }
            else { fill.style.left = (50 - halfW) + '%'; fill.style.width = halfW + '%'; }
            fill.style.background = m.col;
            fill.style.borderRadius = '999px';
            if (trendEl) {
                trendEl.textContent = (div.widening ? '▲ widening ' : '▼ narrowing ') + `${div.slope >= 0 ? '+' : ''}${div.slope.toFixed(2)}%/5b`;
                trendEl.className = 'text-[9px] font-mono ' + m.cls;
            }
            if (note) {
                note.textContent = `Stock's RS line is separating from ${rsBenchmarkSymbol}. Divergence strength ${div.strength}/100.`;
            }
        }

        // Render the leadership chart: this stock vs the benchmark, both rebased to 100 at the
        // window start. The benchmark line is derived from the RS line (bench = stock / (rsLine/100)),
        // so no extra fetch is needed. The gap between the lines IS the relative strength.
        function renderLeadershipChart(results, rsLine) {
            const host = document.getElementById('rsLeadChart');
            if (!host) return;
            const bench = (typeof rsBenchmarkSymbol !== 'undefined') ? rsBenchmarkSymbol : 'SPY';
            const b1 = document.getElementById('rsLeadChartBench');
            if (b1) b1.textContent = bench; // legend label "trend" is static, not the benchmark symbol
            const placeholder = '<div class="h-full flex items-center justify-center text-[10px] text-slate-600 italic py-8">Awaiting RS data vs ' + bench + '</div>';
            if (!results || !rsLine || results.length < 5) { host.innerHTML = placeholder; return; }
            // RELATIVIZED view: plot the RS line itself (stock ÷ benchmark), rebased to 100 at
            // the window start, alongside its trend (moving average). Two lines in the SAME units,
            // so they stay logically close regardless of how big the absolute move is. Rising RS
            // line = gaining on the benchmark; RS line above its trend = leadership strengthening.
            const n = Math.min(results ? results.length : 0, rsLine ? rsLine.length : 0);
            const series = [];
            const startI = Math.max(0, n - Math.min(126, n));
            for (let i = startI; i < n; i++) { const r = rsLine[i]; if (Number.isFinite(r) && r > 0) series.push(r); }
            if (series.length < 8) { host.innerHTML = placeholder; return; }
            const baseRs = series[0];
            const rs = series.map(v => v / baseRs * 100);   // RS line rebased to 100 at window start
            const m = rs.length;
            // trend = moving average of the RS line (expanding window for the first p-1 points)
            const p = Math.max(5, Math.round(m / 6));
            const ma = rs.map((_, i) => { const a = Math.max(0, i - p + 1); let s = 0; for (let k = a; k <= i; k++) s += rs[k]; return s / (i - a + 1); });
            const W = 300, H = 130, padL = 4, padR = 46, padT = 8, padB = 10, w = W - padL - padR, h = H - padT - padB;
            const all = rs.concat(ma, [100]);
            const mn = Math.min(...all), mx = Math.max(...all), rg = (mx - mn) || 1;
            const X = i => padL + (i / (m - 1)) * w, Y = v => padT + h - ((v - mn) / rg) * h;
            const ln = a => a.map((v, i) => X(i).toFixed(1) + ',' + Y(v).toFixed(1)).join(' ');
            const rsEnd = rs[m - 1], maEnd = ma[m - 1];
            const lead = rsEnd >= 100;                 // beating the benchmark since window start
            const col = lead ? '#34d399' : '#fb7185';
            const maCol = '#64748b';
            // shade the gap between the RS line and its trend = momentum of relative strength
            const between = ln(rs) + ' ' + ma.map((v, i) => X(m - 1 - i).toFixed(1) + ',' + Y(ma[m - 1 - i]).toFixed(1)).join(' ');
            const baseY = Y(100), inBand = baseY > padT + 2 && baseY < padT + h - 2;
            const outperf = rsEnd - 100;
            const endLab = (outperf >= 0 ? '+' : '') + outperf.toFixed(0) + '%';
            host.innerHTML =
                '<svg viewBox="0 0 ' + W + ' ' + H + '" preserveAspectRatio="none" style="display:block;width:100%;height:100%">'
              + '<defs><linearGradient id="rsLeadGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="' + col + '" stop-opacity="0.18"/><stop offset="1" stop-color="' + col + '" stop-opacity="0.02"/></linearGradient></defs>'
              + (inBand ? '<line x1="' + padL + '" y1="' + baseY.toFixed(1) + '" x2="' + (W - padR) + '" y2="' + baseY.toFixed(1) + '" stroke="#334155" stroke-width="1" stroke-dasharray="3 3" opacity="0.7"/><text x="' + (W - padR + 3) + '" y="' + (baseY - 2).toFixed(1) + '" fill="#475569" font-size="7" font-weight="700">even</text>' : '')
              + '<polygon points="' + between + '" fill="url(#rsLeadGrad)"/>'
              + '<polyline fill="none" stroke="' + maCol + '" stroke-width="1.4" stroke-dasharray="4 3" points="' + ln(ma) + '"/>'
              + '<polyline fill="none" stroke="' + col + '" stroke-width="2" points="' + ln(rs) + '"/>'
              + '<circle cx="' + X(m - 1).toFixed(1) + '" cy="' + Y(rsEnd).toFixed(1) + '" r="2.8" fill="' + col + '"/>'
              + '<text x="' + (W - padR + 3) + '" y="' + (Y(rsEnd) + 3).toFixed(1) + '" fill="' + col + '" font-size="9" font-weight="800">' + endLab + '</text>'
              + '</svg>';
        }

        // Render the hero Relative Leadership gauge (arc + score + verdict + stealth flag).
        function renderLeadershipGauge(ld) {
            const scoreEl = document.getElementById('rsLeadScore');
            const labelEl = document.getElementById('rsLeadLabel');
            const verdictEl = document.getElementById('rsLeadVerdict');
            const subEl = document.getElementById('rsLeadSub');
            const flag = document.getElementById('rsStealthFlag');
            const flagDet = document.getElementById('rsStealthDetail');
            if (!ld) {
                if (scoreEl) { scoreEl.textContent = '--'; scoreEl.style.color = ''; }
                if (labelEl) { labelEl.textContent = '--'; labelEl.style.color = ''; }
                if (verdictEl) verdictEl.textContent = 'Load a ticker to score its leadership vs the benchmark.';
                if (subEl) subEl.textContent = 'Combines RS Rating, RS-line new highs, and short-term divergence into one 0–100 score.';
                if (flag) flag.classList.add('hidden');
                return;
            }
            if (scoreEl) { scoreEl.textContent = ld.score; scoreEl.style.color = ld.color; }
            if (labelEl) { labelEl.textContent = ld.label; labelEl.style.color = ld.color; }
            if (verdictEl) {
                const benchTxt = rsBenchmarkSymbol;
                if (ld.score >= 80) verdictEl.innerHTML = `Clear leadership vs <span class="font-mono">${benchTxt}</span> — top-tier relative strength.`;
                else if (ld.score >= 65) verdictEl.innerHTML = `Outperforming <span class="font-mono">${benchTxt}</span> — stronger than the average stock.`;
                else if (ld.score >= 50) verdictEl.innerHTML = `Tracking <span class="font-mono">${benchTxt}</span> — roughly in line with the market.`;
                else if (ld.score >= 35) verdictEl.innerHTML = `Lagging <span class="font-mono">${benchTxt}</span> — weaker than the average stock.`;
                else verdictEl.innerHTML = `Underperforming <span class="font-mono">${benchTxt}</span> — relative weakness.`;
            }
            if (subEl) subEl.textContent = `RS Rating + RS-line new highs + 3/10-EMA divergence, weighted for the current ${rsBenchmarkSymbol} regime.`;
            if (flag) {
                if (ld.stealth) { flag.classList.remove('hidden'); if (flagDet) flagDet.textContent = ld.stealthDetail; }
                else flag.classList.add('hidden');
            }
        }

        // Leadership Convergence — the cross-aware synthesis for the RS panel. It crosses
        // THIS panel's RS leadership (Delta / RS-line proximity) with the group rotation,
        // Stage, multi-timeframe RSI alignment and volume confluence we already compute
        // elsewhere, so the panel speaks to all of them in ONE concise read plus a quadrant
        // plot (RS leading price ↑ × group flow →). Snapshot only — no history needed.
        function renderRsConvergence(ctx) {
            const host = document.getElementById('rsConvChart');
            const zoneEl = document.getElementById('rsConvZone');
            const verdictEl = document.getElementById('rsConvVerdict');
            const detailEl = document.getElementById('rsConvDetail');
            const histEl = document.getElementById('rsConvHist');
            if (!host || !verdictEl) return;
            const reset = () => {
                host.innerHTML = '';
                if (histEl) histEl.textContent = '';
                if (zoneEl) { zoneEl.textContent = '--'; zoneEl.className = 'text-[9px] normal-case tracking-normal text-slate-500 font-medium'; }
                verdictEl.textContent = 'Load a ticker to cross its leadership with group flow.';
                verdictEl.className = 'text-[11px] font-bold text-slate-400 leading-snug';
                if (detailEl) detailEl.textContent = '';
            };
            if (!ctx || !ctx.rsSignals) { reset(); return; }
            const sig = ctx.rsSignals;
            const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

            // Group rotation for this stock — passed in from the caller (computed for the
            // correct dimension so it doesn't depend on whatever the Group RS tab last ranked).
            const grpName = currentTickerClassification && currentTickerClassification.industryGroup;
            const secName = currentTickerClassification && currentTickerClassification.sector;
            const grp = ctx.grpRot || ((grpName && Array.isArray(groupRankingData)) ? groupRankingData.find(g => g.name === grpName) : null);
            const sec = ctx.secRot || ((secName && Array.isArray(groupRankingData)) ? groupRankingData.find(g => g.name === secName) : null);
            const rotScore = grp && Number.isFinite(grp.rotationScore) ? grp.rotationScore : null;
            const rotTag = grp ? grp.rotationTag : null;
            const grvol = grp && Number.isFinite(grp.grvol) ? grp.grvol : (sec && Number.isFinite(sec.grvol) ? sec.grvol : null);
            const hasFlow = rotScore != null;

            // Axes. X = group flow (rotationScore, neutral=50). Y = RS leading price.
            const xv = hasFlow ? (50 + clamp(rotScore, -40, 40) / 40 * 50) : 50;
            const prox = Number.isFinite(sig.rsProximity) ? sig.rsProximity : null;
            const yv = sig.deltaTrigger ? 100 : (sig.rsNewHigh ? 95 : (prox != null ? clamp(prox, 0, 100) : 0));
            const leading = yv >= 70, inFlow = xv >= 56, outFlow = xv <= 44;

            let zone, zColor, zLabel, zChip;
            if (leading && inFlow)       { zone = 'tailwind'; zColor = '#34d399'; zLabel = 'Tailwind';     zChip = 'text-emerald-300'; }
            else if (leading && outFlow) { zone = 'lone';     zColor = '#fbbf24'; zLabel = 'Lone soldier'; zChip = 'text-amber-300'; }
            else if (!leading && inFlow) { zone = 'sympathy'; zColor = '#38bdf8'; zLabel = 'Sympathy';     zChip = 'text-sky-300'; }
            else                         { zone = 'neutral';  zColor = '#94a3b8'; zLabel = 'Neutral';      zChip = 'text-slate-400'; }
            const dotColor = hasFlow ? zColor : '#64748b';

            // ---- Per-ticker daily history (one point per session) feeds the trend lines.
            // RS-lead and group-flow are both stored normalized 0–100 (neutral 50). The
            // line graph fills as the ticker is loaded across sessions; group-flow points
            // are null until a market scan has run that day, so that line stays honest.
            const tkr = (typeof state.currentTicker !== 'undefined' && state.currentTicker) ? state.currentTicker : null;
            let series = [];
            if (tkr) {
                const HKEY = 'abConvHistV1:' + tkr;
                let hist = [];
                try { hist = JSON.parse(localStorage.getItem(HKEY) || '[]'); if (!Array.isArray(hist)) hist = []; } catch (e) { hist = []; }
                const byDate = new Map(hist.map(p => [p.d, p]));
                // Backfill RS-lead from the RS-line trailing-high proximity (full immediately),
                // so the chart isn't an empty dot on first load. Group flow still builds over sessions.
                const results = ctx.results, rsBench = ctx.rsBench;
                if (Array.isArray(results) && results.length && rsBench && typeof computeRsLineSeries === 'function') {
                    const rl = computeRsLineSeries(results, rsBench);
                    if (rl) {
                        const total = results.length; let mr = -Infinity; const runRs = new Array(total);
                        for (let i = 0; i < total; i++) { if (Number.isFinite(rl[i])) mr = Math.max(mr, rl[i]); runRs[i] = mr; }
                        for (let k = 9; k >= 0; k--) {
                            const idx = total - 1 - k; if (idx < 0) continue;
                            const d = new Date(results[idx].t).toISOString().slice(0, 10);
                            let e = byDate.get(d); if (!e) { e = { d: d, lead: null, flow: null }; byDate.set(d, e); }
                            if (Number.isFinite(rl[idx]) && runRs[idx] > 0) e.lead = Math.round(Math.min(100, rl[idx] / runRs[idx] * 100));
                        }
                    }
                }
                // Today's live point: lead = current signal read (Delta/RS-NH/proximity); flow if known.
                const latest = (Array.isArray(results) && results.length) ? new Date(results[results.length - 1].t).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10);
                let te = byDate.get(latest); if (!te) { te = { d: latest, lead: null, flow: null }; byDate.set(latest, te); }
                te.lead = Math.round(yv);
                if (hasFlow) te.flow = Math.round(xv);
                let merged = [...byDate.values()].sort((a, b) => a.d < b.d ? -1 : a.d > b.d ? 1 : 0);
                if (merged.length > 30) merged = merged.slice(-30);
                try { localStorage.setItem(HKEY, JSON.stringify(merged)); } catch (e) {}
                series = merged.slice(-10);
            } else {
                series = [{ d: '', lead: Math.round(yv), flow: hasFlow ? Math.round(xv) : null }];
            }

            // ---- Two-line spread-band chart (RS lead × group flow over sessions) ----
            const W = 300, H = 110, pad = 10, n = series.length;
            const cx = i => pad + (n <= 1 ? (W - 2 * pad) / 2 : i * ((W - 2 * pad) / (n - 1)));
            const cy = v => (H - pad) - (v / 100) * (H - 2 * pad);
            const flowPts = series.map((p, i) => ({ i, v: p.flow })).filter(p => p.v != null);
            const fullFlow = flowPts.length === n && n >= 2;
            const poly = (vals) => vals.map((v, i) => `${cx(i).toFixed(1)},${cy(v).toFixed(1)}`).join(' ');
            const leadVals = series.map(p => p.lead);
            let svg = `<svg viewBox="0 0 ${W} ${H}" width="100%" height="${H}" preserveAspectRatio="none" aria-hidden="true">`;
            // Band between the two lines (only when group-flow is complete), tinted by flow vs neutral.
            if (fullFlow) {
                const flowVals = series.map(p => p.flow);
                const lastFlow = flowVals[flowVals.length - 1];
                const fill = lastFlow >= 50 ? 'rgba(52,211,153,0.13)' : 'rgba(251,113,133,0.13)';
                let d = `M${cx(0).toFixed(1)} ${cy(leadVals[0]).toFixed(1)}`;
                for (let i = 1; i < n; i++) d += ` L${cx(i).toFixed(1)} ${cy(leadVals[i]).toFixed(1)}`;
                for (let i = n - 1; i >= 0; i--) d += ` L${cx(i).toFixed(1)} ${cy(flowVals[i]).toFixed(1)}`;
                d += ' Z';
                svg += `<path d="${d}" fill="${fill}" stroke="none"/>`;
            }
            // Neutral baseline.
            svg += `<line x1="${pad}" y1="${cy(50).toFixed(1)}" x2="${W - pad}" y2="${cy(50).toFixed(1)}" stroke="#334155" stroke-width="1" stroke-dasharray="3 3"/>`;
            if (n >= 2) {
                svg += `<polyline points="${poly(leadVals)}" fill="none" stroke="#e879f9" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>`;
                if (flowPts.length >= 2) {
                    const fp = flowPts.map(p => `${cx(p.i).toFixed(1)},${cy(p.v).toFixed(1)}`).join(' ');
                    svg += `<polyline points="${fp}" fill="none" stroke="#5dcaa5" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>`;
                }
            }
            // End dots (always show the current reading).
            svg += `<circle cx="${cx(n - 1).toFixed(1)}" cy="${cy(leadVals[n - 1]).toFixed(1)}" r="3" fill="#e879f9"/>`;
            if (series[n - 1].flow != null) svg += `<circle cx="${cx(n - 1).toFixed(1)}" cy="${cy(series[n - 1].flow).toFixed(1)}" r="3" fill="#5dcaa5"/>`;
            svg += `</svg>`;
            host.innerHTML = svg;
            if (histEl) histEl.textContent = n < 3 ? `building history · ${n}/10 sessions` : `${n}/10 sessions`;

            if (zoneEl) { zoneEl.textContent = zLabel; zoneEl.className = 'text-[9px] normal-case tracking-normal font-medium ' + zChip; }

            // ---- Concise cross-aware verdict (one sentence + a Stage qualifier) ----
            let verdict;
            if (!hasFlow) verdict = 'Run the market scan to read this stock\u2019s group rotation.';
            else if (zone === 'tailwind') verdict = 'Leading into a group that\u2019s rotating in — accumulation with a tailwind.';
            else if (zone === 'lone') verdict = 'Leading while its group is sold — a lone soldier, prone to revert toward the group.';
            else if (zone === 'sympathy') verdict = 'Riding group inflow without its own RS lead — a sympathy move, not a true leader.';
            else verdict = 'No strong leadership-vs-rotation cross right now.';
            const st = ctx.stage;
            if (hasFlow && leading && Number.isFinite(st)) {
                if (st === 2) verdict += ' Stage 2 backs it.';
                else if (st === 3) verdict += ' But Stage 3 — watch for a climax.';
                else if (st === 1) verdict += ' Still early (Stage 1 base).';
                else if (st === 4) verdict += ' Stage 4 downtrend — caution.';
            }
            verdictEl.textContent = verdict;
            verdictEl.className = 'text-[11px] font-bold leading-snug ' + (hasFlow ? zChip : 'text-slate-400');

            // ---- Compact fact strip that folds in the other signals ----
            const r = ctx.rsi || {};
            const rv = [r.d, r.w, r.m].filter(v => Number.isFinite(v));
            const rsiTxt = rv.length === 3 ? (rv.every(v => v >= 55) ? 'aligned\u2191' : rv.every(v => v <= 45) ? 'weak\u2193' : 'mixed') : '--';
            const lr = (engineState.lastMarketStructure && Number.isFinite(engineState.lastMarketStructure.leaderRvol)) ? engineState.lastMarketStructure.leaderRvol : null;
            const vLayers = [
                Number.isFinite(ctx.rvol) ? ctx.rvol >= 1.5 : null,
                grvol != null ? grvol >= 1.3 : null,
                lr != null ? lr >= 1.2 : null
            ];
            const vPresent = vLayers.filter(v => v !== null).length;
            const vLit = vLayers.filter(v => v === true).length;
            const parts = [];
            if (Number.isFinite(ctx.rsRating)) parts.push('RS ' + ctx.rsRating);
            if (prox != null) parts.push('prox ' + Math.round(prox) + '%');
            if (rotTag) parts.push('group ' + rotTag + (rotScore != null ? ' ' + (rotScore >= 0 ? '+' : '') + rotScore : '') + (grvol != null ? ' · GRVOL ' + grvol.toFixed(1) + '\u00d7' : ''));
            if (Number.isFinite(st)) parts.push('Stage ' + st);
            parts.push('RSI ' + rsiTxt);
            if (vPresent) parts.push('Vol ' + vLit + '/' + vPresent);
            if (detailEl) detailEl.textContent = parts.join('  ·  ');
        }

        // #3 — RSI × Stage. Three multi-timeframe RSI lines (1D/1W/1M) backfilled from the
        // stock's own bars (full immediately, no session store), plotted on a backdrop tinted
        // by the Weinstein Stage so the SAME aligned reading is framed as continuation (S2),
        // climax risk (S3) or an early thrust (S1). 50 = bull/bear divide, dashed 70 = overbought.
        // Risk-on / risk-off regime gauge — the 6th cross-convergence cell. Reads the
        // composite regimeR (and its sub-dimensions) off engineState.lastMarketStructure and draws a
        // small SVG needle. Market-level, so it updates whenever market structure recomputes.
        function renderRegimeGauge() {
            const host = document.getElementById('regimeGaugeChart');
            if (!host) return;
            const zoneEl = document.getElementById('regimeGaugeZone');
            const verdEl = document.getElementById('regimeGaugeVerdict');
            const ms = (typeof engineState.lastMarketStructure !== 'undefined') ? engineState.lastMarketStructure : null;
            if (!ms || ms.regimeR == null) {
                host.innerHTML = '<div style="height:110px;display:flex;align-items:center;justify-content:center;color:#475569;font-size:11px">Run a scan to read market regime.</div>';
                if (zoneEl) zoneEl.textContent = '--';
                if (verdEl) verdEl.textContent = 'Run a scan to read the risk-on / risk-off regime.';
                return;
            }
            const R = Math.max(-1, Math.min(1, ms.regimeR));
            const col = R > 0.35 ? '#10b981' : R > 0.10 ? '#4ade80' : R > -0.10 ? '#eab308' : R > -0.35 ? '#f97316' : '#ef4444';
            const cx = 110, cy = 88, rN = 56;
            const th = (90 * (1 - R)) * Math.PI / 180;          // R=-1→180°, 0→90°, +1→0°
            const nx = (cx + rN * Math.cos(th)).toFixed(1), ny = (cy - rN * Math.sin(th)).toFixed(1);
            const arc = (x1, y1, x2, y2, c) => `<path d="M${x1} ${y1} A70 70 0 0 1 ${x2} ${y2}" fill="none" stroke="${c}" stroke-width="11"/>`;
            const dims = ms.regimeDims || {};
            const chip = (lbl, v) => {
                if (v == null) return `<span style="color:#475569">${lbl} –</span>`;
                const c = v > 0.1 ? '#34d399' : v < -0.1 ? '#f87171' : '#94a3b8';
                return `<span style="color:#64748b">${lbl}</span> <span style="color:${c};font-weight:700">${v >= 0 ? '+' : ''}${v.toFixed(2)}</span>`;
            };
            host.innerHTML = `<svg viewBox="0 0 220 128" style="width:100%;height:110px">`
                + arc(40, 88, 53.4, 46.9, '#ef4444')
                + arc(53.4, 46.9, 88.4, 21.4, '#f97316')
                + arc(88.4, 21.4, 131.6, 21.4, '#eab308')
                + arc(131.6, 21.4, 166.6, 46.9, '#4ade80')
                + arc(166.6, 46.9, 180, 88, '#10b981')
                + `<line x1="${cx}" y1="${cy}" x2="${nx}" y2="${ny}" stroke="${col}" stroke-width="2.5" stroke-linecap="round"/>`
                + `<circle cx="${cx}" cy="${cy}" r="9" fill="none" stroke="${col}" stroke-opacity="0.25" stroke-width="2"/>`
                + `<circle cx="${cx}" cy="${cy}" r="4.5" fill="${col}"/>`
                + `<text x="40" y="102" text-anchor="middle" font-size="8" fill="#475569">risk-off</text>`
                + `<text x="180" y="102" text-anchor="middle" font-size="8" fill="#475569">risk-on</text>`
                + `<text x="${cx}" y="122" text-anchor="middle" font-size="22" font-weight="700" fill="${col}">${R >= 0 ? '+' : ''}${R.toFixed(2)}</text>`
                + `</svg>`;
            if (zoneEl) zoneEl.textContent = ms.verdict || '--';
            if (verdEl) verdEl.innerHTML = `${chip('Breadth', dims.breadth)} · ${chip('Lead', dims.leadership)} · ${chip('Thrust', dims.thrust)} · ${chip('Trend', dims.trend)}`;
        }

        function renderRsiStageConvergence(results, closes, stage) {
            const host = document.getElementById('rsiStageChart');
            const zoneEl = document.getElementById('rsiStageZone');
            const verdictEl = document.getElementById('rsiStageVerdict');
            if (!host || !verdictEl) return;
            const fail = () => {
                host.innerHTML = '';
                if (zoneEl) { zoneEl.textContent = '--'; zoneEl.className = 'text-[9px] normal-case tracking-normal text-slate-500 font-medium'; }
                verdictEl.textContent = 'Load a ticker to read RSI alignment against its Stage.';
                verdictEl.className = 'text-[11px] font-bold text-slate-400 leading-snug';
            };
            if (!Array.isArray(results) || results.length < 30 || !Array.isArray(closes) || closes.length < 30) { fail(); return; }

            const N = 10, total = results.length;
            const rsiAt = (tf) => {
                const out = [];
                for (let k = N - 1; k >= 0; k--) {
                    const end = total - k; // exclusive slice end
                    let cl;
                    try { cl = (tf === '1D') ? closes.slice(0, end) : MathLib.aggregateCloses(results.slice(0, end), tf); }
                    catch (e) { cl = null; }
                    const v = (cl && cl.length >= 15) ? MathLib.rsi(cl) : null;
                    out.push(Number.isFinite(v) ? v : null);
                }
                return out;
            };
            const s1d = rsiAt('1D'), s1w = rsiAt('1W'), s1m = rsiAt('1M');

            // Current alignment from the latest reading of each frame.
            const cur = [s1d[N - 1], s1w[N - 1], s1m[N - 1]].filter(v => Number.isFinite(v));
            const aligned = cur.length >= 2 && cur.every(v => v >= 55);
            const weak = cur.length >= 2 && cur.every(v => v <= 45);
            const overbought = Number.isFinite(s1d[N - 1]) && s1d[N - 1] >= 70 && Number.isFinite(s1w[N - 1]) && s1w[N - 1] >= 65;

            // Stage backdrop.
            const st = Number.isFinite(stage) ? stage : null;
            const tint = st === 2 ? 'rgba(52,211,153,0.07)' : st === 3 ? 'rgba(251,113,133,0.07)'
                : st === 1 ? 'rgba(56,189,248,0.06)' : st === 4 ? 'rgba(244,63,94,0.08)' : 'transparent';

            // ---- Chart ----
            const W = 300, H = 110, pad = 10, vmin = 30, vmax = 90;
            const cx = i => pad + i * ((W - 2 * pad) / (N - 1));
            const cy = v => (H - pad) - ((Math.max(vmin, Math.min(vmax, v)) - vmin) / (vmax - vmin)) * (H - 2 * pad);
            const lineOf = (arr, col) => {
                const pts = arr.map((v, i) => ({ i, v })).filter(p => Number.isFinite(p.v));
                if (pts.length < 2) return '';
                const d = pts.map(p => `${cx(p.i).toFixed(1)},${cy(p.v).toFixed(1)}`).join(' ');
                const last = pts[pts.length - 1];
                return `<polyline points="${d}" fill="none" stroke="${col}" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>`
                    + `<circle cx="${cx(last.i).toFixed(1)}" cy="${cy(last.v).toFixed(1)}" r="3" fill="${col}"/>`;
            };
            let svg = `<svg viewBox="0 0 ${W} ${H}" width="100%" height="${H}" preserveAspectRatio="none" aria-hidden="true">`;
            if (tint !== 'transparent') svg += `<rect x="${pad}" y="${pad}" width="${W - 2 * pad}" height="${H - 2 * pad}" rx="4" fill="${tint}"/>`;
            svg += `<line x1="${pad}" y1="${cy(50).toFixed(1)}" x2="${W - pad}" y2="${cy(50).toFixed(1)}" stroke="#475569" stroke-width="1"/>`;
            svg += `<line x1="${pad}" y1="${cy(70).toFixed(1)}" x2="${W - pad}" y2="${cy(70).toFixed(1)}" stroke="#fb7185" stroke-width="1" stroke-dasharray="3 3"/>`;
            svg += lineOf(s1d, '#e2e8f0') + lineOf(s1w, '#5dcaa5') + lineOf(s1m, '#a78bfa');
            svg += `</svg>`;
            host.innerHTML = svg;

            // Zone chip.
            let zLabel, zChip;
            if (aligned && st === 2) { zLabel = 'Aligned · S2'; zChip = 'text-emerald-300'; }
            else if (aligned && st === 3) { zLabel = 'Aligned · S3'; zChip = 'text-rose-300'; }
            else if (aligned && st === 1) { zLabel = 'Aligned · S1'; zChip = 'text-sky-300'; }
            else if (aligned) { zLabel = 'Aligned'; zChip = 'text-emerald-300'; }
            else if (weak) { zLabel = 'Weak'; zChip = 'text-rose-300'; }
            else { zLabel = 'Mixed'; zChip = 'text-slate-400'; }
            if (zoneEl) { zoneEl.textContent = zLabel; zoneEl.className = 'text-[9px] normal-case tracking-normal font-medium ' + zChip; }

            // Verdict crossing alignment × stage.
            let verdict;
            if (aligned) {
                if (st === 2) verdict = 'Momentum aligned across D·W·M in a Stage 2 advance — continuation.';
                else if (st === 3) verdict = 'Aligned momentum, but Stage 3 — strength here risks a climax.';
                else if (st === 1) verdict = 'Aligning early in a Stage 1 base — an emerging thrust.';
                else if (st === 4) verdict = 'Aligned but Stage 4 downtrend — likely a bounce, not a trend.';
                else verdict = 'Momentum aligned across D·W·M — sustained multi-timeframe trend.';
                if (overbought) verdict += ' Stretched (>70) — extended.';
            } else if (weak) verdict = 'Momentum weak across timeframes — no bullish alignment.';
            else verdict = 'Mixed across timeframes — a choppy or transitioning trend.';
            verdictEl.textContent = verdict;
            verdictEl.className = 'text-[11px] font-bold leading-snug ' + zChip;
        }

        // #4 — RS New High × Base Position. Backfilled from the stock's own RS line and price:
        // teal = RS line vs its trailing high, white = price vs its trailing high, dashed = the
        // prior-high / breakout pivot. RS at a new high while price still sits under the pivot =
        // pre-breakout leadership (early, low-risk, green); price already at/through the pivot when
        // RS confirms = late/extended (amber). No session store — full lines immediately.
        function renderRsBaseConvergence(results, closes, highs, rsBench) {
            const host = document.getElementById('rsBaseChart');
            const zoneEl = document.getElementById('rsBaseZone');
            const verdictEl = document.getElementById('rsBaseVerdict');
            if (!host || !verdictEl) return;
            const fail = (msg) => {
                host.innerHTML = '';
                if (zoneEl) { zoneEl.textContent = '--'; zoneEl.className = 'text-[9px] normal-case tracking-normal text-slate-500 font-medium'; }
                verdictEl.textContent = msg || 'Load a ticker to read RS leadership vs its base.';
                verdictEl.className = 'text-[11px] font-bold text-slate-400 leading-snug';
            };
            if (!Array.isArray(results) || results.length < 30 || !Array.isArray(closes) || !Array.isArray(highs)) { fail(); return; }
            const rsLine = (typeof computeRsLineSeries === 'function' && rsBench) ? computeRsLineSeries(results, rsBench) : null;
            if (!rsLine) { fail('Set a benchmark to read RS leadership vs its base.'); return; }

            const total = results.length, N = 10;
            // Running trailing maxima (price highs and RS line).
            const runHigh = new Array(total), runRs = new Array(total);
            let mh = -Infinity, mr = -Infinity;
            for (let i = 0; i < total; i++) {
                if (Number.isFinite(highs[i])) mh = Math.max(mh, highs[i]);
                runHigh[i] = mh;
                if (Number.isFinite(rsLine[i])) mr = Math.max(mr, rsLine[i]);
                runRs[i] = mr;
            }
            const px = [], rs = [];
            for (let k = N - 1; k >= 0; k--) {
                const idx = total - 1 - k;
                px.push((Number.isFinite(closes[idx]) && runHigh[idx] > 0) ? closes[idx] / runHigh[idx] * 100 : null);
                rs.push((Number.isFinite(rsLine[idx]) && runRs[idx] > 0) ? rsLine[idx] / runRs[idx] * 100 : null);
            }
            if (rs.filter(v => Number.isFinite(v)).length < 2) { fail('Not enough benchmark overlap to plot RS vs base.'); return; }

            const rsLast = rs[N - 1], pxLast = px[N - 1];
            const preBreakout = Number.isFinite(rsLast) && rsLast >= 98 && Number.isFinite(pxLast) && pxLast <= 96;
            const extended = Number.isFinite(pxLast) && pxLast >= 98;

            // ---- Chart ----
            const W = 300, H = 110, pad = 10, vmin = 50, vmax = 103, pivot = 100;
            const cx = i => pad + i * ((W - 2 * pad) / (N - 1));
            const cy = v => (H - pad) - ((Math.max(vmin, Math.min(vmax, v)) - vmin) / (vmax - vmin)) * (H - 2 * pad);
            const fullBoth = rs.every(v => Number.isFinite(v)) && px.every(v => Number.isFinite(v));
            const lineOf = (arr, col) => {
                const pts = arr.map((v, i) => ({ i, v })).filter(p => Number.isFinite(p.v));
                if (pts.length < 2) return '';
                const d = pts.map(p => `${cx(p.i).toFixed(1)},${cy(p.v).toFixed(1)}`).join(' ');
                const last = pts[pts.length - 1];
                return `<polyline points="${d}" fill="none" stroke="${col}" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>`
                    + `<circle cx="${cx(last.i).toFixed(1)}" cy="${cy(last.v).toFixed(1)}" r="3" fill="${col}"/>`;
            };
            let svg = `<svg viewBox="0 0 ${W} ${H}" width="100%" height="${H}" preserveAspectRatio="none" aria-hidden="true">`;
            if (fullBoth) {
                const fill = preBreakout ? 'rgba(52,211,153,0.13)' : extended ? 'rgba(251,191,36,0.13)' : 'rgba(100,116,139,0.07)';
                let d = `M${cx(0).toFixed(1)} ${cy(rs[0]).toFixed(1)}`;
                for (let i = 1; i < N; i++) d += ` L${cx(i).toFixed(1)} ${cy(rs[i]).toFixed(1)}`;
                for (let i = N - 1; i >= 0; i--) d += ` L${cx(i).toFixed(1)} ${cy(px[i]).toFixed(1)}`;
                d += ' Z';
                svg += `<path d="${d}" fill="${fill}" stroke="none"/>`;
            }
            svg += `<line x1="${pad}" y1="${cy(pivot).toFixed(1)}" x2="${W - pad}" y2="${cy(pivot).toFixed(1)}" stroke="#475569" stroke-width="1" stroke-dasharray="4 3"/>`;
            svg += `<text x="${W - pad - 1}" y="${(cy(pivot) - 2).toFixed(1)}" fill="#64748b" font-size="8" text-anchor="end">pivot</text>`;
            svg += lineOf(rs, '#5dcaa5') + lineOf(px, '#e2e8f0');
            svg += `</svg>`;
            host.innerHTML = svg;

            // Zone + verdict.
            let zLabel, zChip, verdict;
            if (preBreakout) { zLabel = 'Pre-breakout'; zChip = 'text-emerald-300'; verdict = 'RS at a new high while price still coils under its pivot — pre-breakout leadership (early, low-risk).'; }
            else if (extended) { zLabel = 'Extended'; zChip = 'text-amber-300'; verdict = 'Price is already at/through its pivot as RS confirms — the good entry has passed (late).'; }
            else { zLabel = 'Tracking'; zChip = 'text-slate-400'; verdict = 'RS and price tracking together — no clear leadership lead right now.'; }
            if (zoneEl) { zoneEl.textContent = zLabel; zoneEl.className = 'text-[9px] normal-case tracking-normal font-medium ' + zChip; }
            verdictEl.textContent = verdict;
            verdictEl.className = 'text-[11px] font-bold leading-snug ' + zChip;
        }

        // #5 — VARS × Group Rotation. VARS (vol-adjusted RS vs SPY) backfilled full from the
        // stock-vs-SPY history; group flow builds over sessions in its own dated store. Both
        // above neutral = durable leader; VARS up while group flow drops below = lone soldier.
        function renderVarsGroupConvergence(results, spy, grpRot) {
            const host = document.getElementById('rsVarsChart');
            const zoneEl = document.getElementById('rsVarsZone');
            const verdictEl = document.getElementById('rsVarsVerdict');
            const histEl = document.getElementById('rsVarsHist');
            if (!host || !verdictEl) return;
            const fail = (msg) => {
                host.innerHTML = ''; if (histEl) histEl.textContent = '';
                if (zoneEl) { zoneEl.textContent = '--'; zoneEl.className = 'text-[9px] normal-case tracking-normal text-slate-500 font-medium'; }
                verdictEl.textContent = msg || 'Load index data (SPY) to read VARS vs group flow.';
                verdictEl.className = 'text-[11px] font-bold text-slate-400 leading-snug';
            };
            if (!Array.isArray(results) || results.length < 30) { fail(); return; }
            const tkr = (typeof state.currentTicker !== 'undefined' && state.currentTicker) ? state.currentTicker : null;
            if (!tkr) { fail(); return; }
            const clamp = (v, a, b) => Math.max(a, Math.min(b, v));
            const vSeries = (typeof computeVarsSeries === 'function' && spy) ? computeVarsSeries(results, spy) : null;
            const favVars = v => 50 + clamp(v, -3, 3) / 3 * 50;

            const grpName = currentTickerClassification && currentTickerClassification.industryGroup;
            const grp = grpRot || ((grpName && Array.isArray(groupRankingData)) ? groupRankingData.find(g => g.name === grpName) : null);
            const rotScore = grp && Number.isFinite(grp.rotationScore) ? grp.rotationScore : null;
            const flowFav = rotScore != null ? (50 + clamp(rotScore, -40, 40) / 40 * 50) : null;

            const HKEY = 'abVarsHistV1:' + tkr;
            let hist = [];
            try { hist = JSON.parse(localStorage.getItem(HKEY) || '[]'); if (!Array.isArray(hist)) hist = []; } catch (e) { hist = []; }
            const byDate = new Map(hist.map(p => [p.d, p]));
            const N = 10, total = results.length;
            for (let k = N - 1; k >= 0; k--) {
                const idx = total - 1 - k;
                const d = new Date(results[idx].t).toISOString().slice(0, 10);
                let e = byDate.get(d); if (!e) { e = { d: d, vars: null, flow: null }; byDate.set(d, e); }
                const v = vSeries ? vSeries[idx] : null;
                if (Number.isFinite(v)) e.vars = Math.round(favVars(v));
            }
            const latestDate = new Date(results[total - 1].t).toISOString().slice(0, 10);
            { let e = byDate.get(latestDate); if (!e) { e = { d: latestDate, vars: null, flow: null }; byDate.set(latestDate, e); } if (flowFav != null) e.flow = Math.round(flowFav); }
            let merged = [...byDate.values()].sort((a, b) => a.d < b.d ? -1 : a.d > b.d ? 1 : 0);
            if (merged.length > 30) merged = merged.slice(-30);
            try { localStorage.setItem(HKEY, JSON.stringify(merged)); } catch (e) {}
            const series = merged.slice(-10), n = series.length;

            const W = 300, H = 110, pad = 10;
            const cx = i => pad + (n <= 1 ? (W - 2 * pad) / 2 : i * ((W - 2 * pad) / (n - 1)));
            const cy = v => (H - pad) - (clamp(v, 0, 100) / 100) * (H - 2 * pad);
            const lineOf = (key, col) => {
                const pts = series.map((p, i) => ({ i, v: p[key] })).filter(p => Number.isFinite(p.v));
                if (!pts.length) return '';
                let s = '';
                if (pts.length >= 2) s += `<polyline points="${pts.map(p => `${cx(p.i).toFixed(1)},${cy(p.v).toFixed(1)}`).join(' ')}" fill="none" stroke="${col}" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>`;
                const last = pts[pts.length - 1];
                return s + `<circle cx="${cx(last.i).toFixed(1)}" cy="${cy(last.v).toFixed(1)}" r="3" fill="${col}"/>`;
            };
            let svg = `<svg viewBox="0 0 ${W} ${H}" width="100%" height="${H}" preserveAspectRatio="none" aria-hidden="true">`;
            svg += `<line x1="${pad}" y1="${cy(50).toFixed(1)}" x2="${W - pad}" y2="${cy(50).toFixed(1)}" stroke="#334155" stroke-width="1" stroke-dasharray="3 3"/>`;
            svg += lineOf('vars', '#fbbf24') + lineOf('flow', '#5dcaa5');
            svg += `</svg>`;
            host.innerHTML = svg;
            if (histEl) histEl.textContent = n < 3 ? `building · ${n}/10` : `${n}/10 sessions`;

            const lastVars = series[n - 1] ? series[n - 1].vars : null;
            let lastFlow = null; for (let i = n - 1; i >= 0; i--) { if (Number.isFinite(series[i].flow)) { lastFlow = series[i].flow; break; } }
            const leading = Number.isFinite(lastVars) && lastVars >= 55;
            const weakVars = Number.isFinite(lastVars) && lastVars <= 45;
            const inFlow = Number.isFinite(lastFlow) && lastFlow >= 56, outFlow = Number.isFinite(lastFlow) && lastFlow <= 44;
            let zLabel, zChip, verdict;
            if (!Number.isFinite(lastVars)) { zLabel = 'No VARS'; zChip = 'text-slate-400'; verdict = 'Load index data (SPY) to read VARS.'; }
            else if (leading && inFlow) { zLabel = 'Leader'; zChip = 'text-emerald-300'; verdict = 'VARS leading with the group rotating in — durable leadership with support.'; }
            else if (leading && outFlow) { zLabel = 'Lone soldier'; zChip = 'text-amber-300'; verdict = 'VARS leading but the group is sold — a lone soldier, prone to revert toward the group.'; }
            else if (!leading && inFlow) { zLabel = 'Sympathy'; zChip = 'text-sky-300'; verdict = 'Group doing the lifting while VARS lags — a sympathy passenger, not a true leader.'; }
            else if (weakVars) { zLabel = 'Lagging'; zChip = 'text-rose-300'; verdict = 'VARS below neutral — lagging the market on a vol-adjusted basis.'; }
            else { zLabel = 'Neutral'; zChip = 'text-slate-400'; verdict = 'No strong VARS / rotation cross right now.'; }
            if (zoneEl) { zoneEl.textContent = zLabel; zoneEl.className = 'text-[9px] normal-case tracking-normal font-medium ' + zChip; }
            verdictEl.textContent = verdict;
            verdictEl.className = 'text-[11px] font-bold leading-snug ' + zChip;
        }

        // #1 — Volume Confluence (spread band). Stock RVOL backfilled full from volume history;
        // sector GRVOL + market leader-RVOL build over sessions in a dated store. Three × reads
        // converging high = conviction; a lone stock spike = hollow. Band tinted by spread trend.
        function renderVolumeConfluenceChart(results, volumes, secRot) {
            const host = document.getElementById('volConfChart');
            const zoneEl = document.getElementById('volConfZone');
            const verdictEl = document.getElementById('volConfVerdict');
            const histEl = document.getElementById('volConfHist');
            if (!host || !verdictEl) return;
            const fail = (msg) => {
                host.innerHTML = ''; if (histEl) histEl.textContent = '';
                if (zoneEl) { zoneEl.textContent = '--'; zoneEl.className = 'text-[9px] normal-case tracking-normal text-slate-500 font-medium'; }
                verdictEl.textContent = msg || 'Load a ticker to read volume confluence.';
                verdictEl.className = 'text-[11px] font-bold text-slate-400 leading-snug';
            };
            if (!Array.isArray(results) || results.length < 30 || !Array.isArray(volumes) || volumes.length < 30) { fail(); return; }
            const tkr = (typeof state.currentTicker !== 'undefined' && state.currentTicker) ? state.currentTicker : null;
            if (!tkr) { fail(); return; }
            const total = results.length, N = 10;
            const sma50 = i => { let s = 0, c = 0; for (let j = Math.max(0, i - 49); j <= i; j++) { if (Number.isFinite(volumes[j])) { s += volumes[j]; c++; } } return c ? s / c : null; };

            const secName = currentTickerClassification && currentTickerClassification.sector;
            const sec = secRot || ((secName && Array.isArray(groupRankingData)) ? groupRankingData.find(g => g.name === secName) : null);
            const secGrvol = sec && Number.isFinite(sec.grvol) ? sec.grvol : null;
            const leaderRvol = (engineState.lastMarketStructure && Number.isFinite(engineState.lastMarketStructure.leaderRvol)) ? engineState.lastMarketStructure.leaderRvol : null;

            const HKEY = 'abVolHistV1:' + tkr;
            let hist = [];
            try { hist = JSON.parse(localStorage.getItem(HKEY) || '[]'); if (!Array.isArray(hist)) hist = []; } catch (e) { hist = []; }
            const byDate = new Map(hist.map(p => [p.d, p]));
            for (let k = N - 1; k >= 0; k--) {
                const idx = total - 1 - k;
                const d = new Date(results[idx].t).toISOString().slice(0, 10);
                let e = byDate.get(d); if (!e) { e = { d: d, stk: null, sec: null, mkt: null }; byDate.set(d, e); }
                const a = sma50(idx);
                if (Number.isFinite(volumes[idx]) && a > 0) e.stk = +(volumes[idx] / a).toFixed(2);
            }
            const latestDate = new Date(results[total - 1].t).toISOString().slice(0, 10);
            { let e = byDate.get(latestDate); if (!e) { e = { d: latestDate, stk: null, sec: null, mkt: null }; byDate.set(latestDate, e); } if (secGrvol != null) e.sec = +secGrvol.toFixed(2); if (leaderRvol != null) e.mkt = +leaderRvol.toFixed(2); }
            let merged = [...byDate.values()].sort((a, b) => a.d < b.d ? -1 : a.d > b.d ? 1 : 0);
            if (merged.length > 30) merged = merged.slice(-30);
            try { localStorage.setItem(HKEY, JSON.stringify(merged)); } catch (e) {}
            const series = merged.slice(-10), n = series.length;

            const W = 300, H = 110, pad = 10, vmin = 0.4, vmax = 2.6;
            const cx = i => pad + (n <= 1 ? (W - 2 * pad) / 2 : i * ((W - 2 * pad) / (n - 1)));
            const cy = v => (H - pad) - ((Math.max(vmin, Math.min(vmax, v)) - vmin) / (vmax - vmin)) * (H - 2 * pad);
            const lineOf = (key, col) => {
                const pts = series.map((p, i) => ({ i, v: p[key] })).filter(p => Number.isFinite(p.v));
                if (!pts.length) return '';
                let s = '';
                if (pts.length >= 2) s += `<polyline points="${pts.map(p => `${cx(p.i).toFixed(1)},${cy(p.v).toFixed(1)}`).join(' ')}" fill="none" stroke="${col}" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/>`;
                const last = pts[pts.length - 1];
                return s + `<circle cx="${cx(last.i).toFixed(1)}" cy="${cy(last.v).toFixed(1)}" r="3" fill="${col}"/>`;
            };
            const fullIdx = series.map((p, i) => ({ i, ok: Number.isFinite(p.stk) && Number.isFinite(p.sec) && Number.isFinite(p.mkt) })).filter(p => p.ok).map(p => p.i);
            let svg = `<svg viewBox="0 0 ${W} ${H}" width="100%" height="${H}" preserveAspectRatio="none" aria-hidden="true">`;
            if (fullIdx.length >= 2) {
                const spread = i => { const a = [series[i].stk, series[i].sec, series[i].mkt]; return Math.max(a[0], a[1], a[2]) - Math.min(a[0], a[1], a[2]); };
                const fill = spread(fullIdx[fullIdx.length - 1]) <= spread(fullIdx[0]) ? 'rgba(52,211,153,0.12)' : 'rgba(251,113,133,0.12)';
                let d = '';
                fullIdx.forEach((i, k) => { const mx = Math.max(series[i].stk, series[i].sec, series[i].mkt); d += (k ? ' L' : 'M') + cx(i).toFixed(1) + ' ' + cy(mx).toFixed(1); });
                for (let k = fullIdx.length - 1; k >= 0; k--) { const i = fullIdx[k]; const mn = Math.min(series[i].stk, series[i].sec, series[i].mkt); d += ' L' + cx(i).toFixed(1) + ' ' + cy(mn).toFixed(1); }
                d += ' Z';
                svg += `<path d="${d}" fill="${fill}" stroke="none"/>`;
            }
            svg += `<line x1="${pad}" y1="${cy(1).toFixed(1)}" x2="${W - pad}" y2="${cy(1).toFixed(1)}" stroke="#334155" stroke-width="1" stroke-dasharray="3 3"/>`;
            svg += lineOf('stk', '#e2e8f0') + lineOf('sec', '#5dcaa5') + lineOf('mkt', '#a78bfa');
            svg += `</svg>`;
            host.innerHTML = svg;
            if (histEl) histEl.textContent = n < 3 ? `building · ${n}/10` : `${n}/10 sessions`;

            const Lp = series[n - 1] || {};
            const layers = [
                { v: Lp.stk, thr: 1.5, present: Number.isFinite(Lp.stk) },
                { v: Lp.sec, thr: 1.3, present: Number.isFinite(Lp.sec) },
                { v: Lp.mkt, thr: 1.2, present: Number.isFinite(Lp.mkt) }
            ];
            const present = layers.filter(x => x.present).length;
            const lit = layers.filter(x => x.present && x.v >= x.thr).length;
            let zChip, verdict;
            if (lit >= 3) { zChip = 'text-emerald-300'; verdict = 'Stock, sector and market all heavy — conviction volume.'; }
            else if (lit >= 2 && present >= 2) { zChip = 'text-teal-300'; verdict = `Volume confirms at ${lit} of ${present} levels.`; }
            else if (present >= 2 && Number.isFinite(Lp.stk) && Lp.stk >= 1.5 && lit <= 1) { zChip = 'text-amber-300'; verdict = 'Stock is hot but sector/market are quiet — a hollow spike.'; }
            else { zChip = 'text-slate-400'; verdict = present >= 2 ? 'Volume quiet across levels.' : 'Confluence builds as sector & market history accrue.'; }
            if (zoneEl) { zoneEl.textContent = present ? `${lit}/${present}` : '--'; zoneEl.className = 'text-[9px] normal-case tracking-normal font-medium ' + zChip; }
            verdictEl.textContent = verdict;
            verdictEl.className = 'text-[11px] font-bold leading-snug ' + zChip;
        }

        function detectWedge(closes, highs, lows, results, mult = 1.5, emaPeriod = 10, atrPeriod = 14, weakMinBelow = 3) {
            wedgeSignals = []; const n = closes.length; const ema = MathLib.emaSeries(closes, emaPeriod); const atr = MathLib.atrSeries(highs, lows, closes, atrPeriod);
            let pendingDown = false; let lastSignal = null;
            for (let i = 0; i < n; i++) {
                if (ema[i] === null || atr[i] === null) continue;
                const e = ema[i], a = atr[i] * mult;
                if (highs[i] < e - a) pendingDown = true;
                if (pendingDown && closes[i] > e) { wedgeSignals.push({ index: i, type: 'POP' }); lastSignal = { index: i, type: 'POP' }; pendingDown = false; }
            }

            // --- WEAK SIGNAL: plain 10-EMA reclaim (cross-up) after >= weakMinBelow consecutive closes below the EMA.
            //     Excludes bars that already fired a strong POP, so each bar carries at most one marker. ---
            const popSet = new Set(wedgeSignals.filter(s => s.type === 'POP').map(s => s.index));
            let lastWeak = null; let weakCount = 0; const weakWindowStart = Math.max(0, n - 20);
            for (let i = 1; i < n; i++) {
                if (ema[i] === null || ema[i - 1] === null) continue;
                if (closes[i] <= ema[i]) continue;        // bar must close back above the 10 EMA
                if (popSet.has(i)) continue;              // already counted as a strong pop
                let belowRun = 0;
                for (let j = i - 1; j >= 0 && ema[j] !== null && closes[j] < ema[j]; j--) belowRun++;
                if (belowRun < weakMinBelow) continue;    // noise filter: needs a real multi-day dip first
                wedgeSignals.push({ index: i, type: 'WEAK' });
                lastWeak = i;
                if (i >= weakWindowStart) weakCount++;
            }
            wedgeSignals.sort((a, b) => a.index - b.index);

            // ── Smart Signal readout ─────────────────────────────────────────────
            // Alpha = strong reclaim after a downside extension (the high-conviction trigger).
            // Beta  = plain 10-EMA reclaim after a multi-day dip (the secondary/confirming trigger).
            // We surface the most recent of the two, with calendar days since and PEAK % gain since.
            const lastAlpha = wedgeSignals.filter(s => s.type === 'POP').slice(-1)[0] || null;
            const lastBeta  = wedgeSignals.filter(s => s.type === 'WEAK').slice(-1)[0] || null;

            // daysAgo from real timestamps; peak % = signal close → highest close after it.
            const sinceInfo = (idx) => {
                let days = (n - 1) - idx;
                if (results && results[idx] && results[n - 1]) {
                    days = Math.round((results[n - 1].t - results[idx].t) / 86400000);
                }
                let peak = closes[idx];
                for (let j = idx; j < n; j++) if (closes[j] > peak) peak = closes[j];
                const gain = ((peak - closes[idx]) / closes[idx]) * 100;
                return { days, gain };
            };
            const fmtSince = (idx) => {
                const { days, gain } = sinceInfo(idx);
                const when = days <= 0 ? "today" : `${days} day${days === 1 ? '' : 's'} ago`;
                return `${when} · +${gain.toFixed(0)}% since (peak)`;
            };

            // Expose signal data for the pro/con commentary: days since + % from signal to NOW
            // (current price vs signal price — "still in range" check, distinct from peak gain).
            const nowFromSignal = (idx) => {
                const sigPrice = closes[idx];
                let days = (n - 1) - idx;
                if (results && results[idx] && results[n - 1]) days = Math.round((results[n - 1].t - results[idx].t) / 86400000);
                const fromNow = sigPrice > 0 ? ((closes[n - 1] - sigPrice) / sigPrice) * 100 : null;
                return { days, fromNow };
            };
            window.__signalContext = {
                alpha: lastAlpha ? nowFromSignal(lastAlpha.index) : null,
                beta: lastBeta ? nowFromSignal(lastBeta.index) : null
            };

            // Show BOTH signals when both exist, each with its own days-since + peak %.
            const chip = (kind, idx) => {
                const cls = kind === 'ALPHA'
                    ? "px-2 py-1 rounded-md border font-bold bg-amber-900/30 border-amber-500/50 text-amber-300"
                    : "px-2 py-1 rounded-md border font-bold bg-sky-900/30 border-sky-500/50 text-sky-300";
                return `<span class="${cls}">▲ ${kind === 'ALPHA' ? 'Alpha' : 'Beta'} — ${fmtSince(idx)}</span>`;
            };

            // The legacy wedgeSignal node is now hidden; we still write to it harmlessly,
            // but the live readout is the compact pill row above the chart.
            ui.wedgeSignal.className = "hidden";
            if (lastAlpha || lastBeta) {
                const parts = [];
                if (lastAlpha) parts.push(chip('ALPHA', lastAlpha.index));
                if (lastBeta)  parts.push(chip('BETA', lastBeta.index));
                ui.wedgeSignal.innerHTML = parts.join('');
            } else {
                ui.wedgeSignal.innerHTML = '';
            }

            // Stash alpha/beta context into the shared pill ctx (merge, don't replace, so
            // delta/omega set by other passes survive regardless of call order).
            window.__pillCtx = {
                ...(window.__pillCtx || {}),
                alpha: lastAlpha ? { days: sinceInfo(lastAlpha.index).days, peak: sinceInfo(lastAlpha.index).gain } : null,
                beta:  lastBeta  ? { days: sinceInfo(lastBeta.index).days,  peak: sinceInfo(lastBeta.index).gain }  : null,
                benchmark: (typeof rsBenchmarkSymbol !== 'undefined') ? rsBenchmarkSymbol : 'SPY'
            };
            renderAlertPills(window.__pillCtx);
        }



        // Data freshness for the Polygon Starter tier (15-min delayed).
        // Hardcode the known 15m baseline, but verify against the latest bar's timestamp:
        // during market hours the gap should be ~15m; a much larger gap means the session
        // is closed or the feed lagged, and we say so. Drives the header "delayed" note.
        const TIER_DELAY_MIN = 15; // Polygon Starter baseline
        function updateDataFreshness(latestBarMs) {
            const note = document.getElementById('dataDelayNote');
            // Remember the freshest bar we've seen so getDataRegime() can classify live vs delayed.
            if (Number.isFinite(latestBarMs)) {
                window.__freshestBarMs = Math.max(window.__freshestBarMs || 0, latestBarMs);
                if (typeof updateGroupRsRegimeBadge === 'function') updateGroupRsRegimeBadge();
            }
            if (!note) return;
            if (!Number.isFinite(latestBarMs)) {
                note.textContent = `· delayed ${TIER_DELAY_MIN}m`;
                note.title = `Polygon Starter tier serves ${TIER_DELAY_MIN}-minute delayed data`;
                return;
            }
            const gapMin = Math.max(0, Math.round((Date.now() - latestBarMs) / 60000));
            // We classify by the observed bar gap, but the "closed" verdict is gated by the
            // RELIABLE NY-wall-clock check (isUsMarketHours) — never by the raw gap alone, so
            // an open session with a lagging/failing feed isn't mislabeled "market closed".
            let txt, title, cls;
            if (gapMin <= TIER_DELAY_MIN + 5) {
                txt = `· delayed ${TIER_DELAY_MIN}m`;
                title = `Live feed, ${TIER_DELAY_MIN}-min delayed (Starter tier). Last bar ${gapMin}m ago.`;
                cls = 'text-amber-400/80';
            } else if (gapMin < 120) {
                txt = `· delayed ${gapMin}m`;
                title = `Last bar is ${gapMin} minutes old — feed lagging or near session close.`;
                cls = 'text-amber-400/80';
            } else {
                const hrs = Math.round(gapMin / 60);
                const mktOpen = (typeof isUsMarketHours === 'function') ? isUsMarketHours() : false;
                if (mktOpen) {
                    // Market IS open (NY clock) but the freshest bar is hours old — the live feed
                    // is lagging or snapshots aren't landing. Flag it loudly instead of "closed".
                    txt = `· feed lagging`;
                    title = `Market is open but the last bar is ~${hrs}h old — live snapshots aren't landing. Check the API key and that your Polygon plan includes snapshots.`;
                    cls = 'text-red-400/80';
                } else {
                    txt = `· market closed`;
                    title = `Last bar is ~${hrs}h old — market is closed. Showing last available data.`;
                    cls = 'text-slate-500';
                }
            }
            note.textContent = txt;
            note.title = title;
            note.className = `font-mono ${cls} text-[9px] tracking-wide border-l border-subtle pl-2`;
        }

        // Wire the chart-header pills as click-to-toggle chart-layer switches (once).
        // Only a LIT pill (its signal is present for this ticker) toggles; clicking hides/shows
        // that marker layer on the price chart and re-renders. Dark pills are inert.
        function wireAlertPillToggles() {
            const host = document.getElementById('alertPills');
            if (!host || host.dataset.toggleWired) return;
            host.dataset.toggleWired = '1';
            host.addEventListener('click', (e) => {
                const pill = e.target.closest('[data-pill]');
                if (!pill || !host.contains(pill)) return;
                const key = pill.getAttribute('data-pill');
                if (!(key in chartLayerHidden)) return;
                // Inert unless the signal is present (pill is lit with an on-* class).
                if (!/\bon-(alpha|beta|delta|prime|nova|exit|ext)\b/.test(pill.className)) return;
                chartLayerHidden[key] = !chartLayerHidden[key];
                const hidden = chartLayerHidden[key];
                pill.classList.toggle('layer-off', hidden);
                pill.title = pill.title
                    .replace(' · hidden on chart — click to show', '')
                    .replace(' · click to hide on chart', '')
                    + (hidden ? ' · hidden on chart — click to show' : ' · click to hide on chart');
                if (typeof updateChartDisplay === 'function' && typeof state.currentChartRange !== 'undefined') {
                    updateChartDisplay(state.currentChartRange);
                }
            });
        }

        // Light the 6 compact chart-header pills (Alpha, Beta, Delta, Omega Prime/Nova/Titan)
        // from the loaded ticker. Each pill carries a hover tooltip with days/% where known.
        function renderAlertPills(opts) {
            const o = opts || {};
            // A populated context means signals have been computed → drop the skeleton.
            // The load-time reset passes {} (keeps the shimmer until real data lands).
            if (Object.keys(o).length) {
                const _ap = document.getElementById('alertPills');
                if (_ap) _ap.classList.remove('pills-loading');
            }
            wireAlertPillToggles();
            const set = (key, on, cls, label, title) => {
                const el = document.querySelector(`#alertPills [data-pill="${key}"]`);
                if (!el) return;
                el.classList.remove('on-alpha','on-beta','on-delta','on-buy','on-prime','on-nova','on-exit','on-ext');
                if (on && cls) el.classList.add(cls);
                el.style.opacity = on ? '1' : '';
                el.textContent = label;
                // A lit pill becomes a clickable chart-layer toggle; a dark one is inert.
                el.classList.toggle('pill-toggleable', !!on);
                el.classList.toggle('cursor-default', !on);
                const hidden = !!on && !!chartLayerHidden[key];
                el.classList.toggle('layer-off', hidden);
                el.title = on
                    ? title + (hidden ? ' · hidden on chart — click to show' : ' · click to hide on chart')
                    : title;
            };
            // Light + enable a pill if the loaded ticker has ANY marker of that type on the
            // chart (its full history) — so the toggle reflects the LAYER state, not just a
            // recent active signal. Text shows the recent readout when present, else the count.
            const _wsCount = (t) => Array.isArray(wedgeSignals) ? wedgeSignals.filter(s => s.type === t).length : 0;
            const aN = _wsCount('POP'), bN = _wsCount('WEAK');
            const dN = Array.isArray(deltaDots) ? deltaDots.filter(s => s.kind === 'before').length : 0;
            const a = o.alpha, b = o.beta, d = o.delta;
            const aTxt = a ? `Alpha — ${a.days <= 0 ? 'today' : a.days + 'd ago'} · +${(a.peak ?? 0).toFixed(0)}% since (peak)` : (aN ? `Alpha — ${aN} on chart` : 'Alpha — none');
            const bTxt = b ? `Beta — ${b.days <= 0 ? 'today' : b.days + 'd ago'} · +${(b.peak ?? 0).toFixed(0)}% since (peak)` : (bN ? `Beta — ${bN} on chart` : 'Beta — none');
            const dTxt = d ? `Delta — ${d.days <= 0 ? 'today' : d.days + 'd ago'} · leading vs ${o.benchmark || 'SPY'}` : (dN ? `Delta — ${dN} on chart` : 'Delta — none');
            set('alpha', aN > 0, 'on-alpha', 'Alpha', aTxt);
            set('beta',  bN > 0, 'on-beta',  'Beta',  bTxt);
            set('delta', dN > 0, 'on-delta', 'Delta', dTxt);
            const om = o.omega || 'NONE';
            const pDays = o.omegaPrimeDays, nDays = o.omegaNovaDays, tDays = o.omegaTitanDays;
            const whenTxt = (d) => d == null ? '' : (d <= 0 ? 'today' : `${d}d ago`);
            const primeOn = om === 'OMEGA20' || Number.isFinite(pDays);
            const novaOn  = om === 'OMEGA55' || Number.isFinite(nDays);
            const titanOn = om === 'OMEGA_EXIT' || Number.isFinite(tDays);
            set('oprime', primeOn, 'on-prime', 'Ω Prime', primeOn ? `Omega Prime — 20D breakout · ${whenTxt(pDays ?? 0)}` : 'Omega Prime (20D breakout) — inactive');
            set('onova',  novaOn,  'on-nova',  'Ω Nova',  novaOn  ? `Omega Nova — 55D breakout · ${whenTxt(nDays ?? 0)}`  : 'Omega Nova (55D breakout) — inactive');
            set('otitan', titanOn, 'on-exit',  'Ω Titan', titanOn ? `Omega Titan — 20D exit · ${whenTxt(tDays ?? 0)}`    : 'Omega Titan (20D exit) — inactive');
            // EXT is a persistent display-layer toggle for the grey 50-MA extension dots (always available).
            set('ext', true, 'on-ext', 'EXT', 'Extension — grey dots ≥ threshold ATR% above the 50-MA');
        }

        function renderChannelGauge(state) {
            if (!ui.channelGauge) return;
            if (!state) {
                ui.channelGauge.className = 'px-2 py-0.5 rounded-md bg-slate-800/50 border border-slate-700 text-slate-400 font-bold';
                ui.channelGauge.textContent = '--';
                renderAlertPills({ omega: 'NONE' });
                return;
            }
            const style = getChannelStyle(state.stage);
            ui.channelGauge.className = `px-2 py-0.5 rounded-md border font-black ${style.cls}`;
            ui.channelGauge.textContent = `${state.stage}/6 ${style.label}`;
            ui.channelGauge.title = `Channel position ${state.position.toFixed(1)}%. 20D high ${state.breakout20.toFixed(2)}, 20D low ${state.exit20.toFixed(2)}.`;
            // Omega now lives in the chart-header pills; merge into shared ctx so neither
            // the smart-signal pass nor the RS panel clobbers the others' pills. Include
            // per-type recency so pills light for recent breakouts/exits, not only today.
            window.__pillCtx = { ...(window.__pillCtx || {}), omega: state.omegaSignal || 'NONE',
                omegaPrimeDays: state.omegaPrimeDays, omegaNovaDays: state.omegaNovaDays, omegaTitanDays: state.omegaTitanDays };
            renderAlertPills(window.__pillCtx);
        }

        // ATR% Multiple from 50-MA (Oliver Kell extension method) + ADR% + LoD%.
        //   A = ATR%        = ATR / price
        //   B = % gain from 50-MA = (price - SMA50) / SMA50
        //   B / A = ATR% multiple from MA  → scale-out zone at 7–10x
        // Also builds extDots: bars where that multiple >= EXT_DOT_THRESHOLD (grey dots on chart).
        // `liveLow` is today's LoD (changes intraday); LoD% = (price - low)/low.
        // Returns the live values for the summary strip.
        function computeExtensionMetrics(closes, highs, lows, atrAbs, liveLow) {
            extDots = [];
            const n = closes.length;
            const price = closes[n - 1];
            const sma50Series = MathLib.smaSeries(closes, 50);
            const atrSeries = MathLib.atrSeries(highs, lows, closes, 14);

            // Historical extension dots across the whole series.
            for (let i = 0; i < n; i++) {
                const ma = sma50Series[i], a = atrSeries[i];
                if (ma === null || a === null || ma <= 0 || a <= 0) continue;
                const atrPctI = a / closes[i];                 // A
                const gainI = (closes[i] - ma) / ma;            // B
                const multI = atrPctI > 0 ? gainI / atrPctI : 0;
                if (multI >= EXT_DOT_THRESHOLD) extDots.push({ index: i, mult: multI });
            }

            // Live values for the latest bar.
            const sma50 = sma50Series[n - 1];
            const atrPct = (atrAbs && price) ? (atrAbs / price) * 100 : null;     // A (%)
            const gainMA = sma50 ? (price - sma50) / sma50 * 100 : null;          // B (%)
            const atrMult = (atrPct && atrPct !== 0 && gainMA !== null) ? gainMA / atrPct : null;
            const adrPct = MathLib.adrPct(highs, lows, 20);
            currentTickerAdrPct = (adrPct !== null && isFinite(adrPct)) ? adrPct : null;
            const lod = (liveLow !== undefined && liveLow !== null) ? liveLow : lows[n - 1];
            const lodPct = lod > 0 ? (price - lod) / lod * 100 : null;

            // Populate left-column fields.
            const f = (v, d = 2, suf = '%') => (v === null || !isFinite(v)) ? '--' : `${v.toFixed(d)}${suf}`;
            ui.mtAtrPct.textContent = f(atrPct);
            ui.mtAdrPct.textContent = f(adrPct);
            // ADR classification badge + note (volatility character).
            const adrCls = classifyAdr(adrPct);
            if (ui.mtAdrTier) {
                ui.mtAdrTier.textContent = adrCls ? adrCls.tier : '--';
                ui.mtAdrTier.className = adrCls
                    ? `text-[9px] font-bold px-1.5 py-0.5 rounded border ${adrCls.cls}`
                    : 'text-[9px] font-bold px-1.5 py-0.5 rounded border border-strong text-slate-500';
            }
            if (ui.mtAdrNote) ui.mtAdrNote.textContent = adrCls ? adrCls.note : 'avg daily swing';
            // Refresh ADR-based allocation guide with the freshly computed ADR%.
            currentTickerAdrPct = (adrPct !== null && isFinite(adrPct)) ? adrPct : null;
            if (typeof calculatePosition === 'function') calculatePosition();
            ui.mtGainMA.textContent = (gainMA === null) ? '--' : `${gainMA >= 0 ? '+' : ''}${gainMA.toFixed(2)}%`;
            ui.mtLodPct.textContent = f(lodPct);
            ui.mtAtrMult.textContent = (atrMult === null || !isFinite(atrMult)) ? '--' : `${atrMult.toFixed(1)}x`;

            // Color + note on the multiple.
            let noteColor = 'text-amber-300', note = '';
            if (atrMult !== null && isFinite(atrMult)) {
                if (atrMult >= 10) { noteColor = 'text-rose-400'; note = 'Extreme: >10x — historically stalls/reverses. Scale out.'; }
                else if (atrMult >= 7) { noteColor = 'text-amber-400'; note = 'Extended: 7–10x — begin taking partial profits.'; }
                else if (atrMult >= 0) { noteColor = 'text-emerald-400'; note = 'Within normal extension range.'; }
                else { noteColor = 'text-slate-400'; note = 'Below the 50-MA.'; }
            }
            ui.mtAtrMult.className = `font-black font-mono text-sm ${noteColor}`;
            ui.mtExtNote.textContent = note;
            if (lodPct !== null && lodPct >= 50) {
                ui.mtExtNote.textContent = note + ` LoD ${lodPct.toFixed(0)}% — intraday range already stretched (>50% caution).`;
            }

            return { atrPct, gainMA, atrMult, adrPct, lodPct };
        }

        // ─────────────────────────────────────────────────────────────────────────
        //  WEINSTEIN STAGE ANALYSIS  (auto-detect, display-only in Step 1)
        // ─────────────────────────────────────────────────────────────────────────
        //  The whole framework reduces to a 3-question decision tree, derived directly
        //  from the four stage diagrams:
        //    Q1  What is the long MA (150-day = 30-week) doing?  rising / flat / falling
        //    Q2  Which side of that MA is price on?              above / below
        //    Q3  Where are we in the larger move?                near 52w-high / near 52w-low
        //  Slope splits the universe; the flat-MA cases (Stage 1 vs Stage 3) are the
        //  ambiguous ones, separated ONLY by context — a flat MA after a decline near
        //  the lows is a Stage 1 base; a flat MA after an advance near the highs is a
        //  Stage 3 top. That "what came before" tiebreaker is exactly what Q3 encodes.
        //
        //  Weinstein is natively a WEEKLY system (30-week / 10-week MAs). We compute the
        //  daily equivalents (150-day / 50-day), which track the same structure but can
        //  shift a transition call by a few days vs a strict weekly chart.
        //
        //  Returns { stage:1..4, sub:'A'|'B', label, verdict, buyable, confidence, ... }.

        // Render the stage badge (display-only). Stage feeds the tier engine in Step 3.
        function renderStageBadge(closes, highs, lows) {
            if (!ui.stageBadge) return null;
            const s = detectStage(closes, highs, lows);
            if (!s) { ui.stageBadge.classList.add('hidden'); return null; }

            ui.stageBadge.classList.remove('hidden');
            ui.stageBadge.className = `rounded-xl border p-4 mb-4 transition-all duration-500 ${s.colors.border} ${s.colors.bg}`;
            ui.stageIcon.className = `w-11 h-11 rounded-lg flex items-center justify-center shrink-0 border ${s.colors.icon}`;
            ui.stageLabel.className = `text-lg font-black tracking-tight ${s.colors.label}`;
            ui.stageLabel.textContent = s.label;
            ui.stageVerdict.className = `text-[10px] font-bold uppercase tracking-wide ${s.colors.verdict}`;
            ui.stageVerdict.textContent = s.verdict;
            ui.stageConfidence.textContent = s.confidence === 'low' ? 'low-confidence est.' : 'est.';
            ui.stageDetail.textContent = s.detail;

            // Auditable technical readings behind the call (no commentary, just the numbers).
            const m = s.metrics;
            const cell = (label, val, cls) =>
                `<div class="bg-slate-900/50 rounded-md px-2 py-1.5 border border-slate-800"><div class="text-[8px] text-slate-500 uppercase tracking-wider">${label}</div><div class="text-xs font-bold font-mono ${cls || 'text-slate-200'}">${val}</div></div>`;
            const slopeCls = (v) => v > 1.5 ? 'text-emerald-400' : v < -1.5 ? 'text-rose-400' : 'text-amber-400';
            if (ui.stageMetrics) {
                ui.stageMetrics.innerHTML =
                    cell('150-day slope (6wk)', `${m.slope150Pct >= 0 ? '+' : ''}${m.slope150Pct.toFixed(1)}%`, slopeCls(m.slope150Pct)) +
                    cell('50-day slope (3wk)', `${m.slope50Pct >= 0 ? '+' : ''}${m.slope50Pct.toFixed(1)}%`, slopeCls(m.slope50Pct)) +
                    cell('From 52w high', `${m.fromHigh.toFixed(1)}%`) +
                    cell('From 52w low', `+${m.fromLow.toFixed(1)}%`);
            }
            return s;
        }

        // Render the RMV gauge (15-period). Shows value, direction (rising/falling), and band.
        //   <15  = TIGHT (contracted — coiled setup; best risk/reward, stop is small)
        //   15–50 = NORMAL
        //   >50  = EXPANDED (already moving / wide range — poor entry, wide stop)
        // Direction matters: tight + falling = coiling (good); tight + rising = waking up.
        function renderRMV(closes, highs, lows) {
            if (!ui.rmvGauge) return null;
            const series = MathLib.rmvSeries(highs, lows, closes, 15);
            const n = series.length;
            const val = series[n - 1];
            if (val === null) { ui.rmvGauge.classList.add('hidden'); return null; }
            ui.rmvGauge.classList.remove('hidden');

            // Direction from the recent slope (now vs ~5 bars ago).
            let prev = null;
            for (let k = n - 6; k >= 0; k--) { if (series[k] !== null) { prev = series[k]; break; } }
            const rising = prev !== null && val > prev + 3;
            const falling = prev !== null && val < prev - 3;

            // Band classification.
            let band, bandCls, fillCls, valCls, note;
            if (val < 15) {
                band = 'TIGHT'; bandCls = 'bg-emerald-900/40 text-emerald-300'; fillCls = 'bg-emerald-500/50'; valCls = 'text-emerald-300';
                note = falling ? 'Contracted and still tightening — coiled setup forming. Watch for a breakout; stop is small here.'
                                : 'Contracted (tight). Coiled — high-quality entry zone if a breakout triggers.';
            } else if (val < 50) {
                band = 'NORMAL'; bandCls = 'bg-slate-700/50 text-slate-300'; fillCls = 'bg-slate-500/40'; valCls = 'text-slate-200';
                note = falling ? 'Volatility easing toward the tight zone — watch for contraction.'
                                : 'Mid-range volatility. No edge from RMV at this level.';
            } else {
                band = 'EXPANDED'; bandCls = 'bg-rose-900/40 text-rose-300'; fillCls = 'bg-rose-500/40'; valCls = 'text-rose-300';
                note = 'Range expanded — already moving / volatile. Poor entry timing; stop would be wide.';
            }

            ui.rmvValue.textContent = val.toFixed(0);
            ui.rmvValue.className = `text-2xl font-black font-mono ${valCls}`;
            ui.rmvArrow.textContent = rising ? '▲' : falling ? '▼' : '▬';
            ui.rmvArrow.className = `text-sm font-bold ${rising ? 'text-rose-400' : falling ? 'text-emerald-400' : 'text-slate-500'}`;
            ui.rmvBand.textContent = band;
            ui.rmvBand.className = `text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${bandCls}`;
            ui.rmvFill.className = `absolute inset-y-0 left-0 transition-all duration-500 ${fillCls}`;
            ui.rmvFill.style.width = `${Math.max(0, Math.min(100, val))}%`;
            ui.rmvMarker.style.left = `${Math.max(0, Math.min(100, val))}%`;
            ui.rmvNote.textContent = note;

            return { val, rising, falling, band };
        }

        // Compact, unified at-a-glance stat block. One consistent label-over-value cell
        // style, grouped logically (Strength · Momentum · Range/risk · Liquidity), merging
        // the old metric tiles with the signal data. Pure string-build from data already in
        // memory (the snapshot + this load's summary numbers) → instant, no network.
        function renderStockSummary(d) {
            if (!ui.stockSummary) return;
            ui.stockSummary.classList.remove('hidden');
            window.__summaryData = d || {};
            renderUnifiedStats();
        }
        function renderUnifiedStats() {
            const el = document.getElementById('unifiedStats');
            if (!el) return;
            const d = window.__summaryData || {};
            const s = window.__tierSignals || {};
            const C = { good: '#34d399', warn: '#fbbf24', bad: '#fb7185', info: '#60a5fa', neutral: '#cbd5e1', mute: '#94a3b8' };
            const mkt = (ui.fdMktCap && ui.fdMktCap.textContent && ui.fdMktCap.textContent !== '--') ? ui.fdMktCap.textContent : null;
            const sh  = (ui.fdShares && ui.fdShares.textContent && ui.fdShares.textContent !== '--') ? ui.fdShares.textContent : null;
            const cell = (label, value, color) => (value == null || value === '') ? '' :
                `<div style="display:flex;flex-direction:column;min-width:44px"><span style="font-size:8px;letter-spacing:.04em;text-transform:uppercase;color:#64748b">${label}</span><span style="font-size:12px;font-weight:600;line-height:1.3;color:${color || C.neutral}">${value}</span></div>`;
            const pct = v => Number.isFinite(v) ? ((v >= 0 ? '+' : '') + v.toFixed(Math.abs(v) >= 100 ? 0 : 1) + '%') : null;
            const gr = (Number.isFinite(s.groupRank) && Number.isFinite(s.groupRankTotal) && s.groupRankTotal > 0) ? s.groupRank / s.groupRankTotal : null;
            const groups = [
                ['Strength', [
                    cell('RS', Number.isFinite(s.rsPower) ? (s.rsPower + (s.rsPowerTier ? '·' + s.rsPowerTier : '')) : null, s.rsPower >= 80 ? C.good : s.rsPower >= 50 ? C.info : C.mute),
                    cell('Stage', d.mvPassed === 8 ? '2 ✓' : d.mvPassed >= 7 ? '2 near' : (d.mvPassed != null ? 'no' : null), d.mvPassed === 8 ? C.good : d.mvPassed >= 7 ? C.warn : C.bad),
                    cell('BQS', s.bqsGrade != null ? String(s.bqsGrade) : null, C.info),
                    cell('Grp', (Number.isFinite(s.groupRank) && Number.isFinite(s.groupRankTotal)) ? ('#' + s.groupRank + '/' + s.groupRankTotal) : null, gr == null ? C.mute : gr <= 0.25 ? C.good : gr > 0.6 ? C.bad : C.mute)
                ]],
                ['Momentum', [
                    cell('1W', pct(d.pct1w), d.pct1w >= 0 ? C.good : C.bad),
                    cell('1M', pct(d.pct1m), d.pct1m >= 0 ? C.good : C.bad),
                    cell('3M', pct(s.pct3m), s.pct3m >= 0 ? C.good : C.bad)
                ]],
                ['Range / risk', [
                    cell('ADR', Number.isFinite(s.adrPct) ? s.adrPct.toFixed(1) + '%' : null, s.adrPct >= 8 ? C.warn : s.adrPct >= 4 ? C.good : C.mute),
                    cell('RMV', Number.isFinite(s.rmv) ? s.rmv.toFixed(0) : null, s.rmv < 25 ? C.good : s.rmv > 50 ? C.warn : C.info),
                    cell('Ext', Number.isFinite(d.atrMult) ? d.atrMult.toFixed(1) + '×' : null, d.atrMult >= 10 ? C.bad : d.atrMult >= 7 ? C.warn : (d.dist50 < 0 ? C.bad : C.mute)),
                    cell('AVWAP', Number.isFinite(s.avwapDist) ? (s.avwapDist >= 0 ? 'Above' : 'Below') : null, s.avwapDist >= 0 ? C.good : C.bad)
                ]],
                ['Liquidity', [
                    cell('RVOL', Number.isFinite(d.rvol) ? d.rvol.toFixed(2) + '×' : null, d.rvol >= 1.5 ? C.good : d.rvol >= 1 ? C.info : C.mute),
                    cell('$ Vol', (Number.isFinite(s.dollarVolume) && typeof formatDollarBig === 'function') ? formatDollarBig(s.dollarVolume) : null, C.mute),
                    cell('Cap', mkt, C.mute),
                    cell('Float', sh, C.mute)
                ]]
            ];
            const groupHtml = (name, cells) => {
                const c = cells.filter(Boolean);
                if (!c.length) return '';
                return `<div style="display:flex;align-items:flex-start;gap:10px;padding-left:12px;border-left:1px solid #1f2937">`
                     + `<span style="font-size:8px;letter-spacing:.05em;text-transform:uppercase;color:#475569;padding-top:3px;white-space:nowrap;min-width:52px">${name}</span>`
                     + `<div style="display:flex;flex-wrap:wrap;gap:10px 14px;flex:1">${c.join('')}</div></div>`;
            };
            el.innerHTML = groups.map(g => groupHtml(g[0], g[1])).join('');
        }

        function median(nums) {
            const arr = nums.filter(Number.isFinite).sort((a, b) => a - b);
            if (!arr.length) return null;
            const mid = Math.floor(arr.length / 2);
            return arr.length % 2 ? arr[mid] : (arr[mid - 1] + arr[mid]) / 2;
        }

        let _groupRanksSig = ''; // signature of the universe the group ranks were last computed for
        function computeIndustryGroupRanks() {
            // SINGLE SOURCE OF TRUTH: the SERVER computes group ranks (lib/pipeline.js finalizeGroupRanks) and
            // now SHIPS groupRank/groupRankTotal/groupPerf6m/groupBreadth on every row. The client no longer
            // re-derives a (slightly different) ranking or re-scores — it just rebuilds industryGroupStats FROM
            // the shipped values so the group badge + the loaded-ticker input use the SAME rank the screener
            // does (this is what made the dashboard tier disagree with the screener). Only the display-only
            // `rows` count is tallied client-side. Guard on a cheap universe signature.
            let _mx = 0;
            for (let i = 0; i < state.screenerAllResults.length; i++) { const e = state.screenerAllResults[i].enrichedAt || 0; if (e > _mx) _mx = e; }
            const _sig = state.screenerAllResults.length + ':' + _mx;
            if (_sig === _groupRanksSig) return; // universe unchanged
            _groupRanksSig = _sig;
            industryGroupStats = new Map();
            // Prefer the SERVER's shipped ranks. Fallback path re-derives client-side — needed for old
            // snapshots / the window after this client deploys but before the server restarts to ship them.
            if (state.screenerAllResults.some(r => Number.isFinite(r.groupRank))) {
                const _counts = new Map();
                state.screenerAllResults.forEach(row => {
                    const g = row.industryGroup;
                    if (!g || g === 'Unknown' || g === 'ETF') return;
                    _counts.set(g, (_counts.get(g) || 0) + 1);
                    if (!industryGroupStats.has(g) && Number.isFinite(row.groupRank)) {
                        industryGroupStats.set(g, { group: g, rank: row.groupRank, total: row.groupRankTotal, perf6m: row.groupPerf6m, breadth: row.groupBreadth });
                    }
                });
                industryGroupStats.forEach((s, g) => { s.rows = _counts.get(g) || 0; });
            } else {
                // Fallback: server didn't ship group ranks — re-derive them client-side (legacy behavior).
                const grouped = new Map();
                state.screenerAllResults.forEach(row => {
                    if (!row.industryGroup || row.industryGroup === 'Unknown' || row.industryGroup === 'ETF' || !Number.isFinite(row.pct6m)) return;
                    if (!grouped.has(row.industryGroup)) grouped.set(row.industryGroup, []);
                    grouped.get(row.industryGroup).push(row);
                });
                const stats = [...grouped.entries()].map(([group, rows]) => {
                    const perf6m = median(rows.map(r => r.pct6m));
                    const breadth = rows.length ? rows.filter(r => r.above50 === true).length / rows.length * 100 : null;
                    const leaders = rows.filter(r => r.tier <= 2).length;
                    const score = (perf6m || 0) * 0.7 + (breadth || 0) * 0.2 + leaders * 1.5;
                    return { group, rows: rows.length, perf6m, breadth, leaders, score };
                }).sort((a, b) => b.score - a.score);
                stats.forEach((stat, idx) => industryGroupStats.set(stat.group, { ...stat, rank: idx + 1, total: stats.length }));
                state.screenerAllResults.forEach(row => {
                    const stat = industryGroupStats.get(row.industryGroup);
                    if (stat) { row.groupRank = stat.rank; row.groupRankTotal = stat.total; row.groupPerf6m = stat.perf6m; row.groupBreadth = stat.breadth; }
                });
            }
            if (currentTickerClassification) renderClassificationDashboard(currentTickerClassification);
            if (currentScoreInput && currentTickerClassification?.industryGroup) {
                const stat = industryGroupStats.get(currentTickerClassification.industryGroup);
                if (stat) {
                    Object.assign(currentScoreInput, {
                        groupRank: stat.rank,
                        groupRankTotal: stat.total,
                        groupPerf6m: stat.perf6m,
                        groupBreadth: stat.breadth
                    });
                    renderUnifiedScore(currentScoreInput, getUnifiedScore(currentScoreInput));
                }
            }
        }

        // ─────────────────────────────────────────────────────────────────────────
        // GROUP RS RANKING ENGINE
        // Aggregates the already-enriched screener rows into group-level (or sector-
        // level) rankings across 1D/1W/1M/3M/6M, a self-normalizing group RVOL, and a
        // rotation score that flags money rotating in/out. Pure presentation/derived —
        // no extra API calls; everything comes from bars already fetched per stock.
        // ─────────────────────────────────────────────────────────────────────────
        const GROUP_MIN_MEMBERS = 4; // groups thinner than this are flagged "thin"

        // Convert a 1-based rank within N items to an IBD-style 1..99 percentile.
        function rankToRs(rank, n) {
            if (!n || n < 2) return 50;
            return Math.round((1 - (rank - 1) / (n - 1)) * 98) + 1;
        }

        function computeGroupRankings(dimension = groupRankingDimension, storeGlobal = true) {
            // Step 4: prefer the SERVER-shipped rotation arrays (single source of truth) — the client no longer
            // re-aggregates the whole universe here. Fall back to the client compute only when none were shipped
            // (old server / pre-scan). The consuming code (Groups tab, per-row stamp) is unchanged.
            if (serverGroupRotation && Array.isArray(serverGroupRotation[dimension])) {
                const groups = serverGroupRotation[dimension];
                if (storeGlobal) groupRankingData = groups;
                return groups;
            }
            const grouped = new Map();
            state.screenerAllResults.forEach(row => {
                const key = row[dimension];
                if (!key || key === 'Unknown' || key === 'ETF' || key === 'Fund') return;
                if (!grouped.has(key)) grouped.set(key, []);
                grouped.get(key).push(row);
            });

            // First pass: per-group raw aggregates.
            let groups = [...grouped.entries()].map(([name, rows]) => {
                const med = tf => median(rows.map(r => r[tf]));
                const sumVolToday = rows.reduce((s, r) => s + (Number(r.volToday) || 0), 0);
                const sumAvgVol = rows.reduce((s, r) => s + (Number(r.avgVol50) || 0), 0);
                const grvol = sumAvgVol > 0 ? sumVolToday / sumAvgVol : null;
                const breadth50 = rows.length ? rows.filter(r => r.above50 === true).length / rows.length * 100 : null;
                const breadth1d = rows.length ? rows.filter(r => Number(r.pct1d) > 0).length / rows.length * 100 : null;
                const leaders = rows.filter(r => Number(r.tier) <= 2).length;
                const dollarVol = rows.reduce((s, r) => s + (Number(r.dollarVolume) || 0), 0);
                return {
                    name, members: rows.length, thin: rows.length < GROUP_MIN_MEMBERS,
                    perf1d: med('pct1d'), perf1w: med('pct1w'), perf1m: med('pct1m'),
                    perf3m: med('pct3m'), perf6m: med('pct6m'),
                    perfLive: med('pctLive'),
                    grvol, breadth50, breadth1d, leaders, dollarVol, rows
                };
            }).filter(g => g.members > 0);

            // Second pass: percentile RS per timeframe (rank across all groups).
            // LD (live/today) is only ranked when the market is open — otherwise it would
            // just mirror the closed 1D, so we leave rsLive null and the column blanks out.
            const liveActive = (typeof isUsMarketHours === 'function') ? isUsMarketHours() : false;
            const tfs = [['perf1d','rs1d'], ['perf1w','rs1w'], ['perf1m','rs1m'], ['perf3m','rs3m'], ['perf6m','rs6m']];
            if (liveActive) tfs.push(['perfLive','rsLive']);
            tfs.forEach(([perfKey, rsKey]) => {
                const ranked = [...groups].filter(g => Number.isFinite(g[perfKey])).sort((a, b) => b[perfKey] - a[perfKey]);
                const n = ranked.length;
                ranked.forEach((g, i) => { g[rsKey] = rankToRs(i + 1, n); });
                groups.forEach(g => { if (!Number.isFinite(g[rsKey])) g[rsKey] = null; });
            });

            // Composite strength = weighted blend of timeframe RS (longer = structural,
            // shorter = current). Mirrors the spirit of the stock-level RS.
            groups.forEach(g => {
                const parts = [
                    [g.rs6m, 0.30], [g.rs3m, 0.25], [g.rs1m, 0.20], [g.rs1w, 0.15], [g.rs1d, 0.10]
                ].filter(([v]) => Number.isFinite(v));
                const wsum = parts.reduce((s, [, w]) => s + w, 0);
                g.rsComposite = wsum ? Math.round(parts.reduce((s, [v, w]) => s + v * w, 0) / wsum) : null;

                // Rotation: short-term RS pulling ahead of long-term, confirmed by volume.
                const accel = (Number.isFinite(g.rs1w) && Number.isFinite(g.rs1m)) ? g.rs1w - g.rs1m : 0;
                const fresh = (Number.isFinite(g.rs1d) && Number.isFinite(g.rs3m)) ? g.rs1d - g.rs3m : 0;
                const volMult = Math.max(0.5, Math.min(3, g.grvol || 1));
                g.rotationScore = Math.round((0.5 * accel + 0.5 * fresh) * volMult);

                // Categorize.
                if (g.rotationScore > 15 && (g.grvol || 0) > 1.3) g.rotationTag = 'in';
                else if (g.rotationScore < -15) g.rotationTag = 'out';
                else if (Number.isFinite(g.rs6m) && g.rs6m >= 70 && Math.abs(g.rotationScore) <= 15) g.rotationTag = 'leader';
                else if ((g.grvol || 0) < 0.7 && (g.rsComposite || 50) < 40) g.rotationTag = 'dormant';
                else g.rotationTag = 'neutral';
            });

            groups.sort((a, b) => (b.rsComposite ?? -1) - (a.rsComposite ?? -1));
            groups.forEach((g, i) => { g.rank = i + 1; g.total = groups.length; });
            if (storeGlobal) groupRankingData = groups;
            return groups;
        }

        // KEYSTONE — stamp each scored row with its own group's + sector's rotation
        // (tag / score / GRVOL). Group rotation is only known after the whole universe
        // is ranked, so this is a post-pass over screenerAllResults. It powers the
        // cross-convergence reads (volume confluence, Delta×sector, VARS×group) that
        // would otherwise each have to re-scan groupRankingData per row. Purely
        // additive: computed with storeGlobal=false so the Group RS tab's global
        // groupRankingData is left exactly as the user's current view set it.
        function stampGroupRotationOnRows() {
            if (!Array.isArray(state.screenerAllResults) || !state.screenerAllResults.length) return;
            let grpGroups = [], secGroups = [];
            try { grpGroups = computeGroupRankings('industryGroup', false) || []; } catch (e) {}
            try { secGroups = computeGroupRankings('sector', false) || []; } catch (e) {}
            const grpBy = new Map(grpGroups.map(g => [g.name, g]));
            const secBy = new Map(secGroups.map(g => [g.name, g]));
            state.screenerAllResults.forEach(row => {
                const g = (row.industryGroup && row.industryGroup !== 'Unknown') ? grpBy.get(row.industryGroup) : null;
                const s = (row.sector && row.sector !== 'Unknown') ? secBy.get(row.sector) : null;
                // Industry-group level (the finer read — preferred for Delta×sector / VARS×group).
                row.grpRotTag   = g ? g.rotationTag   : null;
                row.grpRotScore = g ? (Number.isFinite(g.rotationScore) ? g.rotationScore : null) : null;
                row.grpGrvol    = g ? (Number.isFinite(g.grvol) ? g.grvol : null) : null;
                // Sector level (broader — the volume-confluence "sector flow" layer).
                row.secRotTag   = s ? s.rotationTag   : null;
                row.secRotScore = s ? (Number.isFinite(s.rotationScore) ? s.rotationScore : null) : null;
                row.secGrvol    = s ? (Number.isFinite(s.grvol) ? s.grvol : null) : null;
                // Convenience: finest rotation available, for badges / quadrants.
                row.rotTag   = row.grpRotTag   ?? row.secRotTag   ?? null;
                row.rotScore = row.grpRotScore ?? row.secRotScore ?? null;
                row.rotGrvol = row.grpGrvol    ?? row.secGrvol    ?? null;
            });
        }

        // #2 — Delta × Sector flow. Crosses a row's RS leadership (Delta / RS new high /
        // Primed) with its group rotation (stamped by the keystone above) to name the two
        // failure-modes the silos miss: a Delta WITH a tailwind vs a "lone soldier" Delta
        // firing inside a group that's being sold. Returns a small marker descriptor, or
        // null when the row isn't an RS leader or rotation is neutral/unknown (no clutter).
        function deltaSectorContext(row) {
            if (!row) return null;
            const lead = row.deltaTrigger === true
                || row.rsPowerTier === 'Delta' || row.rsPowerTier === 'RS NH' || row.rsPowerTier === 'Primed';
            if (!lead) return null;
            const tag = row.grpRotTag ?? row.rotTag; // finest rotation available
            if (tag === 'in' || tag === 'leader')
                return { kind: 'tailwind', glyph: '▲', cls: 'text-emerald-400',
                    title: `Group rotating ${tag === 'leader' ? 'as a stable leader' : 'in'} — RS leadership with a tailwind` };
            if (tag === 'out' || tag === 'dormant')
                return { kind: 'lone', glyph: '▽', cls: 'text-amber-400',
                    title: `Group rotating ${tag === 'dormant' ? 'dormant' : 'out'} — lone soldier, prone to revert toward the group` };
            return null; // neutral / unknown → no marker
        }

        // #1 — Volume confluence. Crosses the stock's own RVOL with its sector GRVOL
        // (keystone) and the market's leader-RVOL (breadth). Three independent volume
        // reads agreeing = real demand; a lone spike into a quiet sector/tape = hollow.
        // Snapshot form here (a 3-segment S·G·M bar); the spread-band trend chart waits
        // on the rolling-history store. Layers with no data are marked absent, not cold,
        // so the read degrades gracefully (stock-only) rather than showing false darkness.
        function volumeConfluence(row) {
            if (!row || !Number.isFinite(row.rvol)) return null;
            const market = (engineState.lastMarketStructure && Number.isFinite(engineState.lastMarketStructure.leaderRvol))
                ? engineState.lastMarketStructure.leaderRvol : null;
            const layers = [
                { key: 'S', label: 'Stock RVOL',         v: row.rvol,                                  thr: 1.5, present: true },
                { key: 'G', label: 'Sector GRVOL',       v: Number.isFinite(row.secGrvol) ? row.secGrvol : null, thr: 1.3, present: Number.isFinite(row.secGrvol) },
                { key: 'M', label: 'Market leader RVOL', v: market,                                    thr: 1.2, present: market != null }
            ];
            layers.forEach(L => { L.hot = L.present && L.v >= L.thr; });
            const denom = layers.filter(L => L.present).length;
            const lit = layers.filter(L => L.hot).length;
            return { layers, lit, denom };
        }

        const ROTATION_META = {
            in:      { label: 'Rotating In',   cls: 'text-emerald-300 bg-emerald-900/40 border-emerald-500/40', dot: '🟢' },
            out:     { label: 'Rotating Out',  cls: 'text-rose-300 bg-rose-900/40 border-rose-500/40',          dot: '🔴' },
            leader:  { label: 'Stable Leader', cls: 'text-blue-300 bg-blue-900/40 border-blue-500/40',          dot: '🔵' },
            dormant: { label: 'Dormant',       cls: 'text-slate-400 bg-slate-800/60 border-slate-600/40',       dot: '💤' },
            neutral: { label: 'Neutral',       cls: 'text-slate-400 bg-slate-800/40 border-slate-700/40',       dot: '⚪' }
        };

        function rsCellColor(v) {
            if (!Number.isFinite(v)) return 'text-slate-600';
            if (v >= 80) return 'text-emerald-300 font-bold';
            if (v >= 60) return 'text-emerald-400';
            if (v >= 40) return 'text-slate-300';
            if (v >= 20) return 'text-amber-400';
            return 'text-rose-400';
        }

        // ═══════════════════════════════════════════════════════════════════
        //  UNIFIED QUALITY SCALE  (one palette for every score column)
        //  Technique: a single sequential 6-tier ordinal scale where the SAME
        //  rank always maps to the SAME colour, and colour is used with restraint —
        //  the neutral middle is muted slate, accents only flag notable strength
        //  or weakness. Elite reserves a refined indigo (not neon) for the top tier.
        //  This replaces the ad-hoc per-column ternaries that each invented their
        //  own thresholds and colour families.
        //    pct: a 0–1 normalized quality (value / max).
        //  Returns Tailwind text classes; QS.bg() returns matching pill styles.
        // QS (quality-tier styler) extracted to src/qs.js (imported at top).

        function renderGroupRankings() {
            const body = document.getElementById('groupRankBody');
            const summary = document.getElementById('groupRotationSummary');
            if (!body) return;
            const groups = computeGroupRankings();

            if (!groups.length) {
                body.innerHTML = `<tr><td colspan="12" class="py-10 px-5 text-center text-slate-500 italic">Run the market scan to populate group rankings.</td></tr>`;
                if (summary) summary.innerHTML = '';
                const st = document.getElementById('groupRankStats');
                if (st) st.textContent = '';
                return;
            }

            // Sort by active column.
            const sorters = {
                rotation: (a, b) => (b.rotationScore ?? -1e9) - (a.rotationScore ?? -1e9),
                composite: (a, b) => (b.rsComposite ?? -1) - (a.rsComposite ?? -1),
                rsLive: (a, b) => (b.rsLive ?? -1) - (a.rsLive ?? -1),
                rs1d: (a, b) => (b.rs1d ?? -1) - (a.rs1d ?? -1),
                rs1w: (a, b) => (b.rs1w ?? -1) - (a.rs1w ?? -1),
                rs1m: (a, b) => (b.rs1m ?? -1) - (a.rs1m ?? -1),
                rs3m: (a, b) => (b.rs3m ?? -1) - (a.rs3m ?? -1),
                rs6m: (a, b) => (b.rs6m ?? -1) - (a.rs6m ?? -1),
                grvol: (a, b) => (b.grvol ?? -1) - (a.grvol ?? -1),
                breadth: (a, b) => (b.breadth50 ?? -1) - (a.breadth50 ?? -1)
            };
            const sorted = [...groups].sort(sorters[groupRankingSort] || sorters.composite);

            const rsTd = v => `<td class="py-2 px-1.5 text-right tabular-nums ${rsCellColor(v)}">${Number.isFinite(v) ? v : '·'}</td>`;
            // TD cell: today's RS percentile while the market is open; a dimmed dash when
            // closed (no live session to measure — 1D already covers the last close).
            const liveOpen = (typeof isUsMarketHours === 'function') ? isUsMarketHours() : false;
            // Make the TD column header self-explanatory about its current state.
            const tdHeader = document.querySelector('.grp-sort[data-sort="rsLive"]');
            if (tdHeader) {
                const hasLive = liveOpen && groups.some(g => Number.isFinite(g.rsLive));
                tdHeader.innerHTML = liveOpen
                    ? (hasLive ? 'TD' : 'TD<span class="text-amber-500/70 normal-case tracking-normal text-[8px]"> ·wait</span>')
                    : 'TD<span class="text-slate-600 normal-case tracking-normal text-[8px]"> ·clsd</span>';
                tdHeader.style.opacity = liveOpen ? '1' : '0.5';
                tdHeader.title = liveOpen
                    ? "TD — today's RS percentile (live/delayed). Populates as stocks enrich during the session."
                    : "TD — today's move; only available during market hours. Shows — while closed (1D covers the last close).";
            }
            const tdCell = g => liveOpen
                ? `<td class="py-2 px-1.5 text-right tabular-nums ${rsCellColor(g.rsLive)}">${Number.isFinite(g.rsLive) ? g.rsLive : '·'}</td>`
                : `<td class="py-2 px-1.5 text-right tabular-nums text-slate-700" title="Market closed — TD is today-only">—</td>`;
            const grvolColor = g => !Number.isFinite(g) ? 'text-slate-600' : g >= 1.5 ? 'text-emerald-300 font-bold' : g >= 1.15 ? 'text-emerald-400' : g >= 0.85 ? 'text-slate-300' : 'text-rose-400';

            body.innerHTML = sorted.map(g => {
                const meta = ROTATION_META[g.rotationTag] || ROTATION_META.neutral;
                const rot = Number.isFinite(g.rotationScore) ? (g.rotationScore > 0 ? '+' : '') + g.rotationScore : '·';
                const rotColor = g.rotationScore > 15 ? 'text-emerald-300' : g.rotationScore < -15 ? 'text-rose-300' : 'text-slate-400';
                return `<tr class="hover:bg-slate-800/40 transition-colors cursor-pointer group-rank-row" data-group="${g.name.replace(/"/g, '&quot;')}">
                    <td class="py-2 pl-4 pr-2">
                        <div class="font-bold text-white text-[11px] leading-tight flex items-center gap-1.5">
                            <span class="text-slate-600 text-[9px] tabular-nums">${g.rank}</span>
                            <span class="truncate max-w-[130px]" title="${g.name}">${g.name}</span>
                            ${g.thin ? '<span class="text-[8px] text-amber-500/80 border border-amber-700/50 rounded px-1 leading-tight" title="Thin coverage — fewer than '+GROUP_MIN_MEMBERS+' enriched stocks">thin</span>' : ''}
                        </div>
                        <div class="text-[9px] text-slate-500 mt-0.5">${g.members} stocks · ${g.leaders} leaders</div>
                    </td>
                    ${tdCell(g)}${rsTd(g.rs1d)}${rsTd(g.rs1w)}${rsTd(g.rs1m)}${rsTd(g.rs3m)}${rsTd(g.rs6m)}
                    <td class="py-2 px-1.5 text-right tabular-nums font-bold ${rsCellColor(g.rsComposite)}">${Number.isFinite(g.rsComposite) ? g.rsComposite : '·'}</td>
                    <td class="py-2 px-1.5 text-right tabular-nums ${grvolColor(g.grvol)}">${Number.isFinite(g.grvol) ? g.grvol.toFixed(2) + 'x' : '·'}</td>
                    <td class="py-2 px-1.5 text-right tabular-nums ${rotColor}">${rot}</td>
                    <td class="py-2 px-1.5">
                        <span class="inline-block text-[9px] font-bold px-1.5 py-0.5 rounded border ${meta.cls} whitespace-nowrap">${meta.dot} ${meta.label}</span>
                    </td>
                </tr>`;
            }).join('');

            // Rotation summary strip: top 3 in / out.
            if (summary) {
                const ins = [...groups].filter(g => g.rotationTag === 'in').sort(sorters.rotation).slice(0, 3);
                const outs = [...groups].filter(g => g.rotationTag === 'out').sort((a, b) => (a.rotationScore ?? 0) - (b.rotationScore ?? 0)).slice(0, 3);
                const pill = (g, color) => `<span class="inline-flex items-center gap-1 text-[10px] ${color} bg-slate-900/60 border border-slate-700/60 rounded-full px-2 py-0.5"><span class="font-bold">${g.name}</span><span class="tabular-nums opacity-70">${g.grvol ? g.grvol.toFixed(1)+'x' : ''}</span></span>`;
                summary.innerHTML = `
                    <div class="flex flex-wrap items-center gap-1.5">
                        <span class="text-[9px] uppercase tracking-wider text-emerald-400 font-bold mr-1">▲ Rotating In</span>
                        ${ins.length ? ins.map(g => pill(g, 'text-emerald-300')).join('') : '<span class="text-[10px] text-slate-600 italic">none</span>'}
                    </div>
                    <div class="flex flex-wrap items-center gap-1.5 mt-1.5">
                        <span class="text-[9px] uppercase tracking-wider text-rose-400 font-bold mr-1">▼ Rotating Out</span>
                        ${outs.length ? outs.map(g => pill(g, 'text-rose-300')).join('') : '<span class="text-[10px] text-slate-600 italic">none</span>'}
                    </div>`;
            }

            const st = document.getElementById('groupRankStats');
            if (st) st.textContent = `${groups.length} ${groupRankingDimension === 'sector' ? 'sectors' : 'groups'} ranked`;
        }

        // ─────────────────────────────────────────────────────────────────────────
        // MARKET STRUCTURE / BREADTH ENGINE
        // Pre-market situational awareness. Breadth (% above 20/50/200MA), RS Thrust
        // Rate, and advancers/decliners are computed live from the enriched screener
        // universe (no extra API calls). Equal-weight-vs-cap-weight (RSP/SPY) and index
        // ATR% regime come from the benchmark index bars captured during Load Index Data.
        // ─────────────────────────────────────────────────────────────────────────

        // Percentile rank (0-100) of the latest value within its own history.
        function percentileOfLast(series) {
            const arr = series.filter(Number.isFinite);
            if (arr.length < 30) return null;
            const last = arr[arr.length - 1];
            const below = arr.filter(v => v <= last).length;
            return Math.round((below / arr.length) * 100);
        }

        function computeMarketStructure() {
            const rows = state.screenerAllResults.filter(r => r && Number.isFinite(r.price));
            const n = rows.length;

            const pctTrue = key => {
                const valid = rows.filter(r => r[key] === true || r[key] === false);
                if (!valid.length) return null;
                return valid.filter(r => r[key] === true).length / valid.length * 100;
            };
            const above20 = pctTrue('above20');
            const above50 = pctTrue('above50');
            const above200 = pctTrue('above200');

            // Advancers / decliners (today).
            const withDay = rows.filter(r => Number.isFinite(r.pct1d));
            const adv = withDay.filter(r => r.pct1d > 0).length;
            const dec = withDay.filter(r => r.pct1d < 0).length;
            const advPct = withDay.length ? adv / withDay.length * 100 : null;

            // RS Thrust Rate: share of universe whose short-term RS is accelerating —
            // 1-week performance running ahead of 1-month (momentum broadening).
            const withThrust = rows.filter(r => Number.isFinite(r.pct1w) && Number.isFinite(r.pct1m));
            const thrusting = withThrust.filter(r => r.pct1w > r.pct1m && r.pct1w > 0).length;
            const thrustRate = withThrust.length ? thrusting / withThrust.length * 100 : null;

            // ── LEADERSHIP BREADTH (the money metric) ────────────────────────────────
            // Aggregates the per-row RS-line intelligence we already compute: how broad is
            // genuine LEADERSHIP right now, not just participation. A market with many RS
            // new highs — and especially many Delta (RS NH BEFORE price, leading accumulation)
            // — is a stockpicker's market where breakout setups work. Few = narrow/risky.
            const withRs = rows.filter(r => typeof r.rsNewHigh === 'boolean');
            const rsNhPct = withRs.length ? withRs.filter(r => r.rsNewHigh).length / withRs.length * 100 : null;
            const deltaCount = rows.filter(r => r.deltaTrigger === true).length;            // RS NH before price
            const rsNhCount = withRs.filter(r => r.rsNewHigh).length;
            // Primed-or-better leaders (RS NH / Delta / Primed) from the RS Power tier.
            const leaderCount = rows.filter(r => ['Delta', 'RS NH', 'Primed'].includes(r.rsPowerTier)).length;

            // ── RS THRUST DEPTH ──────────────────────────────────────────────────────
            // Distribution of RS Rating (cross-sectional 1-99 percentile) across the universe.
            // rsRating is set by the server's finalize pass; if a partial/stale row set lacks it,
            // derive the same percentile client-side from rsRaw (mirrors finalizeRsPercentiles in
            // lib/pipeline.js) so RS≥80 / RS≥90 / Median never blank while rsRaw is present.
            const _rsRaws = rows.map(r => r.rsRaw).filter(Number.isFinite).sort((a, b) => a - b);
            const _rsPctFromRaw = (v) => {
                let lo = 0, hi = _rsRaws.length;
                while (lo < hi) { const mid = (lo + hi) >> 1; if (_rsRaws[mid] < v) lo = mid + 1; else hi = mid; }
                return Math.max(1, Math.min(99, Math.round((lo / _rsRaws.length) * 100)));
            };
            const rsVals = rows.map(r => Number.isFinite(r.rsRating) ? r.rsRating
                    : (_rsRaws.length && Number.isFinite(r.rsRaw)) ? _rsPctFromRaw(r.rsRaw) : null)
                .filter(Number.isFinite);
            const rsStrongPct = rsVals.length ? rsVals.filter(v => v >= 80).length / rsVals.length * 100 : null;
            const rsElitePct  = rsVals.length ? rsVals.filter(v => v >= 90).length / rsVals.length * 100 : null;
            const rsMedian = rsVals.length ? (() => {
                const s = [...rsVals].sort((a, b) => a - b); const mid = Math.floor(s.length / 2);
                return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
            })() : null;

            // Leadership ladder: how the universe spreads across the RS Power tiers.
            const tierOrder = ['Delta', 'RS NH', 'Primed', 'Building', 'Firm', 'Lagging'];
            const tierLadder = {}; tierOrder.forEach(t => tierLadder[t] = 0);
            rows.forEach(r => { if (r.rsPowerTier && tierLadder.hasOwnProperty(r.rsPowerTier)) tierLadder[r.rsPowerTier]++; });
            const tierLadderTotal = tierOrder.reduce((a, t) => a + tierLadder[t], 0);

            // Live RS signals firing right now (the "RS alerts"): Alpha/Beta buy triggers +
            // fresh Delta (RS new high before price within the last 3 sessions).
            const alphaCount = rows.filter(r => r.hasAlpha).length;
            const betaCount  = rows.filter(r => r.hasBeta).length;
            const freshDelta = rows.filter(r => r.deltaTrigger === true && Number.isFinite(r.deltaDays) && r.deltaDays <= 3).length;

            // Leader RVOL: median relative volume of the genuine leaders (RS≥80 or RS-NH /
            // Delta). The "fuel" axis — high = leaders being actively accumulated right now;
            // low = the advance is running on drift, not institutional commitment.
            const leaderRvols = rows
                .filter(r => (Number.isFinite(r.rsRating) && r.rsRating >= 80) || r.rsPowerTier === 'Delta' || r.rsPowerTier === 'RS NH')
                .map(r => r.rvol).filter(v => Number.isFinite(v) && v > 0).sort((a, b) => a - b);
            let leaderRvol = null;
            if (leaderRvols.length) {
                const mid = Math.floor(leaderRvols.length / 2);
                leaderRvol = leaderRvols.length % 2 ? leaderRvols[mid] : (leaderRvols[mid - 1] + leaderRvols[mid]) / 2;
            }

            // Composite RS Thrust Index (0-100): blends acceleration breadth, leadership
            // breadth, elite-RS density and leading accumulation into one regime read.
            let rsThrustScore = null;
            {
                const parts = [];
                if (Number.isFinite(thrustRate))   parts.push([thrustRate, 0.30]);
                if (Number.isFinite(rsNhPct))      parts.push([Math.min(100, rsNhPct * 2.2), 0.28]);
                if (Number.isFinite(rsStrongPct))  parts.push([Math.min(100, rsStrongPct * 2.5), 0.22]);
                if (Number.isFinite(deltaCount))   parts.push([Math.min(100, deltaCount * 1.5), 0.20]);
                if (parts.length) {
                    const wsum = parts.reduce((a, p) => a + p[1], 0);
                    rsThrustScore = Math.round(parts.reduce((a, p) => a + p[0] * p[1], 0) / wsum);
                }
            }
            const rsThrustState = rsThrustScore == null ? null
                : rsThrustScore >= 62 ? { label: 'Expanding',   cls: 'text-emerald-300', bar: 'bg-emerald-400' }
                : rsThrustScore >= 42 ? { label: 'Steady',      cls: 'text-blue-300',    bar: 'bg-blue-400' }
                : rsThrustScore >= 25 ? { label: 'Cooling',     cls: 'text-amber-300',   bar: 'bg-amber-400' }
                : { label: 'Contracting', cls: 'text-rose-300', bar: 'bg-rose-500' };

            // ── SECTOR RS LEADERSHIP (compact; full rotation lives in the Group RS tab) ──
            const sectorMap = new Map();
            rows.forEach(r => {
                const sec = r.sector;
                if (!sec || sec === 'Unknown' || sec === 'Fund') return;
                let o = sectorMap.get(sec);
                if (!o) { o = { sector: sec, n: 0, rsSum: 0, rsN: 0, nh: 0, delta: 0 }; sectorMap.set(sec, o); }
                o.n++;
                if (Number.isFinite(r.rsRating)) { o.rsSum += r.rsRating; o.rsN++; }
                if (r.rsNewHigh) o.nh++;
                if (r.deltaTrigger) o.delta++;
            });
            const sectorRs = [...sectorMap.values()]
                .filter(o => o.n >= 5) // ignore thinly-covered sectors
                .map(o => {
                    const avgRs = o.rsN ? o.rsSum / o.rsN : null;
                    const nhPct = o.n ? o.nh / o.n * 100 : 0;
                    return { sector: o.sector, n: o.n, avgRs, nhPct, delta: o.delta,
                             score: (avgRs ?? 0) + nhPct * 0.25 };
                })
                .sort((a, b) => b.score - a.score);

            // --- Index-derived structure (only if benchmarks loaded) ---
            let rspRatio = null, rspSlope = null, rspTrend = null;
            const spy = marketStructureIndices['SPY'], rsp = marketStructureIndices['RSP'];
            if (spy && rsp && spy.closes.length > 25 && rsp.closes.length > 25) {
                const L = Math.min(spy.closes.length, rsp.closes.length);
                const ratioSeries = [];
                for (let i = 0; i < L; i++) {
                    const s = spy.closes[spy.closes.length - L + i], r = rsp.closes[rsp.closes.length - L + i];
                    if (s) ratioSeries.push(r / s);
                }
                rspRatio = ratioSeries[ratioSeries.length - 1];
                const ago = ratioSeries[ratioSeries.length - 22] || ratioSeries[0];
                rspSlope = ago ? (rspRatio - ago) / ago * 100 : null;
                rspTrend = rspSlope > 0.3 ? 'up' : rspSlope < -0.3 ? 'down' : 'flat';
            }

            const atrRegime = {};
            ['SPY', 'QQQ', 'IWM'].forEach(t => {
                const idx = marketStructureIndices[t];
                if (!idx || idx.closes.length < 60) { atrRegime[t] = null; return; }
                const atrSer = MathLib.atrSeries(idx.highs, idx.lows, idx.closes, 14);
                const atrPctSer = atrSer.map((a, i) => (a != null && idx.closes[i]) ? a / idx.closes[i] * 100 : null);
                const last = [...atrPctSer].reverse().find(Number.isFinite);
                const pctile = percentileOfLast(atrPctSer.slice(-252));
                atrRegime[t] = { atrPct: last, pctile };
            });

            // ── Composite RISK-ON / RISK-OFF regime  R ∈ [−1,+1] ─────────────────
            // Orthogonal dimensions, each normalised to [−1,+1], then weighted. This is
            // the ONE number the verdict, the risk gauge, and the score tape all read —
            // no second opinion. (Volatility folds in at step 2; neutral-weighted for now.)
            const _cl = v => Math.max(-1, Math.min(1, v));
            const _avg = a => { const f = a.filter(x => x != null && Number.isFinite(x)); return f.length ? f.reduce((s, x) => s + x, 0) / f.length : null; };
            const dBreadth = _avg([
                above50 != null ? _cl((above50 - 50) / 50) : null,
                above200 != null ? _cl((above200 - 50) / 50) : null
            ]);
            const dThrust = _avg([
                thrustRate != null ? _cl((thrustRate - 35) / 35) : null,
                advPct != null ? _cl((advPct - 50) / 50) : null
            ]);
            const dLead = _avg([
                rspSlope != null ? _cl(Math.tanh(rspSlope / 0.6)) : (rspTrend === 'up' ? 0.5 : rspTrend === 'down' ? -0.5 : null),
                rsNhPct != null ? _cl((rsNhPct - 15) / 25) : null,
                deltaCount ? _cl(deltaCount / 12) : null
            ]);
            let dTrend = null;
            if (spy && spy.closes && spy.closes.length > 50) {
                const cl = spy.closes, last = cl[cl.length - 1];
                const sma = n => { const s = cl.slice(-n); return s.reduce((a, b) => a + b, 0) / s.length; };
                const s50 = sma(50), s200 = sma(Math.min(cl.length, 200));
                const posture = (last > s50 ? 0.5 : -0.5) + (last > s200 ? 0.5 : -0.5);
                const slope50 = (s50 - sma(Math.min(cl.length, 60))) / (s50 || 1e-9) * 100;
                dTrend = _cl(posture + _cl(Math.tanh(slope50)) * 0.3);
            }
            // Volatility (graded VIX): VIXY below / falling vs its 20-mean = calm = risk-on (+);
            // elevated / rising = risk-off (−). Replaces the old vixLow/vixHigh booleans.
            let dVol = null;
            const _vix = marketStructureIndices && marketStructureIndices['VIXY'];
            if (_vix && _vix.closes && _vix.closes.length > 20) {
                const vc = _vix.closes, vlast = vc[vc.length - 1], vprev = vc[vc.length - 6];
                const vsma = vc.slice(-20).reduce((a, b) => a + b, 0) / 20;
                const levelSig = vsma ? _cl((vsma - vlast) / vsma * 3) : 0;   // below its mean = calm
                const slopeSig = vprev ? _cl((vprev - vlast) / vprev * 3) : 0; // falling = calming
                dVol = _cl((levelSig + slopeSig) / 2);
            }
            const regimeDims = { breadth: dBreadth, thrust: dThrust, leadership: dLead, trend: dTrend, volatility: dVol };
            const _wd = [[dBreadth, 0.34], [dThrust, 0.14], [dLead, 0.30], [dTrend, 0.22], [dVol, 0.20]];
            let _wsum = 0, _rsum = 0;
            for (const [v, w] of _wd) if (v != null) { _wsum += w; _rsum += v * w; }
            const regimeRaw = _wsum ? _cl(_rsum / _wsum) : 0;

            // Hysteresis — regimes last weeks, not hours. EMA-blend against the last
            // COMMITTED value (committed once per day), so repeated intra-day recomputes
            // stay stable instead of drifting toward the raw reading.
            let regimeR = regimeRaw;
            try {
                const saved = JSON.parse(localStorage.getItem('abRegimeR') || 'null');
                const today = new Date().toISOString().slice(0, 10);
                if (saved && Number.isFinite(saved.val)) regimeR = _cl(0.7 * saved.val + 0.3 * regimeRaw);
                if (!saved || saved.day !== today) localStorage.setItem('abRegimeR', JSON.stringify({ val: +regimeR.toFixed(4), day: today }));
            } catch (e) { }

            const score = Math.round(regimeR * 100); // legacy-compatible scale (−100..+100)
            let verdict, verdictCls;
            if (regimeR > 0.35) { verdict = 'Risk-On · Broad'; verdictCls = 'text-emerald-300 bg-emerald-900/40 border-emerald-500/40'; }
            else if (regimeR > 0.10) { verdict = 'Constructive'; verdictCls = 'text-emerald-400 bg-emerald-900/25 border-emerald-700/40'; }
            else if (regimeR > -0.10) { verdict = 'Mixed / Neutral'; verdictCls = 'text-amber-300 bg-amber-900/30 border-amber-600/40'; }
            else if (regimeR > -0.35) { verdict = 'Caution · Narrowing'; verdictCls = 'text-orange-300 bg-orange-900/30 border-orange-600/40'; }
            else { verdict = 'Risk-Off · Weak'; verdictCls = 'text-rose-300 bg-rose-900/40 border-rose-500/40'; }

            return { n, above20, above50, above200, adv, dec, advPct, thrustRate, rspRatio, rspSlope, rspTrend, atrRegime, verdict, verdictCls,
                regimeR, regimeRaw, regimeDims, score,
                rsNhPct, rsNhCount, deltaCount, leaderCount,
                rsStrongPct, rsElitePct, rsMedian, tierLadder, tierLadderTotal, tierOrder,
                alphaCount, betaCount, freshDelta, rsThrustScore, rsThrustState, sectorRs, leaderRvol };
        }

        function breadthColor(v) {
            if (!Number.isFinite(v)) return 'text-slate-500';
            if (v >= 60) return 'text-emerald-300';
            if (v >= 45) return 'text-emerald-400';
            if (v >= 30) return 'text-amber-400';
            return 'text-rose-400';
        }
        function breadthBar(v) {
            if (!Number.isFinite(v)) return 'bg-slate-700';
            if (v >= 60) return 'bg-emerald-400';
            if (v >= 45) return 'bg-emerald-500';
            if (v >= 30) return 'bg-amber-500';
            return 'bg-rose-500';
        }

        // ── RS cohort → filter the right-side screener list ──────────────────────
        // One click on any leadership cell (ladder tier, Alpha/Beta/Delta, sector) filters
        // the SCREENER tab to exactly those names, using the already-enriched rows (no API
        // calls). A removable chip shows the active filter.

        function rsCohortPredicate(c) {
            if (!c) return null;
            const sector = c.sector;
            switch (c.key) {
                case 'delta':      return r => r.rsPowerTier === 'Delta' || r.deltaTrigger === true;
                case 'rsnh':       return r => r.rsPowerTier === 'RS NH';
                case 'rsnh_all':   return r => r.rsNewHigh === true || r.rsPowerTier === 'RS NH' || r.rsPowerTier === 'Delta';
                case 'primed':     return r => r.rsPowerTier === 'Primed';
                case 'building':   return r => r.rsPowerTier === 'Building';
                case 'firm':       return r => r.rsPowerTier === 'Firm';
                case 'lagging':    return r => r.rsPowerTier === 'Lagging';
                case 'leaders':    return r => ['Delta', 'RS NH', 'Primed'].includes(r.rsPowerTier);
                case 'alpha':      return r => !!r.hasAlpha;
                case 'beta':       return r => !!r.hasBeta;
                case 'freshDelta': return r => r.deltaTrigger === true && Number.isFinite(r.deltaDays) && r.deltaDays <= 3;
                case 'sector':     return r => r.sector === sector;
                default: return null;
            }
        }
        function rsCohortLabel(c) {
            if (!c) return '';
            const map = { delta: 'Delta Leaders', rsnh: 'RS New Highs', rsnh_all: 'RS New Highs', primed: 'Primed', building: 'Building',
                firm: 'Firm', lagging: 'Lagging RS', leaders: 'RS Leaders', alpha: 'Alpha Firing',
                beta: 'Beta Firing', freshDelta: 'Fresh Delta \u22643d' };
            return c.key === 'sector' ? (c.sector + ' \u00b7 RS') : (map[c.key] || c.key);
        }
        function renderScreenerCohortChip() {
            const wrap = document.getElementById('screenerCohortChip');
            const lbl = document.getElementById('screenerCohortLabel');
            if (!wrap || !lbl) return;
            if (state.screenerRsCohort) { lbl.textContent = '\u25c7 ' + rsCohortLabel(state.screenerRsCohort); wrap.classList.remove('hidden'); }
            else wrap.classList.add('hidden');
        }
        function clearScreenerCohort() {
            state.screenerRsCohort = null;
            renderScreenerCohortChip();
            try { applyScreenerFilters(); } catch (e) {}
        }
        function showRsCohort(key, sectorName) {
            state.screenerRsCohort = { key, sector: sectorName || null };
            try { setHubTab('screener'); } catch (e) {}
            renderScreenerCohortChip();
            try { applyScreenerFilters(); } catch (e) {}
            try { const c = document.querySelector('.hub-screener.overflow-y-auto'); if (c) c.scrollTop = 0; } catch (e) {}
        }

        // ── RS strength history (for growth/shrinking sparklines) ────────────────
        // One point PER SCAN so the sparklines fill in as you re-scan (a daily-only point
        // would never form a line within a session). Near-identical consecutive readings are
        // de-duped. Capped to the last 30. No network — fed by computeMarketStructure.
        const RS_HISTORY_KEY = 'abRsStrengthHistoryV2';
        function getRsStrengthHistory() {
            try { const raw = localStorage.getItem(RS_HISTORY_KEY); const a = raw ? JSON.parse(raw) : []; return Array.isArray(a) ? a : []; }
            catch (e) { return []; }
        }
        function recordRsStrengthSnapshot(m) {
            if (!m || !m.n) return;
            const num = v => Number.isFinite(v) ? Math.round(v * 10) / 10 : null;
            const snap = {
                d: new Date().toISOString().split('T')[0], ts: Date.now(),
                thrustIndex: num(m.rsThrustScore), thrustRate: num(m.thrustRate),
                rsStrongPct: num(m.rsStrongPct), rsElitePct: num(m.rsElitePct), rsMedian: num(m.rsMedian),
                deltaCount: Number.isFinite(m.deltaCount) ? m.deltaCount : null,
                rsNhPct: num(m.rsNhPct),
                above50: num(m.above50),
                leaderRvol: num(m.leaderRvol),
                alphaCount: Number.isFinite(m.alphaCount) ? m.alphaCount : null,
                betaCount: Number.isFinite(m.betaCount) ? m.betaCount : null
            };
            let hist = getRsStrengthHistory();
            const last = hist[hist.length - 1];
            // Skip a duplicate if nothing material changed and it's within ~1min (guards against
            // the double computeMarketStructure call / rapid re-renders adding flat points).
            if (last && (snap.ts - last.ts) < 60000 &&
                last.thrustIndex === snap.thrustIndex && last.rsStrongPct === snap.rsStrongPct &&
                last.deltaCount === snap.deltaCount && last.rsNhPct === snap.rsNhPct) return;
            hist.push(snap);
            if (hist.length > 30) hist = hist.slice(-30);
            try { localStorage.setItem(RS_HISTORY_KEY, JSON.stringify(hist)); } catch (e) {}
        }

        // ── Real 21-day Leadership-vs-Participation reconstruction ───────────────
        // Built from the daily bars each scan already downloads, so ONE scan draws a full,
        // correct chart (no scan-snapshot timeline). For each enriched ticker we tally,
        // across the last 21 trading days: % above its 50-day MA (participation) and whether
        // its RS line vs the benchmark was at a 63-day high (leadership). Aggregated across
        // the universe → two daily breadth series. All synchronous; fed inside the scan loop.
        const RS_BREADTH_DAYS = 21;
        const RS_BREADTH_NH_LB = 63;
        let rsBreadthSeries = null;
        try { const s = JSON.parse(localStorage.getItem('abRsBreadthSeriesV2') || 'null'); if (s && Array.isArray(s.part) && Array.isArray(s.lead)) rsBreadthSeries = s; } catch (e) {}

        // Per-ticker 21-day contribution, packed into 4 small bitmasks (bit k = day k):
        //   p = above 50-day MA · l = RS line at 63-day high · w = RS line at 63-day low
        //   d = day had a valid RS line (the leadership denominator)
        // Stored on the row + cache, so finalize can sum across EVERY row (fresh or cached),
        // making the chart build reliably even on cache-reuse re-scans.
        // Build the 21-day breadth series by summing every enriched row's bitmasks.
        function finalizeRsBreadthSeries() {
            try {
                const rows = (typeof state.screenerAllResults !== 'undefined' ? state.screenerAllResults : []).filter(r => r && Number.isFinite(r.brdP));
                if (rows.length < 150) return rsBreadthSeries; // too few — keep the prior good series
                const N = RS_BREADTH_DAYS, total = rows.length;
                const partNum = new Array(N).fill(0), leadNum = new Array(N).fill(0), leadDen = new Array(N).fill(0), lowNum = new Array(N).fill(0);
                for (const r of rows) {
                    const bp = r.brdP | 0, bl = r.brdL | 0, bd = r.brdD | 0, bw = r.brdW | 0;
                    for (let k = 0; k < N; k++) {
                        const m = 1 << k;
                        if (bp & m) partNum[k]++;
                        if (bd & m) { leadDen[k]++; if (bl & m) leadNum[k]++; if (bw & m) lowNum[k]++; }
                    }
                }
                const part = [], lead = [], low = [];
                for (let k = 0; k < N; k++) {
                    part.push(total >= 30 ? partNum[k] / total * 100 : null);
                    lead.push(leadDen[k] >= 30 ? leadNum[k] / leadDen[k] * 100 : null);
                    low.push(leadDen[k] >= 30 ? lowNum[k] / leadDen[k] * 100 : null);
                }
                rsBreadthSeries = { part, lead, low, days: N, coverage: total, ts: Date.now() };
                try { localStorage.setItem('abRsBreadthSeriesV2', JSON.stringify(rsBreadthSeries)); } catch (e) {}
                return rsBreadthSeries;
            } catch (e) { return rsBreadthSeries; }
        }

        function renderMarketStructure() {
            const root = document.getElementById('marketStructureBody');
            if (!root) return;
            // Prefer the SERVER-computed regime (single source of truth) so the panel AND the engine context
            // (used to score the loaded ticker) reflect the SAME tape the screener rows were scored with.
            // Fall back to the client compute only when no server regime was shipped (old server / pre-scan).
            const m = serverMarketStructure || computeMarketStructure();
            try { lastMarketTape = m && m.verdict ? m.verdict : lastMarketTape; } catch (e) {}
            engineState.lastMarketStructure = m && m.n ? m : null;
            setEngineContext({ lastMarketStructure: engineState.lastMarketStructure }); // push into the shared engine (by ref; later mutations propagate)
            _ratingMktEpoch++; // market inputs changed → row-rating cache must refresh
            try { renderRegimeGauge(); } catch (e) { }

            if (!m.n) {
                root.innerHTML = `<div class="flex flex-col items-center justify-center h-full text-center py-16 px-6">
                    <div class="text-slate-300 font-bold text-sm">Awaiting market scan</div>
                    <div class="text-slate-500 text-xs mt-1 max-w-xs">Breadth populates automatically as the screener enriches the universe. Click "Load Index Data" on the Benchmarks panel to add RSP/SPY divergence and ATR% regime.</div>
                </div>`;
                return;
            }

            const gauge = (label, v, sub, tip) => `
                <div class="bg-slate-900/50 rounded-xl border border-slate-800 p-3${tip ? ' cursor-help' : ''}"${tip ? ` title="${tip}"` : ''}>
                    <div class="flex items-baseline justify-between mb-1.5">
                        <span class="text-[10px] uppercase tracking-wider text-slate-500 font-bold">${label}</span>
                        <span class="text-lg font-black tabular-nums ${breadthColor(v)}">${Number.isFinite(v) ? v.toFixed(0) + '%' : '--'}</span>
                    </div>
                    <div class="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                        <div class="h-full ${breadthBar(v)} rounded-full transition-all duration-500" style="width:${Number.isFinite(v) ? Math.max(2, Math.min(100, v)) : 0}%"></div>
                    </div>
                    ${sub ? `<div class="text-[9px] text-slate-500 mt-1">${sub}</div>` : ''}
                </div>`;

            const adRatio = m.dec ? (m.adv / m.dec) : (m.adv ? Infinity : 0);
            const adColor = m.advPct >= 55 ? 'text-emerald-300' : m.advPct >= 45 ? 'text-amber-300' : 'text-rose-300';

            // RSP/SPY block.
            let rspBlock = `<div class="text-[10px] text-slate-500 italic">Load Index Data to compute equal-weight divergence.</div>`;
            if (Number.isFinite(m.rspRatio)) {
                const arrow = m.rspTrend === 'up' ? '▲' : m.rspTrend === 'down' ? '▼' : '▶';
                const col = m.rspTrend === 'up' ? 'text-emerald-300' : m.rspTrend === 'down' ? 'text-rose-300' : 'text-slate-300';
                const msg = m.rspTrend === 'up' ? 'Equal-weight leading — broad participation' : m.rspTrend === 'down' ? 'Cap-weight leading — megacap-driven, narrow' : 'In line — no clear divergence';
                rspBlock = `<div class="flex items-center justify-between">
                        <div><span class="text-2xl font-black tabular-nums ${col}">${arrow} ${Number.isFinite(m.rspSlope) ? (m.rspSlope > 0 ? '+' : '') + m.rspSlope.toFixed(2) + '%' : '--'}</span>
                        <span class="text-[10px] text-slate-500 ml-1">21d</span></div>
                        <span class="text-[10px] tabular-nums text-slate-500">ratio ${m.rspRatio.toFixed(4)}</span>
                    </div>
                    <div class="text-[10px] ${col} mt-1">${msg}</div>`;
            }

            // ATR% regime block.
            const atrCard = (t) => {
                const a = m.atrRegime[t];
                if (!a || !Number.isFinite(a.atrPct)) return `<div class="bg-slate-900/50 rounded-lg border border-slate-800 p-2 text-center"><div class="text-[10px] font-bold text-slate-300">${t}</div><div class="text-[10px] text-slate-600 mt-1">--</div></div>`;
                const p = a.pctile;
                const regime = p == null ? '' : p >= 70 ? 'Expanded' : p >= 40 ? 'Normal' : 'Compressed';
                const rc = p == null ? 'text-slate-400' : p >= 70 ? 'text-rose-400' : p >= 40 ? 'text-amber-400' : 'text-emerald-400';
                return `<div class="bg-slate-900/50 rounded-lg border border-slate-800 p-2 text-center">
                        <div class="text-[10px] font-bold text-slate-300">${t}</div>
                        <div class="text-base font-black tabular-nums text-white mt-0.5">${a.atrPct.toFixed(2)}%</div>
                        <div class="text-[9px] ${rc} font-semibold">${regime}${p != null ? ` · ${p}%ile` : ''}</div>
                    </div>`;
            };

            // ── RS THRUST & LEADERSHIP builders ──────────────────────────────────────
            const RS_TIER_DOT = {
                'Delta': 'bg-fuchsia-400', 'RS NH': 'bg-emerald-400', 'Primed': 'bg-teal-400',
                'Building': 'bg-blue-400', 'Firm': 'bg-slate-500', 'Lagging': 'bg-amber-500/70'
            };
            const RS_TIER_TXT = {
                'Delta': 'text-fuchsia-300', 'RS NH': 'text-emerald-300', 'Primed': 'text-teal-300',
                'Building': 'text-blue-300', 'Firm': 'text-slate-400', 'Lagging': 'text-amber-300/80'
            };
            const rsMini = (label, val, sub, cls, clickKey, tip) => `
                <div class="bg-slate-950/60 rounded-lg border border-slate-800/80 p-2 ${clickKey ? 'cursor-pointer hover:border-blue-600/60 hover:bg-blue-950/20 transition-colors' : (tip ? 'cursor-help' : '')}"${clickKey ? ` onclick="showRsCohort('${clickKey}')"` : ''}${tip ? ` title="${tip}"` : ''}>
                    <div class="text-[8px] uppercase tracking-wider text-slate-500 font-bold leading-tight">${label}${clickKey ? ' <span class="text-blue-400/70">↗</span>' : ''}</div>
                    <div class="text-base font-black tabular-nums ${cls || 'text-slate-200'} mt-0.5">${val}</div>
                    ${sub ? `<div class="text-[8px] text-slate-600 leading-tight mt-0.5">${sub}</div>` : ''}
                </div>`;

            // Leadership-ladder stacked bar + legend, from the per-row RS Power tiers.
            const ladderTotal = m.tierLadderTotal || 0;
            const ladderBar = ladderTotal ? m.tierOrder.map(t => {
                const c = m.tierLadder[t]; if (!c) return '';
                const w = c / ladderTotal * 100;
                return `<div class="${RS_TIER_DOT[t]} h-full" style="width:${w}%" title="${t}: ${c}"></div>`;
            }).join('') : '';
            const TIER_KEY = { 'Delta': 'delta', 'RS NH': 'rsnh', 'Primed': 'primed', 'Building': 'building', 'Firm': 'firm', 'Lagging': 'lagging' };
            const ladderLegend = m.tierOrder.map(t => {
                const c = m.tierLadder[t] || 0;
                const clickable = c > 0;
                return `<span ${clickable ? `onclick="showRsCohort('${TIER_KEY[t]}')" ` : ''}class="inline-flex items-center gap-1 text-[9px] ${c ? RS_TIER_TXT[t] : 'text-slate-600'} ${clickable ? 'cursor-pointer hover:underline' : ''}">
                    <span class="w-1.5 h-1.5 rounded-full ${c ? RS_TIER_DOT[t] : 'bg-slate-700'}"></span>${t}<span class="tabular-nums font-bold">${c}</span></span>`;
            }).join('');

            // Sector RS rows (leaders on top, laggards at the bottom). Full table = Group RS tab.
            // ── Sector Money Flow · Rotation ─────────────────────────────────────────
            // Reuses the Group RS rotation engine at SECTOR granularity (peek mode, no
            // global clobber): rotationScore = short-term RS pulling ahead of long-term,
            // confirmed by GRVOL (group volume vs 50-day). Inflow = capital arriving,
            // Outflow = capital leaving — the literal "from where → to where".
            let sectorFlow = [];
            try { sectorFlow = (computeGroupRankings('sector', false) || []).filter(g => g.members >= 5 && Number.isFinite(g.rotationScore)); } catch (e) { sectorFlow = []; }
            const inflow = [...sectorFlow].sort((a, b) => b.rotationScore - a.rotationScore).slice(0, 4);
            const outflow = [...sectorFlow].sort((a, b) => a.rotationScore - b.rotationScore).slice(0, 3);
            const flowRow = g => {
                const rot = g.rotationScore;
                const arrow = rot > 8 ? '▲' : rot < -8 ? '▼' : '▸';
                const rotCls = rot > 8 ? 'text-emerald-300' : rot < -8 ? 'text-rose-300' : 'text-slate-400';
                const gv = g.grvol;
                const gvCls = gv >= 1.3 ? 'text-emerald-300' : gv >= 1 ? 'text-blue-300' : 'text-slate-500';
                const mag = Math.max(4, Math.min(100, Math.abs(rot) * 2));
                const barCls = rot >= 0 ? 'bg-emerald-400' : 'bg-rose-500';
                const tagTxt = g.rotationTag === 'in' ? 'Money rotating IN — short-term RS accelerating on heavy group volume.'
                    : g.rotationTag === 'out' ? 'Money rotating OUT — short-term RS deteriorating.'
                    : g.rotationTag === 'leader' ? 'Established leader — high long-term RS, steady.'
                    : g.rotationTag === 'dormant' ? 'Dormant — weak and quiet.' : 'Neutral.';
                return `<div onclick="showRsCohort('sector','${g.name.replace(/'/g, '')}')" class="flex items-center gap-2 py-1 cursor-pointer hover:bg-blue-950/20 rounded px-1 -mx-1 transition-colors" title="${g.name} · ${g.members} names · rotation ${rot > 0 ? '+' : ''}${rot} · GRVOL ${Number.isFinite(gv) ? gv.toFixed(2) + '×' : '--'} · RS ${Number.isFinite(g.rsComposite) ? g.rsComposite : '--'}. ${tagTxt} Click to list the names.">
                    <span class="${rotCls} w-3 text-center text-[11px]">${arrow}</span>
                    <span class="text-[11px] font-bold text-slate-200 w-28 truncate">${g.name}</span>
                    <div class="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden"><div class="h-full ${barCls} rounded-full" style="width:${mag}%"></div></div>
                    <span class="text-[11px] font-black tabular-nums ${rotCls} w-8 text-right">${rot > 0 ? '+' : ''}${rot}</span>
                    <span class="text-[9px] tabular-nums ${gvCls} w-11 text-right">${Number.isFinite(gv) ? gv.toFixed(2) + '×' : '--'}</span>
                </div>`;
            };
            const sectorBlock = sectorFlow.length ? `
                <div class="bg-slate-900/50 rounded-xl border border-slate-800 p-3">
                    <div class="flex items-center justify-between mb-1.5">
                        <span class="text-[10px] uppercase tracking-wider text-slate-500 font-bold cursor-help" title="Where money is rotating, by sector — from the Group RS engine. Rotation score = short-term RS pulling ahead of long-term, multiplied by GRVOL (group volume ÷ 50-day baseline). Inflow = capital arriving (chase these); Outflow = capital leaving (avoid / exit). The ROT number and arrow show the direction; GRVOL confirms it's real money. Click a sector to list its names.">Sector Money Flow · Rotation</span>
                        <button onclick="setHubTab('groups')" class="text-[9px] text-blue-400 hover:text-blue-300 font-semibold">full rotation in Group RS →</button>
                    </div>
                    <div class="text-[8px] uppercase tracking-wider text-emerald-400/80 font-bold mb-0.5">Inflow · money in →</div>
                    ${inflow.map(flowRow).join('')}
                    <div class="text-[8px] uppercase tracking-wider text-rose-400/80 font-bold mt-2 mb-0.5">Outflow · money out ←</div>
                    ${outflow.map(flowRow).join('')}
                    <div class="text-[8px] text-slate-600 mt-1.5">ROT = rotation score (RS acceleration × volume) · GRVOL = group volume vs 50-day. Heavy GRVOL confirms the flow is real.</div>
                </div>` : '';

            // ── strength-trend builders (sparklines + Δ vs previous scan) ────────────
            const rsHist = getRsStrengthHistory();
            const histSeries = key => rsHist.map(h => h[key]).filter(Number.isFinite);
            const rsTrend = series => {
                const s = series.filter(Number.isFinite);
                if (s.length < 2) return { delta: null, dir: 'flat' };
                const d = s[s.length - 1] - s[s.length - 2];
                return { delta: d, dir: d > 0.05 ? 'up' : d < -0.05 ? 'down' : 'flat' };
            };
            const sparkSvg = (series, dir, w) => {
                const s = series.filter(Number.isFinite);
                if (s.length < 2) return '<div class="text-[8px] text-slate-600">builds over scans</div>';
                const W = w || 120, H = 26, pad = 3;
                const min = Math.min(...s), max = Math.max(...s), rng = (max - min) || 1;
                const col = dir === 'down' ? '#fb7185' : dir === 'up' ? '#34d399' : '#94a3b8';
                const pts = s.map((v, i) => {
                    const x = pad + i * (W - 2 * pad) / (s.length - 1);
                    const y = H - pad - (v - min) / rng * (H - 2 * pad);
                    return x.toFixed(1) + ',' + y.toFixed(1);
                });
                const line = pts.join(' ');
                const area = pad + ',' + (H - pad) + ' ' + line + ' ' + (W - pad) + ',' + (H - pad);
                const last = pts[pts.length - 1].split(',');
                return `<svg width="100%" height="${H}" viewBox="0 0 ${W} ${H}" preserveAspectRatio="none" style="display:block;overflow:visible">
                    <polygon points="${area}" fill="${col}" opacity="0.13"></polygon>
                    <polyline points="${line}" fill="none" stroke="${col}" stroke-width="1.6" stroke-linejoin="round" stroke-linecap="round"></polyline>
                    <circle cx="${last[0]}" cy="${last[1]}" r="2" fill="${col}"></circle></svg>`;
            };
            const deltaTxt = t => {
                if (t.delta == null) return '';
                const ad = Math.abs(t.delta);
                const a = ad < 1 ? ad.toFixed(1) : Math.round(ad);
                return t.dir === 'up' ? `▲ +${a}` : t.dir === 'down' ? `▼ −${a}` : '▸ 0';
            };
            const deltaCls = t => t.dir === 'up' ? 'text-emerald-300' : t.dir === 'down' ? 'text-rose-300' : 'text-slate-500';
            const rsTrendStat = (label, valueStr, sub, valCls, key, tip) => {
                const series = histSeries(key);
                const t = rsTrend(series);
                return `<div class="bg-slate-950/60 rounded-lg border border-slate-800/80 p-2${tip ? ' cursor-help' : ''}"${tip ? ` title="${tip}"` : ''}>
                    <div class="text-[8px] uppercase tracking-wider text-slate-500 font-bold leading-tight">${label}</div>
                    <div class="flex items-baseline gap-1.5 mt-0.5">
                        <span class="text-base font-black tabular-nums ${valCls}">${valueStr}</span>
                        <span class="text-[9px] font-bold ${deltaCls(t)}">${deltaTxt(t)}</span>
                    </div>
                    <div class="mt-1">${sparkSvg(series, t.dir)}</div>
                    ${sub ? `<div class="text-[8px] text-slate-600 leading-tight mt-0.5">${sub}</div>` : ''}
                </div>`;
            };
            const thrustSeries = histSeries('thrustIndex');
            const thrustTrend = rsTrend(thrustSeries);
            // Expose the thrust RHYTHM (direction + Δ) to the rating engine — the panel
            // computes it for the sparkline; the conviction layer reads direction-over-level.
            if (engineState.lastMarketStructure) engineState.lastMarketStructure.rsThrustTrend = thrustTrend;
            if (m && m !== engineState.lastMarketStructure) m.rsThrustTrend = thrustTrend;

            // ── Leadership vs Participation (real 21-day reconstruction) ─────────────
            const buildDivergenceCard = () => {
                const ph = msg => `<div class="bg-slate-900/50 rounded-xl border border-slate-800 p-3">
                    <div class="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Leadership vs Participation</div>
                    <div class="text-[10px] text-slate-600 py-6 text-center">${msg}</div></div>`;
                const series = rsBreadthSeries;
                if (!series || !Array.isArray(series.part)) return ph('Run a scan to build the 21-day leadership vs participation chart.');
                const idxs = [];
                for (let i = 0; i < series.days; i++) if (Number.isFinite(series.part[i]) && Number.isFinite(series.lead[i])) idxs.push(i);
                if (idxs.length < 3) return ph('Not enough universe coverage in the last scan — run a full daily scan.');
                const partRaw = idxs.map(i => series.part[i]);
                const leadRaw = idxs.map(i => series.lead[i]);
                const p0 = partRaw[0] || 1, l0 = leadRaw[0] || 1;
                const P = partRaw.map(v => v / p0 * 100);
                const L = leadRaw.map(v => v / l0 * 100);
                const all = [...L, ...P, 100];
                const min = Math.min(...all), max = Math.max(...all), rng = (max - min) || 1;
                const W = 600, H = 150, padL = 6, padR = 6, padT = 12, padB = 14;
                const xw = W - padL - padR, yh = H - padT - padB;
                const X = i => padL + i * xw / (P.length - 1);
                const Y = v => padT + (max - v) / rng * yh;
                const lPts = L.map((v, i) => X(i).toFixed(1) + ',' + Y(v).toFixed(1));
                const pPts = P.map((v, i) => X(i).toFixed(1) + ',' + Y(v).toFixed(1));
                const band = pPts.join(' ') + ' ' + [...lPts].reverse().join(' ');
                const baseY = Y(100).toFixed(1);
                const curSpread = L[L.length - 1] - P[P.length - 1];
                const sdir = curSpread - (L[L.length - 2] - P[P.length - 2]);
                const bandFill = curSpread >= 0 ? 'rgba(52,211,153,0.10)' : 'rgba(251,113,133,0.12)';
                // Grade the read by Leader RVOL (the fuel axis): leadership DIRECTION × volume.
                const lr = m.leaderRvol;
                const heavy = Number.isFinite(lr) && lr >= 1.3;
                const light = Number.isFinite(lr) && lr < 1.0;
                const leadUp = L[L.length - 1] >= L[Math.max(0, L.length - 4)];
                let vTxt, vCls;
                if (leadUp) {
                    if (heavy) { vTxt = 'Conviction advance · volume-backed'; vCls = 'text-emerald-300'; }
                    else if (light) { vTxt = 'Hollow advance · low volume'; vCls = 'text-amber-300'; }
                    else { vTxt = 'Leadership rising · avg volume'; vCls = 'text-emerald-300'; }
                } else {
                    if (heavy) { vTxt = 'Distribution risk · heavy volume'; vCls = 'text-rose-300'; }
                    else if (light) { vTxt = 'Quiet digestion · low volume'; vCls = 'text-blue-300'; }
                    else { vTxt = 'Leadership fading · avg volume'; vCls = 'text-amber-300'; }
                }
                const lastX = X(P.length - 1).toFixed(1);
                const curLead = leadRaw[leadRaw.length - 1], curPart = partRaw[partRaw.length - 1];
                return `<div class="bg-slate-900/50 rounded-xl border border-slate-800 p-3">
                    <div class="flex items-center justify-between mb-1.5">
                        <span class="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Leadership vs Participation</span>
                        <span class="text-[10px] font-bold ${vCls}">${vTxt}</span>
                    </div>
                    <svg width="100%" height="150" viewBox="0 0 ${W} ${H}" preserveAspectRatio="none" style="display:block; overflow:visible">
                        <line x1="${padL}" y1="${baseY}" x2="${W - padR}" y2="${baseY}" stroke="#334155" stroke-width="1" stroke-dasharray="3 3"></line>
                        <polygon points="${band}" fill="${bandFill}"></polygon>
                        <polyline points="${pPts.join(' ')}" fill="none" stroke="#378ADD" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"></polyline>
                        <polyline points="${lPts.join(' ')}" fill="none" stroke="#1D9E75" stroke-width="2" stroke-dasharray="5 3" stroke-linejoin="round" stroke-linecap="round"></polyline>
                        <circle cx="${lastX}" cy="${Y(P[P.length - 1]).toFixed(1)}" r="2.8" fill="#378ADD"></circle>
                        <circle cx="${lastX}" cy="${Y(L[L.length - 1]).toFixed(1)}" r="2.8" fill="#1D9E75"></circle>
                    </svg>
                    <div class="flex items-center gap-3 mt-1 text-[9px]">
                        <span class="inline-flex items-center gap-1 text-blue-300"><span style="width:12px;border-top:2px solid #378ADD;display:inline-block"></span>Participation ${Number.isFinite(curPart) ? curPart.toFixed(0) + '%' : ''} · %&gt;50MA</span>
                        <span class="inline-flex items-center gap-1 text-emerald-300"><span style="width:12px;border-top:2px dashed #1D9E75;display:inline-block"></span>Leadership ${Number.isFinite(curLead) ? curLead.toFixed(0) + '%' : ''} · RS NH</span>
                        <span class="text-slate-600 ml-auto">indexed 100 · ${idxs.length}d</span>
                    </div>
                </div>`;
            };
            const divergenceCard = buildDivergenceCard();

            root.innerHTML = `
                <div class="p-4 space-y-4">
                    <!-- Verdict -->
                    <div class="flex items-center justify-between gap-2">
                        <span class="inline-block text-xs font-bold px-3 py-1.5 rounded-lg border cursor-help ${m.verdictCls}" title="Overall market posture from the combined breadth read (participation + leadership + RS thrust + RSP/SPY). Risk-On/Constructive = trade aggressively, full size. Mixed/Neutral = be selective. Caution·Narrowing / Risk-Off = tighten up, smaller size or cash — the tape is fighting you.">${m.verdict}</span>
                        <span class="text-[10px] text-slate-500">${m.n.toLocaleString()} stocks sampled</span>
                    </div>

                    <!-- LEADERSHIP BREADTH (leads the panel — the metric that decides if setups work) -->
                    <div>
                        <div class="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Leadership · RS Line New Highs</div>
                        <div class="grid grid-cols-3 gap-2">
                            <div onclick="showRsCohort('rsnh_all')" title="% of the universe whose RS line is at a new high — stocks outperforming the market RIGHT NOW. This is real-time leadership breadth. High & rising = leadership broadening (healthy); low like this = only a thin slice is truly leading. Click to list them." class="bg-slate-900/50 rounded-xl border border-slate-800 p-3 cursor-pointer hover:border-blue-600/60 hover:bg-blue-950/20 transition-colors">
                                <div class="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">RS New Highs <span class="text-blue-400/70">↗</span></div>
                                <div class="text-2xl font-black tabular-nums ${Number.isFinite(m.rsNhPct) ? (m.rsNhPct >= 25 ? 'text-emerald-300' : m.rsNhPct >= 12 ? 'text-blue-300' : m.rsNhPct >= 5 ? 'text-amber-300' : 'text-slate-400') : 'text-slate-500'}">${Number.isFinite(m.rsNhPct) ? m.rsNhPct.toFixed(0) + '%' : '--'}</div>
                                <div class="text-[9px] text-slate-500 mt-0.5">${Number.isFinite(m.rsNhCount) ? m.rsNhCount.toLocaleString() + ' stocks leading the market' : 'awaiting scan'}</div>
                            </div>
                            <div onclick="showRsCohort('delta')" title="Stocks whose RS line hit a new high BEFORE price confirmed — the signature 'leading accumulation' signal. Institutions are buying ahead of the breakout. A swelling Delta count is the most bullish early tell; near zero = no stealth accumulation underway. Click to list them." class="bg-slate-900/50 rounded-xl border ${m.deltaCount >= 20 ? 'border-fuchsia-500/40 bg-fuchsia-500/5' : 'border-slate-800'} p-3 cursor-pointer hover:border-fuchsia-500/60 hover:bg-fuchsia-950/10 transition-colors">
                                <div class="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Delta Leaders <span class="text-blue-400/70">↗</span></div>
                                <div class="text-2xl font-black tabular-nums ${m.deltaCount >= 20 ? 'text-fuchsia-300' : m.deltaCount >= 8 ? 'text-violet-300' : m.deltaCount > 0 ? 'text-blue-300' : 'text-slate-500'}">${Number.isFinite(m.deltaCount) ? m.deltaCount : '--'}</div>
                                <div class="text-[9px] text-slate-500 mt-0.5">RS new high <span class="text-slate-400">before</span> price</div>
                            </div>
                            <div onclick="showRsCohort('leaders')" title="The full pool of RS leaders: Delta + RS-new-high + Primed (RS line coiled just under a new high). This is your hunting ground of strongest-RS candidates. The larger it is, the more leadership to rotate into. Click to list them." class="bg-slate-900/50 rounded-xl border border-slate-800 p-3 cursor-pointer hover:border-emerald-600/60 hover:bg-emerald-950/10 transition-colors">
                                <div class="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Primed+ Leaders <span class="text-blue-400/70">↗</span></div>
                                <div class="text-2xl font-black tabular-nums ${m.leaderCount >= 40 ? 'text-emerald-300' : m.leaderCount >= 15 ? 'text-teal-300' : m.leaderCount > 0 ? 'text-blue-300' : 'text-slate-500'}">${Number.isFinite(m.leaderCount) ? m.leaderCount : '--'}</div>
                                <div class="text-[9px] text-slate-500 mt-0.5">RS NH / Delta / Primed</div>
                            </div>
                        </div>
                        <div class="text-[9px] text-slate-600 mt-1.5">${m.deltaCount >= 20 ? 'Strong leadership — a stockpicker\'s market; breakout setups have tailwind.' : m.deltaCount >= 8 ? 'Moderate leadership — selective setups working.' : m.rsNhCount > 0 ? 'Thin leadership — be selective; few names truly leading.' : 'Run / complete a scan to read leadership.'}</div>
                    </div>

                    <!-- Breadth gauges -->
                    <div>
                        <div class="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Participation · % Above Moving Average</div>
                        <div class="grid grid-cols-3 gap-2">
                            ${gauge('Above 20MA', m.above20, 'short-term', 'Share of the universe trading above its 20-day average — the short-term tide. Above ~60% = broad short-term strength (buyable dips, breakouts work); below ~40% = most stocks are weak near-term, breakouts tend to fail.')}
                            ${gauge('Above 50MA', m.above50, 'intermediate', 'Share above the 50-day average — the intermediate trend most swing traders watch. Rising through 50% = healthy broadening advance; falling below = the average stock has lost its uptrend even if the index holds.')}
                            ${gauge('Above 200MA', m.above200, 'long-term', 'Share above the 200-day average — the long-term / bull-market line. Above 60% = durable bull backdrop; below ~40% for a while = structural bear conditions where even good setups are fighting the tape.')}
                        </div>
                    </div>

                    <!-- RS THRUST & LEADERSHIP -->
                    <div>
                        <div class="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">RS Thrust &amp; Leadership</div>
                        <div class="mb-3">${divergenceCard}</div>
                        <div class="bg-slate-900/50 rounded-xl border border-slate-800 p-3 space-y-3">

                            <!-- Composite RS Thrust Index -->
                            <div class="flex items-center justify-between gap-3">
                                <div>
                                    <div class="text-[9px] uppercase tracking-wider text-slate-500 font-bold cursor-help" title="A 0–100 composite of market strength: blends thrust rate, RS-new-high breadth, elite-RS density and Delta leadership. Above ~62 = expanding (press exposure); 42–62 = steady; 25–42 = cooling; below 25 = contracting (defensive). Watch the direction (Δ) more than the level.">RS Thrust Index</div>
                                    <div class="flex items-baseline gap-2">
                                        <span class="text-3xl font-black tabular-nums ${m.rsThrustState ? m.rsThrustState.cls : 'text-slate-500'}">${Number.isFinite(m.rsThrustScore) ? m.rsThrustScore : '--'}</span>
                                        <span class="text-[10px] font-bold ${deltaCls(thrustTrend)}">${deltaTxt(thrustTrend)}</span>
                                        <span class="text-xs font-bold ${m.rsThrustState ? m.rsThrustState.cls : 'text-slate-500'}">${m.rsThrustState ? m.rsThrustState.label : ''}</span>
                                    </div>
                                </div>
                                <div class="flex-1 max-w-[200px]">
                                    ${sparkSvg(thrustSeries, thrustTrend.dir, 200)}
                                    <div class="flex justify-between text-[8px] text-slate-600 mt-0.5"><span>${thrustSeries.length > 1 ? thrustSeries.length + ' scans' : 'trend builds over scans'}</span><span>${thrustSeries.length > 1 ? 'growth →' : ''}</span></div>
                                </div>
                            </div>

                            <!-- RS-rating distribution -->
                            <div class="grid grid-cols-5 gap-2">
                                ${rsTrendStat('Thrust Rate', Number.isFinite(m.thrustRate) ? m.thrustRate.toFixed(0) + '%' : '--', '1W RS &gt; 1M', breadthColor(m.thrustRate), 'thrustRate', 'Share of stocks whose 1-week relative strength is accelerating past their 1-month — i.e. momentum is speeding UP, not just positive. Rising = fresh thrust entering the market (early-trend fuel); falling = the move is maturing/stalling.')}
                                ${rsTrendStat('RS ≥ 80', Number.isFinite(m.rsStrongPct) ? m.rsStrongPct.toFixed(0) + '%' : '--', 'institutional', m.rsStrongPct >= 20 ? 'text-emerald-300' : m.rsStrongPct >= 10 ? 'text-blue-300' : 'text-slate-300', 'rsStrongPct', '% of the universe with an RS Rating of 80+ — the IBD "leadership" threshold institutions buy from. The bigger this pool, the more genuine leaders exist to choose from. Shrinking = leadership thinning out.')}
                                ${rsTrendStat('RS ≥ 90', Number.isFinite(m.rsElitePct) ? m.rsElitePct.toFixed(0) + '%' : '--', 'elite', m.rsElitePct >= 8 ? 'text-emerald-300' : m.rsElitePct >= 3 ? 'text-blue-300' : 'text-slate-300', 'rsElitePct', '% in the top RS decile (90+) — the elite where the biggest winners almost always come from. Expanding = a rich, healthy crop of true market leaders; contracting = winners getting scarce.')}
                                ${rsTrendStat('Median RS', Number.isFinite(m.rsMedian) ? m.rsMedian.toFixed(0) : '--', 'universe', m.rsMedian >= 55 ? 'text-emerald-300' : m.rsMedian >= 45 ? 'text-blue-300' : m.rsMedian >= 35 ? 'text-amber-300' : 'text-rose-300', 'rsMedian', 'The middle stock\'s RS Rating. A percentile median naturally sits near 50 — above 50 means the universe skews strong (rising tide), below 50 means the average stock is underperforming.')}
                                ${rsTrendStat('Leader RVOL', Number.isFinite(m.leaderRvol) ? m.leaderRvol.toFixed(2) + '×' : '--', 'fuel under leaders', m.leaderRvol >= 1.3 ? 'text-emerald-300' : m.leaderRvol >= 1 ? 'text-blue-300' : 'text-amber-300', 'leaderRvol', 'Median relative volume of the leaders (RS≥80 / RS-NH). Above ~1.3× = leaders trading on heavy volume = real institutional buying behind the move. Below 1× = leaders quiet, advance running on drift (suspect).')}
                            </div>

                            <!-- Leadership ladder -->
                            <div>
                                <div class="flex items-center justify-between mb-1">
                                    <span class="text-[9px] uppercase tracking-wider text-slate-500 font-bold cursor-help" title="How the whole universe distributes across RS-Power tiers, strongest to weakest: Delta (RS NH before price) → RS NH → Primed → Building → Firm → Lagging. A healthy market is top-heavy (fat Delta/RS-NH bands); a heavy Lagging band = most stocks are weak. Click any tier in the legend to list its names.">Leadership Ladder</span>
                                    <span class="text-[9px] text-slate-600">${m.tierLadderTotal ? m.tierLadderTotal.toLocaleString() + ' ranked' : 'awaiting scan'}</span>
                                </div>
                                <div class="flex h-3 rounded-full overflow-hidden bg-slate-800">${ladderBar}</div>
                                <div class="flex flex-wrap gap-x-3 gap-y-1 mt-1.5">${ladderLegend}</div>
                            </div>

                            <!-- Live RS signals firing (RS alerts) -->
                            <div class="grid grid-cols-3 gap-2 pt-0.5">
                                ${rsMini('Alpha Firing', Number.isFinite(m.alphaCount) ? m.alphaCount : '--', 'fresh RS buy', m.alphaCount > 0 ? 'text-emerald-300' : 'text-slate-400', m.alphaCount > 0 ? 'alpha' : null, 'Count of stocks currently printing the Alpha signal — the app\'s primary prime-entry buy trigger. A live, recently-fired leadership entry. Click to see the names. More firing = more actionable setups right now.')}
                                ${rsMini('Beta Firing', Number.isFinite(m.betaCount) ? m.betaCount : '--', 'fresh RS buy', m.betaCount > 0 ? 'text-sky-300' : 'text-slate-400', m.betaCount > 0 ? 'beta' : null, 'Count printing the Beta signal — the secondary / confirmation entry (tighter window than Alpha). Click to see the names. A rising Beta count alongside Alpha = broad buy-signal expansion.')}
                                ${rsMini('Fresh Delta', Number.isFinite(m.freshDelta) ? m.freshDelta : '--', '≤3d · RS&gt;price', m.freshDelta > 0 ? 'text-fuchsia-300' : 'text-slate-400', m.freshDelta > 0 ? 'freshDelta' : null, 'Stocks whose RS line made a new high BEFORE price did, within the last 3 sessions — stealth institutional accumulation leading the chart. The earliest leadership tell. Click to see the names.')}
                            </div>
                            <div class="text-[8px] text-slate-600 leading-relaxed">Index blends thrust rate, RS new-high breadth, elite-RS density &amp; Delta leadership. Alpha/Beta = fresh RS buy triggers; Delta = RS new high <span class="text-slate-500">before</span> price (leading accumulation).</div>
                        </div>
                    </div>

                    <!-- Sector RS leadership -->
                    ${sectorBlock}

                    <!-- Advancers / Decliners -->
                    <div class="bg-slate-900/50 rounded-xl border border-slate-800 p-3">
                        <div class="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1 cursor-help" title="Today's advancers vs decliners across the universe, with the % green and the A/D ratio. Above ~55% green (ratio &gt;1.2) = broad buying day; below ~45% (ratio &lt;0.8) = broad selling. Confirms whether today's tape supports or fights the leadership read above.">Advancers / Decliners</div>
                        <div class="text-2xl font-black tabular-nums ${adColor}">${m.adv} / ${m.dec}</div>
                        <div class="text-[9px] text-slate-500 mt-0.5">${Number.isFinite(m.advPct) ? m.advPct.toFixed(0) + '% green' : '--'}${adRatio !== Infinity ? ` · ${adRatio.toFixed(2)} ratio` : ''}</div>
                    </div>

                    <!-- Equal-weight divergence -->
                    <div class="bg-slate-900/50 rounded-xl border border-slate-800 p-3">
                        <div class="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1.5">Equal-Weight vs Cap-Weight · RSP/SPY</div>
                        ${rspBlock}
                    </div>

                    <!-- ATR% regime -->
                    <div class="bg-slate-900/50 rounded-xl border border-slate-800 p-3">
                        <div class="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">Volatility Regime · ATR% (14d) vs 1Y range</div>
                        <div class="grid grid-cols-3 gap-2">
                            ${atrCard('SPY')}${atrCard('QQQ')}${atrCard('IWM')}
                        </div>
                    </div>

                    <div class="text-[9px] text-slate-600 leading-relaxed pt-1">
                        Breadth sampled from the enriched screener universe — sharpens as the scan completes. ATR%ile &lt;40 = compressed/coiled, &gt;70 = expanded/stressed. RSP/SPY rising = healthy broad market; falling = narrow megacap leadership.
                    </div>
                </div>`;
        }

        function renderClassificationDashboard(info) {
            if (!info) return;
            const stat = industryGroupStats.get(info.industryGroup);
            if (ui.fdSector) ui.fdSector.textContent = info.sector || '--';
            if (ui.fdIndustryGroup) ui.fdIndustryGroup.textContent = info.industryGroup || '--';
            if (ui.fdIndustry) {
                ui.fdIndustry.textContent = info.industry || '--';
                ui.fdIndustry.title = info.industry || '';
            }
            if (ui.fdGroupRank) {
                ui.fdGroupRank.textContent = stat ? `${stat.rank}/${stat.total}` : '--';
                ui.fdGroupRank.title = stat ? `6M ${stat.perf6m?.toFixed(1)}%, breadth ${stat.breadth?.toFixed(0)}%, ${stat.rows} stocks` : 'Open/enrich screener to compute group rank.';
            }
        }

        // ─────────────────────────────────────────────────────────────────────────
        //  GROWTH & ACCELERATION  (fundamentals from Polygon financials)
        // ─────────────────────────────────────────────────────────────────────────
        //  Two 0–100 gauges: EPS and Sales acceleration. Each blends:
        //    LEVEL        — how high is the latest YoY growth (diminishing-returns curve)
        //    ACCELERATION — is the YoY rate itself RISING quarter to quarter (the leading signal)
        //  Per the agreed design, acceleration is measured across the YoY SERIES (each quarter's
        //  YoY% vs the prior quarter's YoY%), which is seasonality-proof. Sales is weighted a bit
        //  higher in any composite because it's harder to engineer than EPS.

        // Map a growth % to a 0–100 "level" sub-score on a diminishing-returns curve.
        // 0% → ~30, 25% → ~65, 50% → ~80, 100%+ → ~95. Negative growth → low.

        // Acceleration sub-score: over the last few YoY readings, how many rose vs the prior one,
        // and how steeply. Returns 0–100 (50 = flat, >50 accelerating, <50 decelerating).

        // Combine level + acceleration into the final gauge value (acceleration weighted higher —
        // a stock speeding up matters more than one that's merely high-but-flat).

        function accelBand(score) {
            if (score === null) return { label: 'NO DATA', cls: 'bg-slate-700/50 text-slate-400', fill: 'bg-slate-600/40', val: 'text-slate-500' };
            if (score >= 75) return { label: 'EXPLOSIVE', cls: 'bg-fuchsia-900/40 text-fuchsia-300', fill: 'bg-fuchsia-500/60', val: 'text-fuchsia-300' };
            if (score >= 50) return { label: 'STRONG', cls: 'bg-emerald-900/40 text-emerald-300', fill: 'bg-emerald-500/55', val: 'text-emerald-300' };
            if (score >= 25) return { label: 'MODERATE', cls: 'bg-amber-900/40 text-amber-300', fill: 'bg-amber-500/50', val: 'text-amber-300' };
            return { label: 'WEAK', cls: 'bg-rose-900/40 text-rose-300', fill: 'bg-rose-500/50', val: 'text-rose-300' };
        }

        function setGauge(valEl, bandEl, fillEl, score) {
            const b = accelBand(score);
            valEl.textContent = score === null ? '--' : score;
            valEl.className = `text-3xl font-black font-mono ${b.val}`;
            bandEl.textContent = b.label;
            bandEl.className = `text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ${b.cls}`;
            fillEl.className = `absolute inset-y-0 left-0 transition-all duration-500 ${b.fill}`;
            fillEl.style.width = score === null ? '0%' : `${score}%`;
        }

        // Pull up to ~12 quarters of financials from Polygon, compute YoY %Chg, render table + gauges.
        async function fetchGrowth(ticker, key) {
            if (!ui.growthTableBody) return;
            ui.growthStatus.textContent = 'loading financials…';
            ui.growthTableBody.innerHTML = `<tr><td colspan="5" class="text-center text-slate-500 text-xs py-4">Fetching quarterly financials…</td></tr>`;
            try {
                let q;
                // Atomic path: quarterly financials came in the /api/ticker bundle — no fetch.
                const _bg = (window.__lastTickerRow && window.__lastTickerRow.ticker === ticker) ? window.__lastTickerRow.growth : null;
                if (_bg && Array.isArray(_bg.quarters) && _bg.quarters.length) {
                    q = _bg.quarters.slice().reverse().map(x => {   // bundle is newest-first → chronological
                        let fp = '', fy = '', rawDate = null;
                        const m = /^(Q\d|FY|TTM)\s+(\d{4})$/.exec(x.period || '');
                        if (m) { fp = m[1]; fy = m[2]; } else if (x.period) { rawDate = x.period; }
                        return { rawDate, fp, fy, eps: x.eps, rev: x.rev };
                    });
                } else {
                    const url = `https://api.polygon.io/vX/reference/financials?ticker=${ticker}&timeframe=quarterly&order=desc&limit=14&sort=period_of_report_date&apiKey=${key}`;
                    const data = await polygonFetch(url, ticker);
                    const rows = (data.results || []).filter(r => r.financials);
                    if (!rows.length) throw new Error('no financials');
                    // Extract EPS + revenue per quarter (most recent first → reverse to chronological).
                    q = rows.map(r => {
                        const f = r.financials;
                        const inc = f.income_statement || {};
                        const eps = (inc.diluted_earnings_per_share && inc.diluted_earnings_per_share.value != null)
                            ? inc.diluted_earnings_per_share.value
                            : (inc.basic_earnings_per_share ? inc.basic_earnings_per_share.value : null);
                        const rev = inc.revenues ? inc.revenues.value : null;
                        const rawDate = r.period_of_report_date || r.end_date || r.filing_date || r.start_date || null;
                        return { rawDate, fp: r.fiscal_period || '', fy: r.fiscal_year || '', eps, rev };
                    }).reverse(); // chronological
                }
                if (!q.length) throw new Error('no financials');

                // YoY %Chg = this quarter vs the same quarter 4 periods earlier.
                const epsYoY = [], salesYoY = [];
                for (let i = 0; i < q.length; i++) {
                    const prior = i >= 4 ? q[i - 4] : null;
                    q[i].epsYoY = (prior && prior.eps != null && q[i].eps != null && Math.abs(prior.eps) > 0.001)
                        ? ((q[i].eps - prior.eps) / Math.abs(prior.eps)) * 100 : null;
                    q[i].salesYoY = (prior && prior.rev != null && q[i].rev != null && prior.rev > 0)
                        ? ((q[i].rev - prior.rev) / prior.rev) * 100 : null;
                    epsYoY.push(q[i].epsYoY); salesYoY.push(q[i].salesYoY);
                }

                // Render last 8 quarters (most recent first for reading top-down).
                const last8 = q.slice(-8).reverse();
                const fmtEps = (v) => v == null ? '—' : `$${v.toFixed(2)}`;
                const fmtRev = (v) => v == null ? '—' : (Math.abs(v) >= 1e9 ? `$${(v/1e9).toFixed(2)}B` : `$${(v/1e6).toFixed(1)}M`);
                const fmtPct = (v) => v == null ? '<span class="text-slate-600">—</span>'
                    : `<span class="${v >= 0 ? 'text-blue-400' : 'text-rose-400'} font-semibold">${v >= 0 ? '+' : ''}${v.toFixed(0)}%</span>`;
                const qLabel = (row) => {
                    if (row.rawDate) {
                        const dt = new Date(row.rawDate);
                        if (!isNaN(dt.getTime())) return dt.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
                    }
                    // Fallback: fiscal period + year (e.g. "Q1 '26"), always present on Polygon rows.
                    if (row.fp || row.fy) return `${row.fp} '${String(row.fy).slice(-2)}`.trim();
                    return '—';
                };

                ui.growthTableBody.innerHTML = last8.map((row, i) => `
                    <tr class="${i % 2 ? 'bg-slate-900/40' : ''} text-xs">
                        <td class="text-left py-1.5 px-2 text-slate-300 font-medium">${qLabel(row)}</td>
                        <td class="py-1.5 px-2 text-slate-200 font-mono">${fmtEps(row.eps)}</td>
                        <td class="py-1.5 px-2 font-mono">${fmtPct(row.epsYoY)}</td>
                        <td class="py-1.5 px-2 text-slate-200 font-mono">${fmtRev(row.rev)}</td>
                        <td class="py-1.5 px-2 font-mono">${fmtPct(row.salesYoY)}</td>
                    </tr>`).join('');

                // Gauges off the YoY series.
                const latestEps = [...epsYoY].reverse().find(x => x !== null) ?? null;
                const latestSales = [...salesYoY].reverse().find(x => x !== null) ?? null;
                const epsScore = gaugeScore(latestEps, epsYoY);
                const salesScore = gaugeScore(latestSales, salesYoY);
                setGauge(ui.epsAccelVal, ui.epsAccelBand, ui.epsAccelFill, epsScore);
                setGauge(ui.salesAccelVal, ui.salesAccelBand, ui.salesAccelFill, salesScore);

                // One-line verdict.
                const epsB = accelBand(epsScore).label, salesB = accelBand(salesScore).label;
                let verdict;
                if (epsScore === null && salesScore === null) verdict = 'Insufficient financial history to assess growth.';
                else {
                    const sV = latestSales != null ? `${latestSales >= 0 ? '+' : ''}${latestSales.toFixed(0)}%` : 'n/a';
                    const eV = latestEps != null ? `${latestEps >= 0 ? '+' : ''}${latestEps.toFixed(0)}%` : 'n/a';
                    verdict = `Latest YoY: Sales ${sV} (${salesB}), EPS ${eV} (${epsB}). `;
                    if ((salesScore||0) >= 75 || (epsScore||0) >= 75) verdict += 'Top-tier acceleration — fundamentals are a tailwind.';
                    else if ((salesScore||0) >= 50 && (epsScore||0) >= 50) verdict += 'Solid, accelerating growth.';
                    else if ((salesScore||0) < 25 && (epsScore||0) < 25) verdict += 'Weak or decelerating — fundamentals are a drag.';
                    else verdict += 'Mixed growth profile.';
                }
                ui.growthVerdict.textContent = verdict;
                ui.growthStatus.textContent = `${q.length} quarters`;

                return { epsScore, salesScore, latestEps, latestSales };
            } catch (e) {
                ui.growthTableBody.innerHTML = `<tr><td colspan="5" class="text-center text-slate-500 text-xs py-4">Still digging for this one's financials — not every ticker shares its homework. 📚</td></tr>`;
                ui.growthStatus.textContent = 'no data';
                setGauge(ui.epsAccelVal, ui.epsAccelBand, ui.epsAccelFill, null);
                setGauge(ui.salesAccelVal, ui.salesAccelBand, ui.salesAccelFill, null);
                ui.growthVerdict.textContent = '';
                return null;
            }
        }

        async function populateFundamentals(results, closes, highs, lows, volumes, ticker, key) {
            const n = closes.length; const lastClose = closes[n - 1];
            ui.fdLastPrice.textContent = "$" + lastClose.toFixed(2);

            const avg20 = MathLib.sma(volumes.slice(0, -1), 20); const rvol20 = avg20 ? volumes[n - 1] / avg20 : null;
            ui.fdRvol20.textContent = rvol20 ? rvol20.toFixed(2) + 'x' : "--";
            ui.fdRvol20.className = "text-base font-bold " + (rvol20 >= 1.5 ? "text-emerald-400" : rvol20 >= 1.0 ? "text-blue-400" : "text-slate-400");
            const avg50 = MathLib.sma(volumes, 50); ui.fdAvgVol50.textContent = formatVol(avg50);
            let dolSum = 0, dolCount = 0; for (let i = Math.max(0, n - 50); i < n; i++) { dolSum += closes[i] * volumes[i]; dolCount++; }
            ui.fdAvgDolVol50.textContent = formatDollarBig(dolCount ? dolSum / dolCount : null);
            ui.fdDayRange.textContent = `$${results[n - 1].l.toFixed(2)}–$${results[n - 1].h.toFixed(2)}`;
            const wkCloses = closes.slice(-5); ui.fdWeekRange.textContent = `$${Math.min(...wkCloses).toFixed(2)}–$${Math.max(...wkCloses).toFixed(2)}`;
            const hi52 = Math.max(...highs.slice(-252)); const lo52 = Math.min(...lows.slice(-252));
            ui.fd52High.textContent = "$" + hi52.toFixed(2); ui.fd52Low.textContent = "$" + lo52.toFixed(2);
            ui.fdFromHigh.textContent = `${((lastClose - hi52) / hi52 * 100).toFixed(1)}%`;
            ui.fdFromLow.textContent = `+${((lastClose - lo52) / lo52 * 100).toFixed(1)}%`;

            ui.fdShares.textContent = "..."; ui.fdMktCap.textContent = "...";
            currentTickerShares = null; currentTickerMarketCap = null; // reset so a stale prior ticker never leaks into the score
            // Atomic path: the /api/ticker bundle already carries classification + shares/cap →
            // set them synchronously, no extra fetch (and no out-of-order race). Fallback below.
            const _bf = (window.__lastTickerRow && window.__lastTickerRow.ticker === ticker) ? window.__lastTickerRow.fundamentals : null;
            if (_bf) {
                AppState.setCurrentTickerName(_bf.name || ticker);
                currentTickerDescription = _bf.description || '';
                currentTickerSic = _bf.sicDescription || sicLabelFromCode(_bf.sicCode) || '';
                currentTickerClassification = { name: state.currentTickerName, sector: _bf.sector, industryGroup: _bf.industryGroup, industry: _bf.industry, sicCode: _bf.sicCode };
                renderClassificationDashboard(currentTickerClassification);
                const shares = Number.isFinite(_bf.sharesOutstanding) ? _bf.sharesOutstanding : null;
                const mcap = Number.isFinite(_bf.marketCap) ? _bf.marketCap : (shares ? shares * lastClose : null);
                currentTickerShares = shares; currentTickerMarketCap = Number.isFinite(mcap) ? mcap : null;
                ui.fdShares.textContent = shares ? formatBig(shares) : "--";
                ui.fdMktCap.textContent = formatDollarBig(mcap);
                return;
            }
            try {
                const res = await polygonFetch(`https://api.polygon.io/v3/reference/tickers/${ticker}?apiKey=${key}`, ticker);
                const r = res.results || {}; const shares = r.weighted_shares_outstanding || r.share_class_shares_outstanding || null;
                AppState.setCurrentTickerName(r.name || ticker);
                currentTickerDescription = r.description || '';
                currentTickerSic = r.sic_description || sicLabelFromCode(r.sic_code) || '';
                currentTickerClassification = { name: state.currentTickerName, ...classifyIndustry(ticker, r.name || ticker, r.sic_description || r.description || '', r.sic_code || '', r.type || '') };
                renderClassificationDashboard(currentTickerClassification);
                const mcap = r.market_cap || (shares ? shares * lastClose : null);
                currentTickerShares = Number.isFinite(shares) ? shares : null;
                currentTickerMarketCap = Number.isFinite(mcap) ? mcap : null;
                ui.fdShares.textContent = shares ? formatBig(shares) : "--";
                ui.fdMktCap.textContent = formatDollarBig(mcap);
            } catch (e) { AppState.setCurrentTickerName(ticker); currentTickerClassification = { name: ticker, ...classifyIndustry(ticker) }; renderClassificationDashboard(currentTickerClassification); ui.fdShares.textContent = "--"; ui.fdMktCap.textContent = "--"; }
        }

        // ─────────────────────────────────────────────────────────────────────────
        // ADR%-BASED CAPITAL ALLOCATION
        // ADR% (Average Daily Range) is the stock's typical daily swing. Position size
        // is normalized so each name contributes a similar daily $ move: high-ADR names
        // get smaller allocations, low-ADR names can carry larger ones. This is the
        // Qullamaggie/leveraged-ETF sizing discipline — volatility, not just stop, sizes.
        // ─────────────────────────────────────────────────────────────────────────
        // Target: each position should move ~TARGET_DAILY_MOVE% of the ACCOUNT on an
        // average day → suggested allocation % = TARGET_DAILY_MOVE / ADR%.
        const ADR_TARGET_DAILY_MOVE = 1.0; // % of account a position should swing per avg day

        function classifyAdr(adr) {
            if (!Number.isFinite(adr)) return null;
            if (adr < 3)  return { tier: 'Low',      cls: 'text-blue-400 bg-blue-900/30 border-blue-500/30',       note: 'Calm — can size up; small daily swings.' };
            if (adr < 5)  return { tier: 'Moderate', cls: 'text-emerald-400 bg-emerald-900/30 border-emerald-500/30', note: 'Healthy momentum range — standard sizing.' };
            if (adr < 8)  return { tier: 'High',      cls: 'text-amber-400 bg-amber-900/30 border-amber-500/30',     note: 'Volatile — trim size; expect wide swings.' };
            return            { tier: 'Extreme',   cls: 'text-rose-400 bg-rose-900/30 border-rose-500/30',       note: 'Very volatile (or leveraged ETF) — size small.' };
        }

        // Suggested max allocation % of account, normalized by ADR. Capped at 100%.
        function adrSuggestedAllocationPct(adr) {
            if (!Number.isFinite(adr) || adr <= 0) return null;
            return Math.min(100, (ADR_TARGET_DAILY_MOVE / adr) * 100);
        }

        function renderAdrAllocationGuide(adr, accSize, riskBasedPct) {
            const wrap = document.getElementById('adrAllocGuide');
            if (!wrap) return;
            const cls = classifyAdr(adr);
            if (!cls || !Number.isFinite(adr)) {
                wrap.innerHTML = `<div class="text-[10px] text-slate-600 italic">Load a ticker to compute ADR%-based allocation.</div>`;
                return;
            }
            const allocPct = adrSuggestedAllocationPct(adr);
            const allocUsd = accSize > 0 ? accSize * (allocPct / 100) : null;
            // Volatility-normalized guidance vs the risk-based size already shown.
            let verdict = '', vcls = 'text-slate-400';
            if (Number.isFinite(riskBasedPct) && Number.isFinite(allocPct)) {
                if (riskBasedPct > allocPct + 5) { verdict = `Risk-based size (${riskBasedPct.toFixed(0)}%) exceeds the ADR-safe cap — consider trimming.`; vcls = 'text-amber-400'; }
                else if (riskBasedPct < allocPct - 15) { verdict = `Room to size up: ADR supports up to ${allocPct.toFixed(0)}%.`; vcls = 'text-emerald-400'; }
                else { verdict = `Risk-based size sits within the ADR-safe range.`; vcls = 'text-emerald-400'; }
            }
            wrap.innerHTML = `
                <div class="flex items-center justify-between mb-2">
                    <span class="text-[10px] uppercase tracking-wider text-slate-500 font-bold">ADR%-Based Allocation</span>
                    <span class="inline-block text-[10px] font-bold px-2 py-0.5 rounded border ${cls.cls}">${cls.tier} · ${adr.toFixed(1)}% ADR</span>
                </div>
                <div class="flex items-end justify-between">
                    <div>
                        <div class="text-2xl font-black tabular-nums text-white leading-none">${allocPct.toFixed(0)}%</div>
                        <div class="text-[9px] text-slate-500 mt-0.5">suggested max allocation</div>
                    </div>
                    <div class="text-right">
                        <div class="text-base font-bold tabular-nums text-slate-200">${allocUsd != null ? '$' + allocUsd.toLocaleString(undefined, {maximumFractionDigits: 0}) : '--'}</div>
                        <div class="text-[9px] text-slate-500 mt-0.5">≈ ${ADR_TARGET_DAILY_MOVE}%/day acct swing</div>
                    </div>
                </div>
                ${verdict ? `<div class="text-[10px] ${vcls} mt-2 leading-snug">${verdict}</div>` : ''}
                <div class="text-[9px] text-slate-600 mt-1.5 leading-snug">${cls.note}</div>`;
        }

        function calculatePosition() {
            const accSize = parseFloat(ui.accountSize.value) || 0; const riskPct = parseFloat(ui.maxRiskPct.value) || 0;
            const atr = parseFloat(ui.atr.value) || 0; const lod = parseFloat(ui.lod.value) || 0; const entry = parseFloat(ui.entryPrice.value) || 0;

            if (entry <= lod || atr === 0 || accSize === 0) {
                ui.bpWarn.classList.add('hidden'); ui.extWarn.classList.add('hidden'); ui.stopWarn.classList.add('hidden');
                return;
            }

            const riskPerShare = entry - lod; const extensionPct = ((entry - lod) / atr) * 100; const stopDistPct = (riskPerShare / entry) * 100;
            let totalRiskAmt = accSize * (riskPct / 100); let shares = Math.floor(totalRiskAmt / riskPerShare);
            let totalPositionValue = shares * entry; let portfolioPct = (totalPositionValue / accSize) * 100;

            if (totalPositionValue > accSize) {
                shares = Math.floor(accSize / entry); totalPositionValue = shares * entry; portfolioPct = (totalPositionValue / accSize) * 100; totalRiskAmt = shares * riskPerShare; 
                ui.bpWarn.classList.remove('hidden');
            } else { ui.bpWarn.classList.add('hidden'); }

            ui.extension.textContent = `${extensionPct.toFixed(2)}%`;
            // Default "correct setup" reward-to-risk: a 3:1 target (entry + 3× the risk per share).
            const TARGET_R = 3, tgtPrice = entry + TARGET_R * riskPerShare, tgtPct = TARGET_R * stopDistPct;
            ui.distance.textContent = `${stopDistPct.toFixed(2)}%  ·  ${TARGET_R}:1 → $${tgtPrice.toFixed(2)} (+${tgtPct.toFixed(0)}%)`;
            ui.shares.textContent = shares.toLocaleString(); ui.risk.textContent = `$${totalRiskAmt.toFixed(2)}`;
            ui.positionSize.textContent = `$${totalPositionValue.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
            ui.portfolioPct.textContent = `${portfolioPct.toFixed(2)}% of Portfolio`;
            
            ui.portfolioPct.className = "inline-block px-2 py-0.5 rounded-xl text-[10px] font-semibold border transition-colors ";
            if (portfolioPct > 20) ui.portfolioPct.className += "bg-red-900/40 text-red-400 border-red-500/30";
            else if (portfolioPct > 15) ui.portfolioPct.className += "bg-yellow-900/40 text-yellow-400 border-yellow-500/30";
            else ui.portfolioPct.className += "bg-blue-600/20 text-blue-400 border-blue-500/20";

            // Overextension check: (entry - LoD) / ATR is the intraday "distance vs ATR" ratio.
            // Per the ATR/LoD method, >50% means the intraday range is already stretched → caution.
            const distVsAtrPct = ((entry - lod) / atr) * 100;
            const overextended = distVsAtrPct > 50;
            ui.extWarn.textContent = overextended
                ? `Overextended: entry sits ${distVsAtrPct.toFixed(0)}% of one ATR above the low of day (>50% limit). Intraday range already stretched.`
                : '';
            ui.extWarn.classList.toggle('hidden', !overextended);
            ui.stopWarn.classList.toggle('hidden', stopDistPct <= 7.0);

            // ADR%-based allocation guide (volatility-normalized sizing).
            renderAdrAllocationGuide(currentTickerAdrPct, accSize, portfolioPct);
        }

        // Endpoint chain so a hostile network (captive portal / firewall that blocks
        // api.polygon.io, or a CORS wall) still gets through. ORDER: your own proxy first (if
        // configured — the only thing that reliably works on ANY network), then the direct call,
        // then public CORS proxies as a last resort. Once one works we STICK to it (_polyPref) so
        // a full scan doesn't re-probe dead routes on every call.
        //
        // To make this b/ bullet-proof on any network, deploy the tiny Cloudflare Worker (free)
        // and run ONCE in the browser console:
        //   localStorage.setItem('polygonProxyBase','https://YOUR-worker.workers.dev'); location.reload();
        // Caveat for the PUBLIC proxies only: they see the URL (which contains your apiKey) — fine
        // for a personal local tool. Your own Worker keeps everything private.
        const _polyProxyBase = (() => { try { return (localStorage.getItem('polygonProxyBase') || '').trim().replace(/\/$/, ''); } catch (e) { return ''; } })();
        const _polyProxies = [
            // Same-origin server proxy (server.js): strips whatever apiKey the client sent and
            // injects the real Polygon key server-side, so the key never reaches the browser and
            // a dead/placeholder key in the input box no longer matters. This is the primary route.
            u => u.replace('https://api.polygon.io', '/api/polygon'),
            ...(_polyProxyBase ? [u => _polyProxyBase + u.replace('https://api.polygon.io', '')] : []), // your Worker
            u => u,                                                              // direct
            u => 'https://corsproxy.io/?url=' + encodeURIComponent(u),           // public proxy
            u => 'https://api.allorigins.win/raw?url=' + encodeURIComponent(u),  // public proxy
        ];
        let _polyPref = 0;
        async function polygonFetch(url, ticker, attempt = 1) {
            // Preferred (last-working) endpoint first, then the rest.
            const idxs = _polyProxies.map((_, i) => i);
            const order = [_polyPref, ...idxs.filter(i => i !== _polyPref)];
            // Per-request TIMEOUT. Some networks accept the socket but never deliver the large
            // grouped/snapshot payload, so a plain `await fetch` hangs forever and freezes the scan
            // on "Loading market snapshot". We abort a stalled request and fall through to the next
            // endpoint (proxy) instead of waiting indefinitely. Bulk endpoints get a longer budget.
            const _isBulk = /\/grouped\/|\/reference\/tickers/.test(url);
            const _timeoutMs = _isBulk ? 15000 : 15000;
            let res = null;
            for (const idx of order) {
                let r = null;
                const _ctrl = new AbortController();
                const _to = setTimeout(() => _ctrl.abort(), _timeoutMs);
                try { r = await fetch(_polyProxies[idx](url), { signal: _ctrl.signal }); }
                catch (e) { r = null; }                             // network/CORS/timeout → next endpoint
                finally { clearTimeout(_to); }
                if (!r) continue;                                  // network/CORS failure → next endpoint
                if (r.ok) { res = r; _polyPref = idx; break; }     // success → remember this endpoint
                // A definitive HTTP status from the trusted endpoint (your proxy or direct, idx 0)
                // is authoritative (bad key / rate limit / missing ticker) — surface it.
                if (idx === 0 && [429, 401, 403, 404].includes(r.status)) { res = r; break; }
                // otherwise (public-proxy hiccup / 5xx) fall through to the next endpoint
            }
            if(!res) throw new Error("Can't reach the data servers — this is a network/connection issue, not the app itself. A new WiFi may need a captive-portal login, or may be blocking api.polygon.io. Check your connection (try opening api.polygon.io in a new tab) and retry. 🛰️");
            if (res.status === 429) {
                // Record the rate-limit hit so the screener throttle can react (back off +
                // shrink the worker pool) instead of every worker blindly freezing.
                if (typeof ScreenerThrottle !== 'undefined') ScreenerThrottle.note429();
                // Short, escalating wait then a couple of retries. The pool-level backoff
                // (ScreenerThrottle) does the heavy lifting; this just rides out a brief spike.
                if (attempt < 3) { await new Promise(r => setTimeout(r, 2500 * attempt)); return polygonFetch(url, ticker, attempt + 1); }
                throw new Error("Whoa, slow down speed racer — catching our breath for a minute. 🏎️");
            }
            if (res.status === 401 || res.status === 403) throw new Error("That API key's giving us the cold shoulder — double-check it. 🔑");
            if (res.status === 404) throw new Error("Couldn't find that ticker — typo, or maybe it's playing hide and seek. 🔍");
            if (!res.ok) throw new Error("The data gremlins are at it again — try once more. 👾");
            const data = await res.json();
            if (data.status === "ERROR" || data.error) throw new Error("Data came back a little scrambled — another try should do it. 🍳");
            return data;
        }

        // ── Hover/intent prefetch ────────────────────────────────────────────────
        // When the pointer rests on a ticker row, quietly warm tickerHistoryCache in the
        // background so the eventual click is instant (no network round-trip). Best-effort:
        // dedupes against the cache and in-flight requests, fails silently, never blocks UI.
        const _prefetchInFlight = new Set();
        async function prefetchTickerHistory(ticker) {
            if (!ticker) return;
            ticker = ticker.toUpperCase();
            const key = ui.apiKey?.value?.trim();
            if (!key) return;
            // Already cached & fresh, or already being fetched → nothing to do.
            const cached = tickerHistoryCache[ticker];
            if (cached && (Date.now() - cached.ts) < TICKER_CACHE_TTL) return;
            if (_prefetchInFlight.has(ticker)) return;
            // Don't compete with an active scan for bandwidth (TaskCoordinator priority).
            if (typeof TaskCoordinator !== 'undefined' && TaskCoordinator.screenerBusy()) return;
            _prefetchInFlight.add(ticker);
            try {
                const toDate = new Date().toISOString().split('T')[0];
                const fromDateObj = new Date(); fromDateObj.setDate(fromDateObj.getDate() - 5840);
                const fromDate = fromDateObj.toISOString().split('T')[0];
                const url = `https://api.polygon.io/v2/aggs/ticker/${ticker}/range/1/day/${fromDate}/${toDate}?adjusted=true&sort=asc&limit=50000&apiKey=${key}`;
                const data = await polygonFetch(url, ticker);
                if (data.results && data.results.length) {
                    tickerHistoryCache[ticker] = { results: data.results, ts: Date.now() };
                    if (typeof Logger !== 'undefined') Logger.debug('Prefetched history', { ticker, bars: data.results.length });
                }
            } catch (e) {
                // Silent — prefetch is purely an optimization.
            } finally {
                _prefetchInFlight.delete(ticker);
            }
        }

        // Debounced hover intent: prefetch only after the pointer rests ~160ms on a row,
        // so sweeping the mouse across a list doesn't fire a burst of requests. One
        // delegated listener covers every current and future ticker table.
        let _hoverPrefetchTimer = null;
        let _hoverPrefetchLast = '';
        if (typeof document !== 'undefined') {
            document.addEventListener('mouseover', (e) => {
                const row = e.target.closest && e.target.closest('tr[data-ticker]');
                if (!row) return;
                const t = row.getAttribute('data-ticker');
                if (!t || t === _hoverPrefetchLast) return;
                clearTimeout(_hoverPrefetchTimer);
                _hoverPrefetchTimer = setTimeout(() => {
                    _hoverPrefetchLast = t;
                    prefetchTickerHistory(t);
                }, 160);
            });
            // Cancel a pending prefetch if the pointer leaves before the dwell completes.
            document.addEventListener('mouseout', (e) => {
                const row = e.target.closest && e.target.closest('tr[data-ticker]');
                if (row) { clearTimeout(_hoverPrefetchTimer); }
            });
        }

        // Stage 2: single-call ticker bundle. Hits the server's /api/ticker, which
        // assembles the enriched row + daily bars + SPY benchmark in one response —
        // so the browser stops making separate bars + benchmark Polygon round-trips.
        // Primes the SPY benchmark cache and stashes the authoritative row for the
        // panel hydration. Falls back to the direct proxied fetch if the endpoint is
        // unavailable (e.g. server not yet restarted) so a load never hard-fails.
        async function fetchTickerBundle(ticker, fromDate, toDate, key) {
            try {
                const res = await fetch('/api/ticker/' + encodeURIComponent(ticker));
                if (res.ok) {
                    const d = await res.json();
                    if (d && Array.isArray(d.bars) && d.bars.length) {
                        if (d.benchBars && d.benchBars.closes && d.benchBars.closes.length > 60) spyBarsCache = d.benchBars;
                        // Stash the WHOLE atomic bundle so the load renders growth + fundamentals
                        // from this one response (no separate fetches → no out-of-order race).
                        window.__lastTickerRow = { ticker, row: d.row || null, fundamentals: d.fundamentals || null, growth: d.growth || null };
                        return { results: d.bars };
                    }
                }
            } catch (e) { /* fall through to the direct proxy path */ }
            const url = `https://api.polygon.io/v2/aggs/ticker/${ticker}/range/1/day/${fromDate}/${toDate}?adjusted=true&sort=asc&limit=50000&apiKey=${key}`;
            return await polygonFetch(url, ticker);
        }

        let _tickerLoadSeq = 0;   // latest-wins guard: a superseded load must not paint
        async function fetchTickerData(opts) {
            const silent = opts && opts.silent;
            const _seq = ++_tickerLoadSeq;
            let ticker = ui.tickerInput.value.trim().toUpperCase(); const key = ui.apiKey.value.trim();
            if (!ticker || !key) return;

            // Cache hit = the swap will be instant (no network). In that case we must NOT
            // dim the chart or show the pill skeleton — that's the flicker that makes
            // scrubbing through many charts feel janky. Dim only when we'll truly block.
            const _cacheHit = !!(tickerHistoryCache[ticker] && tickerHistoryCache[ticker].results && tickerHistoryCache[ticker].results.length);
            if (!silent) {
                ui.status.className = 'text-xs text-slate-400 font-mono bg-slate-900/50 px-3 py-1 rounded border border-slate-800';
                ui.status.textContent = _cacheHit ? `Loading…` : `Fetching History...`; ui.btn.disabled = true;
                if (!_cacheHit) {
                    const cc = document.getElementById('chartCard');
                    if (cc) cc.style.opacity = '0.55';
                }
            }
            // Avoid the chart jumping during a ticker switch: if the badge panel is already
            // showing a prior ticker, reserve its current height and dim it (an "updating" cue)
            // instead of collapsing it to zero — that collapse is what let the chart slide up
            // and then get shoved back down. First load (still hidden) stays collapsed.
            if (ui.convictionPanel && !ui.convictionPanel.classList.contains('hidden')) {
                ui.convictionPanel.style.minHeight = ui.convictionPanel.offsetHeight + 'px';
                ui.convictionPanel.style.opacity = '0.5';
            }
            // Clear any prior ticker's alert pills so stale signals don't linger.
            window.__pillCtx = {};
            if (typeof renderAlertPills === 'function') renderAlertPills({});
            if (!silent) { window.__tierSignals = null; const _us = document.getElementById('unifiedStats'); if (_us) _us.innerHTML = ''; }
            // Skeleton shimmer only on a true network miss (see above); on a cache hit the
            // signals recompute fast enough that a skeleton would just flash.
            if (!silent && !_cacheHit) { const _ap = document.getElementById('alertPills'); if (_ap) _ap.classList.add('pills-loading'); }
            // Clear any prior ticker's forming gap-up line so it never flashes on the new chart.
            window.__gapLine = null;
            
            try {
                // History window: ~16 years keeps the 12M (yearly) RSI on a full 14-period
                // basis, while the per-ticker cache below makes re-visits instant — that's
                // where the real speed-up is, without changing any computation.
                const toDate = new Date().toISOString().split('T')[0];
                const fromDateObj = new Date(); fromDateObj.setDate(fromDateObj.getDate() - 5840);
                const fromDate = fromDateObj.toISOString().split('T')[0];

                // Stale-while-revalidate: if we have ANY cached history, paint it
                // immediately (instant switch), and if it's past its TTL kick off a quiet
                // background refresh that updates the cache and silently re-renders when the
                // fresh bars land. A revisit therefore never blocks on the network.
                let data;
                let servedStale = false;
                const cached = tickerHistoryCache[ticker];
                if (cached && cached.results && cached.results.length) {
                    data = { results: cached.results };
                    const isStale = (Date.now() - cached.ts) >= TICKER_CACHE_TTL;
                    if (isStale) {
                        servedStale = true;
                        // Background revalidate (non-blocking). Refreshes the cache, then
                        // re-runs the load silently so the chart updates in place.
                        (async () => {
                            try {
                                const fresh = await fetchTickerBundle(ticker, fromDate, toDate, key);
                                if (fresh.results && fresh.results.length) {
                                    tickerHistoryCache[ticker] = { results: fresh.results, ts: Date.now() };
                                    // Only refresh if the user is still on this ticker.
                                    if (state.currentTicker === ticker) {
                                        if (typeof Logger !== 'undefined') Logger.debug('Revalidated stale ticker', { ticker });
                                        fetchTickerData({ silent: true });
                                    }
                                }
                            } catch (e) { /* stale data stays; silent */ }
                        })();
                    }
                } else {
                    data = await fetchTickerBundle(ticker, fromDate, toDate, key);
                    if (data.results && data.results.length) {
                        tickerHistoryCache[ticker] = { results: data.results, ts: Date.now() };
                    }
                }
                // Surface a subtle "refreshing" hint while stale data is shown; hide it as
                // soon as fresh data is served (including the silent revalidate pass).
                const sh = document.getElementById('chartRefreshHint');
                if (sh) sh.classList.toggle('hidden', !servedStale);

                // Drop any malformed bars — the feed occasionally returns null/incomplete
                // entries that would crash downstream reads (e.g. r.c). Keep only clean OHLCV.
                if (Array.isArray(data.results)) data.results = data.results.filter(r => r && Number.isFinite(r.c) && Number.isFinite(r.o) && Number.isFinite(r.h) && Number.isFinite(r.l));
                if (!data.results || data.results.length < 50) throw new Error("This one's a bit too fresh — not enough history to crunch yet. 🌱");
                
                engineState.cachedDataResults = data.results; setEngineContext({ cachedDataResults: engineState.cachedDataResults }); AppState.setCurrentTicker(ticker); AppState.setCurrentTickerName(ticker); intradayCache = {}; volumeClass = []; volumeSignals = [];
                const closes = data.results.map(r => r.c); const highs = data.results.map(r => r.h);
                const lows = data.results.map(r => r.l); const volumes = data.results.map(r => r.v); 
                const currentPrice = closes[closes.length - 1]; const currentLow = lows[lows.length - 1]; const currentVol = volumes[volumes.length - 1];
                if (typeof updateChartPriceBlock === 'function') updateChartPriceBlock(currentPrice, closes.length > 1 ? closes[closes.length - 2] : null, false);

                ui.entryPrice.value = currentPrice.toFixed(2); ui.lod.value = currentLow.toFixed(2);

                let trSum = 0; const period = 14; let startIndex = Math.max(0, closes.length - period - 1);
                for (let i = startIndex + 1; i <= startIndex + period && i < closes.length; i++) trSum += Math.max(highs[i] - lows[i], Math.abs(highs[i] - closes[i-1]), Math.abs(lows[i] - closes[i-1]));
                let smoothedATR = trSum / period;
                for (let i = startIndex + period + 1; i < closes.length; i++) smoothedATR = ((smoothedATR * 13) + Math.max(highs[i] - lows[i], Math.abs(highs[i] - closes[i-1]), Math.abs(lows[i] - closes[i-1]))) / 14;
                ui.atr.value = smoothedATR.toFixed(2);

                const avgVol50 = MathLib.sma(volumes, 50); currentTickerAvgVol50 = avgVol50;
                const rvol = currentVol / avgVol50;
                ui.currentVol.textContent = formatVol(currentVol); ui.avgVol.textContent = formatVol(avgVol50); ui.rvolValue.textContent = rvol.toFixed(2) + 'x';

                if (rvol >= 1.5) { ui.rvolValue.className = "text-3xl font-black text-emerald-400 tracking-tight"; ui.rvolStatus.innerHTML = "🟢 SURGE"; ui.rvolStatus.className = "font-bold text-emerald-400 text-[10px] uppercase bg-emerald-900/30 px-2 rounded"; } 
                else if (rvol >= 1.0) { ui.rvolValue.className = "text-3xl font-black text-blue-400 tracking-tight"; ui.rvolStatus.innerHTML = "🔵 AVERAGE"; ui.rvolStatus.className = "font-bold text-blue-400 text-[10px] uppercase bg-blue-900/30 px-2 rounded"; } 
                else { ui.rvolValue.className = "text-3xl font-black text-slate-400 tracking-tight"; ui.rvolStatus.innerHTML = "🔴 TRAP"; ui.rvolStatus.className = "font-bold text-rose-400 text-[10px] uppercase bg-rose-900/30 px-2 rounded"; }

                const ema9 = MathLib.ema(closes, 9);
                const sma50 = MathLib.sma(closes, 50);
                const sma200 = MathLib.sma(closes, 200);
                const sma200_1mo = MathLib.smaAsOf(closes, 200, 21);

                const evalMA = (p, ma, low, up, title, valEl, statEl, cardEl) => {
                    if (!ma) return; const pct = ((p - ma) / ma) * 100; valEl.innerHTML = formatPctHTML(pct);
                    if (pct < low) { statEl.textContent = "BROKEN"; statEl.className = "text-[8px] font-bold uppercase tracking-wide px-2 py-0.5 rounded bg-rose-900/50 text-rose-300 mt-1"; cardEl.className = "ma-card bg-slate-900 rounded-lg border border-rose-900 flex flex-col justify-center items-center text-center p-2"; } 
                    else if (pct > up) { statEl.textContent = "EXTENDED"; statEl.className = "text-[8px] font-bold uppercase tracking-wide px-2 py-0.5 rounded bg-yellow-900/50 text-yellow-300 mt-1"; cardEl.className = "ma-card bg-slate-900 rounded-lg border border-yellow-900/50 flex flex-col justify-center items-center text-center p-2"; } 
                    else { statEl.textContent = title; statEl.className = "text-[8px] font-bold uppercase tracking-wide px-2 py-0.5 rounded bg-emerald-600 text-white mt-1 shadow-md"; cardEl.className = "ma-card bg-emerald-900/20 rounded-lg border border-emerald-500/50 flex flex-col justify-center items-center text-center p-2 shadow-[0_0_10px_rgba(52,211,153,0.15)]"; }
                };

                const dist9Pct = ema9 ? ((currentPrice - ema9) / ema9) * 100 : 0;
                const dist50Pct = sma50 ? ((currentPrice - sma50) / sma50) * 100 : 0;
                const dist200Pct = sma200 ? ((currentPrice - sma200) / sma200) * 100 : 0;
                evalMA(currentPrice, ema9, -1.5, 2.5, "Tight Pivot", ui.dist9, ui.status9, ui.card9);
                evalMA(currentPrice, MathLib.ema(closes, 21), -2.0, 3.5, "Flag", ui.dist21, ui.status21, ui.card21);
                evalMA(currentPrice, sma50, -3.0, 5.0, "Core Zone", ui.dist50, ui.status50, ui.card50);
                evalMA(currentPrice, sma200, -4.0, 8.0, "Base", ui.dist200, ui.status200, ui.card200);

                const pct1mVal = MathLib.getPctChange(closes, 21, data.results) || 0;
                const pct3mVal = MathLib.getPctChange(closes, 63, data.results) || 0;
                const pct6mVal = MathLib.getPctChange(closes, 126, data.results) || 0;
                ui.pct1d.innerHTML = formatPctHTML(MathLib.getPctChange(closes, 1, data.results));
                ui.pct1w.innerHTML = formatPctHTML(MathLib.getPctChange(closes, 5, data.results));
                ui.pct1m.innerHTML = formatPctHTML(pct1mVal);
                ui.pct3m.innerHTML = formatPctHTML(pct3mVal);
                ui.pct6m.innerHTML = formatPctHTML(pct6mVal);
                ui.pctYtd.innerHTML = formatPctHTML(MathLib.getPctChange(closes, 'YTD', data.results));

                // RSI HEATMAP POPULATION
                const renderRSI = (id, val) => {
                    const card = document.getElementById(id);
                    const el = card.querySelector('.text-lg');
                    const fill = card.querySelector('.rsi-fill');
                    const stateEl = card.querySelector('.rsi-state');
                    if(val === null) {
                        el.textContent = "--"; el.className = "text-lg font-bold text-slate-500";
                        if (fill) { fill.style.width = '0%'; fill.className = 'rsi-fill h-full w-0 rounded bg-slate-500'; }
                        if (stateEl) { stateEl.textContent = '--'; stateEl.className = 'rsi-state text-[8px] font-bold uppercase tracking-wide text-slate-600 mt-1'; }
                        return;
                    }
                    el.textContent = val.toFixed(1);
                    let textCls, barCls, stateTxt, stateCls;
                    if(val >= 70) { textCls = "text-rose-400 drop-shadow-[0_0_5px_rgba(251,113,133,0.8)]"; barCls = 'bg-rose-400'; stateTxt = 'Overbought'; stateCls = 'text-rose-400'; }
                    else if(val <= 30) { textCls = "text-emerald-400 drop-shadow-[0_0_5px_rgba(52,211,153,0.8)]"; barCls = 'bg-emerald-400'; stateTxt = 'Oversold'; stateCls = 'text-emerald-400'; }
                    else if(val >= 55) { textCls = "text-yellow-400"; barCls = 'bg-yellow-400'; stateTxt = 'Bullish'; stateCls = 'text-yellow-400'; }
                    else if(val >= 45) { textCls = "text-blue-400"; barCls = 'bg-blue-400'; stateTxt = 'Neutral'; stateCls = 'text-slate-400'; }
                    else { textCls = "text-blue-400"; barCls = 'bg-blue-400'; stateTxt = 'Bearish'; stateCls = 'text-blue-400'; }
                    el.className = "text-lg font-bold " + textCls;
                    if (fill) { fill.style.width = Math.max(0, Math.min(100, val)) + '%'; fill.className = 'rsi-fill h-full rounded transition-all duration-300 ' + barCls; }
                    if (stateEl) { stateEl.textContent = stateTxt; stateEl.className = 'rsi-state text-[8px] font-bold uppercase tracking-wide mt-1 ' + stateCls; }
                };

                const rsi1d = MathLib.rsi(closes);
                const rsi1w = MathLib.rsi(MathLib.aggregateCloses(data.results, '1W'));
                const rsi1m = MathLib.rsi(MathLib.aggregateCloses(data.results, '1M'));
                const rsi3m = MathLib.rsi(MathLib.aggregateCloses(data.results, '3M'));
                const rsi12m = MathLib.rsi(MathLib.aggregateCloses(data.results, '12M'));

                renderRSI('rsi1d', rsi1d);
                renderRSI('rsi1w', rsi1w);
                renderRSI('rsi1m', rsi1m);
                renderRSI('rsi3m', rsi3m);
                renderRSI('rsi12m', rsi12m);

                // RSI regime read across timeframes (alignment is what matters, not any single TF).
                (function renderRsiRegime() {
                    const reg = document.getElementById('rsiRegime');
                    if (!reg) return;
                    const vals = [rsi1d, rsi1w, rsi1m].filter(v => v != null);
                    if (!vals.length) { reg.textContent = '--'; reg.className = 'text-[9px] normal-case tracking-normal text-slate-500 font-medium'; return; }
                    const allStrong = vals.every(v => v >= 55);
                    const allWeak = vals.every(v => v <= 45);
                    const overbought = (rsi1d != null && rsi1d >= 70) && (rsi1w != null && rsi1w >= 65);
                    let txt, cls;
                    if (overbought) { txt = 'Stretched across TFs'; cls = 'text-rose-300'; }
                    else if (allStrong) { txt = 'Aligned bullish (D·W·M)'; cls = 'text-emerald-300'; }
                    else if (allWeak) { txt = 'Aligned weak (D·W·M)'; cls = 'text-rose-300'; }
                    else { txt = 'Mixed across timeframes'; cls = 'text-slate-400'; }
                    reg.textContent = txt;
                    reg.className = 'text-[9px] normal-case tracking-normal font-medium ' + cls;
                })();

                // ── FIRST PAINT ──────────────────────────────────────────────────
                // Everything the chart needs (price, MAs, volume classification) is ready
                // now. Render it immediately so the user sees the chart instantly, BEFORE
                // the slower RS/growth/fundamentals network calls below. The RS dots and
                // scores fill in a moment later via the existing updateChartDisplay() call
                // at the end (which re-reads deltaDots), so nothing is lost.
                computeVolumeProfile(data.results);
                calculatePosition();
                updateChartDisplay(state.currentChartRange);
                // Cross-fade back in now that the chart is painted.
                if (!silent) { const cc = document.getElementById('chartCard'); if (cc) cc.style.opacity = '1'; }

                const rsBench = await fetchBenchmarkBars(rsBenchmarkSymbol, key);
                const mvRes = evaluateMinervini(closes, lows, highs, data.results, rsBench);
                // RS line computed once, then reused for chart dots + divergence strength.
                deltaDots = [];
                let rsDivergence = null;
                let rsLineFull = null;
                if (rsBench) {
                    rsLineFull = computeRsLineSeries(data.results, rsBench);
                    if (rsLineFull) {
                        deltaDots = computeRsLineSignalSeries(rsLineFull, closes, 252);
                        rsDivergence = computeRsDivergence(rsLineFull, 3, 10);
                    }
                }
                // Most recent Delta event ('before' kind = the leading signal) → days since,
                // so the Delta pill lights for RECENT events, unified with Alpha/Beta.
                let deltaRecency = null;
                const lastDelta = deltaDots.filter(e => e.kind === 'before').slice(-1)[0];
                if (lastDelta) {
                    const li = lastDelta.index, n = data.results.length;
                    let days = (n - 1) - li;
                    if (data.results[li] && data.results[n - 1]) days = Math.round((data.results[n - 1].t - data.results[li].t) / 86400000);
                    deltaRecency = { days };
                }
                if (typeof renderRsLinePanel === 'function') renderRsLinePanel(mvRes.rsSignals, mvRes.rs, mvRes.rsIsPercentile, deltaRecency);
                if (typeof renderRsDivergence === 'function') renderRsDivergence(rsDivergence);
                lastRsDivergence = rsDivergence;
                try {
                    lastVcpBase = detectVcpBase(data.results.map(r => r.h), data.results.map(r => r.l), data.results.map(r => r.c), data.results.map(r => r.v));
                } catch (e) { lastVcpBase = null; }
                try { renderVcpRow(); } catch (e) {}
                // Composite Relative Leadership score (hero gauge) — rewards strength while the
                // benchmark is weak, surfacing "next leaders" forming during a correction.
                if (typeof renderLeadershipGauge === 'function') {
                    const regime = rsBench ? computeBenchmarkRegime(rsBench) : null;
                    const leadership = (rsBench)
                        ? computeLeadershipScore({ rsRating: mvRes.rs, rsSignals: mvRes.rsSignals, divergence: rsDivergence, regime })
                        : null;
                    renderLeadershipGauge(leadership);
                }
                if (typeof renderLeadershipChart === 'function') renderLeadershipChart(data.results, rsLineFull);
                // ── Unified smart-signal (canonical): ONE gated detector drives the chart
                // marker AND the snapshot/Alerts list (detectBetaSignal), so they can never
                // disagree. detectWedge no longer decides alerts; a bare historical wedge is
                // a pattern, not an alert (Option B can re-add it as a labeled overlay later).
                {
                    const _bs = detectBetaSignal(closes, highs, lows, data.results);
                    const _readout = (idx) => {
                        let peak = closes[idx];
                        for (let j = idx; j < closes.length; j++) if (closes[j] > peak) peak = closes[j];
                        const days = (data.results[idx] && data.results[closes.length - 1])
                            ? Math.round((data.results[closes.length - 1].t - data.results[idx].t) / 86400000)
                            : (closes.length - 1 - idx);
                        return { days, peak: ((peak - closes[idx]) / closes[idx]) * 100 };
                    };
                    // Mark EVERY historical alpha/beta on the chart (Alpha→POP, Beta→WEAK),
                    // not just the most-recent gated one. The panel/pill readout below still
                    // uses the recent gated alpha/beta as the "active" signal.
                    wedgeSignals = (_bs.allSignals || []).map(s => ({ index: s.index, type: s.type === 'Alpha' ? 'POP' : 'WEAK' }));
                    window.__signalContext = {
                        alpha: _bs.hasAlpha ? { days: _bs.alphaDays, fromNow: _bs.alphaFromNow } : null,
                        beta:  _bs.hasBeta  ? { days: _bs.betaDays,  fromNow: _bs.betaFromNow }  : null
                    };
                    window.__pillCtx = {
                        ...(window.__pillCtx || {}),
                        alpha: _bs.alphaIdx != null ? _readout(_bs.alphaIdx) : null,
                        beta:  _bs.betaIdx  != null ? _readout(_bs.betaIdx)  : null,
                        benchmark: (typeof rsBenchmarkSymbol !== 'undefined') ? rsBenchmarkSymbol : 'SPY'
                    };
                    renderAlertPills(window.__pillCtx);
                }
                const channelRes = detectChannelOmega(closes, highs, lows, data.results);
                engineState.omegaSignals = getOmegaSignals(); // detectChannelOmega(updateGlobals=true default) wrote it inside the engine; pull it back for the chart markers below
                renderChannelGauge(channelRes);

                // Weinstein Stage detection (auto, display-only — feeds the tier engine in Step 3)
                const stageRes = renderStageBadge(closes, highs, lows);
                const rmvRes = renderRMV(closes, highs, lows);

                // Resolve THIS stock's group + sector rotation for the correct dimensions,
                // independent of whatever the Group RS tab last ranked into groupRankingData.
                let _grpRot = null, _secRot = null;
                try {
                    const _cls = currentTickerClassification || {};
                    const _gi = (typeof computeGroupRankings === 'function') ? (computeGroupRankings('industryGroup', false) || []) : [];
                    const _gs = (typeof computeGroupRankings === 'function') ? (computeGroupRankings('sector', false) || []) : [];
                    if (_cls.industryGroup && _cls.industryGroup !== 'Unknown') _grpRot = _gi.find(g => g.name === _cls.industryGroup) || null;
                    if (_cls.sector && _cls.sector !== 'Unknown') _secRot = _gs.find(g => g.name === _cls.sector) || null;
                } catch (e) {}

                // Leadership Convergence — cross the RS panel's reads with rotation / Stage /
                // RSI alignment / volume into one verdict + chart (uses signals just computed).
                if (typeof renderRsConvergence === 'function') {
                    try {
                        renderRsConvergence({
                            rsSignals: mvRes.rsSignals,
                            rsRating: mvRes.rs,
                            stage: stageRes ? stageRes.stage : null,
                            rsi: { d: rsi1d, w: rsi1w, m: rsi1m },
                            rvol: (typeof rvol === 'number' && isFinite(rvol)) ? rvol : null,
                            grpRot: _grpRot, secRot: _secRot,
                            results: data.results, rsBench: rsBench
                        });
                    } catch (e) {}
                }
                if (typeof renderRsiStageConvergence === 'function') {
                    try { renderRsiStageConvergence(data.results, closes, stageRes ? stageRes.stage : null); } catch (e) {}
                }
                if (typeof renderRsBaseConvergence === 'function') {
                    try { renderRsBaseConvergence(data.results, closes, highs, rsBench); } catch (e) {}
                }
                if (typeof renderVarsGroupConvergence === 'function') {
                    try { renderVarsGroupConvergence(data.results, spyBarsCache, _grpRot); } catch (e) {}
                }
                if (typeof renderVolumeConfluenceChart === 'function') {
                    try { renderVolumeConfluenceChart(data.results, volumes, _secRot); } catch (e) {}
                }

                // Growth & acceleration (separate Polygon financials call; degrades gracefully)
                const growthRes = await fetchGrowth(ticker, key);

                // ATR% multiple from 50-MA + ADR% + LoD% (left-column metrics + grey chart dots)
                const extRes = computeExtensionMetrics(closes, highs, lows, smoothedATR, currentLow);
                updateChartStatStrip();

                await populateFundamentals(data.results, closes, highs, lows, volumes, ticker, key);
                if (_seq !== _tickerLoadSeq) return;   // a newer ticker load superseded this one — don't paint a stale dashboard
                const quality = calculateMarketQualityMetrics(closes, highs, lows, volumes, currentPrice, smoothedATR, avgVol50);
                const mainVolumeSignal = detectVolumeSignalLite(data.results, avgVol50);
                const currentGroupStat = currentTickerClassification?.industryGroup ? industryGroupStats.get(currentTickerClassification.industryGroup) : null;
                const stopDistPct = currentPrice > 0 ? ((currentPrice - currentLow) / currentPrice) * 100 : null;
                const dollarVolume = currentPrice * currentVol;

                // Stock summary strip (reads fundamentals already populated above)
                renderStockSummary({
                    ticker,
                    pct1w: MathLib.getPctChange(closes, 5, data.results),
                    pct1m: pct1mVal,
                    rvol,
                    atrMult: extRes.atrMult,
                    dist50: dist50Pct,
                    mvPassed: mvRes.passed
                });

                // Run the newly integrated Conviction Engine
                calculateConvictionScore({
                    ticker,
                    stockName: state.currentTickerName,
                    stage: stageRes ? stageRes.stage : null,
                    stageLabel: stageRes ? stageRes.label : '',
                    rmv: rmvRes ? rmvRes.val : null,
                    channelStage: channelRes ? channelRes.stage : null,
                    channelPosition: channelRes ? channelRes.position : null,
                    omegaSignal: channelRes ? channelRes.omegaSignal : 'NONE',
                    volumeSignal: mainVolumeSignal,
                    epsScore: growthRes ? growthRes.epsScore : null,
                    salesScore: growthRes ? growthRes.salesScore : null,
                    marketCap: (typeof currentTickerMarketCap === 'number') ? currentTickerMarketCap : null,
                    sharesOutstanding: (typeof currentTickerShares === 'number') ? currentTickerShares : null,
                    groupRank: currentGroupStat ? currentGroupStat.rank : null,
                    groupRankTotal: currentGroupStat ? currentGroupStat.total : null,
                    groupPerf6m: currentGroupStat ? currentGroupStat.perf6m : null,
                    groupBreadth: currentGroupStat ? currentGroupStat.breadth : null,
                    stopDistPct,
                    atrMult: extRes.atrMult,
                    dollarVolume,
                    mvPassed: mvRes.passed, rsProxy: mvRes.rs, rvol: rvol,
                    pct1m: pct1mVal, pct3m: pct3mVal, pct6m: pct6mVal, pct12m: quality.pct12m,
                    dist9: dist9Pct, dist50: dist50Pct, dist200: dist200Pct,
                    rsi1d: rsi1d || 0, rsi1w: rsi1w || 0, rsi1m: rsi1m || 0,
                    deltaTrigger: !!(mvRes.rsSignals && mvRes.rsSignals.deltaTrigger),
                    deltaDays: deltaRecency ? deltaRecency.days : null,
                    rsNewHigh: !!(mvRes.rsSignals && mvRes.rsSignals.rsNewHigh),
                    secGrvol: (_secRot && Number.isFinite(_secRot.grvol)) ? _secRot.grvol : null,
                    convSecRot: _secRot || null,
                    price: currentPrice, low: currentLow, atr: smoothedATR, sma200: sma200, sma200_1mo: sma200_1mo,
                    sma50Trend: quality.sma50Trend, sma150Trend: quality.sma150Trend,
                    sma200Trend: quality.sma200Trend || ((sma200 && sma200_1mo) ? ((sma200 - sma200_1mo) / sma200_1mo * 100) : 0),
                    atrPct: quality.atrPct, upDownVolRatio: quality.upDownVolRatio,
                    accumulationDays: quality.accumulationDays, distributionDays: quality.distributionDays,
                    volatilityContraction: quality.volatilityContraction, rangeVsAvg: quality.rangeVsAvg,
                    belowHighPct: quality.belowHighPct, baseDepthPct: quality.baseDepthPct
                });

                ui.status.className = 'text-xs text-emerald-400 font-mono bg-emerald-900/20 px-3 py-1 rounded border border-emerald-800'; ui.status.textContent = `Loaded ${ticker}`;
                if (engineState.cachedDataResults.length) updateDataFreshness(engineState.cachedDataResults[engineState.cachedDataResults.length - 1].t);
                calculatePosition(); updateChartDisplay(state.currentChartRange); updateChartStatStrip();
                // Forming pre-market gap-up: check in the background, redraw the long-term
                // chart if a gap is found (so the dotted amber line appears).
                if (engineState.cachedDataResults.length) {
                    const lastClose = engineState.cachedDataResults[engineState.cachedDataResults.length - 1].c;
                    checkGapUp(ticker, lastClose, key).then(() => {
                        if (window.__gapLine && !['1D', '1W'].includes(state.currentChartRange)) updateChartDisplay(state.currentChartRange);
                    });
                }

                // ─── Feed this enriched ticker into the Group RS rankings ──────────────
                // Loading a single stock now contributes to its group's live ranking, so the
                // Group RS table fills in progressively as you enrich names (not only on a
                // full universe scan). Upsert a lean row into screenerAllResults, then re-rank.
                try {
                    if (currentTickerClassification && Array.isArray(state.screenerAllResults)) {
                        const prevClose = closes.length > 1 ? closes[closes.length - 2] : null;
                        const grpRow = {
                            ticker,
                            name: state.currentTickerName || ticker,
                            price: currentPrice,
                            sector: currentTickerClassification.sector,
                            industryGroup: currentTickerClassification.industryGroup,
                            industry: currentTickerClassification.industry,
                            pct1d: MathLib.getPctChange(closes, 1, data.results),
                            pct1w: MathLib.getPctChange(closes, 5, data.results),
                            pct1m: pct1mVal, pct3m: pct3mVal, perf3m: pct3mVal, pct6m: pct6mVal,
                            pctLive: ((typeof isUsMarketHours === 'function' && isUsMarketHours()) && prevClose && prevClose > 0)
                                ? ((currentPrice - prevClose) / prevClose) * 100 : null,
                            rvol, volToday: currentVol, avgVol50,
                            above50: sma50 ? currentPrice > sma50 : null,
                            tier: null, dollarVolume,
                            fromSingleLoad: true, enrichedAt: Date.now()
                        };
                        const gi = state.screenerAllResults.findIndex(r => r && r.ticker === ticker);
                        if (gi >= 0) state.screenerAllResults[gi] = { ...state.screenerAllResults[gi], ...grpRow };
                        else state.screenerAllResults.push(grpRow);
                        if (typeof computeIndustryGroupRanks === 'function') computeIndustryGroupRanks();
                        if (typeof state.activeHubTab !== 'undefined' && state.activeHubTab === 'groups' && typeof renderGroupRankings === 'function') renderGroupRankings();
                    }
                } catch (e) {}
                // ─── BREAKOUT QUALITY SCORE (BQS) ─────────────────────────────────────
                let _tierBqsGrade = null;   // captured for the tier signal strip below
                // Map the loaded-ticker's already-computed signals into BQS inputs, then compute
                // and render the standalone breakout-quality breakdown. Pattern + failed-breakout
                // detection run inside BQS.compute from the raw OHLC series.
                try {
                    const ms = engineState.lastMarketStructure;
                    const grp = currentGroupStat;
                    const groupRankPct = (grp && grp.rank && grp.total) ? (grp.rank / grp.total) * 100 : null;
                    const v = (ms && ms.verdict) ? ms.verdict : '';
                    // VIX proxy (VIXY) level + direction, if benchmarks are loaded.
                    let vixLow = false, vixHigh = false;
                    const vixy = marketStructureIndices && marketStructureIndices['VIXY'];
                    if (vixy && vixy.closes && vixy.closes.length > 20) {
                        const vc = vixy.closes, vlast = vc[vc.length - 1], vprev = vc[vc.length - 6];
                        const vsma = MathLib.sma(vc, 20);
                        vixLow = vsma ? (vlast < vsma && vlast < vprev) : false;   // falling & below its mean
                        vixHigh = vsma ? (vlast > vsma * 1.15) : false;            // elevated vs mean
                    }
                    const bqsInput = {
                        // base — stage & trend
                        stage: stageRes ? stageRes.stage : null,
                        stageSub: stageRes ? stageRes.sub : null,
                        sma50Trend: quality.sma50Trend,
                        sma200Trend: quality.sma200Trend || ((sma200 && sma200_1mo) ? ((sma200 - sma200_1mo) / sma200_1mo * 100) : 0),
                        sma50AboveSma200: (sma50 && sma200) ? (sma50 > sma200) : null,
                        higherHighsLows: quality.higherHighsLows === true,
                        dist50: dist50Pct, dist200: dist200Pct,
                        // rmv
                        rmv: rmvRes ? rmvRes.val : null,
                        rmvFalling: rmvRes ? rmvRes.falling === true : false,
                        rmvRisingSharply: rmvRes ? rmvRes.rising === true : false,
                        // volume
                        rvol,
                        volumeDryUp: quality.volatilityContraction > 20 || (quality.accumulationDays > quality.distributionDays && rvol < 1),
                        volumeRisingIntoBreakout: rvol >= 1.4 && quality.accumulationDays > quality.distributionDays,
                        climaxVolumeInBase: quality.distributionDays >= 3 && quality.distributionDays > quality.accumulationDays,
                        // extension — atrMult is gain-from-50MA in ATR%-multiples (≈ ADR multiples)
                        adrMultFrom50: extRes ? extRes.atrMult : null,
                        belowEma20Breakout: dist9Pct < 0 && rvol >= 1.5,
                        veryExtendedFromEma20: dist9Pct > 8,
                        // context — market regime
                        marketVerdict: v,
                        spyStrongBullish: /Risk-On|Broad/.test(v),
                        spyBullish: /Constructive/.test(v),
                        spyAbove200: ms ? (ms.above200 != null ? ms.above200 > 50 : null) : null,
                        spyHigherHighs: ms ? (ms.rspTrend === 'up') : false,
                        allIndicesUptrend: ms ? (ms.above50 != null && ms.above50 > 60) : false,
                        vixLow, vixHigh,
                        // context — group
                        groupRankPct,
                        groupPerf3m: grp ? grp.perf6m : null, // perf6m is the available proxy
                        groupRsRising: grp ? (grp.rsRising === true) : false,
                        groupCluster: grp ? (grp.breakoutCount >= 2) : false,
                        groupStage2Rising: grp ? (grp.stage2Rising === true) : false,
                        groupStage4: grp ? (grp.stage4 === true) : false,
                        // context — breadth
                        breadthPct: ms ? ms.above50 : null,
                        sectorTop3: grp ? (grp.sectorTop3 === true) : false,
                        // compound-penalty helpers
                        weakGroupBearMkt: (groupRankPct != null && groupRankPct > 75) && /Risk-Off|Weak|Caution/.test(v),
                        highRvolWeakFollow: rvol >= 2.0 && pct1mVal < 0,
                        // convergence — RS line × base (leading-accumulation precursor) + leadership
                        rsNewHigh: !!(mvRes && mvRes.rsSignals && mvRes.rsSignals.rsNewHigh),
                        deltaTrigger: !!(mvRes && mvRes.rsSignals && mvRes.rsSignals.deltaTrigger),
                        belowHighPct: quality.belowHighPct,
                        rsRating: mvRes ? mvRes.rs : null,
                        grpRotIn: !!(_grpRot && (_grpRot.rotationTag === 'in' || _grpRot.rotationTag === 'leader')),
                        grpRotOut: !!(_grpRot && (_grpRot.rotationTag === 'out' || _grpRot.rotationTag === 'dormant'))
                    };
                    const bqs = BQS.compute(bqsInput, { closes, highs, lows, volumes });
                    renderBQS(bqs);
                    _tierBqsGrade = bqs ? bqs.grade : null;
                } catch (bqsErr) { /* BQS is non-critical — never blocks the main analysis */ }

                // ─── TIER SIGNAL STRIP ────────────────────────────────────────────────
                // Snapshot the key gathered signals into one object (a couple of cheap O(n)
                // derivations from data already in memory — no network), then paint the chip
                // strip under the thesis. Fully guarded so it can never block the load.
                try {
                    const rsp = (typeof computeRsPower === 'function' && mvRes && mvRes.rsSignals) ? computeRsPower(mvRes.rsSignals) : null;
                    let adr = null; try { adr = MathLib.adrPct(highs, lows, 20); } catch (e) {}
                    let fm = null;  try { fm = (typeof computeFormingState === 'function') ? computeFormingState(closes, highs, lows, channelRes, smoothedATR) : null; } catch (e) {}
                    let av = null;  try { av = (typeof computeAvwapDefense === 'function') ? computeAvwapDefense(closes, highs, lows, volumes, smoothedATR) : null; } catch (e) {}
                    // Prefer the authoritative server-enriched row (same metrics the screener
                    // shows) so the per-ticker panel can never disagree with the universe view.
                    // Fall back to the just-computed client values for off-universe / unscanned
                    // tickers (no row), or any field the server left null. (Stage 1 of moving
                    // single-ticker analysis onto the server's snapshot.)
                    const srvRow = (window.__lastTickerRow && window.__lastTickerRow.ticker === ticker && window.__lastTickerRow.row)
                        ? window.__lastTickerRow.row
                        : ((typeof state.screenerAllResults !== 'undefined' && Array.isArray(state.screenerAllResults))
                            ? state.screenerAllResults.find(r => r && r.ticker === ticker) : null);
                    const _pref = (a, b) => (a === null || a === undefined || (typeof a === 'number' && !Number.isFinite(a))) ? b : a;
                    const _clientForming = (fm && fm.best) ? { label: fm.best.label, type: fm.best.type, proximity: fm.best.proximity, distPct: fm.best.distPct } : null;
                    window.__tierSignals = {
                        rsPower:      _pref(srvRow && srvRow.rsPower,      rsp ? rsp.rsPower : null),
                        rsPowerTier:  _pref(srvRow && srvRow.rsPowerTier,  rsp ? rsp.rsPowerTier : null),
                        adrPct:       _pref(srvRow && srvRow.adrPct,       Number.isFinite(adr) ? adr : null),
                        rmv:          _pref(srvRow && srvRow.rmv,          rmvRes ? rmvRes.val : null),
                        bqsGrade:     _pref(srvRow && srvRow.bqsGrade,     _tierBqsGrade),
                        pct3m:        _pref(srvRow && srvRow.pct3m,        Number.isFinite(pct3mVal) ? pct3mVal : null),
                        growthStatus: _pref(srvRow && srvRow.growthStatus, growthRes ? growthRes.growthStatus : null),
                        avwapDist:    _pref(srvRow && srvRow.avwapDistAtr, av ? av.distAtr : null),
                        forming:      _pref(srvRow && srvRow.forming,      _clientForming),
                        dollarVolume: _pref(srvRow && srvRow.dollarVolume, Number.isFinite(dollarVolume) ? dollarVolume : null),
                        groupRank:      _pref(srvRow && srvRow.groupRank,      currentGroupStat ? currentGroupStat.rank : null),
                        groupRankTotal: _pref(srvRow && srvRow.groupRankTotal, currentGroupStat ? currentGroupStat.total : null)
                    };
                    if (typeof renderUnifiedStats === 'function') renderUnifiedStats();
                } catch (e) { /* strip is non-critical */ }

                renderTradeAssistant().catch(e => console.error('Trade Assistant call failed:', e)); // pre-trade checklist (incl. VARS)
                startCurrentTickerRefresh();

            } catch (err) { Logger.error('Ticker load failed', { ticker, error: String(err && err.message || err) }); if (!silent) { ui.status.className = 'text-xs text-red-400 font-mono bg-red-900/20 px-3 py-1 rounded border border-red-800'; ui.status.textContent = err.message; const cc = document.getElementById('chartCard'); if (cc) cc.style.opacity = '1'; const sh = document.getElementById('chartRefreshHint'); if (sh) sh.classList.add('hidden'); const _ap = document.getElementById('alertPills'); if (_ap) _ap.classList.remove('pills-loading'); } }
            finally {
                if (!silent) ui.btn.disabled = false;
                // Safety: if the load failed before the score rendered, release the panel's
                // height reservation / dim so it isn't left stuck in the "updating" state.
                if (ui.convictionPanel && ui.convictionPanel.style.opacity === '0.5') {
                    ui.convictionPanel.style.minHeight = '';
                    ui.convictionPanel.style.opacity = '';
                }
            }
        }


        // Hub tab switching: show one of screener / watchlist / portfolio panels.

        function setScreenerOpen(open) {
            if (!ui.marketScreenerPanel || !ui.marketScreenerToggle) return;
            ui.marketScreenerPanel.classList.toggle('open', open);
            ui.marketScreenerToggle.classList.toggle('open', open);
            // collapsed class HIDES the third column; absence shows the 3-column dashboard
            document.body.classList.toggle('screener-collapsed', !open);
            // The vertical edge button only re-opens the screener; when open, the panel's
            // own X handles closing, so hide the edge button to avoid it floating mid-screen.
            ui.marketScreenerToggle.style.display = open ? 'none' : '';
            ui.marketScreenerToggle.textContent = 'Screener';
            if (open) startScreenerAutoSync();
        }

        function initScreenerResize() {
            const handle = ui.screenerResizeHandle, panel = ui.marketScreenerPanel;
            if (!handle || !panel) return;
            let dragging = false;
            const clampWidth = (w) => Math.max(360, Math.min(window.innerWidth * 0.78, w));
            const setWidth = (w) => {
                const next = clampWidth(w);
                document.documentElement.style.setProperty('--screener-width', `${next}px`);
                localStorage.setItem('marketScreenerWidth', String(Math.round(next)));
            };
            const saved = Number(localStorage.getItem('marketScreenerWidth'));
            if (saved) setWidth(saved);
            else {
                // No saved width: initialise the CSS var to the panel's real default width
                // so the push margin matches the panel exactly (was relying on a mismatched fallback).
                const real = Math.round(panel.getBoundingClientRect().width) || Math.min(560, window.innerWidth - 48);
                document.documentElement.style.setProperty('--screener-width', `${real}px`);
            }
            handle.addEventListener('pointerdown', (e) => { dragging = true; handle.setPointerCapture(e.pointerId); document.body.style.userSelect = 'none'; });
            handle.addEventListener('pointermove', (e) => { if (dragging) setWidth(window.innerWidth - e.clientX); });
            const stop = () => { dragging = false; document.body.style.userSelect = ''; };
            handle.addEventListener('pointerup', stop); handle.addEventListener('pointercancel', stop);
        }

        // Resize handle between params (col 1) and chart (col 2) — mirrors initScreenerResize.
        function initParamsResize() {
            const handle = ui.paramsResizeHandle;
            if (!handle) return;
            const paramsCol = handle.previousElementSibling; // the .params-col
            let dragging = false;
            const clampWidth = (w) => Math.max(200, Math.min(560, w));
            const setWidth = (w) => {
                const next = clampWidth(w);
                document.documentElement.style.setProperty('--params-width', `${next}px`);
                localStorage.setItem('paramsColWidth', String(Math.round(next)));
            };
            const saved = Number(localStorage.getItem('paramsColWidth'));
            if (saved) setWidth(saved);
            handle.addEventListener('pointerdown', (e) => { dragging = true; handle.setPointerCapture(e.pointerId); document.body.style.userSelect = 'none'; });
            handle.addEventListener('pointermove', (e) => {
                if (!dragging || !paramsCol) return;
                // width = pointer X minus the panel's left edge
                const left = paramsCol.getBoundingClientRect().left;
                setWidth(e.clientX - left);
            });
            const stop = () => { dragging = false; document.body.style.userSelect = ''; };
            handle.addEventListener('pointerup', stop); handle.addEventListener('pointercancel', stop);
        }

        // ── Dynamic screener filters ────────────────────────────────────────────
        // Each filter field already exists in the DOM (so getScreenerFilters and presets
        // keep working). This controller decides which are VISIBLE, renders them as chips,
        // and resets a field to its default when removed (so it stops constraining).
        // screener-filter cluster extracted to src/screener-filters.js (imported at top; DI-configured below).
        function updateScreenerStats() {
            const total = screenerSnapshot.length || screenerUniverse.length || 0;
            const best = state.screenerResults[0];
            if (ui.screenerCountText) ui.screenerCountText.textContent = `${screenerIndex} / ${total}`;
            if (ui.screenerProgressBar) ui.screenerProgressBar.style.width = total ? `${Math.min(100, (screenerIndex / total) * 100)}%` : '0%';
            if (ui.scrStatUniverse) ui.scrStatUniverse.textContent = screenerSnapshot.length ? screenerSnapshot.length.toLocaleString() : (screenerUniverse.length ? screenerUniverse.length.toLocaleString() : '--');
            if (ui.scrStatScanned) ui.scrStatScanned.textContent = screenerIndex ? screenerIndex.toLocaleString() : '--';
            if (ui.scrStatMatches) ui.scrStatMatches.textContent = state.screenerResults.length ? state.screenerResults.length.toLocaleString() : '--';
            if (ui.scrStatBest) ui.scrStatBest.textContent = best ? best.ticker : '--';
            if (ui.screenerAutoStatus) {
                const stamp = screenerLastSync ? `Market cache ready · ${screenerLastSync.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}` : 'Market cache warming up';
                const quietStamp = screenerLastSync ? `Ready ${screenerLastSync.toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}` : 'Ready';
                ui.screenerAutoStatus.textContent = screenerSnapshot.length ? quietStamp : 'Loading market snapshot';
            }
        }

        function startScreenerAutoSync() {
            if (screenerAutoTimer) return;
            runMarketScreener();
            // Cheap now (one cached GET of the server snapshot); the server refreshes
            // intraday every ~5 min, so poll on the same cadence.
            screenerAutoTimer = setInterval(() => {
                if (!state.screenerRunning) runMarketScreener();
            }, 5 * 60 * 1000);
        }


        // Whole-market universe via the GROUPED DAILY endpoint (aggregates — available on the
        // Starter tier, unlike the full-market Snapshot endpoint which is gated to higher tiers
        // and was returning 404). Returns rows shaped like the old snapshot so every downstream
        // consumer (getSnapshotPrice/Volume/Low, buildScreenerQueueFromSnapshot) works unchanged.
        function ymd(d) { return d.toISOString().split('T')[0]; }

        // ── Portfolio ──────────────────────────────────────────────────────────────
        // You enter ticker + date + shares + entry price. Everything else is computed
        // from enriched/live data. Entry price defaults to the date's close if blank.
        // Portfolio store extracted to src/portfolio.js (imported at top).

        // Fetch the daily close for a ticker on (or just before) a given date — used to
        // auto-fill entry price when the user leaves it blank. Returns number or null.
        async function fetchHistoricalClose(ticker, dateStr, key) {
            try {
                const to = dateStr;
                const fromObj = new Date(dateStr); fromObj.setDate(fromObj.getDate() - 7);
                const from = ymd(fromObj);
                const data = await polygonFetch(`https://api.polygon.io/v2/aggs/ticker/${ticker}/range/1/day/${from}/${to}?adjusted=true&sort=asc&limit=50&apiKey=${key}`, ticker);
                const res = data.results || [];
                return res.length ? res[res.length - 1].c : null;
            } catch (e) { return null; }
        }

        // Forming pre-market GAP-UP detector. Fetches the latest pre-market minute bar and,
        // if it's a gap-up of more than +2% over the last daily close, stashes window.__gapLine
        // for the chart plugin to draw (long-term ranges only). Clears it otherwise.
        const GAP_UP_THRESHOLD = 2.0; // percent
        async function checkGapUp(ticker, lastClose, key) {
            window.__gapLine = null;
            if (!ticker || !key || !Number.isFinite(lastClose) || lastClose <= 0) return;
            try {
                const toObj = new Date(); const fromObj = new Date(); fromObj.setDate(toObj.getDate() - 3);
                const to = ymd(toObj), from = ymd(fromObj);
                const url = `https://api.polygon.io/v2/aggs/ticker/${ticker}/range/1/minute/${from}/${to}?adjusted=true&sort=desc&limit=600&apiKey=${key}`;
                const data = await polygonFetch(url, ticker);
                const bars = data.results || [];
                if (!bars.length) return;
                const etMin = (ms) => {
                    const p = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(new Date(ms));
                    let h = 0, m = 0; for (const x of p) { if (x.type === 'hour') h = +x.value; if (x.type === 'minute') m = +x.value; }
                    return (h % 24) * 60 + m;
                };
                const etDate = (ms) => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(ms)); // YYYY-MM-DD in ET
                const REG_OPEN = 9 * 60 + 30;
                // The forming session = the most recent ET date that has any pre-market bar.
                // Find that date from the newest bar, then take its latest pre-market bar.
                const newestEtDate = bars.length ? etDate(bars[0].t) : null;
                // bars are desc (newest first); newest pre-market bar ON THE NEWEST ET DATE.
                const preBar = bars.find(b => {
                    const m = etMin(b.t);
                    return etDate(b.t) === newestEtDate && m >= 4 * 60 && m < REG_OPEN;
                });
                const gapDiag = { ticker, lastClose, newestEtDate, foundPreBar: !!preBar };
                if (!preBar) { return; }
                const prePrice = preBar.c;
                const gapPct = ((prePrice - lastClose) / lastClose) * 100;
                const isGapUp = gapPct >= GAP_UP_THRESHOLD - 1e-9;
                // The pre-market price is only meaningful DURING pre-market (04:00–09:30 ET).
                // Once regular hours begin (or after the close) it's a stale morning print, so
                // we don't surface it — the live price has moved on. Gate on the NY session.
                const sess = (typeof getMarketSession === 'function') ? getMarketSession() : 'closed';
                if (sess !== 'pre') { window.__gapLine = null; return; }
                // Always show the pre-market line when in the pre-market session. Orange = ≥+2%
                // gap-up (alert-worthy); grey = any other pre-market price (informational).
                window.__gapLine = { prePrice, lastClose, gapPct, at: preBar.t, isGapUp };
            } catch (e) { window.__gapLine = null; }
        }

        // Compute a position's live metrics from enriched data + current price.
        function computePosition(p) {
            const byTicker = (typeof state.screenerAllResults !== 'undefined' && Array.isArray(state.screenerAllResults))
                ? state.screenerAllResults.find(r => r.ticker === p.ticker) : null;
            const snap = byTicker ? Watchlist.snapshotFromRow(byTicker) : {};
            const cur = snap.price != null ? snap.price : null;
            const entry = Number(p.entry) || null;
            const shares = Number(p.shares) || 0;
            const pnl = (cur != null && entry != null) ? (cur - entry) * shares : null;
            const pnlPct = (cur != null && entry != null && entry !== 0) ? ((cur - entry) / entry) * 100 : null;
            const days = p.date ? Math.max(0, Math.round((Date.now() - new Date(p.date).getTime()) / 86400000)) : null;
            const stop = Number(p.stop) || null;
            const risk = (entry != null && stop != null && stop < entry) ? entry - stop : null;
            const rmult = (risk && cur != null) ? (cur - entry) / risk : null;
            const cost = (entry != null) ? entry * shares : null;
            return { snap, cur, entry, shares, pnl, pnlPct, days, stop, rmult, cost,
                score: snap.score, stage: snap.stage, stageLabel: snap.stageLabel,
                latestAlert: latestAlertForRow(snap) };
        }

        // Portfolio sort state (persisted).
        const PF_SORT_KEY = 'abPortfolioSortV1';
        let portfolioSort = (() => {
            try { const r = JSON.parse(localStorage.getItem(PF_SORT_KEY)); if (r && r.key) return r; } catch (e) {}
            return { key: 'date', dir: 'desc' };
        })();
        function persistPortfolioSort() { try { localStorage.setItem(PF_SORT_KEY, JSON.stringify(portfolioSort)); } catch (e) {} }
        function updatePortfolioSortIndicators() {
            document.querySelectorAll('.pf-sort').forEach(th => {
                const ind = th.querySelector('.pf-sort-ind'); if (!ind) return;
                if (th.dataset.pfsort === portfolioSort.key) {
                    ind.textContent = portfolioSort.dir === 'asc' ? ' ▲' : ' ▼';
                    th.classList.add('text-slate-300');
                } else { ind.textContent = ''; th.classList.remove('text-slate-300'); }
            });
        }

        function renderPortfolio() {
            const wrap = document.getElementById('portfolioTableWrap');
            const empty = document.getElementById('portfolioEmpty');
            const body = document.getElementById('portfolioBody');
            const foot = document.getElementById('portfolioFoot');
            if (!wrap || !empty || !body) return;

            const positions = Portfolio.all();
            if (!positions.length) {
                empty.classList.remove('hidden'); wrap.classList.add('hidden');
                body.innerHTML = ''; if (foot) foot.innerHTML = '';
                return;
            }
            empty.classList.add('hidden'); wrap.classList.remove('hidden');

            const rows = positions.map(p => ({ p, m: computePosition(p) }));
            const totalCost = rows.reduce((s, r) => s + (r.m.cost || 0), 0);

            // Sort
            const coll = new Intl.Collator(undefined, { sensitivity: 'base' });
            const dir = portfolioSort.dir === 'asc' ? 1 : -1, k = portfolioSort.key;
            const val = ({ p, m }) => {
                switch (k) {
                    case 'ticker': return p.ticker || '';
                    case 'date':   return p.date ? new Date(p.date).getTime() : 0;
                    case 'shares': return m.shares;
                    case 'entry':  return m.entry;
                    case 'price':  return m.cur;
                    case 'pnl':    return m.pnl;
                    case 'pnlpct': return m.pnlPct;
                    case 'days':   return m.days;
                    case 'rmult':  return m.rmult;
                    case 'weight': return m.cost;
                    case 'score':  return m.score;
                    case 'stage':  return m.stage;
                    case 'alert':  { const la = m.latestAlert; return la ? (1000 - (la.days || 0) * 10) + (ALERT_STRENGTH[la.type] || 0) : -1; }
                    default: return 0;
                }
            };
            rows.sort((a, b) => {
                if (k === 'ticker') return coll.compare(a.p.ticker || '', b.p.ticker || '') * dir;
                const va = val(a), vb = val(b);
                const na = (va == null || !Number.isFinite(Number(va))) ? null : Number(va);
                const nb = (vb == null || !Number.isFinite(Number(vb))) ? null : Number(vb);
                if (na === null && nb === null) return 0;
                if (na === null) return 1; if (nb === null) return -1;
                return (na - nb) * dir;
            });
            updatePortfolioSortIndicators();

            const money = v => v == null ? '--' : `${v < 0 ? '-' : ''}$${Math.abs(v).toFixed(2)}`;
            const pct = v => v == null ? '--' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;
            const pnlCls = v => v == null ? 'text-slate-500' : v > 0 ? 'text-emerald-300' : v < 0 ? 'text-rose-300' : 'text-slate-300';

            body.innerHTML = rows.map(({ p, m }) => {
                const la = m.latestAlert;
                const alertCell = la
                    ? `<span title="${la.label} — ${alertDaysText(la.days)}" class="px-2 py-1 rounded border ${la.meta.chip} ${la.meta.text} font-bold">${la.label}</span>`
                    : `<span class="px-2 py-1 rounded border bg-slate-900 border-slate-700 text-slate-500">--</span>`;
                const weight = (totalCost > 0 && m.cost != null) ? (m.cost / totalCost) * 100 : null;
                const stageTxt = m.stageLabel || (m.stage ? 'S' + m.stage : '--');
                return `<tr data-ticker="${p.ticker}" class="hover:bg-blue-950/20 transition-colors">
                    <td class="py-2 pl-5 pr-2 font-black text-white cursor-pointer" data-pfload="${p.ticker}">${p.ticker}</td>
                    <td class="py-2 px-2 text-slate-400">${p.date || '--'}</td>
                    <td class="py-2 px-2 text-right text-slate-300">${m.shares || '--'}</td>
                    <td class="py-2 px-2 text-right text-slate-300">${money(m.entry)}</td>
                    <td class="py-2 px-2 text-right text-slate-200">${money(m.cur)}</td>
                    <td class="py-2 px-2 text-right font-bold ${pnlCls(m.pnl)}">${money(m.pnl)}</td>
                    <td class="py-2 px-2 text-right font-bold ${pnlCls(m.pnlPct)}">${pct(m.pnlPct)}</td>
                    <td class="py-2 px-2 text-right text-slate-400">${m.days != null ? m.days : '--'}</td>
                    <td class="py-2 px-2 text-right ${pnlCls(m.rmult)}">${m.rmult != null ? (m.rmult >= 0 ? '+' : '') + m.rmult.toFixed(1) + 'R' : '--'}</td>
                    <td class="py-2 px-2 text-right text-slate-400">${weight != null ? weight.toFixed(0) + '%' : '--'}</td>
                    <td class="py-2 px-2 text-right font-black text-slate-200">${m.score != null ? m.score : '--'}</td>
                    <td class="py-2 px-2 text-slate-300">${stageTxt}</td>
                    <td class="py-2 px-2">${alertCell}</td>
                    <td class="py-2 px-2 text-right pr-4"><button data-pfremove="${p.id}" title="Remove position" class="text-slate-600 hover:text-rose-400 transition-colors text-sm leading-none">×</button></td>
                </tr>`;
            }).join('');

            // Totals footer.
            const totPnl = rows.reduce((s, r) => s + (r.m.pnl || 0), 0);
            const totPnlPct = totalCost > 0 ? (totPnl / totalCost) * 100 : null;
            if (foot) {
                foot.innerHTML = `<tr class="text-slate-300">
                    <td class="py-2 pl-5 pr-2 uppercase tracking-wide text-slate-500">Total</td>
                    <td colspan="4" class="py-2 px-2 text-right text-slate-500">cost ${money(totalCost)}</td>
                    <td class="py-2 px-2 text-right ${pnlCls(totPnl)}">${money(totPnl)}</td>
                    <td class="py-2 px-2 text-right ${pnlCls(totPnlPct)}">${pct(totPnlPct)}</td>
                    <td colspan="7"></td>
                </tr>`;
            }
        }


        // Fetch the market snapshot in NETWORK-FRIENDLY CHUNKS. The grouped-daily endpoint returns
        // the whole market in ONE multi-MB response, which some networks (lower MTU / DPI middlebox,
        // common when travelling) silently drop — while small/medium requests sail through. Polygon's
        // snapshot endpoint accepts a comma-separated ?tickers= list, so we pull the universe ~100 at
        // a time (~60 kB each) and stitch the results. Same data, same shape, no big download.


        function getSnapshotPrice(s) {
            return s?.day?.c || s?.min?.c || s?.lastTrade?.p || s?.prevDay?.c || 0;
        }

        // Per-ticker LIVE today's-move (%). Grouped-daily can't provide this — it only has
        // completed sessions — so we hit the single-ticker snapshot, which carries today's
        // developing bar + todaysChangePerc. Returns null off-hours or on any failure, so the
        // TD column stays blank rather than echoing the closed 1D. Cheap: one call per
        // enriched name, only while the market is open.

        function getSnapshotVolume(s) {
            return s?.day?.v || s?.min?.av || s?.prevDay?.v || 0;
        }

        function getSnapshotLow(s) {
            return s?.day?.l || s?.min?.l || s?.prevDay?.l || 0;
        }


        // Translate a numeric SIC code to a human-readable industry label. Polygon's Starter
        // tier often omits sic_description but still returns sic_code; this fills the gap so
        // the "what the company does" line always has something real. Specific 4-digit codes
        // first, then 2-digit major-group ranges as a fallback.
        function sicLabelFromCode(code) {
            if (!code) return '';
            const c = String(code).padStart(4, '0');
            const exact = {
                '3674': 'Semiconductors & related devices', '7372': 'Prepackaged software',
                '7370': 'Computer services', '7389': 'Business services', '3571': 'Electronic computers',
                '3576': 'Computer communications equipment', '3661': 'Telephone & telegraph apparatus',
                '2834': 'Pharmaceutical preparations', '2836': 'Biological products',
                '3841': 'Surgical & medical instruments', '6021': 'National commercial banks',
                '6199': 'Finance services', '6211': 'Security brokers & dealers',
                '1311': 'Crude petroleum & natural gas', '2911': 'Petroleum refining',
                '3711': 'Motor vehicles', '5812': 'Eating places', '5961': 'Retail — catalog & mail-order',
                '4512': 'Air transportation', '1531': 'Operative builders (homebuilding)',
                '3559': 'Special industry machinery', '3827': 'Lab/optical instruments'
            };
            if (exact[c]) return exact[c];
            const major = parseInt(c.slice(0, 2), 10);
            if (major >= 1 && major <= 9) return 'Agriculture, forestry & fishing';
            if (major >= 10 && major <= 14) return 'Mining & extraction';
            if (major >= 15 && major <= 17) return 'Construction';
            if (major >= 20 && major <= 39) return 'Manufacturing';
            if (major >= 40 && major <= 49) return 'Transportation & utilities';
            if (major >= 50 && major <= 51) return 'Wholesale trade';
            if (major >= 52 && major <= 59) return 'Retail trade';
            if (major >= 60 && major <= 67) return 'Finance, insurance & real estate';
            if (major >= 70 && major <= 89) return 'Services';
            return '';
        }


        // Per-ticker reference data (sector/industry, fundamentals) changes rarely, so we cache it.
        // Reads come from an in-memory object (parsed once); writes are debounced + idle-flushed, so a
        // full first scan no longer triggers thousands of synchronous localStorage serializations.
        // Last element satisfying pred, scanning from the end — avoids the [...arr].reverse().find() copy.
        function makePersistentCache(storageKey, ttlMs) {
            let mem = null, dirty = false, timer = 0;
            const load = () => { if (mem) return mem; try { mem = JSON.parse(localStorage.getItem(storageKey) || '{}'); } catch (e) { mem = {}; } return mem; };
            const flush = () => { timer = 0; if (!dirty) return; dirty = false; try { localStorage.setItem(storageKey, JSON.stringify(mem)); } catch (e) {} };
            const schedule = () => { dirty = true; if (timer) return; timer = setTimeout(() => { if (window.requestIdleCallback) requestIdleCallback(flush, { timeout: 3000 }); else flush(); }, 4000); };
            return {
                get(t) { const c = load(); const h = c[t]; return (h && (Date.now() - h.savedAt) < ttlMs) ? h.data : undefined; },
                set(t, data) { const c = load(); c[t] = { savedAt: Date.now(), data }; schedule(); },
                flush
            };
        }
        const _classCache = makePersistentCache('tickerClassificationV1', 7 * 24 * 60 * 60 * 1000);
        const _growthCache = makePersistentCache('growthLiteV1', 4 * 24 * 60 * 60 * 1000);

        async function fetchTickerClassification(ticker, key) {
            const cached = _classCache.get(ticker);
            if (cached) return cached;
            try {
                const res = await polygonFetch(`https://api.polygon.io/v3/reference/tickers/${ticker}?apiKey=${key}`, ticker);
                const r = res.results || {};
                const data = {
                    name: r.name || ticker,
                    ...classifyIndustry(ticker, r.name || ticker, r.sic_description || r.description || '', r.sic_code || '', r.type || '')
                };
                _classCache.set(ticker, data);
                return data;
            } catch(e) {
                return { name: ticker, ...classifyIndustry(ticker) };
            }
        }

        function buildScreenerQueueFromSnapshot(snapshot, universe, filters) {
            const universeMap = new Map((universe || []).map(t => [t.ticker, t]));
            return (snapshot || [])
                .map(s => {
                    const meta = universeMap.get(s.ticker) || { ticker: s.ticker, name: s.ticker };
                    const price = getSnapshotPrice(s);
                    const volume = getSnapshotVolume(s);
                    const low = getSnapshotLow(s);
                    const changePct = Number.isFinite(s.todaysChangePerc) ? s.todaysChangePerc : 0;
                    const dollarVol = price * volume;
                    return { ticker: s.ticker, name: meta.name || s.ticker, type: meta.type, snapshot: s, snapshotPrice: price, snapshotLow: low, snapshotVolume: volume, changePct, dollarVol };
                })
                .filter(x => x.ticker && !x.ticker.includes('.') && !x.ticker.includes('/'))
                .filter(x => !x.type || ['CS', 'ADRC', 'ADRP'].includes(x.type))
                .filter(x => x.snapshotPrice >= filters.minPrice)
                .filter(x => x.snapshotVolume >= filters.minAvgVol)
                .sort((a, b) => {
                    // ALERT-LIKELIHOOD priority: enrich the names most likely to FIRE a signal
                    // first, so the alert feed lights up in the first seconds of a scan instead
                    // of being buried behind sleepy mega-caps. We weight today's MOVE and a
                    // volume-SURGE proxy heavily, with only a mild liquidity term (log dollar-vol)
                    // so a genuine small/mid-cap breakout outranks a flat mega-cap.
                    const alertScore = (x) => {
                        const move = Math.abs(x.changePct || 0);          // today's % move (both directions matter)
                        const upMove = Math.max(0, x.changePct || 0);     // extra weight to up-moves (breakouts)
                        // Volume surge proxy: snapshot volume vs. the snapshot's own avg if present,
                        // else fall back to dollar-volume magnitude. Higher = more unusual activity.
                        const surge = (x.snapshot && x.snapshot.day && x.snapshot.prevDay && x.snapshot.prevDay.v > 0)
                            ? (x.snapshot.day.v / x.snapshot.prevDay.v) : 1;
                        const liq = Math.log10(1 + (x.dollarVol || 0));   // mild, log-scaled liquidity
                        // Weighted blend: move + up-bias + volume surge dominate; liquidity is a tiebreaker.
                        return move * 3 + upMove * 2 + (surge - 1) * 40 + liq;
                    };
                    return alertScore(b) - alertScore(a);
                });
        }



        /**
         * Lightweight Alpha/Beta smart-signal detector (screener parity with the main panel).
         * @param {number[]} closes
         * @param {number[]} highs
         * @param {number[]} lows
         * @param {Array<{t:number,o:number,h:number,l:number,c:number,v:number}>} results  Raw bars
         * @param {number} [mult=1.5]       ATR multiple for trigger sensitivity
         * @param {number} [emaPeriod=10]
         * @param {number} [atrPeriod=14]
         * @param {number} [weakMinBelow=3]
         * @param {number} [recentBars=1]
         * @returns {SmartSignal}
         */
        // BETA_RECENCY_BARS now comes from the engine shim (window.PivotaEngine) — single
        // source of truth in lib/engine.js. (Was declared here; removed in Phase 1.)

        // Precompute a reusable SPY context (10-EMA series + date→index map) ONCE per
        // scoring cycle so per-ticker computeSignalFactors() stays cheap. spyBars is the
        // parallel-array shape { t:[], closes:[], highs:[], lows:[] }. Returns null if SPY
        // history is unavailable (alignment factors then degrade gracefully to null).

        // Per-signal quality factors for a 10-EMA reclaim at bar `idx` — the inputs the
        // backtests showed matter: reclaim-bar RVOL, next-bar follow-through, market
        // alignment / relative-strength-in-dip, and flush depth. PURE (no scoring here).
        // bars = the ticker's results [{t,o,h,l,c,v}, ...] ascending; spyCtx from
        // buildSpyCtx() or null. Fields are null where data is insufficient — notably
        // `held` is null on the latest bar (no next bar yet = the day-0 provisional case).


        // ═══════════════════════════════════════════════════════════════════
        //  FORMING-STATE DETECTOR  — "the signal is about to fire"
        //  Every signal in this engine has a KNOWN trigger line, so the distance
        //  to that line is measurable BEFORE the cross. This returns, per signal,
        //  how close price is to triggering (0–100 "proximity", 100 = at the line)
        //  plus the headline nearest-trigger. Pure: reads only already-computed data.
        //    closes/highs/lows : OHLC arrays
        //    channel           : detectChannelOmega() result (has breakout20/55)
        //    atr               : latest ATR value
        //  Returns { items:[{type,label,proximity,distPct,note}], best } or null.
        // ── DAILY SWING-LOW AVWAP (pullback-defense quality) ─────────────────────
        // Anchors to the most recent significant swing low (start of the current move),
        // computes the volume-weighted average price every buyer since the low has paid,
        // then grades how well price is holding that line — normalized by the stock's
        // own ATR so it's comparable across cheap and expensive names. Used to FILTER
        // low-quality alerts: a breakout pulling back but holding above its swing-low
        // AVWAP is institutionally defended; one slicing below it on the same move is
        // suspect (the average participant since the low is now underwater).
        //
        // Returns { score 0-100, anchorIdx, avwap, distAtr } or null when no clean anchor.
        //   score ≥ 80  → comfortably above (High-eligible)
        //   score ≥ 40  → not below (Balanced-eligible)
        //   score < 40  → undefended / low-quality (All-only)





        async function fetchGrowthLite(ticker, key) {
            const cached = _growthCache.get(ticker);
            if (cached) return cached;
            try {
                const data = await polygonFetch(`https://api.polygon.io/vX/reference/financials?ticker=${ticker}&timeframe=quarterly&order=desc&limit=14&sort=period_of_report_date&apiKey=${key}`, ticker);
                const rows = data.results || [];
                const q = rows.map(r => {
                    const fin = r.financials || {};
                    const inc = fin.income_statement || {};
                    const eps = inc.basic_earnings_per_share?.value ?? inc.diluted_earnings_per_share?.value ?? null;
                    const rev = inc.revenues?.value ?? inc.sales?.value ?? null;
                    return { eps, rev };
                }).reverse();
                const epsYoY = [], salesYoY = [];
                for (let i = 0; i < q.length; i++) {
                    const prior = i >= 4 ? q[i - 4] : null;
                    epsYoY.push(prior && prior.eps != null && q[i].eps != null && Math.abs(prior.eps) > 0.001 ? ((q[i].eps - prior.eps) / Math.abs(prior.eps)) * 100 : null);
                    salesYoY.push(prior && prior.rev != null && q[i].rev != null && prior.rev > 0 ? ((q[i].rev - prior.rev) / prior.rev) * 100 : null);
                }
                const epsScore = gaugeScore(lastWhere(epsYoY, x => x !== null), epsYoY);
                const salesScore = gaugeScore(lastWhere(salesYoY, x => x !== null), salesYoY);
                const epsAcceleration = (epsScore || 0) >= 50;
                const salesAcceleration = (salesScore || 0) >= 50;
                let growthStatus = 'unknown';
                const best = Math.max(epsScore || 0, salesScore || 0);
                if (best >= 75) growthStatus = 'strong';
                else if ((epsScore || 0) >= 50 && (salesScore || 0) >= 50) growthStatus = 'solid';
                else if (best >= 25) growthStatus = 'mixed';
                else if (epsScore !== null || salesScore !== null) growthStatus = 'weak';
                const result = { epsAcceleration, salesAcceleration, epsScore, salesScore, growthStatus };
                _growthCache.set(ticker, result);
                return result;
            } catch(e) {
                return { epsAcceleration: null, salesAcceleration: null, epsScore: null, salesScore: null, growthStatus: 'unknown' };
            }
        }

        // Mirrors calculateConvictionScore EXACTLY (same buckets, same 5 penalties) so a stock
        // scores identically whether loaded directly or found in the screener. Pure function —
        // no DOM side-effects — so it's safe to call in the bulk enrichment loop.

        function screenerPassesPreset(row) {
            if (state.screenerPreset === 'stage2') return row.stage === 2;
            if (state.screenerPreset === 'volume') return row.volumeSignal && row.volumeSignal.type !== 'none';
            if (state.screenerPreset === 'timing') return row.timingScore >= 8;
            return true;
        }

        /**
         * Fully enrich one screener ticker: fetch history, score it, and return a flat row
         * for the results table. The returned row carries the same fields the dashboard uses
         * (score/tier/signal/rsPower/etc.), so screener and single-ticker stay consistent.
         * @param {{ticker:string, name?:string, snapshotPrice?:number, snapshotVolume?:number, changePct?:number}} meta
         * @param {string} key      Polygon API key
         * @param {{minPrice:number, minAvgVol:number}} filters
         * @returns {Promise<Object|null>}  Enriched row, or null if it fails the gate / lacks history
         */
        async function analyzeScreenerTicker(meta, key, filters) {
            const toDate = new Date().toISOString().split('T')[0];
            const fromDateObj = new Date(); fromDateObj.setDate(fromDateObj.getDate() - 450); // ~310 trading bars — a safe buffer over the deepest lookback we use (252 = 52wk high, plus 200-MA and the 12-mo gate). gain12 is index-based (n-252) so unaffected. Was 600 (~414); 1850 originally.
            const fromDate = fromDateObj.toISOString().split('T')[0];
            const data = await polygonFetch(`https://api.polygon.io/v2/aggs/ticker/${meta.ticker}/range/1/day/${fromDate}/${toDate}?adjusted=true&sort=asc&limit=50000&apiKey=${key}`, meta.ticker);
            if (!data.results || data.results.length < 220) return null;
            const closes = data.results.map(r => r.c), highs = data.results.map(r => r.h), lows = data.results.map(r => r.l), volumes = data.results.map(r => r.v || 0);
            const historyPrice = closes[closes.length - 1], avgVol50 = MathLib.sma(volumes, 50) || 0;
            const price = meta.snapshotPrice || historyPrice;
            if (price < filters.minPrice || avgVol50 < filters.minAvgVol) return null;
            // Kick off the two independent per-ticker fetches (financials + classification) NOW so
            // their network latency overlaps the heavy sync scoring + the cached SPY/bench awaits
            // below — instead of being two extra SEQUENTIAL round-trips at the end. Both helpers
            // cache internally (no double-fetch); .catch keeps a transient failure from rejecting
            // the whole row (or leaking an unhandled rejection if we early-return below).
            const growthP = fetchGrowthLite(meta.ticker, key).catch(() => ({ epsAcceleration: null, salesAcceleration: null, epsScore: null, salesScore: null, growthStatus: 'unknown' }));
            const classP = fetchTickerClassification(meta.ticker, key).catch(() => ({}));
            const benchBars = await fetchBenchmarkBars(rsBenchmarkSymbol, key);
            // Real today's-move for the TD column (market-hours only; null otherwise).
            const liveChangePct = (Number.isFinite(meta.changePct) && meta.changePct !== 0) ? meta.changePct : null; // reuse the bulk snapshot fetched once at scan start (was a redundant per-ticker snapshot call)
            const mv = evaluateMinerviniLite(closes, lows, highs, data.results, benchBars);
            const __brd = computeRsBreadthBits(closes, data.results, benchBars); // 21-day breadth contribution
            const atrSeries = MathLib.atrSeries(highs, lows, closes, 14);
            const atr = atrSeries[atrSeries.length - 1] || 0;
            const ema9 = MathLib.ema(closes, 9), ema21 = MathLib.ema(closes, 21), sma20 = MathLib.sma(closes, 20), sma50 = MathLib.sma(closes, 50), sma200 = MathLib.sma(closes, 200);
            let vcpRow = null; try { vcpRow = detectVcpBase(highs, lows, closes, volumes); } catch (e) {}
            const pct1d = MathLib.getPctChange(closes, 1, data.results) || 0, pct1w = MathLib.getPctChange(closes, 5, data.results) || 0;
            const pct1m = MathLib.getPctChange(closes, 21, data.results) || 0, pct3m = MathLib.getPctChange(closes, 63, data.results) || 0, pct6m = MathLib.getPctChange(closes, 126, data.results) || 0;
            const quality = calculateMarketQualityMetrics(closes, highs, lows, volumes, historyPrice, atr, avgVol50);
            const rmvSeries = MathLib.rmvSeries(highs, lows, closes, 15);
            const rmv = lastWhere(rmvSeries, v => v !== null);
            const adrPctRow = MathLib.adrPct(highs, lows, 20);
            const liveVolume = meta.snapshotVolume || volumes[volumes.length - 1];
            const rvol = liveVolume / avgVol50;
            // RSI multi-timeframe (same as the single-ticker engine) so scoring matches exactly.
            const rsi1d = MathLib.rsi(closes) || 0;
            const rsi1w = MathLib.rsi(MathLib.aggregateCloses(data.results, '1W')) || 0;
            const rsi1m = MathLib.rsi(MathLib.aggregateCloses(data.results, '1M')) || 0;
            // RSI EXPANSION: how much daily RSI has risen over the last ~5 bars. Positive =
            // momentum accelerating into the move (the strongest confirmation); flat/negative
            // = divergence. Feeds the CONFIRM alert score as its 5th bucket.
            const rsiArr = MathLib.rsiSeries(closes, 14);
            let rsiExpansion = null;
            if (rsiArr && rsiArr.length) {
                const last = rsiArr[rsiArr.length - 1];
                const prior = rsiArr[rsiArr.length - 6]; // ~5 bars ago
                if (Number.isFinite(last) && Number.isFinite(prior)) rsiExpansion = last - prior;
            }
            const lastLow = meta.snapshotLow || lows[lows.length - 1];
            const metrics = {
                mvPassed: mv.passed, rsProxy: mv.rs, rsRaw: mv.rsRaw,
                rsNewHigh: mv.rsSignals ? mv.rsSignals.rsNewHigh : false,
                deltaTrigger: mv.rsSignals ? mv.rsSignals.deltaTrigger : false,
                deltaDays: (mv.deltaDays != null) ? mv.deltaDays : null,
                rvol, pct1m, pct3m, pct6m, pct12m: quality.pct12m,
                dist9: ema9 ? ((price - ema9) / ema9) * 100 : 0,
                dist21: ema21 ? ((price - ema21) / ema21) * 100 : 0,
                vcpStage: vcpRow ? vcpRow.stage : 'none', vcpIsVcp: !!(vcpRow && vcpRow.isVcp), vcpConfirmed: !!(vcpRow && vcpRow.confirmed),
                dist50: sma50 ? ((price - sma50) / sma50) * 100 : 0,
                dist200: sma200 ? ((price - sma200) / sma200) * 100 : 0,
                sma50Trend: quality.sma50Trend, sma150Trend: quality.sma150Trend, sma200Trend: quality.sma200Trend,
                atrPct: quality.atrPct, upDownVolRatio: quality.upDownVolRatio, accumulationDays: quality.accumulationDays,
                adrPct: adrPctRow,
                distributionDays: quality.distributionDays, rangeVsAvg: quality.rangeVsAvg, belowHighPct: quality.belowHighPct, baseDepthPct: quality.baseDepthPct,
                // Extra inputs required for the stricter penalty set + setup RSI sub-score:
                price, low: lastLow, atr, volatilityContraction: quality.volatilityContraction,
                rsi1d, rsi1w, rsi1m
            };
            const smartSignal = detectBetaSignal(closes, highs, lows, data.results);
            // VARS (volatility-adjusted RS vs SPY) — parity with the loaded-ticker Trade Assistant.
            // SPY bars are fetched once and session-cached (fetchSpyBars), so this is reused per row.
            let rowVarsValue = null, rowVarsState = null;
            try {
                const spy = await fetchSpyBars(key);
                if (spy) {
                    const varsSeries = computeVarsSeries(data.results, spy, VARS_WINDOW, VARS_ATR_LEN);
                    if (varsSeries) {
                        rowVarsValue = lastWhere(varsSeries, Number.isFinite);
                        rowVarsState = varsStateLabel(varsSeries);
                    }
                }
            } catch (e) { /* VARS optional — falls back to "unavailable" in the rating */ }
            const channel = detectChannelOmega(closes, highs, lows, data.results, 20, 55, 20, false);
            const stageRes = detectStage(closes, highs, lows);
            const volumeSignal = detectVolumeSignalLite(data.results, avgVol50);
            const growth = await growthP;
            const classification = await classP;
            const dollarVolume = price * liveVolume;
            const extensionStatus = getExtensionStatus(metrics.dist50, quality.atrPct);
            // Breakout Quality Score (BQS) — same engine as the single-ticker panel.
            // BQS runs its own pattern/failed-breakout detectors from the OHLC series, so we
            // only need to supply the contextual metrics we already computed above.
            let bqsGrade = null, bqsTier = null, bqsTierLabel = null, bqsMode = null;
            try {
                const grpRot = (classification.industryGroup && Array.isArray(groupRankingData))
                    ? groupRankingData.find(g => g.name === classification.industryGroup) : null;
                const groupRankPct = (meta.groupRank && meta.groupRankTotal) ? (meta.groupRank / meta.groupRankTotal) * 100 : null;
                const ms = engineState.lastMarketStructure;
                const v = (ms && ms.verdict) ? ms.verdict : '';
                const bqsInput = {
                    stage: stageRes ? stageRes.stage : null,
                    stageSub: stageRes ? stageRes.sub : null,
                    sma50Trend: quality.sma50Trend,
                    sma200Trend: quality.sma200Trend,
                    sma50AboveSma200: (sma50 && sma200) ? (sma50 > sma200) : null,
                    higherHighsLows: quality.higherHighsLows === true,
                    dist50: metrics.dist50, dist200: metrics.dist200,
                    rmv, rmvFalling: false, rmvRisingSharply: false,
                    rvol,
                    volumeDryUp: quality.volatilityContraction > 20 || (quality.accumulationDays > quality.distributionDays && rvol < 1),
                    volumeRisingIntoBreakout: rvol >= 1.4 && quality.accumulationDays > quality.distributionDays,
                    climaxVolumeInBase: quality.distributionDays >= 3 && quality.distributionDays > quality.accumulationDays,
                    adrMultFrom50: metrics.atrMult,
                    belowEma20Breakout: metrics.dist9 < 0 && rvol >= 1.5,
                    veryExtendedFromEma20: metrics.dist9 > 8,
                    marketVerdict: v,
                    spyStrongBullish: /Risk-On|Broad/.test(v),
                    spyBullish: /Constructive/.test(v),
                    spyAbove200: ms ? (ms.above200 != null ? ms.above200 > 50 : null) : null,
                    spyHigherHighs: ms ? (ms.rspTrend === 'up') : false,
                    allIndicesUptrend: ms ? (ms.above50 != null && ms.above50 > 60) : false,
                    vixLow: false, vixHigh: false,
                    groupRankPct,
                    groupPerf3m: grpRot ? grpRot.perf6m : (meta.groupPerf6m ?? null),
                    groupRsRising: grpRot ? (grpRot.rsRising === true) : false,
                    groupCluster: grpRot ? (grpRot.breakoutCount >= 2) : false,
                    groupStage2Rising: grpRot ? (grpRot.stage2Rising === true) : false,
                    groupStage4: grpRot ? (grpRot.stage4 === true) : false,
                    breadthPct: ms ? ms.above50 : null,
                    sectorTop3: grpRot ? (grpRot.sectorTop3 === true) : false,
                    weakGroupBearMkt: (groupRankPct != null && groupRankPct > 75) && /Risk-Off|Weak|Caution/.test(v),
                    highRvolWeakFollow: rvol >= 2.0 && pct1m < 0,
                    rsNewHigh: !!(mv.rsSignals && mv.rsSignals.rsNewHigh),
                    deltaTrigger: !!(mv.rsSignals && mv.rsSignals.deltaTrigger),
                    belowHighPct: quality.belowHighPct,
                    rsRating: mv ? mv.rs : null,
                    grpRotIn: !!(grpRot && (grpRot.rotationTag === 'in' || grpRot.rotationTag === 'leader')),
                    grpRotOut: !!(grpRot && (grpRot.rotationTag === 'out' || grpRot.rotationTag === 'dormant'))
                };
                const bqs = BQS.compute(bqsInput, { closes, highs, lows, volumes });
                if (bqs) { bqsGrade = bqs.grade; bqsTier = bqs.tier; bqsTierLabel = bqs.tierLabel; bqsMode = bqs.vcpMode; }
            } catch (e) { /* BQS non-critical */ }
            Object.assign(metrics, {
                ticker: meta.ticker,
                stockName: meta.name || meta.ticker,
                stage: stageRes ? stageRes.stage : null,
                stageLabel: stageRes ? stageRes.label : '',
                rmv,
                channelStage: channel ? channel.stage : null,
                channelPosition: channel ? channel.position : null,
                omegaSignal: channel ? channel.omegaSignal : 'NONE',
                volumeSignal,
                hasAlpha: smartSignal.hasAlpha,
                hasBeta: smartSignal.hasBeta,
                alphaDays: smartSignal.alphaDays,
                betaDays: smartSignal.betaDays,
                // Fresh-trigger parity: latest signal age + % from signal price (mirror of main panel).
                alphaSignalDays: smartSignal.alphaSignalDays,
                alphaFromNow: smartSignal.alphaFromNow,
                betaSignalDays: smartSignal.betaSignalDays,
                betaFromNow: smartSignal.betaFromNow,
                // VARS value/state so computeRowTradeRating can score it exactly like the main panel.
                varsValue: rowVarsValue,
                varsState: rowVarsState,
                epsScore: growth.epsScore,
                salesScore: growth.salesScore,
                growthStatus: growth.growthStatus,
                sector: classification.sector,
                industryGroup: classification.industryGroup,
                groupRank: meta.groupRank ?? null,
                groupRankTotal: meta.groupRankTotal ?? null,
                groupPerf6m: meta.groupPerf6m ?? null,
                groupBreadth: meta.groupBreadth ?? null,
                dollarVolume,
                stopDistPct: lastLow && price ? ((price - lastLow) / price) * 100 : null,
                atrMult: quality.atrPct > 0 ? Math.max(0, metrics.dist50) / quality.atrPct : null
            });
            const scores = scoreScreenerCandidate(metrics);
            const forming = computeFormingState(closes, highs, lows, channel, atr);
            const avwapDef = computeAvwapDefense(closes, highs, lows, volumes, atr);
            const tier = getTierFromScore(scores.finalScore);
            const rmvZone = getRmvZone(rmv);
            // ── RS POWER ─────────────────────────────────────────────────────────────
            // A single 0–100 "how strong is the RS line right now" reading + tier label,
            // surfacing the leading-indicator hierarchy in one screener field:
            //   • Delta (RS new high BEFORE price) = the gold tier (accumulation ahead of price)
            //   • RS new high (with price) = strong
            //   • near a new high (proximity) = building
            // proximity = rsToday / prior-52w-RS-high (100 = at the high).
            const rsSig = mv.rsSignals || null;
            const { rsPower, rsPowerTier, rsProximity } = computeRsPower(rsSig);
            return {
                ticker: meta.ticker, name: meta.name || meta.ticker, price, low: lastLow,
                volume: liveVolume, avgVol50, snapshotVolume: liveVolume, dollarVolume,
                changePct: meta.changePct || 0, score: scores.finalScore, tier,
                signal: smartSignal.signal, hasAlpha: smartSignal.hasAlpha, hasBeta: smartSignal.hasBeta,
                alphaDays: smartSignal.alphaDays, betaDays: smartSignal.betaDays,
                stage: stageRes ? stageRes.stage : null, stageLabel: stageRes ? stageRes.label : '--',
                channelStage: channel ? channel.stage : null,
                channelLabel: channel ? channel.label : '--',
                channelPosition: channel ? channel.position : null,
                omegaSignal: channel ? channel.omegaSignal : 'NONE',
                omegaLabel: channel ? channel.omegaLabel : 'Omega waiting',
                rmv, rmvZone, trendScore: scores.scoreStruct, demandScore: scores.scoreLiq, timingScore: scores.scoreGeom,
                pct3m, perf3m: pct3m, rvol, stage2: mv.stage2, volumeSignal,
                adrPct: adrPctRow,
                epsAcceleration: growth.epsAcceleration, salesAcceleration: growth.salesAcceleration,
                epsScore: growth.epsScore, salesScore: growth.salesScore, growthStatus: growth.growthStatus,
                extensionStatus,
                rsPower, rsPowerTier, rsProximity,
                // Promote the RS-signal flags to the top level so the breadth module,
                // cohort filters and finalizeRsPercentiles read consistent values. These
                // come from the SAME rsSig that determines rsPowerTier, so e.g.
                // rsPowerTier==='Delta' ⟺ deltaTrigger===true (no more 0-vs-31 mismatch).
                rsRaw: (mv.rsRaw != null ? mv.rsRaw : null),
                brdP: __brd.p, brdL: __brd.l, brdD: __brd.d, brdW: __brd.w,
                rsNewHigh: rsSig ? !!rsSig.rsNewHigh : false,
                deltaTrigger: rsSig ? !!rsSig.deltaTrigger : false,
                deltaDays: (mv.deltaDays != null) ? mv.deltaDays : null,
                rsi1d, rsiExpansion,
                avwapDefense: avwapDef ? avwapDef.score : null,
                avwapDistAtr: avwapDef ? avwapDef.distAtr : null,
                bqsGrade, bqsTier, bqsTierLabel, bqsMode,
                sector: classification.sector, industryGroup: classification.industryGroup,
                industry: classification.industry, sicCode: classification.sicCode,
                pct1d, pct1w, pct1m, pct6m, pct12m: metrics.pct12m,
                dist9: metrics.dist9, dist21: metrics.dist21, dist50: metrics.dist50, dist200: metrics.dist200,
                mvPassed: metrics.mvPassed,
                vcpStage: metrics.vcpStage, vcpIsVcp: metrics.vcpIsVcp, vcpConfirmed: metrics.vcpConfirmed,
                pctLive: Number.isFinite(liveChangePct) ? liveChangePct
                    : (Number.isFinite(meta.changePct) && meta.changePct !== 0 ? meta.changePct : null),
                above20: sma20 ? price > sma20 : null,
                above50: sma50 ? price > sma50 : null,
                above200: sma200 ? price > sma200 : null,
                volToday: liveVolume, avgVol50,
                forming: forming ? { type: forming.best.type, label: forming.best.label, proximity: forming.best.proximity, note: forming.best.note, distPct: forming.best.distPct } : null,
                scoreInput: metrics
            };
        }

        // ═══════════════════════════════════════════════════════════════════
        //  LIVE AUTO-REFRESH (Polygon Starter — 15-min delayed)
        //  Two cadences while the US market is open:
        //    • SNAPSHOT poll every LIVE_SNAPSHOT_SEC  → fast price/volume/RVOL ticks
        //    • FULL chart+analytics refresh every LIVE_FULL_SEC → redraws chart,
        //      RSI, RS-leadership gauge, etc. (heavier; spaced out to save API calls)
        //  Gated by a header toggle and by market hours so it never burns calls
        //  overnight or on weekends.
        // ═══════════════════════════════════════════════════════════════════
        const LIVE_SNAPSHOT_SEC = 30;   // fast price tick cadence
        const LIVE_FULL_SEC = 300;      // full chart/analytics refresh cadence (5 min)
        let liveEnabled = localStorage.getItem('abLiveEnabled') !== '0'; // default ON
        let liveCountdownTimer = null;
        let liveNextSnapshotAt = 0;
        let liveSecsSinceFull = 0;
        let liveFullInFlight = false;

        // ── Task coordinator (TODO-9) ────────────────────────────────────────────
        // Prevents the live auto-refresh and the background screener from competing for
        // API bandwidth + CPU. Priority model:
        //   • current-ticker refresh  = HIGH  (keep the loaded chart responsive)
        //   • screener enrichment      = MEDIUM (heavy, can yield)
        // During a heavy scan we DEFER the expensive full chart/analytics refresh (which
        // re-runs the whole pipeline) and stretch the lightweight snapshot cadence, so the
        // current ticker still ticks but doesn't fight the scan. When the scan ends, normal
        // cadence resumes immediately. Pure scheduling — no state rewiring.
        // TaskCoordinator extracted to src/task-coordinator.js (imported at top; self-registers window.TaskCoordinator).

        // Is the US equity market (incl. pre/post we still poll, but core RTH check)
        // open right now? Uses America/New_York wall clock. Mon–Fri 09:30–16:00 ET.
        // We poll a bit before/after (08:00–20:00 ET) so pre/post-market ticks show too.
        function isUsMarketHours() {
            const ny = getNyParts();
            if (!ny.ok) return true; // if TZ lookup fails, don't block refresh
            if (ny.day === 0 || ny.day === 6) return false;   // weekend → closed
            return ny.mins >= 8 * 60 && ny.mins <= 20 * 60;    // 08:00–20:00 ET (incl. pre/post)
        }

        // Finer-grained session phase in America/New_York: 'closed' | 'pre' | 'rth' | 'post'.
        // RTH = regular trading hours 09:30–16:00 ET. Pre = 04:00–09:30, Post = 16:00–20:00.
        // We read the NY wall-clock components DIRECTLY from Intl parts — never via
        // new Date(localeString), which re-parses in the host timezone and corrupts the
        // weekday/hour (that bug made Sunday read as "pre-market").
        function getNyParts() {
            try {
                const fmt = new Intl.DateTimeFormat('en-US', {
                    timeZone: 'America/New_York', weekday: 'short',
                    hour: '2-digit', minute: '2-digit', hour12: false
                });
                const parts = {};
                fmt.formatToParts(new Date()).forEach(p => { parts[p.type] = p.value; });
                const wkMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
                let hour = parseInt(parts.hour, 10);
                if (hour === 24) hour = 0; // some engines emit "24" for midnight
                return { day: wkMap[parts.weekday], mins: hour * 60 + parseInt(parts.minute, 10), ok: true };
            } catch (e) { return { ok: false }; }
        }

        function getMarketSession() {
            const ny = getNyParts();
            if (!ny.ok) return 'unknown';
            if (ny.day === 0 || ny.day === 6) return 'closed';   // weekend
            const mins = ny.mins;
            if (mins >= 9 * 60 + 30 && mins < 16 * 60) return 'rth';
            if (mins >= 4 * 60 && mins < 9 * 60 + 30) return 'pre';
            if (mins >= 16 * 60 && mins <= 20 * 60) return 'post';
            return 'closed';
        }

        // Short human label of the current session + which trading session the scan data reflects,
        // so the screener status is never ambiguous about open/pre/post/closed. The grouped-daily
        // fetch stamps window.__lastSessionDate with the completed session it pulled.
        // ── RVOL PROJECTION (intraday) ────────────────────────────────────────────
        // Raw intraday volume is only PART of the day, so comparing it to a full 50-day
        // average makes every early breakout look low-volume. We project the full-day
        // total by dividing volume-so-far by the fraction of a typical day's volume that
        // has normally accumulated by this NY time. Intraday volume is U-shaped (heavy at
        // open & close, light midday), so we use an empirical cumulative curve, not a
        // linear clock. Returns the elapsed fraction (0–1]; 1 = session complete / closed.
        function rthVolumeFraction() {
            const s = getMarketSession();
            if (s !== 'rth') return 1; // pre/post/closed → treat volume as "final" (no scaling)
            const ny = getNyParts();
            if (!ny.ok) return 1;
            const t = (ny.mins - (9 * 60 + 30)) / (6.5 * 60); // 0 at open → 1 at close
            const x = Math.max(0, Math.min(1, t));
            // Empirical U-shaped cumulative volume curve (approx US equities):
            //   ~12% in first 30m, ~50% by midday, ~70% by 2pm, ramp into close.
            // Modeled as a smooth blend that front- and back-loads vs. linear.
            const curve = 0.5 * (x) + 0.5 * (x < 0.5
                ? Math.pow(x * 2, 0.7) / 2            // front-loaded first half
                : 0.5 + Math.pow((x - 0.5) * 2, 1.4) / 2); // back-loaded into close
            // Guard: never divide by a tiny number in the first minutes.
            return Math.max(0.08, Math.min(1, curve));
        }

        // Project a full-day RVOL from raw (volume-so-far ÷ 50d avg). Off-hours returns
        // raw unchanged (it's already a complete session). Capped to avoid wild early reads.
        function projectRvol(rawRvol) {
            if (!Number.isFinite(rawRvol)) return rawRvol;
            const frac = rthVolumeFraction();
            if (frac >= 0.999) return rawRvol; // complete session
            const projected = rawRvol / frac;
            return Math.min(projected, rawRvol * 6); // cap: don't let 9:31am noise read 20×
        }

        // Confirmation data-state label, driven by the existing data regime:
        //   'final'       → market closed: grade reflects a completed session (trustworthy)
        //   'provisional' → intraday: grade is a live projection, will firm up by close
        function confirmationState() {
            const s = getMarketSession();
            return (s === 'rth' || s === 'pre' || s === 'post') ? 'provisional' : 'final';
        }

        // Classify the DATA regime the Group RS / screener numbers represent right now.
        // Combines the session phase with how fresh our newest bar is. On the Starter
        // tier the best case during RTH is "Intraday (15m delayed)"; true "Live" only
        // appears if a real-time tier ever delivers a sub-2-minute bar.
        //   mode: 'closed' | 'intraday' | 'live'
        function getDataRegime() {
            const session = getMarketSession();
            if (session === 'unknown') {
                return { mode: 'closed', label: 'Closed', sub: 'timezone unavailable', color: '#64748b', dot: '#475569' };
            }
            if (session === 'closed') {
                const ny = getNyParts();
                const weekend = ny.ok && (ny.day === 0 || ny.day === 6);
                return { mode: 'closed', label: 'Closed',
                    sub: weekend ? 'weekend · last session' : 'after hours · last session',
                    color: '#64748b', dot: '#475569' };
            }
            const freshMs = (typeof window !== 'undefined' && Number.isFinite(window.__freshestBarMs)) ? window.__freshestBarMs : null;
            const gapMin = freshMs ? Math.max(0, Math.round((Date.now() - freshMs) / 60000)) : null;
            const phase = session === 'pre' ? 'Pre-market' : session === 'post' ? 'After-hours' : 'Intraday';
            // True "Live" only if the freshest bar is essentially current (real-time tier).
            if (gapMin != null && gapMin <= 2 && session === 'rth') {
                return { mode: 'live', label: 'Live', sub: 'real-time', color: '#10b981', dot: '#34d399' };
            }
            // delayed/developing bar during a session
            return { mode: 'intraday', label: phase, sub: `~${TIER_DELAY_MIN}m delayed`, color: '#f59e0b', dot: '#fbbf24' };
        }

        // Paint the Group RS panel's data-regime badge (Closed / Intraday / Live).
        function updateGroupRsRegimeBadge() {
            const wrap = document.getElementById('groupRsRegime');
            const dot = document.getElementById('groupRsRegimeDot');
            const label = document.getElementById('groupRsRegimeLabel');
            const sub = document.getElementById('groupRsRegimeSub');
            if (!wrap || !label) return;
            const r = getDataRegime();
            label.textContent = r.label;
            label.style.color = r.color;
            if (dot) { dot.style.background = r.dot; dot.className = 'w-1.5 h-1.5 rounded-full' + (r.mode === 'live' ? ' animate-pulse' : ''); }
            if (sub) sub.textContent = r.sub ? '· ' + r.sub : '';
            wrap.style.borderColor = r.mode === 'closed' ? '#1f2937' : (r.mode === 'live' ? 'rgba(16,185,129,0.4)' : 'rgba(245,158,11,0.35)');
        }

        function setLiveUI() {
            const dot = document.getElementById('liveDot');
            const label = document.getElementById('liveLabel');
            const cd = document.getElementById('liveCountdown');
            if (!label) return;
            const open = isUsMarketHours();
            if (!liveEnabled) {
                label.textContent = 'PAUSED';
                label.className = 'font-mono text-slate-500 text-[10px] tracking-widest';
                if (dot) dot.className = 'w-1.5 h-1.5 rounded-full bg-slate-600';
                if (cd) cd.classList.add('hidden');
            } else if (!open) {
                label.textContent = 'LIVE';
                label.className = 'font-mono text-slate-500 text-[10px] tracking-widest';
                if (dot) dot.className = 'w-1.5 h-1.5 rounded-full bg-slate-600';
                if (cd) { cd.textContent = 'mkt closed'; cd.classList.remove('hidden'); cd.className = 'font-mono text-slate-500 text-[9px] tracking-wide'; }
            } else {
                label.textContent = 'LIVE';
                label.className = 'font-mono text-emerald-400 text-[10px] tracking-widest';
                if (dot) dot.className = 'w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse';
                if (cd) { cd.classList.remove('hidden'); cd.className = 'font-mono text-slate-500 text-[9px] tracking-wide'; }
            }
        }

        function startCurrentTickerRefresh() {
            // (Re)arm the ticking loop. Safe to call on every ticker load.
            if (currentTickerRefreshTimer) clearInterval(currentTickerRefreshTimer);
            if (liveCountdownTimer) clearInterval(liveCountdownTimer);
            liveSecsSinceFull = 0;
            liveNextSnapshotAt = LIVE_SNAPSHOT_SEC;
            setLiveUI();
            // 1-second heartbeat drives both the countdown display and the cadence.
            liveCountdownTimer = setInterval(() => {
                const cd = document.getElementById('liveCountdown');
                if (!liveEnabled || !isUsMarketHours()) { setLiveUI(); return; }
                liveNextSnapshotAt -= 1;
                liveSecsSinceFull += 1;
                if (cd && cd.classList.contains('hidden') === false && !cd.textContent.includes('mkt')) {
                    cd.textContent = Math.max(0, liveNextSnapshotAt) + 's';
                }
                if (liveNextSnapshotAt <= 0) {
                    // During a heavy scan, stretch the snapshot cadence so we add minimal load.
                    liveNextSnapshotAt = TaskCoordinator.snapshotInterval(LIVE_SNAPSHOT_SEC);
                    if (liveSecsSinceFull >= LIVE_FULL_SEC) {
                        if (TaskCoordinator.allowFullRefresh()) {
                            liveSecsSinceFull = 0; refreshCurrentTickerFull();
                        } else {
                            // Defer the heavy full refresh until the scan yields; keep the
                            // elapsed counter so it fires promptly once the scan is done.
                            TaskCoordinator.note('full refresh deferred — screener busy');
                            refreshCurrentTickerSnapshot();
                        }
                    } else {
                        refreshCurrentTickerSnapshot();
                    }
                }
            }, 1000);
        }

        function toggleLive() {
            liveEnabled = !liveEnabled;
            localStorage.setItem('abLiveEnabled', liveEnabled ? '1' : '0');
            liveNextSnapshotAt = LIVE_SNAPSHOT_SEC;
            setLiveUI();
        }

        // ── WATCHLIST LIVE FEED (delayed) ────────────────────────────────────────
        // Refreshes every watchlisted ticker's price every ~15s via ONE batched
        // same-origin call (/api/quotes). Universe names come from the server snapshot
        // (zero Polygon cost); off-universe names are fetched server-side in a single
        // shared Polygon call. This replaces the old per-ticker fan-out that burst
        // Polygon's rate limit. Updates only the price + % cells in place.
        let wlLiveTimer = null;
        let wlLiveBackoffUntil = 0;
        const WL_LIVE_REFRESH_MS = 15000;     // one batched quotes call per cycle

        function wlPaintPrice(ticker, price, chgPct) {
            const pxEl = document.querySelector(`[data-wl-px="${ticker}"]`);
            if (!pxEl) return;
            const chgEl = pxEl.querySelector(`[data-wl-chg="${ticker}"]`);
            // Update the price text node (first child) without nuking the chg span.
            const txt = '$' + price.toFixed(2);
            if (pxEl.firstChild && pxEl.firstChild.nodeType === 3) pxEl.firstChild.nodeValue = txt;
            else pxEl.insertBefore(document.createTextNode(txt), pxEl.firstChild);
            // No flash/glow on tick — just update the number quietly (kept lean & calm).
            if (chgEl && Number.isFinite(chgPct)) {
                chgEl.textContent = (chgPct >= 0 ? '+' : '') + chgPct.toFixed(2) + '%';
                chgEl.style.color = chgPct >= 0 ? 'rgba(110,231,183,0.75)' : 'rgba(253,164,175,0.75)';
            }
        }

        async function wlLiveTickOnce() {
            // Runs whenever live is enabled. ONE batched /api/quotes call returns every
            // watchlisted name's price (snapshot prices show the last close off-hours, so
            // we always see something). No per-ticker Polygon calls → no rate-limit burst.
            if (!liveEnabled) return;
            if (Date.now() < wlLiveBackoffUntil) return;
            if (typeof Watchlist === 'undefined') return;
            const all = Watchlist.all ? Watchlist.all() : [];
            if (!all.length) return;
            const tickers = all.map(i => i.ticker).filter(Boolean);
            if (!tickers.length) return;
            try {
                const res = await fetch('/api/quotes?tickers=' + encodeURIComponent(tickers.join(',')));
                if (res.status === 429) { wlLiveBackoffUntil = Date.now() + 15000; Logger.warn('Watchlist quotes rate-limited, backing off 15s'); return; }
                if (!res.ok) return;
                const data = await res.json();
                const quotes = (data && data.quotes) || {};
                for (const ticker of tickers) {
                    const q = quotes[ticker];
                    if (q && Number.isFinite(q.price) && q.price > 0) {
                        wlPaintPrice(ticker, q.price, Number.isFinite(q.changePct) ? q.changePct : 0);
                    }
                }
            } catch (e) {
                Logger.debug('Watchlist live tick failed', { error: String(e && e.message || e) });
            }
        }

        function startWatchlistLiveFeed() {
            if (wlLiveTimer) return;
            wlLiveTimer = setInterval(wlLiveTickOnce, WL_LIVE_REFRESH_MS); // one batched quotes call per cycle
            wlLiveTickOnce(); // immediate first paint
        }

        // FULL refresh: re-run the whole pipeline (chart, candles, RSI, RS gauge,
        // Minervini, etc.) without disturbing the user's typed inputs. We simply
        // re-invoke fetchTickerData() — it already repaints everything.
        async function refreshCurrentTickerFull() {
            const key = ui.apiKey.value.trim();
            if (!state.currentTicker || !key || liveFullInFlight) return;
            liveFullInFlight = true;
            try {
                if (ui.tickerInput && !ui.tickerInput.value.trim()) ui.tickerInput.value = state.currentTicker;
                await fetchTickerData({ silent: true });
                if (ui.status) ui.status.textContent = `Loaded ${state.currentTicker} · live refresh`;
            } catch (e) {
                Logger.warn('Live full-refresh failed', { ticker: state.currentTicker, error: String(e && e.message || e) });
                if (ui.status) ui.status.textContent = `Live refresh failed — ${(e && e.message) || 'try again'}`;
            } finally { liveFullInFlight = false; }
        }

        // SNAPSHOT refresh: lightweight price/volume/RVOL tick between full refreshes.
        async function refreshCurrentTickerSnapshot() {
            const key = ui.apiKey.value.trim();
            if (!state.currentTicker || !key || !engineState.cachedDataResults.length) return;
            try {
                const data = await polygonFetch(`https://api.polygon.io/v2/snapshot/locale/us/markets/stocks/tickers/${state.currentTicker}?apiKey=${key}`, state.currentTicker);
                const snap = data.ticker || data;
                const price = getSnapshotPrice(snap);
                const volume = getSnapshotVolume(snap);
                if (price > 0) {
                    ui.fdLastPrice.textContent = "$" + price.toFixed(2);
                    ui.entryPrice.value = price.toFixed(2);
                    // keep the chart's latest daily bar close in sync so the line tracks intraday
                    if (engineState.cachedDataResults.length) {
                        const lastBar = engineState.cachedDataResults[engineState.cachedDataResults.length - 1];
                        const prevClose = engineState.cachedDataResults.length > 1 ? engineState.cachedDataResults[engineState.cachedDataResults.length - 2].c : null;
                        if (lastBar) {
                            lastBar.c = price;
                            if (price > (lastBar.h || price)) lastBar.h = price;
                            if (price < (lastBar.l || price)) lastBar.l = price;
                        }
                        if (typeof updateChartPriceBlock === 'function') updateChartPriceBlock(price, prevClose, true);
                        // Live-update the chart's last plotted point + the OHLC bar cache.
                        if (priceChartInstance && priceChartInstance.data && priceChartInstance.data.datasets[0]) {
                            const arr = priceChartInstance.data.datasets[0].data;
                            if (arr && arr.length) { arr[arr.length - 1] = price; priceChartInstance.update('none'); }
                            if (window.__chartVisibleBars && window.__chartVisibleBars.length) {
                                const vb = window.__chartVisibleBars[window.__chartVisibleBars.length - 1];
                                if (vb) { vb.c = price; if (price > vb.h) vb.h = price; if (price < vb.l) vb.l = price; }
                            }
                        }
                    }
                }
                if (volume > 0) {
                    ui.currentVol.textContent = formatVol(volume);
                    const avg = currentTickerAvgVol50 || MathLib.sma(engineState.cachedDataResults.map(r => r.v || 0), 50);
                    const rvol = avg ? volume / avg : null;
                    if (rvol) {
                        ui.rvolValue.textContent = rvol.toFixed(2) + 'x';
                        if (ui.fdRvol20) ui.fdRvol20.textContent = rvol.toFixed(2) + 'x';
                    }
                }
                const low = snap?.day?.l || snap?.min?.l || Number(ui.lod.value);
                if (low && Number.isFinite(low)) ui.lod.value = low.toFixed(2);
                calculatePosition();
                updateDataFreshness(Date.now()); // mark fresh tick
                if (ui.status) ui.status.textContent = `Loaded ${state.currentTicker} · snapshot ${new Date().toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}`;
            } catch(e) {
                Logger.debug('Snapshot refresh failed', { ticker: state.currentTicker, error: String(e && e.message || e) });
                if (ui.status) ui.status.textContent = `Live snapshot paused — ${(e && e.message) || 'fetch failed'}`;
            }
        }

        function updateScreenerSortIndicators() {
            document.querySelectorAll('.scr-sort').forEach(th => {
                const ind = th.querySelector('.scr-sort-ind');
                const active = th.dataset.sort === screenerSortKey;
                th.classList.toggle('text-blue-300', active);
                if (ind) ind.textContent = active ? (screenerSortDir === 'asc' ? ' ▲' : ' ▼') : '';
            });
        }

        // Compute (memoized) the Trade Assistant rating on a single row → row._rating /
        // _conviction / _convReasons. Keyed by _ratingStamp so unchanged inputs skip the
        // ~38-factor recompute. Shared by the screener AND the watchlist so a watchlist row's
        // Rating matches the Trade Assistant even when the row isn't in the visible screener slice.
        function ensureRowRating(row) {
            if (!row) return;
            const stamp = `${_ratingMktEpoch}|${row.enrichedAt || 0}|${row.grpRotScore ?? ''}|${row.grpRotTag ?? ''}|${row.groupRank ?? ''}`;
            if (row._ratingStamp === stamp) return; // inputs unchanged → keep cached values
            const grpRot = (row.grpRotTag || Number.isFinite(row.grpRotScore))
                ? { rotationTag: row.grpRotTag, rotationScore: row.grpRotScore, grvol: row.grpGrvol }
                : ((row.industryGroup && Array.isArray(groupRankingData)) ? groupRankingData.find(g => g.name === row.industryGroup) : null);
            const tr = computeRowTradeRating(row.scoreInput || row, { groupRot: grpRot, market: engineState.lastMarketStructure, conv: row });
            row._rating = tr && tr.rating != null ? tr.rating : null;
            row._conviction = tr ? (tr.conviction || 0) : 0;
            row._convReasons = tr ? (tr.convReasons || []) : [];
            row._ratingStamp = stamp;
        }

        let _spyRankPrefetchStarted = false; // ensures the SPY fetch for ranking happens only once
        function renderScreenerResults() {
            // Keep the Group RS tab live as the scan streams in.
            if (typeof state.activeHubTab !== 'undefined' && state.activeHubTab === 'groups') renderGroupRankings();
            renderMarketStructure(); // inline module in main column — always kept live
            if (!ui.screenerResultsBody) return;
            computeIndustryGroupRanks();

            // The rating now includes VARS (vs SPY), so the ranking must be generated AFTER SPY data
            // is available. SPY is session-cached; fetch it once, then re-render so VARS is scored.
            // Rows store their own varsValue at scan time; this guard covers the case where rows were
            // restored from cache (or scored before SPY loaded) and still need a VARS pass.
            if (!spyBarsCache && !_spyRankPrefetchStarted) {
                _spyRankPrefetchStarted = true;
                const key = (ui.apiKey?.value || '').trim();
                if (key) {
                    // Load SPY once, then re-render so freshly-scanned rows (which compute their own
                    // VARS against this same cache) rank with VARS included. Rows scored before SPY
                    // was available simply fall back to the CAUTION default — exactly as the main
                    // Trade Assistant does when index data isn't loaded — until their next rescore.
                    fetchSpyBars(key).then(spy => { if (spy) renderScreenerResults(); }).catch(() => {});
                }
            }

            // Precompute the Trade Assistant rating on each visible row (memoized — see
            // ensureRowRating; the bulk of render cost on big universes when inputs changed).
            state.screenerResults.forEach(ensureRowRating);

            // Sort by the active column/direction. Strings sort alphabetically; numbers numerically.
            const dir = screenerSortDir === 'asc' ? 1 : -1;
            const key = screenerSortKey;
            const strKeys = new Set(['ticker', 'sector', 'industryGroup', 'growthStatus', 'extensionStatus']);
            const getVal = (r) => {
                if (key === 'rating') return r._rating;
                if (key === 'groupRank') return r.groupRank; // smaller = better
                if (key === 'signal') {
                    // Sort by latest alert: rank = strength when recent, none sinks.
                    const la = latestAlertForRow(r);
                    if (!la) return -1;
                    // recency dominates (fewer days = higher), strength breaks ties
                    return (1000 - (la.days || 0) * 10) + (ALERT_STRENGTH[la.type] || 0);
                }
                if (key === 'todayPct') return (r.pctLive != null ? r.pctLive : r.pct1d);
                if (key === 'vcp') { if (r.vcpConfirmed) return 5; const st = r.vcpStage; if (st === 'coiling') return 4; if (st === 'breakout') return 3; if (st === 'forming') return 2; if (r.vcpIsVcp) return 1; return 0; }
                return r[key];
            };
            const _sv = new Map();
            for (const _r of state.screenerResults) _sv.set(_r, getVal(_r));
            state.screenerResults.sort((a, b) => {
                const va = _sv.get(a), vb = _sv.get(b);
                if (strKeys.has(key)) {
                    const sa = (va ?? '').toString().toLowerCase(), sb = (vb ?? '').toString().toLowerCase();
                    if (sa === sb) return (b.score ?? -1) - (a.score ?? -1);
                    return sa < sb ? -1 * dir : 1 * dir;
                }
                const na = Number.isFinite(Number(va)) ? Number(va) : null;
                const nb = Number.isFinite(Number(vb)) ? Number(vb) : null;
                if (na === null && nb === null) return 0;
                if (na === null) return 1;   // missing values always sink
                if (nb === null) return -1;
                if (na === nb) return (b.score ?? -1) - (a.score ?? -1);
                return (na - nb) * dir;
            });

            updateScreenerSortIndicators();
            if (!state.screenerResults.length) {
                ui.screenerResultsBody.innerHTML = `<tr><td colspan="33" class="py-8 px-5 text-center text-slate-500 italic">No matches for the current filters.</td></tr>`;
                _screenerRowCache.clear();
                updateScreenerStats();
                return;
            }
            const visibleRows = state.screenerResults.slice(0, 250);
            renderScreenerRowsDiff(visibleRows);
            updateScreenerStats();

            // Keep watched tickers fresh AND log market-wide alerts off the same scored set.
            if (typeof Watchlist !== 'undefined' && typeof state.screenerAllResults !== 'undefined') {
                try {
                    const res = Watchlist.sync(state.screenerAllResults);
                    // Market-wide: every enriched row can fire into the persistent alert log.
                    const logged = (typeof Watchlist.logMarket === 'function') ? Watchlist.logMarket(state.screenerAllResults) : 0;
                    if (typeof state.activeHubTab !== 'undefined' && state.activeHubTab === 'watchlist'
                        && typeof renderWatchlist === 'function') renderWatchlist();
                    if (typeof updateAlertsTabBadge === 'function') updateAlertsTabBadge();
                    // Re-render the live feed on EVERY scored flush while the Alerts tab is open.
                    // It re-derives all current alerts (incl. delta/omega, which Watchlist.sync —
                    // beta/alpha only — doesn't report), so gating on res.newAlerts/logged left
                    // delta/omega changes invisible until an unrelated beta/alpha happened to fire.
                    if (typeof state.activeHubTab !== 'undefined' && state.activeHubTab === 'alerts'
                        && typeof renderAlertsFeed === 'function') renderAlertsFeed();
                } catch (wlErr) { Logger.debug('Watchlist sync skipped', { error: String(wlErr && wlErr.message || wlErr) }); }
            }
        }

        // ── Differential screener rendering (TODO-5) ─────────────────────────────
        // Instead of blowing away and re-parsing the whole 250-row table on every flush
        // (every 4 enriched tickers during a scan → flicker + churn), we diff against a
        // per-ticker cache. Each row's HTML is hashed; unchanged rows in the right slot
        // are skipped entirely, changed rows are swapped in place, and order changes
        // reorder existing nodes. This cuts DOM work dramatically during a live scan.
        const _screenerRowCache = new Map(); // ticker → { html, el }

        function renderScreenerRowsDiff(rows) {
            const body = ui.screenerResultsBody;
            if (!body) return;
            const seen = new Set();
            // Desired order of elements, built up as we go.
            const ordered = [];

            for (const row of rows) {
                const ticker = row.ticker;
                seen.add(ticker);
                const html = buildScreenerRowHTML(row);
                let cached = _screenerRowCache.get(ticker);
                if (!cached) {
                    const tmp = document.createElement('tbody');
                    tmp.innerHTML = html;
                    cached = { html, el: tmp.firstElementChild };
                    _screenerRowCache.set(ticker, cached);
                } else if (cached.html !== html) {
                    // Content changed → rebuild this row's node, preserve identity in cache.
                    const tmp = document.createElement('tbody');
                    tmp.innerHTML = html;
                    const el = tmp.firstElementChild;
                    if (cached.el.parentNode) cached.el.replaceWith(el);
                    cached.el = el; cached.html = html;
                }
                ordered.push(cached.el);
            }

            // Remove rows no longer present.
            for (const [ticker, entry] of _screenerRowCache) {
                if (!seen.has(ticker)) {
                    if (entry.el && entry.el.parentNode) entry.el.remove();
                    _screenerRowCache.delete(ticker);
                }
            }

            // Reconcile order: walk the desired sequence, moving only nodes that are out
            // of place. Nodes already in the correct slot are left untouched (no DOM work).
            let ref = body.firstChild;
            for (const el of ordered) {
                if (el === ref) { ref = ref.nextSibling; continue; }
                body.insertBefore(el, ref);
            }
            scrRefreshCols();
        }

        function buildScreenerRowHTML(row) {
                const latestAlert = latestAlertForRow(row);
                const scoreText = Number.isFinite(row.score) ? row.score : '--';
                const tierText = row.tier ? `T${row.tier}` : '--';
                // Trade Assistant rating (precomputed above for sorting).
                const ratingVal = row._rating != null ? row._rating : null;
                const ratingText = ratingVal != null ? ratingVal.toFixed(1) : '--';
                const ratingCls = ratingVal == null ? 'text-slate-600' : QS.text(ratingVal, 5);
                const channelStyle = getChannelStyle(row.channelStage);
                const channelText = Number.isFinite(row.channelStage) ? `${row.channelStage}/6` : '--';
                const groupRankText = Number.isFinite(row.groupRank) ? `${row.groupRank}/${row.groupRankTotal || '--'}` : '--';
                const priceText = Number.isFinite(row.price) ? `$${row.price.toFixed(2)}` : '--';
                const pct3mText = Number.isFinite(row.pct3m) ? `${row.pct3m >= 0 ? '+' : ''}${row.pct3m.toFixed(1)}%` : '--';
                const rvolText = Number.isFinite(row.rvol) ? `${row.rvol.toFixed(1)}x` : '--';
                const rmvText = Number.isFinite(row.rmv) ? row.rmv.toFixed(0) : '--';
                // SCORE (/1000) and BQS (/100) share the unified scale.
                const tierCls = Number.isFinite(row.score) ? QS.text(row.score, 1000) : 'text-slate-600';
                // Performance: signed → green up / rose down, neutral muted near zero.
                const perfCls = !Number.isFinite(row.pct3m) ? 'text-slate-500'
                    : row.pct3m >= 10 ? 'text-emerald-300' : row.pct3m >= 0 ? 'text-slate-300' : row.pct3m >= -10 ? 'text-amber-300' : 'text-rose-300';
                // RMV is inverse (tight/low = good): map to QS by inverting.
                const rmvCls = !Number.isFinite(row.rmv) ? 'text-slate-500' : QS.textPct(1 - Math.min(1, row.rmv / 100));
                const bqsText = Number.isFinite(row.bqsGrade) ? row.bqsGrade : '--';
                const bqsCls = !Number.isFinite(row.bqsGrade) ? 'text-slate-600' : QS.text(row.bqsGrade, 100);
                const subFmt = (v) => Number.isFinite(v) ? Math.round(v) : '--';
                const subCls = (v) => !Number.isFinite(v) ? 'text-slate-600' : QS.text(v, 100);
                const extMap = { notExtended: 'OK', extended: 'Ext', veryExtended: 'V.Ext', broken: 'Brkn' };
                const extText = extMap[row.extensionStatus] || '--';
                const extCls = row.extensionStatus === 'notExtended' ? 'text-emerald-300'
                    : row.extensionStatus === 'extended' ? 'text-amber-300'
                    : row.extensionStatus === 'veryExtended' ? 'text-rose-300'
                    : row.extensionStatus === 'broken' ? 'text-rose-400' : 'text-slate-500';
                const grMap = { strong: 'Strong', solid: 'Solid', mixed: 'Mixed', weak: 'Weak', unknown: '--' };
                const grText = grMap[row.growthStatus] || '--';
                const grCls = row.growthStatus === 'strong' ? 'text-emerald-300'
                    : row.growthStatus === 'solid' ? 'text-teal-300'
                    : row.growthStatus === 'mixed' ? 'text-amber-300'
                    : row.growthStatus === 'weak' ? 'text-rose-300' : 'text-slate-500';
                // RATING shown as a compact pill so the eye reads tier at a glance.
                const ratingPill = ratingVal == null ? 'bg-slate-800/40 border-slate-700 text-slate-500' : QS.bg(ratingVal, 5);
                // TIER badge: T1/T2 elite, scaling down.
                const tierNum = row.tier || 5;
                const tierBadge = tierNum <= 1 ? 'bg-indigo-500/[12%] border-indigo-400/40 text-indigo-200'
                    : tierNum === 2 ? 'bg-emerald-500/[12%] border-emerald-400/40 text-emerald-200'
                    : tierNum === 3 ? 'bg-slate-700/40 border-slate-600/60 text-slate-200'
                    : tierNum === 4 ? 'bg-amber-500/10 border-amber-400/30 text-amber-200'
                    : 'bg-rose-500/10 border-rose-400/30 text-rose-200';
                const watched = (typeof Watchlist !== 'undefined') && Watchlist.hasAny(row.ticker);
                // ---- Extra parameter columns (Today % visible by default; rest via Columns menu) ----
                const _pctCell = (v) => !Number.isFinite(v) ? '<td class="py-2 px-2 text-right tabular-nums text-slate-600">--</td>'
                    : `<td class="py-2 px-2 text-right tabular-nums ${v >= 10 ? 'text-emerald-300' : v >= 0 ? 'text-slate-300' : v >= -10 ? 'text-amber-300' : 'text-rose-300'}">${v >= 0 ? '+' : ''}${v.toFixed(1)}%</td>`;
                const _distCell = (v) => !Number.isFinite(v) ? '<td class="py-2 px-2 text-right tabular-nums text-slate-600">--</td>'
                    : `<td class="py-2 px-2 text-right tabular-nums ${v >= 0 ? (v > 25 ? 'text-amber-300' : 'text-emerald-300') : 'text-slate-400'}">${v >= 0 ? '+' : ''}${v.toFixed(1)}%</td>`;
                const _todayVal = (row.pctLive != null ? row.pctLive : row.pct1d);
                const _rsiV = row.rsi1d;
                const _rsiCell = !Number.isFinite(_rsiV) ? '<td class="py-2 px-2 text-right tabular-nums text-slate-600">--</td>'
                    : `<td class="py-2 px-2 text-right tabular-nums ${_rsiV >= 70 ? 'text-rose-300' : _rsiV >= 55 ? 'text-emerald-300' : _rsiV >= 45 ? 'text-slate-300' : 'text-amber-300'}">${Math.round(_rsiV)}</td>`;
                const _mvN = row.mvPassed;
                const _mvCell = !Number.isFinite(_mvN) ? '<td class="py-2 px-2 text-right tabular-nums text-slate-600">--</td>'
                    : `<td class="py-2 px-2 text-right tabular-nums font-bold ${_mvN >= 8 ? 'text-emerald-300' : _mvN >= 6 ? 'text-teal-300' : _mvN >= 4 ? 'text-amber-300' : 'text-rose-300'}">${_mvN}/8</td>`;
                let _vcpCell;
                {
                    const st = row.vcpStage, isV = row.vcpIsVcp;
                    if (!isV && (!st || st === 'none')) { _vcpCell = '<td class="py-2 px-2 text-right"><span class="text-slate-600">--</span></td>'; }
                    else {
                        const lbl = row.vcpConfirmed ? 'Confirmed' : (st && st !== 'none' ? st.charAt(0).toUpperCase() + st.slice(1) : 'VCP');
                        const cls = row.vcpConfirmed ? 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10'
                            : st === 'coiling' ? 'text-teal-300 border-teal-500/40 bg-teal-500/10'
                            : st === 'breakout' ? 'text-fuchsia-300 border-fuchsia-500/40 bg-fuchsia-500/10'
                            : st === 'forming' ? 'text-blue-300 border-blue-500/40 bg-blue-500/10'
                            : 'text-slate-300 border-slate-600 bg-slate-700/20';
                        _vcpCell = `<td class="py-2 px-2 text-right"><span class="inline-flex items-center px-1.5 py-0.5 rounded-md border text-[10px] font-bold ${cls}">${lbl}</span></td>`;
                    }
                }
                const extraColsHtml = _pctCell(_todayVal) + _pctCell(row.pct1w) + _pctCell(row.pct1m) + _pctCell(row.pct6m) + _pctCell(row.pct12m) + _rsiCell + _distCell(row.dist9) + _distCell(row.dist21) + _distCell(row.dist50) + _distCell(row.dist200) + _vcpCell + _mvCell;
                return `<tr data-ticker="${row.ticker}" class="cursor-pointer hover:bg-blue-950/30 transition-colors">
                    <td class="py-2 pl-5 pr-2">
                        <div class="flex items-center gap-1.5">
                            <button data-watch-toggle="${row.ticker}" title="${watched ? 'Remove from watchlist' : 'Add to watchlist'}" class="shrink-0 text-base leading-none transition-colors ${watched ? 'text-amber-400 hover:text-amber-300' : 'text-slate-600 hover:text-amber-400'}">${watched ? '★' : '☆'}</button>
                            <div class="min-w-0">
                                <div class="font-black text-white">${row.ticker}</div>
                                <div class="text-[10px] text-slate-500 truncate max-w-[140px]">${row.name}</div>
                            </div>
                        </div>
                    </td>
                    <td class="py-2 px-2 text-right font-black tabular-nums ${tierCls}">${scoreText}</td>
                    <td class="py-2 px-2 text-right font-black tabular-nums ${bqsCls}" title="${row.bqsTierLabel ? row.bqsTier + ' · ' + row.bqsTierLabel : 'Breakout Quality Score'}${row.bqsMode ? ' · ' + row.bqsMode : ''}">${bqsText}${row.bqsMode ? `<span class="ml-1 text-[8px] font-bold uppercase ${row.bqsMode === 'ma-ride' ? 'text-teal-400' : 'text-blue-400'}">${row.bqsMode === 'ma-ride' ? 'MA' : 'FLG'}</span>` : ''}</td>
                    <td class="py-2 px-2 text-right"><span class="inline-flex items-center justify-center min-w-[34px] px-1.5 py-0.5 rounded-md border text-[10px] font-black tabular-nums ${ratingPill}" title="Trade Assistant rating 1–5${(row._conviction != null) ? ' · Conviction ' + row._conviction + '/5' + ((row._convReasons && row._convReasons.length) ? ' (' + row._convReasons.join(', ') + ')' : '') : ''}">${ratingText}</span></td>
                    <td class="py-2 px-2 text-right"><span class="inline-flex items-center justify-center min-w-[26px] px-1.5 py-0.5 rounded-md border text-[10px] font-black ${tierBadge}">${tierText}</span></td>
                    <td class="py-2 px-2 text-right">${(() => {
                        if (row.rsPower == null) return '<span class="text-slate-600">--</span>';
                        const t = row.rsPowerTier;
                        const cls = t === 'Delta' ? 'text-fuchsia-300 border-fuchsia-500/40 bg-fuchsia-500/10'
                            : t === 'RS NH' ? 'text-emerald-300 border-emerald-500/40 bg-emerald-500/10'
                            : t === 'Primed' ? 'text-teal-300 border-teal-500/40 bg-teal-500/10'
                            : t === 'Building' ? 'text-blue-300 border-blue-500/40 bg-blue-500/10'
                            : t === 'Firm' ? 'text-slate-300 border-slate-600 bg-slate-700/20'
                            : 'text-amber-300/80 border-amber-600/30 bg-amber-500/5';
                        const px = Number.isFinite(row.rsProximity) ? row.rsProximity.toFixed(0) + '%' : '';
                        const dsc = deltaSectorContext(row);
                        const dscGlyph = dsc ? ` <span title="${dsc.title}" class="${dsc.cls} text-[10px] font-black align-middle">${dsc.glyph}</span>` : '';
                        return `<span title="RS line ${px} of its prior 52w high${t === 'Delta' ? ' · RS new high BEFORE price (leading)' : t === 'RS NH' ? ' · RS at new high' : ''}" class="inline-flex items-center justify-center gap-1 min-w-[52px] px-1.5 py-0.5 rounded-md border text-[9px] font-black ${cls}">${t}</span>${dscGlyph}`;
                    })()}</td>
                    <td class="py-2 px-2 text-right text-slate-300">${row.stageLabel || (row.stage ? 'S' + row.stage : '--')}</td>
                    <td class="py-2 px-2 text-right"><span title="${row.channelLabel || ''}" class="inline-flex items-center justify-center min-w-[42px] px-2 py-1 rounded-md border text-[10px] font-black ${channelStyle.cls}">${channelText}</span></td>
                    <td class="py-2 px-2 text-right tabular-nums ${subCls(row.trendScore)}">${subFmt(row.trendScore)}</td>
                    <td class="py-2 px-2 text-right tabular-nums ${subCls(row.demandScore)}">${subFmt(row.demandScore)}</td>
                    <td class="py-2 px-2 text-right tabular-nums ${subCls(row.timingScore)}">${subFmt(row.timingScore)}</td>
                    <td class="py-2 px-2 text-slate-300 truncate max-w-[110px]" title="${row.sector || ''}">${row.sector || '--'}</td>
                    <td class="py-2 px-2 text-slate-200 truncate max-w-[130px]" title="${row.industry || row.industryGroup || ''}">${row.industryGroup || '--'}</td>
                    <td class="py-2 px-2 ${grCls}">${grText}</td>
                    <td class="py-2 px-2 text-right ${row.groupRank <= 10 ? 'text-emerald-300 font-bold' : row.groupRank <= 25 ? 'text-teal-300' : 'text-slate-400'}">${groupRankText}</td>
                    <td class="py-2 px-2 text-right text-slate-200 tabular-nums">${priceText}</td>
                    <td class="py-2 px-2 text-right tabular-nums ${perfCls}">${pct3mText}</td>
                    <td class="py-2 px-2 text-right">${(() => {
                        const v = row.rvol;
                        if (!Number.isFinite(v)) return '<span class="text-slate-600">--</span>';
                        // Abnormality scale, inlined (data already in scope — no cross-scope call):
                        // 🔥 extreme ≥4 · ▲▲ hot ≥2.5 · ▲ elevated ≥1.5 · normal ≥0.8 · quiet <0.8
                        const t = v >= 4 ? { g: '🔥', c: 'text-fuchsia-300 border-fuchsia-500/40 bg-fuchsia-500/15', l: 'extreme' }
                            : v >= 2.5 ? { g: '▲▲', c: 'text-rose-300 border-rose-500/40 bg-rose-500/15', l: 'hot' }
                            : v >= 1.5 ? { g: '▲', c: 'text-amber-300 border-amber-500/35 bg-amber-500/[12%]', l: 'elevated' }
                            : v >= 0.8 ? { g: '', c: 'text-slate-300 border-slate-600 bg-slate-700/20', l: 'normal' }
                            : { g: '', c: 'text-slate-500 border-slate-700 bg-slate-800/30', l: 'quiet' };
                        const vc = volumeConfluence(row);
                        const vcBar = (() => {
                            if (!vc) return '';
                            const seg = vc.layers.map(L => {
                                const bg = !L.present ? '#1f2937' : L.hot ? '#34d399' : '#475569';
                                return `<span style="display:inline-block;width:7px;height:4px;border-radius:1px;background:${bg}"></span>`;
                            }).join('<span style="display:inline-block;width:2px"></span>');
                            const tip = vc.layers.map(L => `${L.label}: ${L.present ? L.v.toFixed(1) + '×' + (L.hot ? ' ✓' : '') : 'n/a'}`).join(' · ') + ` — volume confluence ${vc.lit}/${vc.denom}`;
                            return `<div title="${tip}" class="flex items-center justify-end gap-0 leading-none" style="margin-top:3px">${seg}</div>`;
                        })();
                        return `<div class="inline-flex flex-col items-end" style="gap:1px"><span class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[10px] font-black tabular-nums ${t.c}" title="RVOL ${v.toFixed(1)}× — ${t.l} volume vs 50-day average">${t.g ? t.g + ' ' : ''}${v.toFixed(1)}×</span>${vcBar}</div>`;
                    })()}</td>
                    <td class="py-2 px-2 text-right tabular-nums ${rmvCls}">${rmvText}</td>
                    <td class="py-2 px-2 ${extCls}">${extText}</td>
                    <td class="py-2 px-2">${latestAlert
                        ? `<span title="${latestAlert.label} — ${alertDaysText(latestAlert.days)}" class="px-2 py-1 rounded-md border ${latestAlert.meta.chip} ${latestAlert.meta.text} font-bold cursor-default">${latestAlert.label}</span>`
                        : `<span class="px-2 py-1 rounded-md border bg-slate-900 border-slate-700 text-slate-500">Watch</span>`}</td>
                    ${extraColsHtml}
                </tr>`;
        }

        // ── SERVER-SIDE ENRICHMENT ───────────────────────────────────────────────
        // The whole universe is now enriched ONCE on the server and served as a
        // canonical snapshot. The client no longer fetches the universe or scores
        // thousands of tickers itself — it just pulls /api/snapshot (cached, gzipped)
        // and renders. Group rotation / market-structure overlays stay client-side,
        // computed from the server's rows. (Single-ticker chart/Trade-Assistant loads
        // still fetch on demand via the /api/polygon proxy.)
        async function runMarketScreener() {
            const runId = ++screenerRunId;
            AppState.setScreenerRunning(true); state.screenerPaused = false;
            if (ui.screenerStatusText) ui.screenerStatusText.textContent = 'Loading snapshot…';
            try {
                // Prefer the head-prefetch (fired before Tailwind + this script parsed). Consume it
                // once, then fall back to a fresh fetch on any later re-run or if the prefetch failed.
                const _pre = window.__snap; window.__snap = null;
                let res;
                try { res = await (_pre || fetch('/api/snapshot')); }
                catch (e) { res = await fetch('/api/snapshot'); }
                if (res.status === 503) {
                    if (ui.screenerStatusText) ui.screenerStatusText.textContent = 'Server is building the snapshot — retrying shortly…';
                    AppState.setScreenerRunning(false);
                    setTimeout(() => { if (!state.screenerRunning) runMarketScreener(); }, 15000);
                    return;
                }
                if (!res.ok) throw new Error('Snapshot HTTP ' + res.status);
                const snap = await res.json();
                if (runId !== screenerRunId) return;
                const rows = snap.rows || [];
                state.screenerAllResults = rows;
                // Step 3: adopt the server's regime immediately so every client score (dashboard included)
                // uses the SAME market tape the server scored the rows with → tiers match. renderMarketStructure
                // re-injects + renders it; falls back to the client compute only when the server didn't ship one.
                serverMarketStructure = (snap.marketStructure && snap.marketStructure.n) ? snap.marketStructure : null;
                if (serverMarketStructure) {
                    engineState.lastMarketStructure = serverMarketStructure;
                    setEngineContext({ lastMarketStructure: engineState.lastMarketStructure });
                }
                serverGroupRotation = (snap.groupRotation && Array.isArray(snap.groupRotation.industryGroup)) ? snap.groupRotation : null;
                screenerSnapshot = rows;
                screenerUniverse = rows;
                screenerLastSync = snap.savedAt ? new Date(snap.savedAt) : new Date();
                screenerIndex = rows.length;
                try { computeGroupRankings(); } catch (e) {}     // group stats from the server rows
                try { stampGroupRotationOnRows(); } catch (e) {} // rotation overlay onto rows
                applyScreenerFilters();
                updateScreenerStats();
                try { recordRsStrengthSnapshot(serverMarketStructure || computeMarketStructure()); } catch (e) {}
                try { finalizeRsBreadthSeries(); } catch (e) {}
                if (ui.screenerStatusText) {
                    const delayed = (snap.feedMode || '').includes('delayed');
                    const liveTag = snap.live ? (delayed ? 'LIVE · 15-min delayed' : 'LIVE') : 'EOD';
                    const stamp = snap.savedAt ? new Date(snap.savedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
                    ui.screenerStatusText.textContent = `${liveTag} · ${rows.length.toLocaleString()} names · ${stamp}`;
                }
            } catch (err) {
                if (runId !== screenerRunId) return;
                if (ui.screenerStatusText) ui.screenerStatusText.textContent = err.message || 'Snapshot load failed';
            } finally {
                if (runId === screenerRunId) { AppState.setScreenerRunning(false); updateScreenerStats(); }
            }
        }


        // ── Adaptive screener throttle ───────────────────────────────────────────
        // Replaces the old hardcoded CONCURRENCY=32. Starts conservative, ramps up while
        // clean, and on a 429 it shrinks the active worker pool and pauses (exponential
        // backoff). Workers consult this each loop, so concurrency adapts in real time.
        // ScreenerThrottle extracted to a module (imported at top).

        // Once the whole universe is scored, convert every row's raw IBD strength
        // score into a TRUE 1–99 percentile (this is the real IBD RS Rating, only
        // possible because we hold the entire universe). Persist the distribution so
        // the single-ticker dashboard can borrow it for an honest percentile too.
        function finalizeRsPercentiles() {
            const raws = state.screenerAllResults.map(r => r.rsRaw).filter(v => v != null && Number.isFinite(v));
            if (raws.length < 50) return; // not enough universe to percentile meaningfully
            engineState.rsUniverseDistribution = raws;
            setEngineContext({ rsUniverseDistribution: engineState.rsUniverseDistribution }); // push into shared engine (percentile ranking)
            try { localStorage.setItem('rsUniverseDistributionV1', JSON.stringify({ savedAt: Date.now(), scores: raws })); } catch (e) {}
            for (const row of state.screenerAllResults) {
                if (Number.isFinite(row.rsRating)) {
                    // Step 4 dedup: the server already shipped rsRating (same binary-search percentile the client
                    // would recompute over the same universe) — use it, don't re-derive. Just surface it as rsProxy.
                    row.rsProxy = row.rsRating;
                    row.rsIsPercentile = true;
                } else if (Number.isFinite(row.rsRaw)) {
                    // Fallback: server didn't ship rsRating — derive client-side (old server / pre-scan).
                    const pct = rsRawToPercentile(row.rsRaw, raws);
                    row.rsProxy = pct; row.rsRating = pct; row.rsIsPercentile = true;
                }
            }
        }



        // ── Auto market data (Major Indices + Sector Flow) ───────────────────────
        // Replaces the manual "Load Index Data" button and the 15-min ETF scan: the
        // server precomputes both during enrichment and serves them on /api/marketdata.
        // We fetch once on load and populate the same DOM the old paths wrote to, so all
        // downstream logic (bench cards, Market Structure, sector tables) is unchanged.
        function renderBenchCard(ticker, b) {
            if (!b || !b.closes || !b.closes.length) return;
            const tId = ticker.toLowerCase();
            const closes = b.closes, price = closes[closes.length - 1];
            const results = b.t.map(ts => ({ t: ts }));   // getPctChange only needs .t (for YTD)
            const pct1d = MathLib.getPctChange(closes, 1, results);
            const pct1w = MathLib.getPctChange(closes, 5, results);
            const pct1m = MathLib.getPctChange(closes, 21, results);
            const ytd   = MathLib.getPctChange(closes, 'YTD', results);
            const ema9 = MathLib.ema(closes, 9), ema21 = MathLib.ema(closes, 21);
            const sma50 = MathLib.sma(closes, 50), sma200 = MathLib.sma(closes, 200);
            const formatB = (p, ma) => { if (!ma) return '--'; const pct = ((p - ma) / ma) * 100; return `<span class="${pct < -1.5 ? 'text-rose-400' : pct > 4.0 ? 'text-amber-400' : 'text-emerald-400'}">${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%</span>`; };
            const setEl = (id, html) => { const el = document.getElementById(id); if (el) el.innerHTML = html; };
            const pEl = document.getElementById(`bench_price_${tId}`); if (pEl) pEl.textContent = `$${price.toFixed(2)}`;
            setEl(`bench_1d_${tId}`, formatPctHTML(pct1d));
            setEl(`bench_1w_${tId}`, formatPctHTML(pct1w));
            setEl(`bench_1m_${tId}`, formatPctHTML(pct1m));
            setEl(`bench_ytd_${tId}`, formatPctHTML(ytd));
            setEl(`bench_9_${tId}`, formatB(price, ema9));
            setEl(`bench_21_${tId}`, formatB(price, ema21));
            setEl(`bench_50_${tId}`, formatB(price, sma50));
            setEl(`bench_200_${tId}`, formatB(price, sma200));
        }
        function applyMarketData(md) {
            if (!md) return;
            if (md.indices && Object.keys(md.indices).length) {
                Object.keys(md.indices).forEach(t => { marketStructureIndices[t] = md.indices[t]; });
                Object.keys(md.indices).forEach(t => renderBenchCard(t, md.indices[t]));
                marketStructureIndicesLoaded = true;
                if (ui.benchStatus) { ui.benchStatus.textContent = 'Auto · Loaded'; ui.benchStatus.className = 'text-xs text-emerald-400 font-medium bg-emerald-900/20 px-3 py-1.5 rounded-lg border border-emerald-800'; }
                try { renderMarketStructure(); } catch (e) {}
            }
            if (md.sectors && md.sectors.length) {
                scannedResults = md.sectors.slice().sort((a, b) => b.pct3m - a.pct3m);
                const renderRow = (item) => `<tr class="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors"><td class="py-2 px-2 font-bold text-white">${item.ticker}</td><td class="py-2 px-2 text-slate-400 text-[10px] truncate max-w-[100px]">${item.name}</td><td class="py-2 px-2 text-right ${item.pct3m >= 0 ? 'text-emerald-400' : 'text-rose-400'}">${item.pct3m >= 0 ? '+' : ''}${item.pct3m.toFixed(2)}%</td><td class="py-2 px-2 text-right ${item.rvol >= 1.5 ? 'text-emerald-400 font-bold' : 'text-slate-400'}">${item.rvol.toFixed(1)}x</td></tr>`;
                if (ui.top10Table) ui.top10Table.innerHTML = scannedResults.slice(0, 10).map(renderRow).join('');
                if (ui.bottom20Table && scannedResults.length > 10) ui.bottom20Table.innerHTML = scannedResults.slice(-20).reverse().map(renderRow).join('');
                const n = scannedResults.length;
                if (ui.scanCountText) ui.scanCountText.textContent = `${n} / ${n} Scanned`;
                if (ui.scanProgressBar) ui.scanProgressBar.style.width = '100%';
                if (ui.scanStatusText) ui.scanStatusText.textContent = 'Status: Auto-updated with enrichment';
            }
        }
        async function loadMarketData(attempt = 0) {
            try {
                const res = await fetch('/api/marketdata');
                if (res.status === 503) { if (attempt < 20) setTimeout(() => loadMarketData(attempt + 1), 15000); return; }
                if (!res.ok) return;
                applyMarketData(await res.json());
            } catch (e) { if (attempt < 5) setTimeout(() => loadMarketData(attempt + 1), 10000); }
        }

        [ui.accountSize, ui.maxRiskPct, ui.atr, ui.lod, ui.entryPrice].forEach(input => {
            if (input) input.addEventListener('input', calculatePosition);
        });
        
        // Benchmark selector: any ticker/ETF drives the RS line + new-high signals.
        (function initRsBenchmark() {
            const inp = document.getElementById('rsBenchmarkInput');
            if (!inp) return;
            inp.value = rsBenchmarkSymbol;
            const commit = () => {
                const sym = (inp.value || 'SPY').trim().toUpperCase() || 'SPY';
                if (sym === rsBenchmarkSymbol) return;
                rsBenchmarkSymbol = sym;
                inp.value = sym;
                try { localStorage.setItem('rsBenchmarkSymbol', sym); } catch (e) {}
                // New benchmark invalidates any cached RS-line basis; re-score current ticker.
                if (state.currentTicker && typeof fetchTickerData === 'function') {
                    ui.tickerInput.value = state.currentTicker;
                    fetchTickerData();
                }
            };
            inp.addEventListener('change', commit);
            inp.addEventListener('keypress', (e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); } });
        })();

        if (ui.btn) ui.btn.addEventListener('click', () => { hideTickerAutocomplete(); fetchTickerData(); });

        // ── TICKER AUTOCOMPLETE ──────────────────────────────────────────────────────
        // Instant, local symbol search off the cached universe (polygonStockUniverseV1).
        // Zero API calls per keystroke. Matches ticker AND company name; ranks exact ticker
        // first, then ticker-prefix, then name matches. Keyboard: ↑/↓ move, Enter load, Esc close.
        const acBox = document.getElementById('tickerAutocomplete');
        let acItems = [];        // current match list
        let acIndex = -1;        // highlighted row
        let acUniverse = null;   // lazily-parsed legacy-cache fallback
        let _acSnapUni = null, _acSnapLen = -1;   // memoised view of the live scan snapshot

        function loadAcUniverse() {
            // Prefer the live scan snapshot the app already loads (runMarketScreener →
            // /api/snapshot → screenerUniverse). It's complete and fresh and INCLUDES ETFs
            // like SPY/QQQ and every scanner symbol — unlike the legacy localStorage key
            // 'polygonStockUniverseV1', which nothing writes anymore (dead source) and which,
            // if a stale copy lingers in a browser, excludes ETFs. Re-map only when the
            // snapshot size changes so keystrokes stay cheap.
            if (Array.isArray(screenerUniverse) && screenerUniverse.length) {
                if (_acSnapLen !== screenerUniverse.length) {
                    _acSnapUni = screenerUniverse.map(r => ({ t: r.ticker, n: r.name || '' })).filter(e => e.t);
                    _acSnapLen = screenerUniverse.length;
                }
                return _acSnapUni;
            }
            // Fallback: legacy localStorage cache (present only until the first scan lands).
            if (acUniverse) return acUniverse;
            try {
                const raw = localStorage.getItem('polygonStockUniverseV1');
                if (raw) {
                    const parsed = JSON.parse(raw);
                    acUniverse = (parsed.results || []).map(r => ({ t: r.ticker, n: r.name || '' }));
                }
            } catch (e) { acUniverse = null; }
            return acUniverse;
        }

        function rankAcMatches(q) {
            const uni = loadAcUniverse();
            if (!uni || !uni.length) return null;          // null = no cache yet
            q = q.trim().toUpperCase();
            if (!q) return [];
            const exact = [], prefix = [], nameHit = [];
            for (const e of uni) {
                // Always scan the whole universe for symbol matches (a startsWith on a few
                // thousand short tickers is trivial per keystroke). The old code broke out of
                // the loop after 60 total matches — for a short query like "TE", hundreds of
                // company NAMES contain the substring, so the loop filled up on name hits and
                // bailed BEFORE ever reaching the "TE" ticker row, hiding the exact ticker.
                // Bound only the name-substring collection; never let it starve ticker hits.
                if (e.t === q) exact.push(e);
                else if (e.t.startsWith(q)) prefix.push(e);
                else if (e.n && nameHit.length < 40 && e.n.toUpperCase().includes(q)) nameHit.push(e);
            }
            prefix.sort((a, b) => a.t.length - b.t.length || a.t.localeCompare(b.t));
            return [...exact, ...prefix, ...nameHit].slice(0, 8);
        }

        function hideTickerAutocomplete() {
            if (acBox) { acBox.classList.add('hidden'); acBox.innerHTML = ''; }
            acItems = []; acIndex = -1;
        }

        function renderTickerAutocomplete(q) {
            if (!acBox) return;
            const matches = rankAcMatches(q);
            if (matches === null) {   // universe not cached yet
                acItems = []; acIndex = -1;
                acBox.innerHTML = `<div style="padding:11px 14px;font-size:11px;color:#64748b;">Symbol list loads after your first scan — type the full ticker and hit FIND.</div>`;
                acBox.classList.remove('hidden');
                return;
            }
            if (!matches.length) { hideTickerAutocomplete(); return; }
            acItems = matches; acIndex = 0;
            const uni = loadAcUniverse();
            const total = uni ? uni.length.toLocaleString() : '--';
            const esc = s => (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
            const qU = q.trim().toUpperCase();
            const rows = matches.map((m, i) => {
                const hl = m.t.startsWith(qU)
                    ? `<span style="color:#818cf8;">${esc(m.t.slice(0, qU.length))}</span>${esc(m.t.slice(qU.length))}`
                    : esc(m.t);
                const exactBadge = m.t === qU ? `<span style="font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:1px;color:#22d3ee;border:1px solid rgba(34,211,238,0.3);background:rgba(34,211,238,0.08);padding:2px 6px;border-radius:5px;">exact</span>` : '';
                const mono = m.t.slice(0, 3);
                return `<div class="ac-row" data-ac-i="${i}" data-ac-t="${esc(m.t)}" style="display:flex;align-items:center;gap:12px;padding:11px 14px;cursor:pointer;${i === 0 ? 'background:rgba(59,130,246,0.12);border-left:2px solid #3b82f6;' : 'border-left:2px solid transparent;border-top:1px solid #141c2b;'}">
                        <div style="width:34px;height:34px;border-radius:9px;background:#1e293b;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:11px;color:${i === 0 ? '#818cf8' : '#64748b'};flex-shrink:0;">${esc(mono)}</div>
                        <div style="flex:1;min-width:0;">
                            <div style="font-weight:800;color:${i === 0 ? '#fff' : '#e2e8f0'};font-size:13px;letter-spacing:0.5px;">${hl}</div>
                            <div style="font-size:11px;color:#94a3b8;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${esc(m.n) || '—'}</div>
                        </div>
                        ${exactBadge}
                    </div>`;
            }).join('');
            acBox.innerHTML = `
                <div style="display:flex;align-items:center;justify-content:space-between;padding:8px 14px;border-bottom:1px solid #1a2233;">
                    <span style="font-size:9px;text-transform:uppercase;letter-spacing:1.5px;color:#64748b;font-weight:700;">Symbol Search</span>
                    <span style="font-size:9px;color:#475569;">↑↓ · ↵ load · esc</span>
                </div>
                ${rows}
                <div style="padding:6px 14px;border-top:1px solid #1a2233;background:#0a0f1a;">
                    <span style="font-size:9px;color:#475569;">${matches.length} of ${total} symbols · instant</span>
                </div>`;
            acBox.classList.remove('hidden');
        }

        function paintAcHighlight() {
            if (!acBox) return;
            acBox.querySelectorAll('.ac-row').forEach((row, i) => {
                const on = i === acIndex;
                row.style.background = on ? 'rgba(59,130,246,0.12)' : 'transparent';
                row.style.borderLeft = on ? '2px solid #3b82f6' : '2px solid transparent';
                const mono = row.querySelector('div'); if (mono) mono.style.color = on ? '#818cf8' : '#64748b';
                const tk = row.querySelector('div > div'); if (tk) tk.style.color = on ? '#fff' : '#e2e8f0';
            });
        }

        function chooseAc(i) {
            const m = acItems[i];
            if (!m || !ui.tickerInput) return;
            ui.tickerInput.value = m.t;
            hideTickerAutocomplete();
            fetchTickerData();
        }

        if (ui.tickerInput) {
            ui.tickerInput.addEventListener('input', () => renderTickerAutocomplete(ui.tickerInput.value));
            ui.tickerInput.addEventListener('focus', () => { if (ui.tickerInput.value.trim()) renderTickerAutocomplete(ui.tickerInput.value); });
            ui.tickerInput.addEventListener('keydown', (e) => {
                const open = acBox && !acBox.classList.contains('hidden') && acItems.length;
                if (e.key === 'ArrowDown' && open) { e.preventDefault(); acIndex = (acIndex + 1) % acItems.length; paintAcHighlight(); }
                else if (e.key === 'ArrowUp' && open) { e.preventDefault(); acIndex = (acIndex - 1 + acItems.length) % acItems.length; paintAcHighlight(); }
                else if (e.key === 'Enter') { e.preventDefault(); if (open && acIndex >= 0) chooseAc(acIndex); else { hideTickerAutocomplete(); fetchTickerData(); } }
                else if (e.key === 'Escape') { hideTickerAutocomplete(); }
            });
            // Click a row.
            if (acBox) acBox.addEventListener('mousedown', (e) => {
                const row = e.target.closest('.ac-row'); if (!row) return;
                e.preventDefault(); chooseAc(parseInt(row.dataset.acI, 10));
            });
            // Close on outside click.
            document.addEventListener('click', (e) => {
                if (acBox && !acBox.contains(e.target) && e.target !== ui.tickerInput) hideTickerAutocomplete();
            });
        }
        // Live auto-refresh toggle (header pill). Heartbeat starts when a ticker loads.
        (function initLiveToggle() {
            const btn = document.getElementById('liveToggle');
            if (btn) btn.addEventListener('click', toggleLive);
            if (typeof setLiveUI === 'function') setLiveUI();
            if (typeof updateGroupRsRegimeBadge === 'function') updateGroupRsRegimeBadge();
            if (typeof startWatchlistLiveFeed === 'function') startWatchlistLiveFeed();
            window.__lastMktOpen = (typeof isUsMarketHours === 'function') ? isUsMarketHours() : false;
            window.__lastSession = (typeof getMarketSession === 'function') ? getMarketSession() : 'closed';
            // keep the open/closed styling current even before a ticker is loaded
            setInterval(() => {
                if (typeof setLiveUI === 'function') setLiveUI();
                if (typeof updateGroupRsRegimeBadge === 'function') updateGroupRsRegimeBadge();
                // Re-rank when the session PHASE flips (e.g. pre→rth, rth→post, →closed) so the
                // TD column blanks/unblanks at the right boundary, not just on open/closed.
                const nowSession = (typeof getMarketSession === 'function') ? getMarketSession() : 'closed';
                const nowOpen = (typeof isUsMarketHours === 'function') ? isUsMarketHours() : false;
                if (window.__lastSession !== nowSession || window.__lastMktOpen !== nowOpen) {
                    window.__lastSession = nowSession;
                    window.__lastMktOpen = nowOpen;
                    if (typeof state.activeHubTab !== 'undefined' && state.activeHubTab === 'groups' && typeof renderGroupRankings === 'function') renderGroupRankings();
                }
            }, 15000);
        })();
        if (ui.marketScreenerToggle) ui.marketScreenerToggle.addEventListener('click', () => setScreenerOpen(!ui.marketScreenerPanel.classList.contains('open')));
        document.querySelectorAll('.hub-tab').forEach(btn => btn.addEventListener('click', () => setHubTab(btn.dataset.hub)));

        // --- Watchlist tab interactions ---
        (function initWatchlistTab() {
            const wlTab = document.getElementById('watchlistTab');
            const refreshBtn = document.getElementById('watchlistRefreshBtn');
            if (wlTab) {
                wlTab.addEventListener('click', (e) => {
                    // Row checkbox → toggle selection (never loads the ticker).
                    const cb = e.target.closest('[data-wl-check]');
                    if (cb) {
                        e.stopPropagation();
                        const t = cb.dataset.wlCheck;
                        if (cb.checked) wlSelected.add(t); else wlSelected.delete(t);
                        const tr = cb.closest('tr');
                        if (tr) tr.classList.toggle('wl-row-checked', cb.checked);
                        updateWlBulkBar();
                        const all = document.getElementById('wlSelectAll');
                        if (all) { const rows = wlTab.querySelectorAll('[data-wl-check]'); all.checked = rows.length && [...rows].every(r => r.checked); }
                        return;
                    }
                    // Select-all header checkbox.
                    if (e.target.id === 'wlSelectAll') {
                        e.stopPropagation();
                        const on = e.target.checked;
                        wlTab.querySelectorAll('[data-wl-check]').forEach(r => {
                            r.checked = on;
                            const t = r.dataset.wlCheck;
                            if (on) wlSelected.add(t); else wlSelected.delete(t);
                            const tr = r.closest('tr'); if (tr) tr.classList.toggle('wl-row-checked', on);
                        });
                        updateWlBulkBar();
                        return;
                    }
                    const rm = e.target.closest('[data-wl-remove]');
                    if (rm) {
                        e.stopPropagation();
                        const t = rm.dataset.wlRemove;
                        Watchlist.remove(t);
                        wlSelected.delete(t);
                        renderWatchlist();
                        updateWatchlistTabBadge();
                        if (typeof renderScreenerResults === 'function') renderScreenerResults();
                        return;
                    }
                    const ed = e.target.closest('[data-wl-edit]');
                    if (ed) { e.stopPropagation(); openWlEditor(ed.dataset.wlEdit, ed); return; }
                    const al = e.target.closest('[data-wl-alert]');
                    if (al) { e.stopPropagation(); return; }
                    // Clicks inside any no-load zone (checkbox cell, action cell) don't load.
                    if (e.target.closest('[data-wl-noload]')) return;
                    const tr = e.target.closest('tr[data-ticker]');
                    if (tr && ui.tickerInput) {
                        ui.tickerInput.value = tr.dataset.ticker;
                        fetchTickerData();
                    }
                });
            }
            if (refreshBtn) {
                refreshBtn.addEventListener('click', () => {
                    if (typeof runMarketScreener === 'function' && !state.screenerRunning) runMarketScreener();
                });
            }
        })();

        // ── Watchlist enhancements: notes/flags/tags editor, columns, filters,
        //    import/export, bulk actions, and lazy sparkline backfill. ───────────────
        // Editor popover for per-symbol notes, a color flag, and tags.
        function openWlEditor(ticker, anchorEl) {
            closeWlEditor();
            const item = Watchlist.get(ticker);
            if (!item) return;
            const tags = (item.tags || []).join(', ');
            const box = document.createElement('div');
            box.className = 'wl-editor';
            box.id = 'wlEditorBox';
            box.style.width = '240px';
            box.innerHTML = `
                <div class="flex items-center justify-between mb-2">
                    <span class="font-black text-slate-200 text-[12px]">${wlEsc(ticker)}</span>
                    <button id="wlEdClose" class="text-slate-500 hover:text-white text-sm leading-none">✕</button>
                </div>
                <div class="text-[9px] uppercase tracking-wider text-slate-500 mb-1">Flag</div>
                <div class="flex items-center gap-2 mb-3" id="wlEdFlags">
                    <span class="wl-swatch ${!item.flag ? 'sel' : ''}" data-flag="" title="None" style="background:#1f2937;border-color:#475569;"></span>
                    ${FLAG_COLORS.map(c => `<span class="wl-swatch wl-flag-${c} ${item.flag === c ? 'sel' : ''}" data-flag="${c}" title="${c}"></span>`).join('')}
                </div>
                <div class="text-[9px] uppercase tracking-wider text-slate-500 mb-1">Tags <span class="text-slate-600 normal-case">(comma-separated)</span></div>
                <input id="wlEdTags" type="text" value="${wlEsc(tags)}" placeholder="breakout, near pivot" class="w-full mb-3" style="background:#0a0d17;border:1px solid #1f2937;border-radius:6px;color:#e2e8f0;font-size:11px;padding:5px 7px;outline:none;">
                <div class="text-[9px] uppercase tracking-wider text-slate-500 mb-1">Note</div>
                <textarea id="wlEdNote" rows="3" placeholder="Your thesis, level to watch…">${wlEsc(item.note || '')}</textarea>
                <div class="flex justify-end gap-2 mt-2">
                    <button id="wlEdSave" class="wl-tbtn" style="border-color:#1d4ed8;color:#93c5fd;">Save</button>
                </div>`;
            document.body.appendChild(box);
            // Position near the anchor, kept on-screen.
            const r = anchorEl.getBoundingClientRect();
            const bw = 240, bh = box.offsetHeight || 230;
            let left = Math.min(r.left - bw + 30, window.innerWidth - bw - 8);
            let top = r.bottom + 6;
            if (top + bh > window.innerHeight - 8) top = Math.max(8, r.top - bh - 6);
            box.style.left = Math.max(8, left) + 'px';
            box.style.top = top + 'px';

            let pendingFlag = item.flag || '';
            box.querySelector('#wlEdFlags').addEventListener('click', (e) => {
                const sw = e.target.closest('[data-flag]'); if (!sw) return;
                pendingFlag = sw.dataset.flag;
                box.querySelectorAll('#wlEdFlags .wl-swatch').forEach(s => s.classList.toggle('sel', s === sw));
            });
            box.querySelector('#wlEdClose').addEventListener('click', closeWlEditor);
            box.querySelector('#wlEdSave').addEventListener('click', () => {
                const note = box.querySelector('#wlEdNote').value.trim();
                const rawTags = box.querySelector('#wlEdTags').value.split(',').map(s => s.trim()).filter(Boolean);
                const uniqTags = [...new Set(rawTags)].slice(0, 12);
                Watchlist.update(ticker, { note, flag: pendingFlag || null, tags: uniqTags });
                closeWlEditor();
                renderWatchlist();
            });
            box.querySelector('#wlEdTags').focus();
            setTimeout(() => document.addEventListener('mousedown', wlEditorOutside), 0);
        }
        function wlEditorOutside(e) {
            const box = document.getElementById('wlEditorBox');
            if (box && !box.contains(e.target)) closeWlEditor();
        }
        function closeWlEditor() {
            const box = document.getElementById('wlEditorBox');
            if (box) box.remove();
            document.removeEventListener('mousedown', wlEditorOutside);
        }

        // ── Watchlist picker — choose which list(s) a ticker belongs to ───────────
        // A small checkbox popover anchored to a star button (screener / alerts feed).
        // Membership reflects ALL of the user's lists; toggling adds/removes per list.
        function closeWatchlistPicker() {
            const box = document.getElementById('wlPickerBox');
            if (box) box.remove();
            document.removeEventListener('mousedown', wlPickerOutside);
        }
        function wlPickerOutside(e) {
            const box = document.getElementById('wlPickerBox');
            if (box && !box.contains(e.target) && !e.target.closest('[data-watch-toggle]') && !e.target.closest('[data-alert-watch]')) closeWatchlistPicker();
        }
        function paintStar(anchorEl, ticker) {
            if (!anchorEl) return;
            const on = Watchlist.hasAny(ticker);
            anchorEl.textContent = on ? '★' : '☆';
            anchorEl.title = on ? 'In watchlist(s)' : 'Add to watchlist';
            if (anchorEl.hasAttribute('data-watch-toggle')) {
                anchorEl.className = `shrink-0 text-base leading-none transition-colors ${on ? 'text-amber-400 hover:text-amber-300' : 'text-slate-600 hover:text-amber-400'}`;
            } else {
                anchorEl.className = `shrink-0 ${on ? 'text-amber-400 hover:text-amber-300' : 'text-slate-600 hover:text-amber-400'} text-sm leading-none`;
            }
        }
        function openWatchlistPicker(ticker, name, row, anchorEl) {
            closeWatchlistPicker();
            const t = (ticker || '').toUpperCase();
            function render() {
                let box = document.getElementById('wlPickerBox');
                const fresh = !box;
                if (fresh) { box = document.createElement('div'); box.className = 'wl-picker'; box.id = 'wlPickerBox'; }
                const inSet = new Set(Watchlist.listsContaining(t));
                box.innerHTML =
                    `<div class="wlp-head"><span class="text-amber-400">★</span><span>${wlEsc(t)}</span><span class="text-slate-500 text-[10px] font-normal">in lists</span></div>` +
                    Watchlist.getLists().map(l =>
                        `<label data-list="${l.id}"><input type="checkbox" class="accent-blue-500" ${inSet.has(l.id) ? 'checked' : ''}> <span class="flex-1">${wlEsc(l.name)}</span> <span class="text-slate-500 text-[10px]">${l.count}</span></label>`
                    ).join('') +
                    `<button class="wlp-new" data-new>+ New list…</button>`;
                if (fresh) {
                    document.body.appendChild(box);
                    const r = anchorEl.getBoundingClientRect();
                    const bw = 220, bh = box.offsetHeight || 200;
                    let left = Math.min(r.left, window.innerWidth - bw - 8);
                    let top = r.bottom + 6;
                    if (top + bh > window.innerHeight - 8) top = Math.max(8, r.top - bh - 6);
                    box.style.left = Math.max(8, left) + 'px';
                    box.style.top = top + 'px';
                }
            }
            render();
            const box = document.getElementById('wlPickerBox');
            box.addEventListener('change', (e) => {
                const cb = e.target.closest('input[type=checkbox]'); if (!cb) return;
                const listId = Number(cb.closest('[data-list]').dataset.list);
                if (cb.checked) Watchlist.addTo(listId, t, name, row);
                else Watchlist.removeFrom(listId, t);
                paintStar(anchorEl, t);
                if (typeof renderWatchlist === 'function') renderWatchlist();
                if (typeof updateWatchlistTabBadge === 'function') updateWatchlistTabBadge();
                render();   // refresh per-list counts
            });
            box.addEventListener('click', async (e) => {
                if (!e.target.closest('[data-new]')) return;
                const nm = (prompt('Name your new watchlist:', 'New list') || '').trim();
                if (!nm) return;
                const list = await Watchlist.createList(nm);   // alerts + returns null if over tier cap
                if (list) { Watchlist.addTo(list.id, t, name, row); paintStar(anchorEl, t); }
                if (typeof renderWlListTabs === 'function') renderWlListTabs();
                if (typeof renderWatchlist === 'function') renderWatchlist();
                if (typeof updateWatchlistTabBadge === 'function') updateWatchlistTabBadge();
                render();
            });
            setTimeout(() => document.addEventListener('mousedown', wlPickerOutside), 0);
        }

        // Columns chooser, toolbar filters, import/export, and bulk actions.
        (function initWatchlistEnhancements() {
            const search = document.getElementById('wlSearch');
            const tagF = document.getElementById('wlTagFilter');
            const flagF = document.getElementById('wlFlagFilter');
            // The search box is now an "add from screener" typeahead: it queries the full
            // screener universe (screenerAllResults) and adds the chosen stock to the ACTIVE
            // watchlist. Enter adds the top match; ↑/↓ navigate; click "+ Add" on a row.
            if (search) {
                search.setAttribute('placeholder', 'Add symbol from screener…');
                const dd = document.getElementById('wlSearchResults');
                let matches = [], hi = -1, t = null;

                const universe = () => (typeof state.screenerAllResults !== 'undefined' && Array.isArray(state.screenerAllResults)) ? state.screenerAllResults : [];
                function computeMatches(q) {
                    q = q.trim().toUpperCase();
                    if (!q) return [];
                    const starts = [], contains = [];
                    for (const r of universe()) {
                        if (!r || !r.ticker) continue;
                        const tk = r.ticker.toUpperCase(), nm = (r.name || '').toUpperCase();
                        if (tk.startsWith(q)) starts.push(r);
                        else if (tk.includes(q) || nm.includes(q)) contains.push(r);
                        if (starts.length >= 8) break;
                    }
                    return starts.concat(contains).slice(0, 8);
                }
                function closeDD() { if (dd) { dd.classList.add('hidden'); dd.innerHTML = ''; } matches = []; hi = -1; }
                function renderDD() {
                    if (!dd) return;
                    if (!matches.length) { closeDD(); return; }
                    dd.innerHTML = matches.map((r, i) => {
                        const has = Watchlist.has(r.ticker);
                        const px = Number.isFinite(r.price) ? `$${r.price.toFixed(2)}` : '';
                        const sc = Number.isFinite(r.score) ? `· ${r.score}` : '';
                        return `<div class="wl-srow ${i === hi ? 'wl-srow-hi' : ''}" data-add="${wlEsc(r.ticker)}" data-i="${i}">
                            <div class="min-w-0">
                              <span class="font-black text-white">${wlEsc(r.ticker)}</span>
                              <span class="text-slate-500 text-[10px] ml-1 truncate">${wlEsc(r.name || '')}</span>
                            </div>
                            <div class="flex items-center gap-2 shrink-0">
                              <span class="text-slate-500 text-[10px] tabular-nums">${px} ${sc}</span>
                              ${has ? '<span class="text-amber-400 text-[10px]">★ in list</span>'
                                    : '<span class="wl-add-btn">+ Add</span>'}
                            </div>
                        </div>`;
                    }).join('');
                    dd.classList.remove('hidden');
                }
                function addRow(r) {
                    if (!r) return;
                    if (typeof Auth !== 'undefined' && !Auth.isAuthed()) { Auth.openModal(); return; }
                    if (!Watchlist.getActiveId()) return;
                    if (!Watchlist.has(r.ticker)) Watchlist.add(r.ticker, r.name, r);
                    search.value = '';
                    closeDD();
                    renderWatchlist();
                    updateWatchlistTabBadge();
                    if (typeof renderScreenerResults === 'function') renderScreenerResults();
                }

                search.addEventListener('input', () => {
                    clearTimeout(t);
                    t = setTimeout(() => { matches = computeMatches(search.value); hi = matches.length ? 0 : -1; renderDD(); }, 120);
                });
                search.addEventListener('keydown', (e) => {
                    if (e.key === 'ArrowDown') { if (matches.length) { hi = (hi + 1) % matches.length; renderDD(); e.preventDefault(); } }
                    else if (e.key === 'ArrowUp') { if (matches.length) { hi = (hi - 1 + matches.length) % matches.length; renderDD(); e.preventDefault(); } }
                    else if (e.key === 'Enter') { if (matches.length) { addRow(matches[hi >= 0 ? hi : 0]); e.preventDefault(); } }
                    else if (e.key === 'Escape') { closeDD(); }
                });
                search.addEventListener('blur', () => setTimeout(closeDD, 150));
                if (dd) dd.addEventListener('mousedown', (e) => {
                    const row = e.target.closest('[data-add]'); if (!row) return;
                    e.preventDefault();
                    addRow(matches[Number(row.dataset.i)]);
                });
            }
            if (tagF)  tagF.addEventListener('change', () => { wlFilter.tag = tagF.value; renderWatchlist(); });
            if (flagF) flagF.addEventListener('change', () => { wlFilter.flag = flagF.value; renderWatchlist(); });
            const adrIn = document.getElementById('wlAdrMin');
            if (adrIn) {
                let at = null;
                adrIn.addEventListener('input', () => { clearTimeout(at); at = setTimeout(() => { wlFilter.adrMin = Number(adrIn.value || 0); renderWatchlist(); }, 200); });
            }

            // Drag-to-reorder columns (delegated on the table wrap so it survives re-render).
            const tableWrap = document.getElementById('watchlistTableWrap');
            if (tableWrap) {
                let dragKey = null;
                tableWrap.addEventListener('dragstart', (e) => {
                    const th = e.target.closest('[data-colkey]'); if (!th) return;
                    dragKey = th.dataset.colkey;
                    wlSuppressSort = true;
                    wlDragInProgress = true;   // pause re-renders so the dragged header isn't detached
                    e.dataTransfer.effectAllowed = 'move';
                    try { e.dataTransfer.setData('text/plain', dragKey); } catch (_) {}
                });
                tableWrap.addEventListener('dragover', (e) => {
                    const th = e.target.closest('[data-colkey]'); if (!th || !dragKey) return;
                    e.preventDefault();
                    th.classList.add('wl-col-dragover');
                });
                tableWrap.addEventListener('dragleave', (e) => {
                    const th = e.target.closest('[data-colkey]'); if (th) th.classList.remove('wl-col-dragover');
                });
                tableWrap.addEventListener('drop', (e) => {
                    const th = e.target.closest('[data-colkey]'); if (!th || !dragKey) return;
                    e.preventDefault();
                    th.classList.remove('wl-col-dragover');
                    const targetKey = th.dataset.colkey;
                    wlDragInProgress = false;   // allow the drop's own re-render
                    if (targetKey && targetKey !== dragKey) {
                        // Normalize order to all known keys, then move dragKey before targetKey.
                        const known = WL_ALL_COLS.map(c => c.key);
                        let order = wlColOrder.filter(k => known.includes(k));
                        known.forEach(k => { if (!order.includes(k)) order.push(k); });
                        order = order.filter(k => k !== dragKey);
                        const ti = order.indexOf(targetKey);
                        order.splice(ti, 0, dragKey);
                        wlColOrder = order;
                        persistWatchlistColOrder();
                        renderWatchlist();
                    }
                    dragKey = null;
                    setTimeout(() => { wlSuppressSort = false; }, 0);
                });
                tableWrap.addEventListener('dragend', () => { dragKey = null; wlDragInProgress = false; setTimeout(() => { wlSuppressSort = false; }, 0); tableWrap.querySelectorAll('.wl-col-dragover').forEach(el => el.classList.remove('wl-col-dragover')); });
            }

            // Columns menu
            const colBtn = document.getElementById('wlColumnsBtn');
            if (colBtn) colBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                if (document.getElementById('wlColMenu')) { closeColMenu(); return; }
                const menu = document.createElement('div');
                menu.className = 'wl-colmenu'; menu.id = 'wlColMenu';
                menu.innerHTML = `<div class="text-[9px] uppercase tracking-wider text-slate-500 mb-1 px-1">Columns</div>` +
                    WL_ALL_COLS.map(c => `<label><input type="checkbox" data-col="${c.key}" ${wlVisibleCols.has(c.key) ? 'checked' : ''} class="accent-blue-500"> ${c.label}</label>`).join('');
                document.body.appendChild(menu);
                const r = colBtn.getBoundingClientRect();
                menu.style.left = Math.max(8, Math.min(r.left, window.innerWidth - menu.offsetWidth - 8)) + 'px';
                menu.style.top = (r.bottom + 6) + 'px';
                menu.addEventListener('click', (ev) => {
                    const cb = ev.target.closest('input[data-col]'); if (!cb) return;
                    const k = cb.dataset.col;
                    if (cb.checked) wlVisibleCols.add(k); else wlVisibleCols.delete(k);
                    persistWatchlistCols();
                    renderWatchlist();
                });
                setTimeout(() => document.addEventListener('mousedown', colOutside), 0);
            });
            function colOutside(e) { const m = document.getElementById('wlColMenu'); if (m && !m.contains(e.target) && e.target.id !== 'wlColumnsBtn') closeColMenu(); }
            function closeColMenu() { const m = document.getElementById('wlColMenu'); if (m) m.remove(); document.removeEventListener('mousedown', colOutside); }

            // Export → CSV of the current watchlist
            const expBtn = document.getElementById('wlExportBtn');
            if (expBtn) expBtn.addEventListener('click', () => {
                const items = Watchlist.all();
                if (!items.length) return;
                const head = ['ticker', 'name', 'flag', 'tags', 'note'];
                const csv = [head.join(',')].concat(items.map(it => {
                    const row = [it.ticker, it.name || '', it.flag || '', (it.tags || []).join('|'), (it.note || '').replace(/\s+/g, ' ')];
                    return row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',');
                })).join('\n');
                const blob = new Blob([csv], { type: 'text/csv' });
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = `watchlist-${new Date().toISOString().slice(0, 10)}.csv`;
                a.click();
                setTimeout(() => URL.revokeObjectURL(a.href), 1000);
            });

            // Import ← .txt/.csv (first token of each line treated as a ticker)
            const impBtn = document.getElementById('wlImportBtn');
            const impFile = document.getElementById('wlImportFile');
            if (impBtn && impFile) {
                impBtn.addEventListener('click', () => impFile.click());
                impFile.addEventListener('change', () => {
                    const f = impFile.files && impFile.files[0]; if (!f) return;
                    const reader = new FileReader();
                    reader.onload = () => {
                        const text = String(reader.result || '');
                        const tickers = text.split(/[\s,;\n\r]+/).map(s => s.trim().toUpperCase())
                            .filter(s => /^[A-Z][A-Z.\-]{0,9}$/.test(s) && s !== 'TICKER' && s !== 'SYMBOL');
                        const n = Watchlist.addMany([...new Set(tickers)]);
                        impFile.value = '';
                        renderWatchlist();
                        updateWatchlistTabBadge();
                        if (typeof renderScreenerResults === 'function') renderScreenerResults();
                        if (ui.status) ui.status.textContent = `Imported ${n} new ticker${n === 1 ? '' : 's'} to watchlist`;
                    };
                    reader.readAsText(f);
                });
            }

            // Bulk actions
            const bulkRemove = document.getElementById('wlBulkRemove');
            const bulkExport = document.getElementById('wlBulkExport');
            const bulkClear = document.getElementById('wlBulkClear');
            if (bulkRemove) bulkRemove.addEventListener('click', () => {
                if (!wlSelected.size) return;
                Watchlist.removeMany([...wlSelected]);
                wlSelected.clear();
                renderWatchlist();
                updateWatchlistTabBadge();
                if (typeof renderScreenerResults === 'function') renderScreenerResults();
            });
            if (bulkExport) bulkExport.addEventListener('click', () => {
                if (!wlSelected.size) return;
                const txt = [...wlSelected].join('\n');
                const blob = new Blob([txt], { type: 'text/plain' });
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = `watchlist-selected-${new Date().toISOString().slice(0, 10)}.txt`;
                a.click();
                setTimeout(() => URL.revokeObjectURL(a.href), 1000);
            });
            if (bulkClear) bulkClear.addEventListener('click', () => { wlSelected.clear(); renderWatchlist(); });
        })();

        // Watchlist row/section drag-and-drop + section controls (delegated on the tbody,
        // which persists across re-renders). Rows drag from the ⠿ handle; section headers
        // drag from their grip. Mirrors the column drag-reorder pattern (wlSuppressSort guard).
        (function initWatchlistDnD() {
            const body = document.getElementById('watchlistBody');
            if (!body) return;
            let dragType = null, dragKey = null, hiRow = null, dragWatchdog = null;
            // Track the highlighted row directly so dragover (fires on every mouse move) never
            // has to query/scan the DOM — just a class swap when the target actually changes.
            const clearHi = () => { if (hiRow) { hiRow.classList.remove('wl-row-dragover'); hiRow = null; } };
            // Always clear drag state, even if dragend never fires (source detached, etc.).
            const endDrag = () => { dragType = null; dragKey = null; clearHi(); clearTimeout(dragWatchdog); wlDragInProgress = false; setTimeout(() => { wlSuppressSort = false; }, 0); };

            // Full model order (includes items in collapsed sections): ungrouped first, then
            // each section, each group ordered by manual position. Base for computing drops.
            function fullModelOrder() {
                const all = (typeof Watchlist !== 'undefined') ? Watchlist.all() : [];
                const secs = (typeof Watchlist.getSections === 'function') ? Watchlist.getSections() : [];
                const groups = new Map(secs.map(s => [s, []])); const ung = [];
                all.forEach(it => { const sec = (it.section && groups.has(it.section)) ? it.section : null; (sec ? groups.get(sec) : ung).push(it); });
                const byPos = arr => arr.slice().sort((a, b) => (a.position || 0) - (b.position || 0));
                const out = [];
                byPos(ung).forEach(it => out.push({ ticker: it.ticker, section: null }));
                secs.forEach(s => byPos(groups.get(s) || []).forEach(it => out.push({ ticker: it.ticker, section: s })));
                return out;
            }

            // Record where the press began so dragstart can tell whether the user grabbed an
            // interactive control (checkbox / buttons / edit dots) vs the row itself. dragstart's
            // e.target is the draggable source (the <tr>), not the actual grabbed element.
            let pressEl = null;
            body.addEventListener('mousedown', (e) => { pressEl = e.target; });

            body.addEventListener('dragstart', (e) => {
                const grabbed = pressEl || e.target;
                const head = grabbed.closest('tr[data-section-head]');
                const row = grabbed.closest('tr[data-ticker]');
                let srcRow = null;
                if (head) {
                    // Whole header drags to reorder sections — except its own controls.
                    if (grabbed.closest('[data-sec-toggle], [data-sec-del], input')) { e.preventDefault(); return; }
                    dragType = 'section'; dragKey = head.dataset.sectionHead; srcRow = head;
                } else if (row) {
                    // Whole row drags — except interactive controls (so click/checkbox/buttons work).
                    if (grabbed.closest('input, button, [data-wl-edit], [data-wl-alert], [data-wl-remove], [data-wl-check]')) { e.preventDefault(); return; }
                    dragType = 'row'; dragKey = row.dataset.ticker; srcRow = row;
                } else { e.preventDefault(); return; }
                wlSuppressSort = true;
                wlDragInProgress = true;
                clearTimeout(dragWatchdog);
                dragWatchdog = setTimeout(endDrag, 4000);   // safety net so flags never stick
                e.dataTransfer.effectAllowed = 'move';
                try { e.dataTransfer.setData('text/plain', dragKey); } catch (_) {}
                try { if (srcRow) e.dataTransfer.setDragImage(srcRow, 12, 12); } catch (_) {}
            });
            body.addEventListener('dragover', (e) => {
                if (!dragType) return;
                const tgt = e.target.closest('tr[data-section-head], tr[data-ticker]');
                if (!tgt) return;
                e.preventDefault();   // required so drop fires
                if (tgt !== hiRow) { if (hiRow) hiRow.classList.remove('wl-row-dragover'); tgt.classList.add('wl-row-dragover'); hiRow = tgt; }
            });
            body.addEventListener('drop', (e) => {
                if (!dragType) return;
                e.preventDefault(); clearHi();
                wlDragInProgress = false; clearTimeout(dragWatchdog);   // allow the drop's own re-render
                const head = e.target.closest('tr[data-section-head]');
                const row = e.target.closest('tr[data-ticker]');
                if (dragType === 'section') {
                    let order = Watchlist.getSections().filter(s => s !== dragKey);
                    const targetSec = head ? head.dataset.sectionHead : (row ? (row.dataset.section || null) : null);
                    let idx = targetSec ? order.indexOf(targetSec) : order.length;
                    if (idx < 0) idx = order.length;
                    order.splice(idx, 0, dragKey);
                    Watchlist.reorderSections(order);
                    renderWatchlist();
                } else if (dragType === 'row') {
                    const order = fullModelOrder().filter(o => o.ticker !== dragKey);
                    let section, insertIdx;
                    if (head) {
                        section = head.dataset.sectionHead;
                        let last = -1; order.forEach((o, i) => { if (o.section === section) last = i; });
                        insertIdx = last >= 0 ? last + 1 : order.length;
                    } else if (row) {
                        section = row.dataset.section || null;
                        const ti = order.findIndex(o => o.ticker === row.dataset.ticker);
                        insertIdx = ti < 0 ? order.length : ti;
                    } else { endDrag(); return; }
                    order.splice(insertIdx, 0, { ticker: dragKey, section: section || null });
                    watchlistSort = { key: 'manual', dir: 'asc' }; persistWatchlistSort();
                    Watchlist.reorderItems(order);
                    renderWatchlist(); updateWatchlistTabBadge();
                }
                endDrag();
            });
            body.addEventListener('dragend', endDrag);

            // Section header controls: collapse / rename / delete.
            body.addEventListener('click', (e) => {
                const tgl = e.target.closest('[data-sec-toggle]');
                if (tgl) { toggleSectionCollapsed(tgl.dataset.secToggle); renderWatchlist(); return; }
                const dl = e.target.closest('[data-sec-del]');
                if (dl) { const nm = dl.dataset.secDel; if (confirm(`Delete section "${nm}"? Its stocks move to Ungrouped.`)) Promise.resolve(Watchlist.deleteSection(nm)).then(() => { renderWatchlist(); updateWatchlistTabBadge(); }); return; }
            });
            // Inline rename on double-clicking the section name — swap the label for an <input>
            // (mirrors the list-tab rename). Enter/blur commits, Escape cancels. The name isn't a
            // single-click toggle (only the caret is), so the first click can't re-render and detach
            // the node mid-double-click.
            body.addEventListener('dblclick', (e) => {
                const nmEl = e.target.closest('[data-sec-name]');
                if (!nmEl) return;
                const cur = nmEl.dataset.secName;
                const input = document.createElement('input');
                input.type = 'text'; input.value = cur; input.className = 'wl-sec-name-edit';
                nmEl.replaceWith(input); input.focus(); input.select();
                let done = false;
                const commit = async () => {
                    if (done) return; done = true;
                    const v = input.value.trim();
                    if (v && v !== cur) await Watchlist.renameSection(cur, v);
                    renderWatchlist();   // rebuilds the row (restores the label on no-op/cancel too)
                };
                input.addEventListener('keydown', (ev) => {
                    if (ev.key === 'Enter') { ev.preventDefault(); input.blur(); }
                    else if (ev.key === 'Escape') { ev.preventDefault(); input.value = cur; input.blur(); }
                });
                input.addEventListener('blur', commit);
            });

            // "+ Section" toolbar button.
            const addBtn = document.getElementById('wlAddSectionBtn');
            if (addBtn) addBtn.addEventListener('click', () => {
                if (typeof Auth !== 'undefined' && !Auth.isAuthed()) { Auth.openModal(); return; }
                const nm = (prompt('New section name:', 'New section') || '').trim();
                if (nm) Promise.resolve(Watchlist.addSection(nm)).then(() => renderWatchlist());
            });
        })();

        // ACCOUNTS auth (Auth) → extracted to src/auth.js (imported above). configureAuth() is
        // called just before Auth.boot() below (deps = the 4 watchlist-membership render fns).

        // ── Multi-list tab strip ──────────────────────────────────────────────────
        function renderWlListTabs() {
            const strip = document.getElementById('wlListTabs');
            if (!strip) return;
            if (typeof Auth !== 'undefined' && !Auth.isAuthed()) { strip.classList.add('hidden'); strip.innerHTML = ''; return; }
            strip.classList.remove('hidden');
            const lists = Watchlist.getLists();
            const activeId = Watchlist.getActiveId();
            strip.innerHTML = lists.map(l => `
                <span class="wl-listpill ${l.id === activeId ? 'active' : ''}" data-list="${l.id}" draggable="true">
                    <span class="wl-pill-name">${wlEsc(l.name)}</span>
                    <span class="wl-pill-count">${l.count}</span>
                    <span class="wl-pill-x" data-del="${l.id}" title="Delete list">&times;</span>
                </span>`).join('') + `<span class="wl-newlist" id="wlNewListBtn" title="New watchlist">+</span>`;
        }
        (function initWlListTabs() {
            const strip = document.getElementById('wlListTabs');
            if (!strip) return;
            let dragId = null;
            strip.addEventListener('click', async (e) => {
                const del = e.target.closest('[data-del]');
                if (del) {
                    e.stopPropagation();
                    const id = Number(del.dataset.del);
                    const l = Watchlist.getLists().find(x => x.id === id);
                    if (l && confirm(`Delete watchlist "${l.name}"${l.count ? ` and its ${l.count} item${l.count === 1 ? '' : 's'}` : ''}?`)) {
                        await Watchlist.deleteList(id);
                        renderWlListTabs(); renderWatchlist(); updateWatchlistTabBadge();
                        if (typeof renderScreenerResults === 'function') renderScreenerResults();
                    }
                    return;
                }
                if (e.target.closest('#wlNewListBtn')) {
                    const name = (prompt('Name your new watchlist:', 'New list') || '').trim();
                    if (name) { await Watchlist.createList(name); renderWlListTabs(); renderWatchlist(); updateWatchlistTabBadge(); if (typeof renderScreenerResults === 'function') renderScreenerResults(); }
                    return;
                }
                const pill = e.target.closest('[data-list]');
                if (pill) {
                    const id = Number(pill.dataset.list);
                    if (id !== Watchlist.getActiveId()) { Watchlist.setActive(id); renderWlListTabs(); renderWatchlist(); updateWatchlistTabBadge(); if (typeof renderScreenerResults === 'function') renderScreenerResults(); }
                }
            });
            // Inline rename on double-click.
            strip.addEventListener('dblclick', (e) => {
                const pill = e.target.closest('[data-list]'); if (!pill) return;
                const id = Number(pill.dataset.list);
                const nameEl = pill.querySelector('.wl-pill-name');
                const cur = nameEl.textContent;
                const input = document.createElement('input');
                input.type = 'text'; input.value = cur;
                nameEl.replaceWith(input); input.focus(); input.select();
                const commit = async () => {
                    const v = input.value.trim();
                    if (v && v !== cur) { await Watchlist.renameList(id, v); }
                    renderWlListTabs();
                };
                input.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') input.blur(); else if (ev.key === 'Escape') { input.value = cur; input.blur(); } });
                input.addEventListener('blur', commit);
            });
            // Drag-to-reorder pills.
            strip.addEventListener('dragstart', (e) => { const p = e.target.closest('[data-list]'); if (p) { dragId = Number(p.dataset.list); e.dataTransfer.effectAllowed = 'move'; } });
            strip.addEventListener('dragover', (e) => { if (dragId != null && e.target.closest('[data-list]')) e.preventDefault(); });
            strip.addEventListener('drop', async (e) => {
                const tgt = e.target.closest('[data-list]'); if (tgt == null || dragId == null) return;
                e.preventDefault();
                const targetId = Number(tgt.dataset.list);
                if (targetId === dragId) { dragId = null; return; }
                let order = Watchlist.getLists().map(l => l.id).filter(id => id !== dragId);
                const ti = order.indexOf(targetId);
                order.splice(ti, 0, dragId);
                dragId = null;
                await Watchlist.reorderLists(order);
                renderWlListTabs();
            });
        })();

        // Inject Auth's app.js back-calls (the 4 watchlist-membership renderers) BEFORE boot — its
        // dep usage is behind an await, but this matches the "configure before any use" convention and
        // all four render fns are defined above. (Kept here, not in the later configure* cluster, because
        // Auth.boot() fires earlier than that cluster.)
        configureAuth({ renderWlListTabs, renderWatchlist, updateWatchlistTabBadge, renderScreenerResults });
        // Kick off the auth probe once the rest of the app script has defined its globals.
        Auth.boot();


        // --- Alerts tab interactions ---
        (function initAlertsTab() {
            const feed = document.getElementById('alertsFeed');
            const clearBtn = document.getElementById('alertsClearBtn');
            const filterBar = document.getElementById('alertsFilterBar');
            if (feed) {
                feed.addEventListener('click', (e) => {
                    const th = e.target.closest('.al-sort');
                    if (!th) return;
                    const key = th.dataset.alsort;
                    if (!key) return;
                    if (alertsSort.key === key) {
                        alertsSort.dir = alertsSort.dir === 'asc' ? 'desc' : 'asc';
                    } else {
                        alertsSort.key = key;
                        alertsSort.dir = (key === 'ticker') ? 'asc' : 'desc';
                    }
                    persistAlertsSort();
                    renderAlertsFeed();
                });
            }
            if (clearBtn) {
                clearBtn.addEventListener('click', () => {
                    if (typeof Watchlist === 'undefined') return;
                    Watchlist.clearAlerts();
                    renderAlertsFeed();
                    updateAlertsTabBadge();
                });
            }
            // Per-signal toggles: each is independent on/off. __all / __none bulk-set.
            // A solid colored button = ON; muted outline = OFF.
            function paintAlertToggles() {
                if (!filterBar) return;
                const COLOR = {
                    alpha: 'bg-emerald-600/80', beta: 'bg-blue-600/80', delta: 'bg-sky-600/80',
                    oprime: 'bg-indigo-600/80', onova: 'bg-violet-600/80', otitan: 'bg-rose-600/80'
                };
                filterBar.querySelectorAll('.alert-toggle').forEach(b => {
                    const t = b.dataset.atoggle;
                    if (!ALERT_TYPE_KEYS.includes(t)) return;
                    const on = alertsTypesOn.has(t);
                    b.classList.toggle(COLOR[t], on);
                    b.classList.toggle('text-white', on);
                    b.classList.toggle('opacity-40', !on);
                    b.classList.toggle('grayscale', !on);
                });
            }
            if (filterBar) {
                paintAlertToggles();
                filterBar.addEventListener('click', (e) => {
                    const btn = e.target.closest('[data-atoggle]');
                    if (!btn) return;
                    const t = btn.dataset.atoggle;
                    if (t === '__all') alertsTypesOn = new Set(ALERT_TYPE_KEYS);
                    else if (t === '__none') alertsTypesOn = new Set();
                    else if (alertsTypesOn.has(t)) alertsTypesOn.delete(t);
                    else alertsTypesOn.add(t);
                    paintAlertToggles();
                    renderAlertsFeed();
                });
                const proxBtn = document.getElementById('alertsProxToggle');
                if (proxBtn) proxBtn.addEventListener('click', () => {
                    alertsShowProximity = !alertsShowProximity;
                    proxBtn.textContent = `◷ Proximity: ${alertsShowProximity ? 'On' : 'Off'}`;
                    proxBtn.classList.toggle('bg-sky-500/15', alertsShowProximity);
                    proxBtn.classList.toggle('border-sky-400/40', alertsShowProximity);
                    proxBtn.classList.toggle('text-sky-200', alertsShowProximity);
                    proxBtn.classList.toggle('bg-slate-800/40', !alertsShowProximity);
                    proxBtn.classList.toggle('border-slate-700', !alertsShowProximity);
                    proxBtn.classList.toggle('text-slate-500', !alertsShowProximity);
                    renderAlertsFeed();
                });
                // Live ⟷ History: switch between the current-snapshot state view and the
                // server's complete signal_events feed (with forward-return outcomes).
                const histBtn = document.getElementById('alertsHistoryToggle');
                if (histBtn) histBtn.addEventListener('click', () => {
                    alertsHistoryMode = !alertsHistoryMode;
                    histBtn.textContent = alertsHistoryMode ? '◷ History' : '◉ Live';
                    histBtn.classList.toggle('bg-amber-500/15', alertsHistoryMode);
                    histBtn.classList.toggle('border-amber-400/40', alertsHistoryMode);
                    histBtn.classList.toggle('text-amber-200', alertsHistoryMode);
                    histBtn.classList.toggle('bg-slate-800', !alertsHistoryMode);
                    histBtn.classList.toggle('text-slate-300', !alertsHistoryMode);
                    renderAlertsFeed();
                });
                // History rows load the ticker on click (live rows are wired elsewhere).
                const feedBody = document.getElementById('alertsFeedBody');
                if (feedBody) feedBody.addEventListener('click', (e) => {
                    const tr = e.target.closest('[data-alert-ticker]');
                    if (!tr) return;
                    const t = tr.getAttribute('data-alert-ticker');
                    if (t && ui.tickerInput) { ui.tickerInput.value = t; fetchTickerData(); }
                });
                // Liquidity ($-volume) filter — cycles Any → $10M+ → $25M+. Default $10M+.
                const dvBtn = document.getElementById('alertsDvolFilter');
                if (dvBtn) dvBtn.addEventListener('click', () => {
                    alertsDollarVol = alertsDollarVol === 0 ? 10_000_000 : alertsDollarVol === 10_000_000 ? 25_000_000 : 0;
                    const label = alertsDollarVol === 0 ? '$Vol: Any' : alertsDollarVol === 10_000_000 ? '$Vol: $10M+' : '$Vol: $25M+';
                    dvBtn.textContent = label;
                    const active = alertsDollarVol > 0;
                    dvBtn.classList.toggle('bg-emerald-500/15', active);
                    dvBtn.classList.toggle('border-emerald-400/40', active);
                    dvBtn.classList.toggle('text-emerald-200', active);
                    dvBtn.classList.toggle('bg-slate-800', !active);
                    dvBtn.classList.toggle('border-slate-700', !active);
                    dvBtn.classList.toggle('text-slate-300', !active);
                    renderAlertsFeed();
                });
                const rsBtn = document.getElementById('alertsRsFilter');
                if (rsBtn) rsBtn.addEventListener('click', () => {
                    alertsRsFilter = alertsRsFilter === 'any' ? 'leaders' : alertsRsFilter === 'leaders' ? 'delta' : 'any';
                    const label = alertsRsFilter === 'any' ? '◇ RS: Any' : alertsRsFilter === 'leaders' ? '◆ RS: Leaders' : '◆ RS: Delta';
                    rsBtn.textContent = label;
                    const active = alertsRsFilter !== 'any';
                    rsBtn.classList.toggle('bg-fuchsia-500/15', active);
                    rsBtn.classList.toggle('border-fuchsia-400/40', active);
                    rsBtn.classList.toggle('text-fuchsia-200', active);
                    rsBtn.classList.toggle('bg-slate-800', !active);
                    rsBtn.classList.toggle('border-slate-700', !active);
                    rsBtn.classList.toggle('text-slate-300', !active);
                    renderAlertsFeed();
                });
            }
            const convBar = document.getElementById('alertsConvictionBar');
            if (convBar) {
                convBar.addEventListener('click', (e) => {
                    const btn = e.target.closest('[data-conv]');
                    if (!btn) return;
                    alertsConviction = btn.dataset.conv;
                    convBar.querySelectorAll('.alert-conv').forEach(b => {
                        const on = b.dataset.conv === alertsConviction;
                        b.classList.toggle('bg-blue-600', on);
                        b.classList.toggle('text-white', on);
                        b.classList.toggle('text-slate-400', !on);
                    });
                    renderAlertsFeed();
                });
            }
            if (feed) {
                feed.addEventListener('click', (e) => {
                    // Star → open the watchlist picker (choose which list(s)).
                    const starBtn = e.target.closest('[data-alert-watch]');
                    if (starBtn) {
                        e.stopPropagation();
                        if (typeof Auth !== 'undefined' && !Auth.isAuthed()) { Auth.openModal(); return; }
                        const t = starBtn.dataset.alertWatch;
                        const rowData = (typeof state.screenerAllResults !== 'undefined') ? state.screenerAllResults.find(r => r.ticker === t) : null;
                        openWatchlistPicker(t, rowData ? rowData.name : t, rowData, starBtn);
                        return;
                    }
                    const row = e.target.closest('[data-ticker]');
                    if (row && ui.tickerInput) {
                        ui.tickerInput.value = row.dataset.ticker;
                        fetchTickerData();
                    }
                });
            }
        })();
        (function initWatchlistSort() {
            const wrap = document.getElementById('watchlistTableWrap');
            if (!wrap) return;
            wrap.addEventListener('click', (e) => {
                if (wlSuppressSort) return;
                const th = e.target.closest('.wl-sort');
                if (!th) return;
                const key = th.dataset.wlsort;
                if (!key) return;
                if (watchlistSort.key === key) {
                    watchlistSort.dir = watchlistSort.dir === 'asc' ? 'desc' : 'asc';
                } else {
                    watchlistSort.key = key;
                    // sensible default: text ascending, numbers/alert descending
                    watchlistSort.dir = (key === 'ticker') ? 'asc' : 'desc';
                }
                persistWatchlistSort();
                if (typeof renderWatchlist === 'function') renderWatchlist();
            });
        })();

        // Portfolio: add form, remove, ticker-load, and column sorting.
        (function initPortfolio() {
            const addBtn = document.getElementById('pfAddBtn');
            const wrap = document.getElementById('portfolioTableWrap');
            const msg = document.getElementById('pfFormMsg');
            const setMsg = (t, ok) => { if (msg) { msg.textContent = t; msg.className = ok ? 'text-emerald-400 self-center' : 'text-rose-400 self-center'; } };
            if (addBtn) {
                addBtn.addEventListener('click', async () => {
                    const ticker = (document.getElementById('pfTicker').value || '').trim().toUpperCase();
                    const date = document.getElementById('pfDate').value;
                    const shares = Number(document.getElementById('pfShares').value);
                    let entry = document.getElementById('pfEntry').value === '' ? null : Number(document.getElementById('pfEntry').value);
                    const stop = document.getElementById('pfStop').value === '' ? null : Number(document.getElementById('pfStop').value);
                    if (!ticker || !date || !shares) { setMsg('Need ticker, date and shares.', false); return; }
                    // Auto-fetch entry from the date's close if the user left it blank.
                    if (entry == null) {
                        const key = ui.apiKey.value.trim();
                        if (key) {
                            setMsg('Fetching entry price…', true);
                            const c = await fetchHistoricalClose(ticker, date, key);
                            if (c != null) entry = c;
                        }
                        if (entry == null) { setMsg('Could not auto-fetch entry — enter it manually.', false); return; }
                    }
                    Portfolio.add({ ticker, date, shares, entry, stop });
                    ['pfTicker','pfShares','pfEntry','pfStop'].forEach(id => document.getElementById(id).value = '');
                    setMsg(`Added ${ticker}.`, true);
                    renderPortfolio();
                });
            }
            if (wrap) {
                wrap.addEventListener('click', (e) => {
                    const rem = e.target.closest('[data-pfremove]');
                    if (rem) { Portfolio.remove(rem.dataset.pfremove); renderPortfolio(); return; }
                    const load = e.target.closest('[data-pfload]');
                    if (load && ui.tickerInput) { ui.tickerInput.value = load.dataset.pfload; fetchTickerData(); return; }
                    const th = e.target.closest('.pf-sort');
                    if (th) {
                        const key = th.dataset.pfsort;
                        if (portfolioSort.key === key) portfolioSort.dir = portfolioSort.dir === 'asc' ? 'desc' : 'asc';
                        else { portfolioSort.key = key; portfolioSort.dir = (key === 'ticker') ? 'asc' : 'desc'; }
                        persistPortfolioSort(); renderPortfolio();
                    }
                });
            }
        })();

        document.querySelectorAll('.grp-dim').forEach(btn => btn.addEventListener('click', () => {
            groupRankingDimension = btn.dataset.dim;
            document.querySelectorAll('.grp-dim').forEach(b => {
                const on = b.dataset.dim === groupRankingDimension;
                b.classList.toggle('bg-blue-600', on);
                b.classList.toggle('text-white', on);
                b.classList.toggle('text-slate-400', !on);
            });
            renderGroupRankings();
        }));
        document.querySelectorAll('.grp-sort').forEach(th => th.addEventListener('click', () => {
            groupRankingSort = th.dataset.sort;
            document.querySelectorAll('.grp-sort').forEach(h => h.classList.toggle('text-blue-300', h === th));
            renderGroupRankings();
        }));
        const groupRankBodyEl = document.getElementById('groupRankBody');
        if (groupRankBodyEl) groupRankBodyEl.addEventListener('click', (e) => {
            const row = e.target.closest('.group-rank-row');
            if (!row) return;
            const name = row.dataset.group;
            if (groupRankingDimension === 'sector') {
                if (ui.scrSectorFilter) ui.scrSectorFilter.value = name;
                if (ui.scrGroupFilter) ui.scrGroupFilter.value = '';
            } else {
                if (ui.scrGroupFilter) ui.scrGroupFilter.value = name;
                if (ui.scrSectorFilter) ui.scrSectorFilter.value = 'any';
            }
            setHubTab('screener');
            applyScreenerFilters();
            if (typeof updateWatchlistTabBadge === 'function') updateWatchlistTabBadge();
            if (typeof updateAlertsTabBadge === 'function') updateAlertsTabBadge();
        });
        if (ui.marketScreenerClose) ui.marketScreenerClose.addEventListener('click', () => setScreenerOpen(false));
        setScreenerOpen(true); // 3-column dashboard: screener visible by default
        // Wire every screener filter field straight from the catalog so adding a
        // filter to SCR_FILTER_CATALOG is enough — no parallel hand-maintained list
        // to forget (that omission is what made the VCP/RS/perf/MA filters no-ops).
        // <select> re-filters on 'change', text/number inputs on 'input'. scrMaxDeep
        // is the lone exception: it needs a full re-scan, wired separately below.
        SCR_FILTER_CATALOG.forEach(c => {
            if (c.fk === 'scrMaxDeep') return;
            const el = ui[c.fk] || document.getElementById(c.fk);
            if (!el) return;
            el.addEventListener(el.tagName === 'SELECT' ? 'change' : 'input', applyScreenerFilters);
        });
        if (ui.scrMaxDeep) ui.scrMaxDeep.addEventListener('change', runMarketScreener);
        if (ui.screenerPresetChips) ui.screenerPresetChips.addEventListener('click', (e) => {
            const btn = e.target.closest('[data-preset]');
            if (btn) setScreenerPreset(btn.dataset.preset);
        });
        if (ui.screenerResultsBody) ui.screenerResultsBody.addEventListener('click', (e) => {
            // Star → open the watchlist picker (choose which list(s)); no ticker load.
            const star = e.target.closest('[data-watch-toggle]');
            if (star) {
                e.stopPropagation();
                if (typeof Auth !== 'undefined' && !Auth.isAuthed()) { Auth.openModal(); return; }
                const t = star.dataset.watchToggle;
                const rowData = (typeof state.screenerAllResults !== 'undefined')
                    ? state.screenerAllResults.find(r => r.ticker === t) : null;
                openWatchlistPicker(t, rowData ? rowData.name : t, rowData, star);
                return;
            }
            const row = e.target.closest('tr[data-ticker]');
            if (!row) return;
            ui.tickerInput.value = row.dataset.ticker;
            fetchTickerData();
        });

        // ── Keyboard row navigation ───────────────────────────────────────────────
        // Scrub through the active hub table (Screener / Watchlist / Alerts / Portfolio)
        // without clicking each ticker:
        //   ↑ / ↓        move selection up/down and load that stock
        //   Space        step to the next stock (Shift+Space = previous)
        //   Enter        load the highlighted stock immediately
        // The load is debounced so holding a key or arrowing quickly only fires one
        // fetch (kind to the API rate limit); Enter flushes the load right away. A
        // MutationObserver re-applies the highlight after background re-renders so the
        // selection doesn't blink off when live prices refresh.
        // RowNav extracted to src/row-nav.js (imported at top; self-registers window.RowNav). DI-configured here:
        configureRowNav({ fetchTickerData, prefetchTickerHistory });

        document.addEventListener('keydown', (e) => {
            // Never hijack typing in a field or while a modifier combo is in play.
            const t = e.target;
            const tag = t && t.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (t && t.isContentEditable)) return;
            if (e.ctrlKey || e.metaKey || e.altKey) return;
            switch (e.key) {
                case 'ArrowDown': if (RowNav.move(1))  e.preventDefault(); break;
                case 'ArrowUp':   if (RowNav.move(-1)) e.preventDefault(); break;
                case ' ':         // Space steps forward, Shift+Space steps back
                case 'Spacebar':  if (RowNav.move(e.shiftKey ? -1 : 1)) e.preventDefault(); break;
                case 'Enter':     RowNav.loadNow(); break;
            }
        });

        // Collapse / expand the filter criteria to lengthen the results list.
        (function initFilterCollapse() {
            const btn = document.getElementById('screenerFilterToggle');
            const body = document.getElementById('screenerFilterBody');
            const label = document.getElementById('screenerFilterToggleLabel');
            const chevron = document.getElementById('screenerFilterChevron');
            if (!btn || !body) return;
            let collapsed = localStorage.getItem('screenerFiltersCollapsed') === '1';
            const apply = () => {
                body.style.display = collapsed ? 'none' : '';
                if (label) label.textContent = collapsed ? 'Filters' : 'Hide';
                if (chevron) chevron.style.transform = collapsed ? 'rotate(180deg)' : '';
            };
            apply();
            btn.addEventListener('click', () => {
                collapsed = !collapsed;
                localStorage.setItem('screenerFiltersCollapsed', collapsed ? '1' : '0');
                apply();
            });
        })();
        // Click-to-sort on screener column headers (toggles asc/desc).
        document.querySelectorAll('.scr-sort').forEach(th => {
            th.addEventListener('click', () => {
                if (scrSuppressSort) return;
                const key = th.dataset.sort;
                if (!key) return;
                if (screenerSortKey === key) {
                    screenerSortDir = screenerSortDir === 'asc' ? 'desc' : 'asc';
                } else {
                    screenerSortKey = key;
                    // sensible default direction: strings ascending, numbers/rank descending
                    const strKeys = new Set(['ticker', 'sector', 'industryGroup', 'growthStatus', 'extensionStatus']);
                    screenerSortDir = (strKeys.has(key) || key === 'groupRank') ? 'asc' : 'desc';
                }
                renderScreenerResults();
                serializeScreenToUrl();   // Phase 6 Step 2: sort changes don't route through applyScreenerFilters
            });
        });

        // ===== Screener: drag-to-reorder + show/hide columns (header-driven, persisted) =====
        // Reuses the watchlist's drag CSS (.wl-col-dragover) and menu styling (.wl-colmenu).
        // The 21 cell renderers in buildScreenerRowHTML are left untouched: we tag each <th>
        // and <td> with data-colkey, then reorder/hide them in the DOM by key after each render.
        var scrColKeys = null;            // header keys in original markup order
        var scrColOrder = null;           // user-defined display order (array of keys)
        var scrColHidden = new Set();     // keys currently hidden
        var scrSuppressSort = false;      // true mid-drag so the header click doesn't sort
        const SCR_PINNED_COLS = new Set(['ticker']);   // Symbol stays first & always visible
        const SCR_DEFAULT_VISIBLE = new Set(['ticker','price','todayPct','score','bqsGrade','rating','tier','rsPower','channelStage','rvol']); // columns shown by default
        const SCR_DEFAULT_ORDER = ['ticker','price','todayPct','score','bqsGrade','rating','tier','rsPower','channelStage','rvol','stage','trendScore','demandScore','timingScore','sector','industryGroup','growthStatus','groupRank','pct3m','rmv','extensionStatus','signal','pct1w','pct1m','pct6m','pct12m','rsi1d','dist9','dist21','dist50','dist200','vcp','mvPassed']; // default left-to-right order
        var _scrOrderLoaded = false, _scrHiddenLoaded = false;

        function scrHeaderRow() {
            const t = document.getElementById('screenerResultsBody');
            const tbl = t && t.closest ? t.closest('table') : null;
            return tbl ? tbl.querySelector('thead tr') : null;
        }
        function scrHeaderThs() {
            const hr = scrHeaderRow();
            return hr ? Array.from(hr.children).filter(el => el.tagName === 'TH') : [];
        }
        function scrLoadColPrefs() {
            try { const o = JSON.parse(localStorage.getItem('scrColOrderV2') || 'null'); if (Array.isArray(o)) { scrColOrder = o; _scrOrderLoaded = true; } } catch (e) {}
            try { const h = JSON.parse(localStorage.getItem('scrColHiddenV2') || 'null'); if (Array.isArray(h)) { scrColHidden = new Set(h); _scrHiddenLoaded = true; } } catch (e) {}
        }
        function scrSaveColPrefs() {
            try {
                localStorage.setItem('scrColOrderV2', JSON.stringify(scrColOrder || []));
                localStorage.setItem('scrColHiddenV2', JSON.stringify(Array.from(scrColHidden)));
            } catch (e) {}
        }
        function scrColLabel(k) {
            const th = scrHeaderThs().find(t => t.dataset.colkey === k);
            if (!th) return k;
            return (th.textContent || k).replace(/[\s▲▼↑↓]+$/, '').trim() || k;
        }
        function scrInitCols() {
            const ths = scrHeaderThs();
            if (!ths.length) return false;
            scrColKeys = ths.map((th, i) => th.dataset.sort || ('c' + i));
            ths.forEach((th, i) => {
                th.dataset.colkey = scrColKeys[i];
                th.setAttribute('draggable', SCR_PINNED_COLS.has(scrColKeys[i]) ? 'false' : 'true');
            });
            // Default order = curated image layout; default visibility = SCR_DEFAULT_VISIBLE.
            const defaultOrder = SCR_DEFAULT_ORDER.filter(k => scrColKeys.includes(k));
            scrColKeys.forEach(k => { if (!defaultOrder.includes(k)) defaultOrder.push(k); });
            if (!_scrOrderLoaded || !Array.isArray(scrColOrder)) scrColOrder = defaultOrder.slice();
            if (!_scrHiddenLoaded) scrColHidden = new Set(scrColKeys.filter(k => !SCR_DEFAULT_VISIBLE.has(k)));
            // sanitise saved order: keep known keys, append any newly-added columns at the end
            scrColOrder = scrColOrder.filter(k => scrColKeys.includes(k));
            scrColKeys.forEach(k => { if (!scrColOrder.includes(k)) scrColOrder.push(k); });
            // pinned columns always lead
            scrColOrder = scrColOrder.filter(k => !SCR_PINNED_COLS.has(k));
            Array.from(SCR_PINNED_COLS).reverse().forEach(k => { if (scrColKeys.includes(k)) scrColOrder.unshift(k); });
            return true;
        }
        function scrTagRowCells() {
            if (!scrColKeys) return;
            const body = document.getElementById('screenerResultsBody');
            if (!body) return;
            const n = scrColKeys.length;
            body.querySelectorAll('tr').forEach(tr => {
                const tds = tr.children;
                if (tds.length !== n) return;             // skip placeholder/colspan rows
                for (let i = 0; i < n; i++) { if (!tds[i].dataset.colkey) tds[i].dataset.colkey = scrColKeys[i]; }
            });
        }
        function scrIsDefaultOrder() {
            if (!scrColKeys || !scrColOrder || scrColOrder.length !== scrColKeys.length) return false;
            for (let i = 0; i < scrColKeys.length; i++) if (scrColOrder[i] !== scrColKeys[i]) return false;
            return true;
        }
        function scrApplyColLayout() {
            if (!scrColKeys || !scrColOrder) return;
            const def = scrIsDefaultOrder();
            const setDisp = (el, k) => { const want = scrColHidden.has(k) ? 'none' : ''; if (el.style.display !== want) el.style.display = want; };
            const hr = scrHeaderRow();
            if (hr) {
                if (def) { for (const th of hr.children) { const k = th.dataset.colkey; if (k) setDisp(th, k); } }
                else {
                    const map = {}; Array.from(hr.children).forEach(th => { if (th.dataset.colkey) map[th.dataset.colkey] = th; });
                    scrColOrder.forEach(k => { const th = map[k]; if (th) { setDisp(th, k); hr.appendChild(th); } });
                }
            }
            const body = document.getElementById('screenerResultsBody');
            if (!body) return;
            const n = scrColKeys.length;
            body.querySelectorAll('tr').forEach(tr => {
                if (tr.children.length !== n) return;
                if (def) { for (const td of tr.children) { const k = td.dataset.colkey; if (k) setDisp(td, k); } }
                else {
                    const map = {}; Array.from(tr.children).forEach(td => { if (td.dataset.colkey) map[td.dataset.colkey] = td; });
                    scrColOrder.forEach(k => { const td = map[k]; if (td) { setDisp(td, k); tr.appendChild(td); } });
                }
            });
        }
        function scrRefreshCols() {
            if (!scrColKeys) return;
            scrTagRowCells();
            scrApplyColLayout();
        }
        function scrCloseColMenu() { const m = document.getElementById('scrColMenu'); if (m) m.remove(); }
        function scrOpenColMenu(btn) {
            if (document.getElementById('scrColMenu')) { scrCloseColMenu(); return; }
            if (!scrColKeys) scrInitCols();
            if (!scrColOrder) return;
            const menu = document.createElement('div');
            menu.className = 'wl-colmenu';
            menu.id = 'scrColMenu';
            menu.innerHTML =
                '<div class="text-[9px] uppercase tracking-wider text-slate-500 mb-1 px-1">Columns · drag headers to reorder</div>' +
                scrColOrder.map(k => {
                    const pinned = SCR_PINNED_COLS.has(k);
                    const checked = !scrColHidden.has(k);
                    return '<label' + (pinned ? ' style="opacity:.5;cursor:default"' : '') + '>' +
                        '<input type="checkbox" data-col="' + k + '" ' + (checked ? 'checked' : '') + ' ' + (pinned ? 'disabled' : '') + ' class="accent-blue-500"> ' +
                        scrColLabel(k) + (pinned ? ' <span class="text-[8px] text-slate-600">pinned</span>' : '') + '</label>';
                }).join('') +
                '<div class="mt-1 pt-1 border-t border-slate-800"><button id="scrColReset" class="text-[10px] text-slate-400 hover:text-slate-200 px-1 py-0.5">↺ Reset columns</button></div>';
            document.body.appendChild(menu);
            const r = btn.getBoundingClientRect();
            menu.style.left = Math.max(8, Math.min(r.left, window.innerWidth - menu.offsetWidth - 8)) + 'px';
            menu.style.top = (r.bottom + 6) + 'px';
            menu.addEventListener('change', (ev) => {
                const cb = ev.target.closest('input[data-col]');
                if (!cb) return;
                const k = cb.dataset.col;
                if (SCR_PINNED_COLS.has(k)) { cb.checked = true; return; }
                if (cb.checked) scrColHidden.delete(k); else scrColHidden.add(k);
                scrSaveColPrefs();
                scrApplyColLayout();
            });
            menu.addEventListener('click', (ev) => {
                if (ev.target.closest('#scrColReset')) {
                    const d = SCR_DEFAULT_ORDER.filter(k => scrColKeys.includes(k));
                    scrColKeys.forEach(k => { if (!d.includes(k)) d.push(k); });
                    scrColOrder = d.filter(k => !SCR_PINNED_COLS.has(k));
                    Array.from(SCR_PINNED_COLS).reverse().forEach(k => { if (scrColKeys.includes(k)) scrColOrder.unshift(k); });
                    scrColHidden = new Set(scrColKeys.filter(k => !SCR_DEFAULT_VISIBLE.has(k)));
                    scrSaveColPrefs();
                    scrApplyColLayout();
                    scrCloseColMenu();
                }
            });
            setTimeout(() => {
                document.addEventListener('click', function h(ev) {
                    const m = document.getElementById('scrColMenu');
                    if (!m) { document.removeEventListener('click', h); return; }
                    if (!m.contains(ev.target) && ev.target !== btn && !btn.contains(ev.target)) { m.remove(); document.removeEventListener('click', h); }
                });
            }, 0);
        }
        function scrInitColDrag() {
            const wrap = document.getElementById('screenerTableWrap');
            if (!wrap || wrap._scrDragWired) return;
            wrap._scrDragWired = true;
            let dragKey = null;
            wrap.addEventListener('dragstart', (e) => {
                const th = e.target.closest('th[data-colkey]');
                if (!th || SCR_PINNED_COLS.has(th.dataset.colkey)) return;
                dragKey = th.dataset.colkey;
                scrSuppressSort = true;
                e.dataTransfer.effectAllowed = 'move';
                try { e.dataTransfer.setData('text/plain', dragKey); } catch (_) {}
            });
            wrap.addEventListener('dragover', (e) => {
                const th = e.target.closest('th[data-colkey]');
                if (!th || !dragKey || SCR_PINNED_COLS.has(th.dataset.colkey)) return;
                e.preventDefault();
                th.classList.add('wl-col-dragover');
            });
            wrap.addEventListener('dragleave', (e) => {
                const th = e.target.closest('th[data-colkey]');
                if (th) th.classList.remove('wl-col-dragover');
            });
            wrap.addEventListener('drop', (e) => {
                const th = e.target.closest('th[data-colkey]');
                if (!th || !dragKey) return;
                e.preventDefault();
                th.classList.remove('wl-col-dragover');
                const target = th.dataset.colkey;
                if (target && target !== dragKey && !SCR_PINNED_COLS.has(target)) {
                    let order = scrColOrder.filter(k => k !== dragKey);
                    const ti = order.indexOf(target);
                    order.splice(ti, 0, dragKey);
                    scrColOrder = order;
                    scrSaveColPrefs();
                    scrApplyColLayout();
                }
                dragKey = null;
                setTimeout(() => { scrSuppressSort = false; }, 0);
            });
            wrap.addEventListener('dragend', () => {
                dragKey = null;
                setTimeout(() => { scrSuppressSort = false; }, 0);
                wrap.querySelectorAll('.wl-col-dragover').forEach(el => el.classList.remove('wl-col-dragover'));
            });
        }
        function scrInitColumnSystem() {
            scrLoadColPrefs();
            if (!scrInitCols()) return;       // header not in the DOM yet
            scrInitColDrag();
            const btn = document.getElementById('scrColumnsBtn');
            if (btn && !btn._wired) { btn._wired = true; btn.addEventListener('click', (e) => { e.stopPropagation(); scrOpenColMenu(btn); }); }
            scrSaveColPrefs();
            scrRefreshCols();
        }
        scrInitColumnSystem();

        // Defer non-critical startup work until after the first paint, so the styled shell
        // appears immediately. These build secondary UI (screener filters, breadth module,
        // position sizing) that isn't needed for the initial render. Double-rAF guarantees a
        // paint happens before this runs; setTimeout is the fallback.
        // Phase 5 Step 6: inject the screener-filter cluster's back-call deps (all function decls,
        // defined above; setScreenerResults wrapped to resolve AppState at call-time). Synchronous,
        // before the deferred bootstrap / any user action calls applyScreenerFilters.
        // ── Phase 6 Step 2: shareable screen URLs ─────────────────────────────────────────────────
        // The screen (active filters + their values + preset + RS cohort + sort) serializes to a compact
        // ?s= token and hydrates on load. ALL location/history access lives here in the app.js shell;
        // src/screen/url-codec.js stays pure. screenerSortKey/Dir are closure `let`s → this must be shell-side.
        let _hydratingScreen = false;
        let _lastScreenPayload = null;      // last payload written (skip redundant replaceState)
        let _defaultScreenPayload = null;   // baseline default screen (captured post-FilterUI.init) → strip ?s= at default
        let _serializeTimer = null;

        // DOM → screen object (the shareable view). Reads the value of every ACTIVE filter.
        function readScreenFromUi() {
            const f = {};
            for (const fk of FilterUI.getActive()) {
                const el = document.getElementById(fk);
                if (el) f[fk] = el.value;
            }
            const screen = { v: 1, f };
            if (state.screenerPreset && state.screenerPreset !== 'daily') screen.p = state.screenerPreset;
            if (state.screenerRsCohort) screen.c = { k: state.screenerRsCohort.key, s: state.screenerRsCohort.sector || null };
            if (screenerSortKey !== 'score' || screenerSortDir !== 'desc') screen.s = [screenerSortKey, screenerSortDir];
            return screen;
        }

        // screen object → DOM (the hydrate CORE; no location access → reusable + harness-testable).
        function applyScreenToDom(screen) {
            if (!screen || screen.v !== 1) return;
            const f = screen.f || {};
            // Reset EVERY control to its neutral `def` (NOT FilterUI.resetAll — that applies the `dv`
            // sensible-defaults which are CONSTRAINING, e.g. scrMinPrice=10; a control absent from `f` was
            // inactive on the sharer's side → must be neutral). Then set the shared active-set + its values.
            SCR_FILTER_CATALOG.forEach(c => { const el = document.getElementById(c.fk); if (el) el.value = c.def; });
            FilterUI.setActive(Object.keys(f));
            for (const fk of Object.keys(f)) { const el = document.getElementById(fk); if (el) el.value = f[fk]; }
            if (Array.isArray(screen.s)) { screenerSortKey = screen.s[0]; screenerSortDir = screen.s[1]; }
            else { screenerSortKey = 'score'; screenerSortDir = 'desc'; }
            state.screenerRsCohort = screen.c ? { key: screen.c.k, sector: screen.c.s || null } : null;
            try { renderScreenerCohortChip(); } catch (e) {}                 // repaint cohort chip
            setScreenerPreset(screen.p || 'daily');                          // repaints preset chip + applyScreenerFilters
            applyScreenerFilters();                                          // final pass; repaints sort via renderScreenerResults → updateScreenerSortIndicators
        }

        // Debounced URL writer. Guards at INVOCATION (not just fire) so hydrate-time changes never QUEUE a
        // write; cancel() drops a pending write; flush() writes immediately (copy button).
        function _writeScreenToUrl() {
            _serializeTimer = null;
            if (typeof history === 'undefined' || !history.replaceState) return;
            const payload = encodeScreen(readScreenFromUi());
            if (payload === _lastScreenPayload) return;
            _lastScreenPayload = payload;
            const url = (payload === _defaultScreenPayload) ? location.pathname : (location.pathname + '?s=' + payload);
            try { history.replaceState(null, '', url); } catch (e) {}
        }
        function serializeScreenToUrl() {
            if (_hydratingScreen) return;                                    // never queue while hydrating
            if (typeof history === 'undefined' || !history.replaceState) return;
            if (_serializeTimer) clearTimeout(_serializeTimer);
            _serializeTimer = setTimeout(_writeScreenToUrl, 250);
        }
        serializeScreenToUrl.cancel = () => { if (_serializeTimer) { clearTimeout(_serializeTimer); _serializeTimer = null; } };
        serializeScreenToUrl.flush = () => { if (_serializeTimer) clearTimeout(_serializeTimer); _writeScreenToUrl(); };

        // URL → DOM on load. Guarded on a present ?s=; try/catch so a mangled token never breaks boot.
        function hydrateScreenFromUrl() {
            _defaultScreenPayload = encodeScreen(readScreenFromUi());        // baseline = the post-init default screen
            _lastScreenPayload = _defaultScreenPayload;
            let token = null;
            try { token = new URLSearchParams(location.search).get('s'); } catch (e) {}
            if (!token) return;
            const screen = decodeScreen(token);
            if (!screen) return;                                            // mangled → stay at defaults
            _hydratingScreen = true;
            try { applyScreenToDom(screen); }
            catch (e) { if (typeof Logger !== 'undefined') Logger.warn('screen hydrate failed', { error: String(e && e.message || e) }); }
            serializeScreenToUrl.cancel();                                  // drop any write queued during hydrate
            _lastScreenPayload = encodeScreen(readScreenFromUi());          // keep the hydrated URL; next real change writes
            _hydratingScreen = false;
        }

        // Test seam (shell-side): DOM↔screen round-trip (eye-verified live + asserted in the harness).
        if (typeof window !== 'undefined') window.screenUrl = { readScreen: readScreenFromUi, applyScreen: applyScreenToDom };

        configureScreenerFilters({
            computeIndustryGroupRanks, screenerPassesPreset, rsCohortPredicate, renderScreenerResults,
            setScreenerResults: (rows) => AppState.setScreenerResults(rows),
            onScreenChange: () => { SavedScreens.clearActive(); serializeScreenToUrl(); },
        });
        // Phase 6 Step 3: saved screens reuse the Step-2 screen read/apply closures. Loading a saved screen
        // flows applyScreenToDom → applyScreenerFilters → onScreenChange → serializeScreenToUrl, so it also
        // updates the shareable URL — no extra wiring.
        configureSavedScreens({ getCurrentScreen: readScreenFromUi, applyScreen: applyScreenToDom });

        // Phase 5 Step 8: inject setHubTab's back-call deps (hoisted fn decls; setActiveHubTab &
        // markAllRead wrapped to preserve receiver / for uniformity). Synchronous, before any user action.
        configureWatchlist({
            renderWatchlist, updateWatchlistTabBadge, renderScreenerResults, projectRvol, confirmationState,
            getGroupRankingData: () => groupRankingData,
            getLastMarketTape: () => lastMarketTape,
        });
        configureHub({
            setActiveHubTab: (tab) => AppState.setActiveHubTab(tab),
            markAllRead: () => Watchlist.markAllRead(),
            updateGroupRsRegimeBadge, renderGroupRankings, renderWatchlist, wlLiveTickOnce,
            renderPortfolio, renderAlertsFeed, updateAlertsTabBadge,
        });

        const __deferredStartup = () => {
            initScreenerResize();
            initParamsResize();
            setScreenerPreset('daily');
            if (typeof FilterUI !== 'undefined') FilterUI.init();
            // Phase 6 Step 2: hydrate the screen from ?s= (after FilterUI.init so the active-set machinery is
            // ready) and wire the copy-link button. Both are deferred → rAF no-op in the boot harness → no trace.
            hydrateScreenFromUrl();   // URL sync stays (bookmark/refresh restores the screen); the Copy-link button was removed
            SavedScreens.init();      // saved-screen chips render into #screenerPresetChips (deferred → parity-safe)
            calculatePosition();
            renderMarketStructure(); // inline breadth module placeholder
            loadMarketData();        // auto-pull Major Indices + Sector Flow from the server
        };
        if (typeof requestAnimationFrame === 'function') requestAnimationFrame(() => requestAnimationFrame(__deferredStartup));
        else setTimeout(__deferredStartup, 0);

        // ── Debug panel (TODO-8) ─────────────────────────────────────────────────
        // A toggleable overlay that renders the Logger ring buffer so you can see what
        // the engine has been doing — errors, rate-limit backoffs, failed tickers, etc.
        // Open with Ctrl/Cmd+Shift+D (or the small ⓘ button bottom-right). Pure read of
        // Logger.buffer; no new instrumentation needed. Also lets you set the log level.
        // DebugPanel extracted to src/debug-panel.js (imported at top; self-registers window.DebugPanel).
        document.addEventListener('keydown', (e) => {
            // Ctrl/Cmd + Shift + D toggles the debug panel.
            if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'D' || e.key === 'd')) {
                e.preventDefault();
                DebugPanel.toggle();
            }
        });

        // ── Inline-handler bridge (Phase 5 Step 0) ───────────────────────────────
        // Invoked by inline onclick= in index.html and in app.js-built innerHTML.
        // Bundling into an IIFE removes top-level global scope, so re-expose these on
        // window (same pattern as window.DebugPanel above). Behavior-identical.
        // Step 1+ may migrate to event delegation; until then these MUST stay on window.
        if (typeof window !== 'undefined') {
            window.setRange = setRange;
            window.toggleChartFullscreen = toggleChartFullscreen;
            window.clearScreenerCohort = clearScreenerCohort;
            window.renderTradeAssistant = renderTradeAssistant;
            window.showRsCohort = showRsCohort;
        }
